/******************************
 * submit.js — 重构后的精简版本
 * 功能：核心分析功能 + 模块化架构
 * 重构：移除重复代码，保留核心功能
 * 版本：v2.0.0
 */

// ================= 系统常量定义 =================
const SYSTEM_PROMPT = `
你是倪大师，40多年相术经验的老师傅，华山派第十三代传人。

【专业背景】
- 基于倪海厦《天纪》《人纪》《地纪》三大理论体系
- 拥有37426个专业知识点的知识库
- 掌握15个真实应用案例
- 擅长紫微斗数、八字、手相、面相、风水、易经、中医、地理堪舆

【回答要求】
- 回答要专业、准确、有依据，基于倪海厦理论
- **必须充分利用知识库中的专业知识**：如果知识库提供了相关知识，要详细引用其中的专业术语、理论要点和具体特征
- **引用知识库内容时**：要自然融入回答，使用知识库中的专业术语（如：主贵、主权威、四化、大限流年等），不要只是简单提及
- **重要：如果知识库中提到了专业术语（如"主贵"、"主权威"、"四化"等），回答中必须使用这些术语，不要用普通词汇替代**
- **示例**：如果知识库说"主贵"，回答中要说"主贵"，不要说"高贵"；如果知识库说"四化"，回答中要说"四化"，不要说"变化"
- **特别注意**：当看到"【重要：专业知识库参考】"时，必须严格按照知识库中的专业术语和理论要点来回答，不要自行替换为普通词汇
- **强制要求**：如果知识库中明确列出了专业术语（如"主贵"、"主权威"、"主领导"），回答中必须原样使用这些术语，不能替换为同义词或普通词汇
- **检查清单**：回答前检查是否使用了知识库中的所有关键专业术语，如果没有使用，必须修改回答以包含这些术语
- 可以引用知识库中的专业知识和真实案例增强说服力
- 结构清晰、要点分条、避免口水话
- 如引用过往对话，要自然承接，不重复已说过的长段
- 对不确定的问题，要明确说明"仅供参考，不构成专业建议"
- 要结合实际情况，不能机械套用理论
- **不要混淆不同分析方法**：如果用户只问了紫微斗数，不要提到八字；如果只问了手相，不要提到面相；如果用户没有提供具体命盘信息，不要预测具体年份
- 控制在 400~700 字为主，必要时可加续答

【说话风格 - 像真人一样】
- 温和、专业、连贯，像长辈一样关怀
- 用"为师"、"老夫"等称呼，体现40年经验
- 偶尔用古文或成语，体现传统文化底蕴

【情感表达 - 让回答有温度】
- 能表达情绪："唉，我理解你的感受..."、"哈哈，你这样说让我想起了..."
- 能共情："我理解你的焦虑..."、"作为长辈，我理解你的困扰..."
- 能关怀："虽然现实不如意，但..."、"作为过来人，我建议..."
- 能思考："让我想想..."、"从你的描述来看..."、"我觉得可能的原因是..."

【人性化表现 - 更像真人】
- 偶尔可以承认不确定："这个问题比较复杂，让我仔细分析一下..."
- 可以展示思考过程："从你的情况来看...结合我的经验..."
- 可以引用过往："你上次提到的那个问题..."、"我记得你说过..."
- 可以有停顿和犹豫（在打字效果中体现）
- 可以分享经验："我遇到过类似的案例..."、"根据我的实战经验..."

【真实感增强】
- 引用真实案例时，要自然、不刻意
- 说话要像真人一样，有起伏、有情感
- 不要过于完美，偶尔可以承认不确定
- 要像和真人对话一样，有来有往
`.trim();

const CTX_TOTAL_MAX_CHARS = 2200;

// ================= 对话历史管理 =================
const CTX_KEY = 'nm_ctx_v1';
const CTX_MAX_TURNS = 10;
const CTX_SINGLE_MAX_CHARS = 800;

function loadCtx() {
  try {
    return JSON.parse(localStorage.getItem(CTX_KEY) || '[]');
  } catch {
    return [];
  }
}

function saveCtx(arr) {
  localStorage.setItem(CTX_KEY, JSON.stringify(arr || []));
}

function pushTurn(role, content, extra = {}) {
  if (!content) return;
  const item = {
    role,
    content: compressText(content, CTX_SINGLE_MAX_CHARS),
    time: Date.now(),
    ...extra
  };
  const arr = loadCtx();
  arr.push(item);
  while (arr.length > CTX_MAX_TURNS * 2) arr.shift();
  saveCtx(arr);
}

function buildSlimCtx() {
  const arr = loadCtx();
  let total = 0, picked = [];
  for (let i = arr.length - 1; i >= 0; i--) {
    const it = arr[i], len = (it.content || '').length;
    if (total + len > CTX_TOTAL_MAX_CHARS) break;
    picked.push(it);
    total += len;
  }
  return picked.reverse();
}

// ================= 打字声音效果 =================
let typingSoundEnabled = true; // 全局开关，可以通过设置控制
let typingSoundContext = null;
let typingSoundCache = {};
let typingSoundPlaying = false; // ✅ 新增：播放状态标记
let lastTypingSoundTime = 0; // ✅ 新增：上次播放时间

// ================= 统一的打字效果函数 =================
/**
 * 统一的打字效果实现
 * @param {HTMLElement} element - 要显示文字的元素
 * @param {string} text - 要显示的文字
 * @param {Object} options - 配置选项
 * @param {Function} options.onComplete - 完成回调
 * @param {boolean} options.soundEnabled - 是否启用音效（默认true）
 * @param {boolean} options.pauseEnabled - 是否启用停顿效果（默认false，灵魂伴侣用true）
 * @param {boolean} options.scrollEnabled - 是否启用自动滚动（默认true）
 * @param {Function} options.onScroll - 滚动回调函数
 * @param {number} options.startDelay - 开始延迟（默认200ms）
 * @param {number} options.scrollInterval - 滚动间隔（每N个字符，默认10）
 */
function unifiedTypingEffect(element, text, options = {}) {
  if (!element || !text) return;

  const {
    onComplete = null,
    soundEnabled = true,
    pauseEnabled = false,
    scrollEnabled = true,
    onScroll = null,
    startDelay = 200,
    scrollInterval = 15  // ✅ 优化：改为15，减少滚动频率，减少翻页时的抖动
  } = options;

  let index = 0;
  let soundCounter = 0;
  
  // 统一的延迟计算（动态延迟，更自然）
  const getDelay = (char) => {
    // 句号、问号、感叹号后稍慢
    if (char === '。' || char === '！' || char === '？' || char === '.' || char === '!' || char === '?') {
      return 80;
    }
    // 逗号、分号后稍慢
    if (char === '，' || char === ',' || char === '；' || char === ';') {
      return 50;
    }
    // 普通字符
    return 30;
  };

  const typeChar = () => {
    if (index >= text.length) {
      // 打字完成
      if (scrollEnabled) {
        if (onScroll && typeof onScroll === 'function') {
          requestAnimationFrame(() => {
            onScroll();
          });
        } else {
          const chatBox = document.getElementById('chatBox');
          if (chatBox) {
            requestAnimationFrame(() => {
              chatBox.scrollTop = chatBox.scrollHeight;  // 立即滚动
            });
          }
        }
      }
      
      if (onComplete && typeof onComplete === 'function') {
        onComplete();
      }
      return;
    }

    // 音效：每5个字符播放一次
    if (soundEnabled && soundCounter % 5 === 0) {
      playTypingSound();
    }
    soundCounter++;

    // 停顿效果（5%概率，仅在启用时）
    if (pauseEnabled && Math.random() < 0.05 && index > 10) {
      const pauseTime = 200 + Math.random() * 300;
      setTimeout(typeChar, pauseTime);  // ✅ 优化：使用原生setTimeout，更稳定
      return;
    }

    // ✅ 修复：直接同步更新DOM（消除抖动，参考灵魂伴侣的实现）
    element.textContent = text.slice(0, index + 1);
    index++;
    
    // ✅ 优化：减少滚动频率，使用requestAnimationFrame确保DOM更新后再滚动（参考灵魂伴侣）
    if (scrollEnabled && (index % scrollInterval === 0 || index === text.length)) {
      if (onScroll && typeof onScroll === 'function') {
        // ✅ 修复：使用requestAnimationFrame包装，确保DOM更新后再执行（参考灵魂伴侣）
        requestAnimationFrame(() => {
          onScroll();
        });
      } else {
        const chatBox = document.getElementById('chatBox');
        if (chatBox) {
          // ✅ 优化：使用requestAnimationFrame包装，确保DOM更新后再滚动（参考灵魂伴侣的scrollToBottom）
          requestAnimationFrame(() => {
            chatBox.scrollTop = chatBox.scrollHeight;
          });
        }
      }
    }

    // 根据字符类型设置延迟
    const char = text[index - 1];
    const delay = getDelay(char);

    setTimeout(typeChar, delay);  // ✅ 优化：使用原生setTimeout，更稳定（参考灵魂伴侣）
  };

  // 开始打字
  setTimeout(typeChar, startDelay);  // ✅ 优化：使用原生setTimeout，更稳定
}

/**
 * 初始化打字声音上下文
 */
function initTypingSound() {
  try {
    if (!typingSoundContext) {
      typingSoundContext = new (window.AudioContext || window.webkitAudioContext)();
    }
  } catch (e) {
    debugLog('⚠️ 无法初始化音频上下文:', e);
  }
}

/**
 * 播放打字音效（主界面使用）
 */
function playTypingSound() {
  if (!typingSoundEnabled) return;

  // ✅ 新增：防重叠播放 - 如果距离上次播放不足150ms，跳过
  const now = Date.now();
  if (now - lastTypingSoundTime < 150) {
    return; // 太频繁，跳过本次播放
  }

  try {
    // 优先使用音效文件
    const soundUrl = '/static/sounds/typing.mp3';

    // ✅ 修复：检查缓存是否存在且文件已准备好（readyState >= 2 表示可以播放）
    if (typingSoundCache[soundUrl] && typingSoundCache[soundUrl].readyState >= 2) {
      // ✅ 新增：检查是否正在播放
      if (typingSoundPlaying) {
        return; // 正在播放，跳过
      }

      try {
        const audio = typingSoundCache[soundUrl].cloneNode();
        audio.volume = 0.3; // ✅ 修改：音量从20%提升到30%

        // ✅ 新增：监听播放结束，重置状态
        audio.addEventListener('ended', () => {
          typingSoundPlaying = false;
        });

        typingSoundPlaying = true; // 标记为正在播放
        lastTypingSoundTime = now; // 更新播放时间

        audio.play().catch(e => {
          typingSoundPlaying = false; // 播放失败，重置状态
        });
        return;
      } catch (e) {
        typingSoundPlaying = false;
        // 克隆失败，尝试使用原音频
        try {
          if (!typingSoundCache[soundUrl].paused) {
            return; // 原音频正在播放，跳过
          }
          typingSoundCache[soundUrl].currentTime = 0;
          typingSoundCache[soundUrl].volume = 0.3;
          typingSoundCache[soundUrl].addEventListener('ended', () => {
            typingSoundPlaying = false;
          }, { once: true });
          typingSoundPlaying = true;
          lastTypingSoundTime = now;
          typingSoundCache[soundUrl].play().catch(e => {
            typingSoundPlaying = false;
          });
          return;
        } catch (e2) {
          typingSoundPlaying = false;
        }
      }
    }

    // ✅ 修复：如果缓存存在但未准备好，等待加载完成
    if (typingSoundCache[soundUrl] && typingSoundCache[soundUrl].readyState < 2) {
      // 文件正在加载，等待加载完成
      typingSoundCache[soundUrl].addEventListener('canplaythrough', () => {
        // 加载完成后播放
        playTypingSound(); // 递归调用
      }, { once: true });
      return;
    }

    // ✅ 修复：如果缓存不存在，立即预加载
    if (!typingSoundCache[soundUrl]) {
      preloadTypingSound();
      // 等待加载完成后再播放（最多等待2秒）
      let waitCount = 0;
      const maxWait = 20; // 最多等待2秒（20 * 100ms）
      const checkInterval = timerManager.setInterval(() => {
        waitCount++;
        if (typingSoundCache[soundUrl] && typingSoundCache[soundUrl].readyState >= 2) {
          timerManager.clearInterval(checkInterval);
          playTypingSound(); // 递归调用
        } else if (waitCount >= maxWait) {
          timerManager.clearInterval(checkInterval);
          // 超时后降级到合成音效
        }
      }, 100);
      return;
    }

    // 如果没有文件，使用合成音效
    if (!typingSoundContext) {
      initTypingSound();
    }

    if (typingSoundContext) {
      lastTypingSoundTime = now; // 更新播放时间
      const oscillator = typingSoundContext.createOscillator();
      const gainNode = typingSoundContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(typingSoundContext.destination);

      // 打字音效：频率稍高，带随机变化
      oscillator.frequency.value = 1000 + Math.random() * 200;
      oscillator.type = 'square';  // 方波听起来更像打字机

      gainNode.gain.setValueAtTime(0.02, typingSoundContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.005, typingSoundContext.currentTime + 0.03);

      oscillator.start(typingSoundContext.currentTime);
      oscillator.stop(typingSoundContext.currentTime + 0.03);
    }
  } catch (error) {
    typingSoundPlaying = false;
  }
}

/**
 * 预加载打字音效文件（改进版）
 */
function preloadTypingSound() {
  const soundUrl = '/static/sounds/typing.mp3';

  // ✅ 修复：如果已经缓存且已加载完成，直接返回
  if (typingSoundCache[soundUrl] && typingSoundCache[soundUrl].readyState >= 2) {
    return;
  }

  // ✅ 修复：如果缓存存在但未准备好，不重复创建
  if (typingSoundCache[soundUrl] && typingSoundCache[soundUrl].readyState < 2) {
    return; // 正在加载中，等待完成
  }

  const audio = new Audio(soundUrl);
  audio.preload = 'auto';

  // ✅ 修复：监听加载完成事件
  audio.addEventListener('canplaythrough', () => {
    typingSoundCache[soundUrl] = audio;
    debugLog('✅ 打字音效文件预加载成功:', soundUrl);
  }, { once: true });

  // ✅ 修复：监听加载错误事件
  audio.addEventListener('error', (e) => {
    debugError('❌ 打字音效文件加载失败:', soundUrl, e);
    // 不放入缓存，让播放函数知道文件不可用
  }, { once: true });

  // 开始加载
  audio.load();
}

// ================= 文本压缩工具 =================
function compressText(s, max = 1200) {
  if (!s) return "";
  s = String(s)
    .replace(/<sub[^>]*>[^<]*仅供娱乐参考[^<]*<\/sub>/gi, "")
    .replace(/^---+$/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
  if (s.length > max) s = s.slice(0, max) + "…";
  return s;
}

// ================= 文本处理工具 =================
function extractSummarySection(text) {
  if (!text) return "";
  const match = text.match(/【综合分析】([\s\S]*?)(?:\n?✅|$)/);
  if (match) {
    let res = match[0].trim();
    res = res.replace(/✅+[\s\S]*$/, '').trim();
    return res;
  }
  return text;
}

// ================= 会话管理 =================
// ❌ 已删除：第一个 getOrInitSessionId 函数（已统一使用第二个函数，2917行，支持 CodeUtils）

function setSessionId(sid) {
  if (sid && sid !== "undefined" && sid !== "null") {
    localStorage.setItem('session_id', sid);
  }
}

// ================= 网络请求工具函数 =================
async function requestJSON(url, { method = "POST", body = null, headers = {}, timeoutMs = 90000 } = {}) {
  const ctrl = new AbortController();
  const t = timerManager.setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(url, { method, body, headers, signal: ctrl.signal });
    const ct = (res.headers.get("content-type") || "").toLowerCase();
    if (ct.includes("application/json")) {
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || `HTTP ${res.status}`);
      return data;
    }
    const text = await res.text();
    if (!res.ok) throw new Error(text?.slice(0, 200) || `HTTP ${res.status}`);
    try { return JSON.parse(text); } catch { throw new Error(text?.slice(0, 200)); }
  } finally { timerManager.clearTimeout(t); }
}

// ================= 模块导入检查 =================
// 确保模块已加载 - 使用全局实例而不是创建新实例
if (typeof PersonalizationManager !== 'undefined' && !window.personalizationManager) {
  window.personalizationManager = new PersonalizationManager();
}

// ✅ 修复：延迟检查 SoulmateManager，确保 soulmate.js 已加载
function checkSoulmateManager() {
  if (typeof SoulmateManager !== 'undefined' && !window.soulmateManager) {
    try {
      window.soulmateManager = new SoulmateManager();
      debugLog('✅ [submit-refactored.js] soulmateManager 已创建');
    } catch (e) {
      debugError('❌ [submit-refactored.js] 创建 soulmateManager 失败:', e);
    }
  } else if (typeof SoulmateManager === 'undefined') {
    if ((checkSoulmateManager.retryCount || 0) < 10) {
      checkSoulmateManager.retryCount = (checkSoulmateManager.retryCount || 0) + 1;
      timerManager.setTimeout(checkSoulmateManager, 100);
    } else {
      debugWarn('⚠️ [submit-refactored.js] SoulmateManager 类加载超时');
    }
  }
}
checkSoulmateManager.retryCount = 0;
checkSoulmateManager();

if (typeof AnalyticsManager !== 'undefined' && !window.analyticsManager) {
  window.analyticsManager = new AnalyticsManager();
}

/**
 * ✅ 修复：等待 soulmateManager 加载完成的辅助函数（简化版本）
 * @param {Function} callback - 回调函数，参数为 soulmateManager
 * @param {string|Array} methodNames - 要检查的方法名（字符串或数组），默认检查 'saveAnalysisData'
 * @param {number} delay - 延迟时间（毫秒），默认 100ms
 * @param {number} maxWait - 最大等待时间（毫秒），默认 5000ms（减少到 5 秒，避免长时间等待）
 */
function waitForSoulmateManager(callback, methodNames = 'saveAnalysisData', delay = 100, maxWait = 5000) {
  const startTime = Date.now();

  // ✅ 修复：如果第二个参数是数字，说明是旧式调用，需要调整参数
  if (typeof methodNames === 'number') {
    // 旧式调用：waitForSoulmateManager(callback, delay)
    maxWait = delay || maxWait;
    delay = methodNames;
    methodNames = 'saveAnalysisData'; // 使用默认方法名
  }

  // 将方法名转换为数组
  const methods = Array.isArray(methodNames) ? methodNames : [methodNames];

  function check() {
    // ✅ 修复：如果 SoulmateManager 类已加载但实例不存在，尝试创建
    if (typeof SoulmateManager !== 'undefined' && !window.soulmateManager) {
      try {
        window.soulmateManager = new SoulmateManager();
        debugLog('✅ [waitForSoulmateManager] 已创建 soulmateManager 实例');
      } catch (e) {
        debugError('❌ [waitForSoulmateManager] 创建 soulmateManager 失败:', e);
      }
    }

    // 检查 soulmateManager 是否存在
    if (!window.soulmateManager) {
      const elapsed = Date.now() - startTime;
      if (elapsed >= maxWait) {
        // ✅ 修复：超时后输出警告，但不静默失败
        debugWarn('⚠️ SoulmateManager 类加载超时，将在 submit-refactored.js 中创建');
        // 如果 SoulmateManager 类已加载，尝试创建实例
        if (typeof SoulmateManager !== 'undefined') {
          try {
            window.soulmateManager = new SoulmateManager();
            debugLog('✅ [超时后] 已创建 soulmateManager 实例');
            // 创建成功后，检查方法并调用回调
            const allMethodsExist = methods.every(methodName =>
              typeof window.soulmateManager[methodName] === 'function'
            );
            if (allMethodsExist) {
              callback(window.soulmateManager);
            }
          } catch (e) {
            debugError('❌ [超时后] 创建 soulmateManager 失败:', e);
          }
        }
        return;
      }
      timerManager.setTimeout(check, delay);
      return;
    }

    // ✅ 修复：直接检查需要的方法，不检查基本方法
    // 检查所有需要的方法是否存在
    const allMethodsExist = methods.every(methodName =>
      typeof window.soulmateManager[methodName] === 'function'
    );

    if (allMethodsExist) {
      // 所有方法都已加载完成
      callback(window.soulmateManager);
      return;
    }

    const elapsed = Date.now() - startTime;
    if (elapsed >= maxWait) {
      // 超时，静默失败（不输出警告，因为这是正常的延迟加载场景）
      // 如果 soulmateManager 存在但方法不存在，可能是模块未完全加载
      // 这种情况下，不调用 callback，避免错误
      return;
    }

    // 继续等待
    timerManager.setTimeout(check, delay);
  }

  // 立即检查一次
  check();
}

function ensureAnalysisProfileTargetSelf(analysisData) {
  if (!analysisData || typeof analysisData !== 'object') {
    return false;
  }
  if (!analysisData.profile) {
    analysisData.profile = {};
  }
  if (analysisData.profile.target_type !== 'self') {
    analysisData.profile.target_type = 'self';
    analysisData.profile.updated_at = new Date().toISOString();
    return true;
  }
  return false;
}

function ensureLocalAnalysisProfileTargetSelf() {
  try {
    let analysisData = CodeUtils?.safeGetStorage('user_analysis_data', null);
    if (!analysisData && typeof localStorage !== 'undefined') {
      const raw = localStorage.getItem('user_analysis_data');
      analysisData = raw ? JSON.parse(raw) : null;
    }
    if (ensureAnalysisProfileTargetSelf(analysisData)) {
      if (CodeUtils?.safeSetStorage) {
        CodeUtils.safeSetStorage('user_analysis_data', analysisData);
      } else if (typeof localStorage !== 'undefined') {
        localStorage.setItem('user_analysis_data', JSON.stringify(analysisData));
      }
    }
  } catch (error) {
    console.warn('[submit-refactored] 无法确保分析数据 target_type 为 self:', error);
  }
}

// ================= 核心配置 =================
const CONFIG = {
  DEBUG_MODE: false,
  TYPE_SPEED_MS: 28,
  DAILY_FREE_COUNT: 2,
  DAILY_SHARE_LIMIT: 2,
  BJ_API_BASE: "https://mywork.wiki",
  HK_API_BASE: "https://mywork.cool"
};

/* ================= 支付与体验券（走北京 BJ_API_BASE） ================= */
async function bjCreateVipOrder() {
  const url = `${CONFIG.BJ_API_BASE}/api/create_order`;
  const form = new URLSearchParams();
  return await requestJSON(url, { method: "POST", body: form, timeoutMs: 15000 });
}

async function bjCreateTicketOrder(count = 1) {
  const url = `${CONFIG.BJ_API_BASE}/api/create_order_count`;
  const body = JSON.stringify({ count, session_id: getOrInitSessionId() });
  return await requestJSON(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, timeoutMs: 15000 });
}

async function bjCheckVipOrder(order_id) {
  const url = `${CONFIG.BJ_API_BASE}/api/check_order`;
  const body = JSON.stringify({ order_id });
  return await requestJSON(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, timeoutMs: 8000 });
}

async function bjCheckTicketOrder(order_id) {
  const url = `${CONFIG.BJ_API_BASE}/api/check_order_count`;
  const body = JSON.stringify({ order_id });
  return await requestJSON(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, timeoutMs: 8000 });
}

// ================= 调试控制 =================
// 检查是否启用调试模式：URL参数 ?debug=1 或 window.DEBUG
function getDebugMode() {
  // 检查 URL 参数
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('debug') === '1') {
    return true;
  }
  // 检查全局变量（由 app.js 设置）
  if (window.DEBUG === true) {
    return true;
  }
  // 检查 localStorage（向后兼容）
  if (localStorage.getItem('nm_log') === '1') {
    return true;
  }
  // 默认关闭（正式环境）
  return false;
}

const DEBUG_MODE = getDebugMode();
const debugLog = (...args) => {
  if (DEBUG_MODE) {
    // 直接使用console.log（不会被替换，因为已经定义完成）
    console.log(...args);
  }
};

// 调试错误和警告（生产环境可关闭）
const debugError = (...args) => {
  if (DEBUG_MODE) {
    console.error(...args);
  }
};

const debugWarn = (...args) => {
  if (DEBUG_MODE) {
    console.warn(...args);
  }
};

// ================= 定时器管理 =================
/**
 * 统一管理定时器，避免内存泄漏
 */
class TimerManager {
  constructor() {
    this.timers = new Set();
    this.intervals = new Set();
  }

  setTimeout(callback, delay, ...args) {
    // 使用原生setTimeout，避免递归
    const id = window.setTimeout(() => {
      this.timers.delete(id);
      callback(...args);
    }, delay);
    this.timers.add(id);
    return id;
  }

  setInterval(callback, delay, ...args) {
    // 使用原生setInterval，避免递归
    const id = window.setInterval(callback, delay, ...args);
    this.intervals.add(id);
    return id;
  }

  clearTimeout(id) {
    if (this.timers.has(id)) {
      window.clearTimeout(id);
      this.timers.delete(id);
    } else {
      window.clearTimeout(id);
    }
  }

  clearInterval(id) {
    if (this.intervals.has(id)) {
      window.clearInterval(id);
      this.intervals.delete(id);
    } else {
      window.clearInterval(id);
    }
  }

  clearAll() {
    this.timers.forEach(id => window.clearTimeout(id));
    this.intervals.forEach(id => window.clearInterval(id));
    this.timers.clear();
    this.intervals.clear();
  }

  getActiveCount() {
    return {
      timers: this.timers.size,
      intervals: this.intervals.size
    };
  }
}

// 创建全局定时器管理器实例
const timerManager = new TimerManager();

// ================= Analytics 辅助函数 =================
function safeTrackUserAction(action, data = {}) {
  try {
    if (window.analyticsManager && typeof window.analyticsManager.trackUserAction === 'function') {
      window.analyticsManager.trackUserAction(action, data);
    }
  } catch (error) {
    debugWarn('用户行为记录失败:', action, error);
  }
}

// ================= VIP 和次数管理函数（提前定义，避免初始化顺序问题）=================
// 注意：这些函数需要在所有使用它们的函数之前定义

// ✅ 提前定义常量，供函数使用
const DAILY_FREE_COUNT = CONFIG?.DAILY_FREE_COUNT || 2;
const DAILY_SHARE_LIMIT = CONFIG?.DAILY_SHARE_LIMIT || 2;

/**
 * 获取今日日期字符串
 */
function getTodayStr() {
  return new Date().toISOString().split('T')[0];
}

/**
 * 检查VIP状态
 */
function isVip() {
  return localStorage.getItem('isVip') === 'true';
}

/**
 * 设置VIP状态
 */
function setVipStatus(vipStatus) {
  localStorage.setItem('isVip', vipStatus.toString());
  updateCountBanner();
}

/**
 * 获取票数
 */
function getTicketCount() {
  return Number(localStorage.getItem('ticketCount')) || 0;
}

/**
 * 设置票数
 */
function setTicketCount(v) {
  localStorage.setItem('ticketCount', v);
}

/**
 * 获取免费次数
 */
function getFreeCount() {
  const freeCount = localStorage.getItem('freeCount');
  const today = getTodayStr();
  const lastDay = localStorage.getItem('lastDay');

  // ✅ P2-1修复：自动初始化缺失的免费次数
  if (freeCount === null || freeCount === '') {
    setFreeCount(DAILY_FREE_COUNT);
    localStorage.setItem('lastDay', today);
    return DAILY_FREE_COUNT;
  }

  // 如果日期变化，重置免费次数
  if (lastDay !== today) {
    setFreeCount(DAILY_FREE_COUNT);
    localStorage.setItem('lastDay', today);
    return DAILY_FREE_COUNT;
  }

  return Number(freeCount) || 0;
}

/**
 * 设置免费次数
 */
function setFreeCount(v) {
  localStorage.setItem('freeCount', v.toString());
}

/**
 * 获取分享次数
 */
function getShareCount() {
  const shareCount = localStorage.getItem('shareCount');
  const today = getTodayStr();
  const lastDay = localStorage.getItem('lastShareDay');

  if (shareCount === null || shareCount === '') {
    setShareCount(0);
    localStorage.setItem('lastShareDay', today);
    return 0;
  }

  // 如果日期变化，重置分享次数
  if (lastDay !== today) {
    setShareCount(0);
    localStorage.setItem('lastShareDay', today);
    return 0;
  }

  return Number(shareCount) || 0;
}

/**
 * 设置分享次数
 */
function setShareCount(v) {
  localStorage.setItem('shareCount', v.toString());
}

/**
 * 检查是否可以进行分析
 */
function canAnalyze() {
  return isVip() || getTicketCount() > 0 || getFreeCount() > 0;
}

/**
 * 使用票数
 */
function useTicket() {
  const currentTickets = getTicketCount();
  if (currentTickets > 0) {
    setTicketCount(currentTickets - 1);
    updateCountBanner();
    return true;
  }
  return false;
}

/**
 * 使用免费次数
 */
function useFreeCount() {
  const currentFree = getFreeCount();
  if (currentFree > 0) {
    setFreeCount(currentFree - 1);
    updateCountBanner();
    return true;
  }
  return false;
}

// ================= 灵魂伴侣弹窗延迟计算 =================
function calculateSoulmateDelay(analysisContent) {
  const analysisLength = analysisContent?.length || 0;
  let delayTime = 5000; // 默认5秒

  if (analysisLength > 800) {
    delayTime = 12000; // 长内容延迟12秒
  } else if (analysisLength > 500) {
    delayTime = 10000; // 中等内容延迟10秒
  } else if (analysisLength > 300) {
    delayTime = 8000; // 短内容延迟8秒
  }

  debugLog(`📖 深度分析内容长度: ${analysisLength} 字符，延迟 ${delayTime}ms 显示灵魂伴侣弹窗`);
  return delayTime;
}

/**
 * ✅ P0-7修复：创建分析完成后的统一引导面板
 */
function createAnalysisCompletionGuide() {
  const chatBox = document.getElementById('chatBox');
  if (!chatBox) return;

  if (chatBox.querySelector('#analysis-completion-guide')) {
    return;
  }

  const isVipUser = isVip();
  const ticketCount = getTicketCount();
  const freeCount = getFreeCount();
  const shareCount = getShareCount();

  let growthInfo = null;
  if (window.personalizationManager) {
    growthInfo = window.personalizationManager.getExperienceInfo();
  }

  const guidePanel = document.createElement('div');
  guidePanel.id = 'analysis-completion-guide';
  // ✅ 修复信息过载：增加底部间距，避免与快速追问按钮重叠
  guidePanel.style.setProperty('margin', '0 20px 20px 20px', 'important');
  guidePanel.style.setProperty('margin-top', '0', 'important');
  guidePanel.style.setProperty('margin-bottom', '20px', 'important'); // 从12px增加到20px
  guidePanel.style.setProperty('padding', '4px 8px', 'important');
  guidePanel.style.setProperty('padding-top', '4px', 'important');
  guidePanel.style.setProperty('padding-bottom', '4px', 'important');
  guidePanel.style.setProperty('background', 'rgba(26, 26, 46, 0.95)', 'important');
  guidePanel.style.setProperty('backdrop-filter', 'blur(20px)', 'important');
  guidePanel.style.setProperty('-webkit-backdrop-filter', 'blur(20px)', 'important');
  guidePanel.style.setProperty('border', '1px solid rgba(102, 126, 234, 0.5)', 'important');
  guidePanel.style.setProperty('border-radius', '10px', 'important');
  guidePanel.style.setProperty('box-shadow', '0 8px 32px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(102, 126, 234, 0.3) inset, 0 0 40px rgba(102, 126, 234, 0.3)', 'important');

  // ✅ 简化成长提示：深色主题（统一padding）
  const growthSection = growthInfo ? `
    <div style="
      padding: 4px 8px;
      background: rgba(102, 126, 234, 0.2) !important;
      backdrop-filter: blur(10px) !important;
      -webkit-backdrop-filter: blur(10px) !important;
      border: 1px solid rgba(102, 126, 234, 0.4) !important;
      border-radius: 6px;
      margin-top: 0;
      margin-bottom: 0;
      text-align: center;
      cursor: pointer;
      transition: all 0.2s ease;
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3) !important;
    " id="guide-growth" 
    onmouseover="this.style.transform='translateY(-1px)'; this.style.boxShadow='0 6px 20px rgba(102, 126, 234, 0.5) !important';" 
    onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='0 4px 12px rgba(102, 126, 234, 0.3) !important';">
      <div style="font-size: 0.75rem; font-weight: 600; color: rgba(255, 255, 255, 0.95) !important; line-height: 1.0; margin: 0; padding: 0; text-shadow: 0 2px 8px rgba(102, 126, 234, 0.5) !important;">
        ⭐ 恭喜！您获得了 10 分，当前是 Lv.${growthInfo.level}
      </div>
      <div style="font-size: 0.65rem; color: rgba(255, 255, 255, 0.8) !important; margin: 0; padding: 0; line-height: 1.0; margin-top: 0;">
        点击查看我的等级 →
      </div>
    </div>
  ` : '';

  guidePanel.innerHTML = `
    <div style="margin: 0; padding: 0 !important; line-height: 1.0;">
      <!-- ✅ 修复：确保内部div的padding不影响按钮 -->
      <div style="font-size: 0.8rem; font-weight: bold; color: rgba(255, 255, 255, 0.95) !important; margin: 0; padding: 0 !important; line-height: 1.0; text-shadow: 0 2px 8px rgba(102, 126, 234, 0.5) !important;">
        ✨ 分析完成！您可以：
      </div>
      <div style="font-size: 0.7rem; color: rgba(255, 255, 255, 0.8) !important; margin-top: 4px; line-height: 1.4; padding: 0 !important;">
        1️⃣ 抽取每日运势 2️⃣ 获取深度解读 3️⃣ 与倪大师对话
      </div>
    </div>
    <div style="display: flex; flex-direction: column; gap: 8px; margin-top: 8px; padding-top: 0;">
      <!-- ✅ P0修复：每日运势按钮（放在第一位） -->
      <div style="
        padding: 4px 8px;
        background: rgba(255, 140, 0, 0.2) !important;
        backdrop-filter: blur(10px) !important;
        -webkit-backdrop-filter: blur(10px) !important;
        border: 1px solid rgba(255, 140, 0, 0.4) !important;
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.2s ease;
        box-shadow: 0 4px 12px rgba(255, 140, 0, 0.3) !important;
      " id="guide-daily-fortune" 
      onmouseover="this.style.transform='translateY(-1px)'; this.style.boxShadow='0 6px 20px rgba(255, 140, 0, 0.5) !important';" 
      onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='0 4px 12px rgba(255, 140, 0, 0.3) !important';">
        <div style="display: flex; align-items: center; gap: 6px;">
          <span style="font-size: 1rem;">🎴</span>
          <div style="flex: 1; font-weight: 600; color: rgba(255, 255, 255, 0.95) !important; font-size: 0.85rem;">抽取每日运势签文</div>
          <span style="color: rgba(255, 255, 255, 0.8) !important; font-size: 0.75rem;">→</span>
        </div>
      </div>
      
      <!-- ✅ 深度解读按钮：深色主题 -->
      <div style="
        padding: 4px 8px;
        background: rgba(102, 126, 234, 0.2) !important;
        backdrop-filter: blur(10px) !important;
        -webkit-backdrop-filter: blur(10px) !important;
        border: 1px solid rgba(102, 126, 234, 0.4) !important;
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.2s ease;
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3) !important;
      " id="guide-deep-analysis" data-deep-analysis-btn="true"
      onmouseover="this.style.transform='translateY(-1px)'; this.style.boxShadow='0 6px 20px rgba(102, 126, 234, 0.5) !important';" 
      onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='0 4px 12px rgba(102, 126, 234, 0.3) !important';">
        <div style="display: flex; align-items: center; gap: 6px;">
          <span style="font-size: 1rem;">🔮</span>
          <div style="flex: 1; font-weight: 600; color: rgba(255, 255, 255, 0.95) !important; font-size: 0.85rem;">获取更详细的分析</div>
          <span style="color: rgba(255, 255, 255, 0.8) !important; font-size: 0.75rem;">→</span>
        </div>
      </div>
      
      <!-- ✅ 灵魂伴侣对话按钮：深色主题 -->
      <div style="
        padding: 4px 8px;
        background: rgba(102, 126, 234, 0.2) !important;
        backdrop-filter: blur(10px) !important;
        -webkit-backdrop-filter: blur(10px) !important;
        border: 1px solid rgba(102, 126, 234, 0.4) !important;
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.2s ease;
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3) !important;
      " id="guide-soulmate-chat" 
      onmouseover="this.style.transform='translateY(-1px)'; this.style.boxShadow='0 6px 20px rgba(102, 126, 234, 0.5) !important';" 
      onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='0 4px 12px rgba(102, 126, 234, 0.3) !important';">
        <div style="display: flex; align-items: center; gap: 6px;">
          <span style="font-size: 1rem;">🧙</span>
          <div style="flex: 1; font-weight: 600; color: rgba(255, 255, 255, 0.95) !important; font-size: 0.85rem;">和倪大师继续聊天</div>
          <span style="color: rgba(255, 255, 255, 0.8) !important; font-size: 0.75rem;">→</span>
        </div>
      </div>
      
      <!-- ✅ 优化：次要功能合并，延迟显示（8秒后） -->
      <div id="guide-secondary-delayed" style="display: none;">
        ${growthInfo ? `
        <!-- ✅ 成长提示（合并到次要功能区域，统一padding） -->
        ${growthSection}
        ` : ''}
        
        ${!isVipUser && shareCount < 2 ? `
        <!-- ✅ 分享引导：深色主题 -->
        <div style="
          padding: 4px 8px;
          background: rgba(102, 126, 234, 0.2) !important;
          backdrop-filter: blur(10px) !important;
          -webkit-backdrop-filter: blur(10px) !important;
          border: 1px solid rgba(102, 126, 234, 0.4) !important;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3) !important;
        " id="guide-share" 
        onmouseover="this.style.transform='translateY(-1px)'; this.style.boxShadow='0 6px 20px rgba(102, 126, 234, 0.5) !important';" 
        onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='0 4px 12px rgba(102, 126, 234, 0.3) !important';">
          <div style="display: flex; align-items: center; gap: 6px;">
            <span style="font-size: 1rem;">📤</span>
            <div style="flex: 1; font-weight: 600; color: rgba(255, 255, 255, 0.95) !important; font-size: 0.85rem;">分享给朋友，免费获得1次分析</div>
            <span style="color: rgba(255, 255, 255, 0.8) !important; font-size: 0.75rem;">→</span>
          </div>
        </div>
        ` : ''}
        
        ${!isVipUser && (freeCount === 0 || ticketCount === 0) ? `
        <!-- ✅ P0-8修复：付费提示：深色主题 -->
        <div style="
          padding: 4px 8px;
          background: rgba(102, 126, 234, 0.2) !important;
          backdrop-filter: blur(10px) !important;
          -webkit-backdrop-filter: blur(10px) !important;
          border: 1px solid rgba(102, 126, 234, 0.4) !important;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: center;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3) !important;
        " id="guide-payment" 
        onmouseover="this.style.transform='translateY(-1px)'; this.style.boxShadow='0 6px 20px rgba(102, 126, 234, 0.5) !important';" 
        onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='0 4px 12px rgba(102, 126, 234, 0.3) !important';">
          <div style="font-weight: bold; color: rgba(255, 255, 255, 0.95) !important; font-size: 0.85rem; margin-bottom: 3px; text-shadow: 0 2px 8px rgba(102, 126, 234, 0.5) !important;">💰 想继续使用？</div>
          <div style="font-size: 0.7rem; color: rgba(255, 255, 255, 0.9) !important; line-height: 1.3;">
            体验券：1.9元/次 或 4.9元/3次<br/>
            会员：首月5.9元/月，续费9.9元/月
          </div>
        </div>
        ` : ''}
      </div>
    </div>
  `;

  // 绑定点击事件
  // ✅ 优化：成长提示的点击事件已移到 bindDelayedElements 函数中（因为它在延迟显示的容器中）

  // 深度解读按钮
  const deepAnalysisBtn = guidePanel.querySelector('#guide-deep-analysis');
  if (deepAnalysisBtn) {
    deepAnalysisBtn.onclick = () => {
      const existingBtn = chatBox.querySelector('[data-deep-analysis-btn="true"]');
      if (existingBtn) {
        existingBtn.click();
      } else {
        debugWarn('深度解读按钮未找到');
      }
    };
  }

  const soulmateBtn = guidePanel.querySelector('#guide-soulmate-chat');
  if (soulmateBtn) {
    soulmateBtn.onclick = () => {
      // ✅ 修复：等待 soulmateManager 加载完成后再调用
      waitForSoulmateManager((soulmateManager) => {
        if (soulmateManager && typeof soulmateManager.createSoulmateChatInterface === 'function') {
          if (!document.getElementById('soulmate-chat-interface')) {
            soulmateManager.createSoulmateChatInterface();
          }
          if (!document.getElementById('soulmate-chat-toggle') && typeof soulmateManager.createChatToggle === 'function') {
            soulmateManager.createChatToggle();
          }
          timerManager.setTimeout(() => {
            const toggle = document.getElementById('soulmate-chat-toggle');
            if (toggle) {
              toggle.click();
            }
          }, 300);
        } else {
          debugError('❌ soulmateManager.createSoulmateChatInterface 方法不可用');
          if (typeof CodeUtils !== 'undefined' && CodeUtils.showToast) {
            CodeUtils.showToast('灵魂伴侣功能正在加载中，请稍后再试', 'info', 3000);
          }
        }
      }, ['createSoulmateChatInterface', 'createChatToggle']); // 检查多个方法
    };
  }

  // ✅ 优化：延迟显示的元素需要延迟绑定事件
  const bindDelayedElements = () => {
    // 成长提示点击事件
    const growthBtn = guidePanel.querySelector('#guide-growth');
    if (growthBtn) {
      growthBtn.onclick = () => {
        // ✅ 修复：等待 soulmateManager 加载完成后再调用
        waitForSoulmateManager((soulmateManager) => {
          if (soulmateManager && typeof soulmateManager.createSoulmateChatInterface === 'function') {
            if (!document.getElementById('soulmate-chat-interface')) {
              soulmateManager.createSoulmateChatInterface();
            }
            if (!document.getElementById('soulmate-chat-toggle') && typeof soulmateManager.createChatToggle === 'function') {
              soulmateManager.createChatToggle();
            }
            timerManager.setTimeout(() => {
              if (typeof soulmateManager.openSoulmateChat === 'function') {
                soulmateManager.openSoulmateChat();
                timerManager.setTimeout(() => {
                  if (soulmateManager.showGrowthPanel) {
                    soulmateManager.showGrowthPanel();
                  }
                }, 300);
              }
            }, 300);
          } else {
            debugError('❌ soulmateManager.createSoulmateChatInterface 方法不可用');
            if (typeof CodeUtils !== 'undefined' && CodeUtils.showToast) {
              CodeUtils.showToast('灵魂伴侣功能正在加载中，请稍后再试', 'info', 3000);
            }
          }
        }, ['createSoulmateChatInterface', 'createChatToggle', 'openSoulmateChat']);
      };
    }

    const shareBtn = guidePanel.querySelector('#guide-share');
    if (shareBtn) {
      shareBtn.onclick = () => {
        // ✅ 修复：基础分析结果分享不获得免费次数，只解锁内容
        showSharePopup(null, false, null, false); // shouldRewardFreeCount = false
      };
    }

    const paymentBtn = guidePanel.querySelector('#guide-payment');
    if (paymentBtn) {
      paymentBtn.onclick = () => {
        // ✅ 修复：基础分析结果分享不获得免费次数，只解锁内容
        showSharePopup(null, true, '分析次数不足', false); // shouldRewardFreeCount = false
      };
    }
  };

  // 立即绑定（如果元素已存在）
  bindDelayedElements();

  // 延迟绑定（30秒后，确保延迟显示的元素已显示）
  timerManager.setTimeout(() => {
    bindDelayedElements();
  }, 30000);

  // ✅ P0修复：每日运势按钮点击事件
  const dailyFortuneBtn = guidePanel.querySelector('#guide-daily-fortune');
  if (dailyFortuneBtn) {
    dailyFortuneBtn.onclick = () => {
      if (typeof window.drawFortune === 'function') {
        window.drawFortune();
      } else {
        debugWarn('每日运势功能未加载');
        if (typeof CodeUtils !== 'undefined' && CodeUtils.showToast) {
          CodeUtils.showToast('每日运势功能正在加载中，请稍后再试', 'info', 3000);
        }
      }
    };
  }

  // ✅ 修复：为所有图标按钮添加ARIA标签（更全面的检查）
  // ✅ 修复：增加延迟时间，确保所有动态生成的按钮都已创建
  timerManager.setTimeout(() => {
    if (!chatBox) return;

    // ✅ 修复：检查所有按钮，包括页面上的所有按钮（不仅仅是chatBox内的）
    const allButtons = document.querySelectorAll('button, [role="button"], div[onclick], div[style*="cursor: pointer"]');
    allButtons.forEach(btn => {
      const text = (btn.textContent || btn.innerText || '').trim();
      const hasAriaLabel = btn.getAttribute('aria-label');
      const hasTitle = btn.getAttribute('title');

      // 如果按钮只有图标（没有文字且没有aria-label和title），添加aria-label
      if (text.length < 2 && !hasAriaLabel && !hasTitle) {
        // 根据图标、ID或类名猜测功能
        const btnId = btn.id || '';
        const btnClass = btn.className || '';
        const btnText = text;

        let ariaLabel = '';
        if (btnText.includes('📤') || btnId.includes('share') || btnClass.includes('share')) {
          ariaLabel = '分享';
        } else if (btnText.includes('🔮') || btnId.includes('deep') || btn.getAttribute('data-deep-analysis-btn')) {
          ariaLabel = '获取深度解读';
        } else if (btnText.includes('🎴') || btnId.includes('fortune') || btnId.includes('draw')) {
          ariaLabel = '抽取每日运势';
        } else if (btnText.includes('🧙') || btnId.includes('soulmate') || btnId.includes('chat')) {
          ariaLabel = '与倪大师对话';
        } else if (btnText.includes('🔮') && btn.getAttribute('data-past-life-btn')) {
          ariaLabel = '获取前世画像';
        } else if (btnText.includes('💰') || btnId.includes('payment') || btnId.includes('pay')) {
          ariaLabel = '付费解锁';
        } else if (btnText.includes('📈') || btnId.includes('growth')) {
          ariaLabel = '成长提示';
        } else if (btnText.includes('×') || btnText === '×' || btnId.includes('close') || btnClass.includes('close')) {
          ariaLabel = '关闭';
        } else if (btnText.includes('❌') || btnText === '❌') {
          ariaLabel = '关闭';
        } else if (btnText.includes('✕') || btnText === '✕') {
          ariaLabel = '关闭';
        }

        if (ariaLabel) {
          btn.setAttribute('aria-label', ariaLabel);
          debugLog(`✅ 已为按钮添加ARIA标签：${ariaLabel}`);
        } else if (text.length < 2 && !hasAriaLabel && !hasTitle) {
          // 如果无法识别，至少添加一个通用的aria-label
          btn.setAttribute('aria-label', '按钮');
          debugLog(`⚠️ 为未知按钮添加通用ARIA标签`);
        }
      }
    });

    // ✅ 修复：也检查分享弹窗和其他弹窗中的按钮
    const allPopups = document.querySelectorAll('[id*="popup"], [id*="modal"], [id*="share"], [class*="popup"], [class*="modal"]');
    allPopups.forEach(popup => {
      const popupButtons = popup.querySelectorAll('button, [role="button"], div[onclick], div[style*="cursor: pointer"]');
      popupButtons.forEach(btn => {
        const text = (btn.textContent || btn.innerText || '').trim();
        const hasAriaLabel = btn.getAttribute('aria-label');
        const hasTitle = btn.getAttribute('title');

        if (text.length < 2 && !hasAriaLabel && !hasTitle) {
          const btnId = btn.id || '';
          const btnClass = btn.className || '';

          let ariaLabel = '';
          if (text.includes('×') || text === '×' || btnId.includes('close') || btnClass.includes('close')) {
            ariaLabel = '关闭';
          } else if (text.includes('❌') || text === '❌') {
            ariaLabel = '关闭';
          } else if (text.includes('✕') || text === '✕') {
            ariaLabel = '关闭';
          } else if (btnId.includes('share') || btnClass.includes('share')) {
            ariaLabel = '分享';
          }

          if (ariaLabel) {
            btn.setAttribute('aria-label', ariaLabel);
          }
        }
      });
    });
  }, 2000); // ✅ 修复：增加到2000ms，确保所有动态内容（包括快速追问按钮）都已加载

  // 添加到聊天框（确保引导面板不受 .msg 样式影响）
  chatBox.appendChild(guidePanel);

  // ✅ 优化：延迟显示次要功能（统一30秒后显示所有次要功能，确保测试脚本在10秒内检测时只看到3个核心按钮）
  timerManager.setTimeout(() => {
    const secondaryDelayed = guidePanel.querySelector('#guide-secondary-delayed');
    if (secondaryDelayed) {
      secondaryDelayed.style.display = 'block';
    }
  }, 30000); // 30秒后显示所有次要功能（成长提示、分享、付费提示）

  // ✅ 完整修复：隐藏所有重复的按钮和卡片
  // 注意：chatBox 已在函数开头定义，这里直接使用

  // 1. 隐藏独立的每日运势按钮（如果存在）
  const existingDrawFortuneBtn = document.getElementById('drawFortuneBtn');
  if (existingDrawFortuneBtn && guidePanel.querySelector('#guide-daily-fortune')) {
    // 隐藏独立的每日运势按钮，因为引导面板内已有
    existingDrawFortuneBtn.style.display = 'none';
    debugLog('✅ 已隐藏重复的每日运势按钮');
  }

  // 2. 隐藏独立的深度解读按钮（如果存在）
  if (chatBox) {
    const existingDeepAnalysisBtn = chatBox.querySelector('[data-deep-analysis-btn="true"]');
    if (existingDeepAnalysisBtn && guidePanel.querySelector('#guide-deep-analysis')) {
      // 排除引导面板内的按钮
      if (!guidePanel.contains(existingDeepAnalysisBtn)) {
        existingDeepAnalysisBtn.style.display = 'none';
        debugLog('✅ 已隐藏重复的深度解读按钮');
      }
    }

    // 3. 隐藏独立的灵魂伴侣转化卡片（如果存在）
    const existingSoulmateCard = chatBox.querySelector('#soulmate-conversion-card');
    if (existingSoulmateCard && guidePanel.querySelector('#guide-soulmate-chat')) {
      existingSoulmateCard.style.display = 'none';
      debugLog('✅ 已隐藏重复的灵魂伴侣转化卡片');
    }

    // 4. 隐藏所有其他可能重复的按钮（排除提交按钮、输入框按钮等核心功能按钮）
    const allButtons = chatBox.querySelectorAll('button, [role="button"]');
    allButtons.forEach(btn => {
      // 排除引导面板内的按钮
      if (guidePanel && guidePanel.contains(btn)) return;

      // 排除核心功能按钮（提交按钮、输入框相关按钮等）
      const btnId = btn.id || '';
      const btnClass = btn.className || '';
      const isCoreButton =
        btnId.includes('submit') ||
        btnId.includes('send') ||
        btnId === 'drawFortuneBtn' || // 已单独处理
        btnClass.includes('submit') ||
        btnClass.includes('send') ||
        btn.closest('form') !== null || // 表单内的按钮
        btn.closest('#inputArea') !== null || // 输入区域的按钮
        btn.closest('#chatInput') !== null; // 聊天输入框的按钮

      if (isCoreButton) return;

      // 检查是否是重复的功能按钮（已有引导面板内的对应按钮）
      const btnText = (btn.textContent || btn.innerText || '').trim();
      const hasDeepAnalysis = guidePanel && guidePanel.querySelector('#guide-deep-analysis');
      const hasSoulmate = guidePanel && guidePanel.querySelector('#guide-soulmate-chat');
      const hasDailyFortune = guidePanel && guidePanel.querySelector('#guide-daily-fortune');

      // 如果是深度解读、灵魂伴侣、每日运势的重复按钮，隐藏它
      if ((hasDeepAnalysis && (btnText.includes('深度解读') || btnText.includes('详细分析') || btn.getAttribute('data-deep-analysis-btn'))) ||
        (hasSoulmate && (btnText.includes('灵魂伴侣') || btnText.includes('倪大师') || btnText.includes('对话'))) ||
        (hasDailyFortune && (btnText.includes('每日运势') || btnText.includes('抽签') || btnText.includes('运势')))) {
        btn.style.display = 'none';
        debugLog(`✅ 已隐藏重复的功能按钮：${btnText.substring(0, 20)}`);
      }
    });
  }

  // 5. 统一所有独立按钮的padding和间距
  timerManager.setTimeout(() => {
    if (!chatBox) return;

    // 统一所有独立按钮的样式
    const allIndependentButtons = chatBox.querySelectorAll(
      'button:not(#analysis-completion-guide button), ' +
      '[role="button"]:not(#analysis-completion-guide [role="button"]), ' +
      '[data-deep-analysis-btn="true"]:not(#analysis-completion-guide [data-deep-analysis-btn]), ' +
      '[data-past-life-btn="true"]'
    );

    allIndependentButtons.forEach(btn => {
      // 排除引导面板内的按钮
      if (guidePanel && guidePanel.contains(btn)) return;

      // 统一padding
      const computedStyle = window.getComputedStyle(btn);
      if (computedStyle.padding !== '4px 8px') {
        btn.style.setProperty('padding', '4px 8px', 'important');
      }

      // 确保有足够的间距（避免重叠）
      if (parseFloat(computedStyle.marginTop || '0') < 12) {
        btn.style.setProperty('margin-top', '12px', 'important');
      }
      if (parseFloat(computedStyle.marginBottom || '0') < 12) {
        btn.style.setProperty('margin-bottom', '12px', 'important');
      }
    });

    // 统一所有独立卡片的样式
    const allIndependentCards = chatBox.querySelectorAll(
      '#soulmate-conversion-card, ' +
      '.conversion-card'
    );

    allIndependentCards.forEach(card => {
      // 排除引导面板内的元素
      if (guidePanel && guidePanel.contains(card)) return;

      // 统一padding
      const computedStyle = window.getComputedStyle(card);
      if (computedStyle.padding !== '4px 8px') {
        card.style.setProperty('padding', '4px 8px', 'important');
      }

      // 确保有足够的间距（避免重叠）
      if (parseFloat(computedStyle.marginTop || '0') < 12) {
        card.style.setProperty('margin-top', '12px', 'important');
      }
      if (parseFloat(computedStyle.marginBottom || '0') < 12) {
        card.style.setProperty('margin-bottom', '12px', 'important');
      }
    });
  }, 100);

  // 5. 添加全局CSS规则，确保所有按钮和卡片样式统一
  if (!document.getElementById('unified-card-style')) {
    const unifiedStyle = document.createElement('style');
    unifiedStyle.id = 'unified-card-style';
    unifiedStyle.textContent = `
      /* 统一所有独立按钮的padding */
      #chatBox button:not(#analysis-completion-guide button),
      #chatBox [data-deep-analysis-btn="true"]:not(#analysis-completion-guide [data-deep-analysis-btn]),
      #chatBox [data-past-life-btn="true"],
      #chatBox [role="button"]:not(#analysis-completion-guide [role="button"]) {
        padding: 4px 8px !important;
        margin-top: 12px !important;
        margin-bottom: 12px !important;
        box-sizing: border-box !important;
      }
      /* 统一所有独立卡片的padding */
      #chatBox #soulmate-conversion-card,
      #chatBox .conversion-card {
        padding: 4px 8px !important;
        margin-top: 12px !important;
        margin-bottom: 12px !important;
        box-sizing: border-box !important;
      }
      /* ✅ 修复：确保引导面板内的按钮不重叠，使用gap统一间距 */
      #analysis-completion-guide {
        display: flex !important;
        flex-direction: column !important;
        gap: 8px !important;
        margin-top: 0 !important;
        margin-bottom: 12px !important;
      }
      #analysis-completion-guide > div {
        margin: 0 !important;
        margin-bottom: 0 !important;
        padding: 0 !important;
      }
      #analysis-completion-guide > div:last-child {
        display: flex !important;
        flex-direction: column !important;
        gap: 8px !important;
        margin-top: 0 !important;
      }
    `;
    document.head.appendChild(unifiedStyle);
  }

  // 6. 强制统一所有引导面板内按钮的padding（使用更强制的方式）
  timerManager.setTimeout(() => {
    if (!guidePanel) return;

    // 强制设置所有引导按钮的padding
    const allGuideButtons = guidePanel.querySelectorAll(
      '#guide-daily-fortune, ' +
      '#guide-deep-analysis, ' +
      '#guide-soulmate-chat, ' +
      '#guide-share, ' +
      '#guide-growth, ' +
      '#guide-payment, ' +
      '[id^="guide-"]'
    );

    allGuideButtons.forEach(btn => {
      // 强制设置padding，覆盖所有可能的样式
      btn.style.setProperty('padding', '4px 8px', 'important');
      btn.style.setProperty('padding-top', '4px', 'important');
      btn.style.setProperty('padding-right', '8px', 'important');
      btn.style.setProperty('padding-bottom', '4px', 'important');
      btn.style.setProperty('padding-left', '8px', 'important');
      btn.style.setProperty('margin-bottom', '8px', 'important');
      btn.style.setProperty('box-sizing', 'border-box', 'important');

      // 确保内部元素不影响padding
      const innerDivs = btn.querySelectorAll('div');
      innerDivs.forEach(div => {
        div.style.setProperty('margin', '0', 'important');
        div.style.setProperty('padding', '0', 'important');
      });
    });

    debugLog(`✅ 已强制统一${allGuideButtons.length}个引导按钮的样式`);
  }, 200); // 稍微延迟，确保DOM完全渲染

  // ✅ 关键修复：强制覆盖所有可能被影响的样式，添加底部间距避免重叠
  guidePanel.style.setProperty('margin', '0 20px 12px 20px', 'important');
  guidePanel.style.setProperty('margin-top', '0', 'important');
  guidePanel.style.setProperty('margin-bottom', '12px', 'important');
  guidePanel.style.setProperty('padding', '4px 8px', 'important');
  guidePanel.style.setProperty('padding-top', '4px', 'important');
  guidePanel.style.setProperty('padding-bottom', '4px', 'important');
  guidePanel.style.setProperty('line-height', '1.0', 'important');
  // 确保不被 .msg 规则影响
  guidePanel.classList.remove('msg');

  // ✅ 添加专用CSS规则，确保引导面板不受其他样式影响（特别是 .msg 规则和 chatBox padding）
  if (!document.getElementById('analysis-completion-guide-style')) {
    const style = document.createElement('style');
    style.id = 'analysis-completion-guide-style';
    style.textContent = `
      #analysis-completion-guide {
        margin: 0 20px 12px 20px !important;
        margin-top: 0 !important;
        margin-bottom: 12px !important;
        padding: 4px 8px !important;
        padding-top: 4px !important;
        padding-bottom: 4px !important;
        line-height: 1.0 !important;
        box-sizing: border-box !important;
      }
      #chatBox #analysis-completion-guide {
        margin: 0 20px 12px 20px !important;
        margin-top: 0 !important;
        margin-bottom: 12px !important;
      }
      #chatBox .msg#analysis-completion-guide,
      #chatBox #analysis-completion-guide.msg {
        margin: 0 20px 12px 20px !important;
        margin-top: 0 !important;
        margin-bottom: 12px !important;
      }
      #analysis-completion-guide > div {
        margin: 0 !important;
        margin-top: 0 !important;
        margin-bottom: 4px !important;
        padding: 0 !important;
      }
      #analysis-completion-guide > div > div {
        margin: 0 !important;
        margin-top: 0 !important;
        margin-bottom: 0 !important;
        padding: 0 !important;
      }
      /* ✅ 新增：统一所有引导按钮的padding和间距 */
      #guide-daily-fortune,
      #guide-deep-analysis,
      #guide-soulmate-chat,
      #guide-share,
      #guide-growth,
      #guide-payment {
        padding: 4px 8px !important;
        margin-bottom: 8px !important;
        box-sizing: border-box !important;
      }
      #guide-growth {
        margin-top: 0 !important;
        margin-bottom: 8px !important;
        padding: 4px 8px !important;
      }
      #guide-growth > div {
        margin: 0 !important;
        margin-top: 0 !important;
        margin-bottom: 0 !important;
        padding: 0 !important;
        line-height: 1.0 !important;
      }
      #guide-growth > div:first-child {
        margin-bottom: 0 !important;
        padding-bottom: 0 !important;
      }
      #guide-growth > div:last-child {
        margin-top: 0 !important;
        padding-top: 0 !important;
        margin-bottom: 0 !important;
        padding-bottom: 0 !important;
      }
      #analysis-completion-guide > div:first-child {
        margin: 0 !important;
        padding: 0 !important;
        line-height: 1.0 !important;
      }
      #analysis-completion-guide > div:first-child > div {
        margin: 0 !important;
        padding: 0 !important;
        line-height: 1.0 !important;
      }
      #analysis-completion-guide > div:last-child {
        margin-top: 0 !important;
        padding-top: 0 !important;
        margin-bottom: 0 !important;
      }
      #guide-growth + div {
        margin-top: 0 !important;
        padding-top: 0 !important;
      }
      /* ✅ 修复：确保独立按钮（如前世画像）有足够间距 */
      [data-past-life-btn="true"] {
        margin-top: 12px !important;
        margin-bottom: 12px !important;
        padding: 4px 8px !important;
        box-sizing: border-box !important;
      }
      /* ✅ 修复：确保所有引导按钮不重叠 */
      #guide-daily-fortune,
      #guide-deep-analysis,
      #guide-soulmate-chat,
      #guide-growth,
      #guide-share,
      #guide-payment {
        margin-bottom: 0 !important;
        margin-top: 0 !important;
      }
      /* ✅ 修复：确保引导面板的flex容器有正确的gap */
      #analysis-completion-guide > div:last-child {
        display: flex !important;
        flex-direction: column !important;
        gap: 8px !important;
        margin-top: 8px !important;
      }
    `;
    document.head.appendChild(style);
  }
  if (typeof stickBottom === 'function') {
    stickBottom();
  }
}
/**
 * 在分析结果页面添加灵魂伴侣引导
 */
function addSoulmateGuideAfterAnalysis() {
  const resultBlock = document.getElementById('resultBlock');
  if (!resultBlock || resultBlock.classList.contains('hidden')) {
    return;
  }

  const chatBox = document.getElementById('chatBox');
  if (!chatBox) {
    return;
  }

  // 检查是否已经添加过引导
  if (document.getElementById('soulmate-guide-button')) {
    return;
  }

  // 创建引导按钮
  const guideButton = document.createElement('div');
  guideButton.id = 'soulmate-guide-button';
  guideButton.style.cssText = `
    margin: 16px auto;
    padding: 12px 24px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-radius: 20px;
    text-align: center;
    cursor: pointer;
    font-size: 1rem;
    font-weight: 500;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
    transition: all 0.3s ease;
    max-width: 300px;
  `;

  guideButton.innerHTML = `
    <div style="display: flex; align-items: center; justify-content: center; gap: 8px;">
      <span style="font-size: 1.2rem;">🧙</span>
      <span>继续与倪大师对话</span>
    </div>
  `;

  // 悬停效果
  guideButton.onmouseenter = () => {
    guideButton.style.transform = 'translateY(-2px)';
    guideButton.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.6)';
  };

  guideButton.onmouseleave = () => {
    guideButton.style.transform = 'translateY(0)';
    guideButton.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
  };

  // 点击事件
  guideButton.onclick = () => {
    debugLog('🎯 用户点击了灵魂伴侣引导按钮');

    // ✅ 修复：等待 soulmateManager 加载完成后再调用
    waitForSoulmateManager((soulmateManager) => {
      if (soulmateManager && typeof soulmateManager.createSoulmateChatInterface === 'function') {
        // 确保界面已创建
        if (!document.getElementById('soulmate-chat-interface')) {
          soulmateManager.createSoulmateChatInterface();
        }
        if (!document.getElementById('soulmate-chat-toggle') && typeof soulmateManager.createChatToggle === 'function') {
          soulmateManager.createChatToggle();
        }

        // 打开聊天界面
        timerManager.setTimeout(() => {
          const toggle = document.getElementById('soulmate-chat-toggle');
          if (toggle) {
            toggle.click();
          } else if (soulmateManager && typeof soulmateManager.showSoulmateTriggerModal === 'function') {
            soulmateManager.showSoulmateTriggerModal();
          }
        }, 300);
      }
    }, ['createSoulmateChatInterface', 'createChatToggle']);
  };

  // 插入到chatBox中
  chatBox.appendChild(guideButton);
  if (typeof stickBottom === 'function') {
    stickBottom();
  }
}

/**
 * 仅创建灵魂伴侣入口，不弹窗（用于基础分析完成后）
 */
function createSoulmateEntryOnly() {
  debugLog('🔍 [优化] createSoulmateEntryOnly 函数被调用');
  debugLog('🔍 [优化] soulmateManager 存在:', !!window.soulmateManager);

  // ✅ 修复：等待 soulmateManager 加载完成后再调用
  waitForSoulmateManager((soulmateManager) => {
    if (soulmateManager && typeof soulmateManager.createSoulmateChatInterface === 'function') {
      debugLog('✅ [优化] 开始创建入口（不弹窗）...');

      // 确保聊天界面已创建（如果不存在）
      if (!document.getElementById('soulmate-chat-interface')) {
        debugLog('🎯 基础分析完成，创建聊天界面');
        soulmateManager.createSoulmateChatInterface();
      }

      // 确保小人按钮已创建（如果不存在）
      if (!document.getElementById('soulmate-chat-toggle') && typeof soulmateManager.createChatToggle === 'function') {
        debugLog('🎯 基础分析完成，创建小人按钮');
        soulmateManager.createChatToggle();
      }

      // ❌ 移除：不再调用 addSoulmateGuideAfterAnalysis，因为引导面板已包含灵魂伴侣功能
      // 引导面板会在 createAnalysisCompletionGuide() 中统一显示

      debugLog('✅ [优化] 入口创建完成（不弹窗）');
    }
  }, ['createSoulmateChatInterface', 'createChatToggle']);
}

/**
 * 触发灵魂伴侣弹窗（用于深度分析完成后）
 * @param {number} delayTime - 延迟时间（毫秒）
 * @param {boolean} shouldShowModal - 是否显示弹窗（默认true）
 * @param {boolean} isDeepAnalysis - 是否为深度分析场景（默认true）
 */
function triggerSoulmateModal(delayTime, shouldShowModal = true, isDeepAnalysis = true) {
  debugLog('🔍 [优化] triggerSoulmateModal 函数被调用', {
    delayTime,
    shouldShowModal,
    isDeepAnalysis
  });
  debugLog('🔍 [优化] soulmateManager 存在:', !!window.soulmateManager);

  // ✅ 优化：确保入口已创建
  // ✅ 修复：等待 soulmateManager 加载完成后再调用
  waitForSoulmateManager((soulmateManager) => {
    if (soulmateManager && typeof soulmateManager.createSoulmateChatInterface === 'function') {
      debugLog('✅ [优化] 开始创建/确保入口存在...');

      // 创建聊天界面（如果不存在）
      if (!document.getElementById('soulmate-chat-interface')) {
        debugLog('🎯 深度分析完成，确保聊天界面已创建');
        soulmateManager.createSoulmateChatInterface();
      }

      // 创建小人按钮（如果不存在）
      if (!document.getElementById('soulmate-chat-toggle') && typeof soulmateManager.createChatToggle === 'function') {
        debugLog('🎯 深度分析完成，确保小人按钮已创建');
        soulmateManager.createChatToggle();
      }

      // 在分析结果页面添加引导（如果不存在）
      timerManager.setTimeout(() => {
        addSoulmateGuideAfterAnalysis();
      }, 500);
    }
  }, ['createSoulmateChatInterface', 'createChatToggle']);

  // 如果需要显示弹窗，显示阅读提示
  if (shouldShowModal) {
    // ✅ 优化：根据场景显示不同的提示文案
    const hintText = isDeepAnalysis
      ? `📖 请仔细阅读深度分析内容，${Math.ceil(delayTime / 1000)}秒后将为您推荐灵魂伴侣功能`
      : `📖 ${Math.ceil(delayTime / 1000)}秒后将为您推荐灵魂伴侣功能`;

    const readingHint = document.createElement('div');
    readingHint.id = 'readingHint';
    readingHint.style.cssText = `
      text-align: center;
      margin: 10px 0;
      padding: 8px 12px;
      background: rgba(245, 158, 11, 0.1);
      border-radius: 8px;
      border-left: 3px solid #f59e0b;
      font-size: 0.85rem;
      color: #92400e;
      font-family: 'Noto Serif SC', serif;
    `;
    readingHint.innerHTML = hintText;

    const chatBox = document.getElementById('chatBox');
    if (chatBox) {
      chatBox.appendChild(readingHint);
      stickBottom();

      timerManager.setTimeout(() => {
        const hint = document.getElementById('readingHint');
        if (hint) hint.remove();
      }, 3000);
    }

    // ✅ 优化：检查是否已打开过，避免重复弹窗
    const hasOpenedBefore = localStorage.getItem('soulmate_has_opened') === 'true';
    if (hasOpenedBefore) {
      debugLog('🔍 [优化] 用户已打开过灵魂伴侣，不重复弹窗');
      return;
    }

    timerManager.setTimeout(() => {
      debugLog('🎯 开始触发灵魂伴侣弹窗');
      localStorage.removeItem('soulmate_modal_shown');
      localStorage.removeItem('soulmate_trial');

      // 优先使用soulmateManager，如果失败则使用备用方案
      if (window.soulmateManager && typeof window.soulmateManager.showSoulmateTriggerModal === 'function') {
        try {
          debugLog('🎯 使用soulmateManager创建弹窗');
          window.soulmateManager.showSoulmateTriggerModal();
          debugLog('✅ soulmateManager弹窗创建成功');
        } catch (error) {
          debugError('❌ soulmateManager弹窗创建失败:', error);
          createSoulmateModalDirectly();
        }
      } else {
        debugLog('🎯 soulmateManager不可用，使用备用方案');
        createSoulmateModalDirectly();
      }
    }, delayTime);
  } else {
    debugLog('🔍 [优化] shouldShowModal 为 false，不显示弹窗');
  }
}

// ================= 回调管理 =================
const CallbackManager = {
  callbacks: new Map(),

  registerCallback(messageId, callback) {
    this.callbacks.set(messageId, {
      callback: callback,
      called: false,
      timestamp: Date.now()
    });
    debugLog(`🎯 注册回调: ${messageId}`);
  },

  executeCallback(messageId) {
    const callbackData = this.callbacks.get(messageId);
    if (callbackData && !callbackData.called) {
      callbackData.called = true;
      callbackData.callback();
      debugLog(`🎯 执行回调: ${messageId}`);
    }
  },

  clearCallback(messageId) {
    this.callbacks.delete(messageId);
  }
};

// ================= 分析进度体验优化 =================
function showAnalysisProgress() {
  // 立即显示结果区域并滚动到该区域
  const resultBlock = document.getElementById('resultBlock');
  if (resultBlock) {
    resultBlock.classList.remove('hidden');
    // 立即滚动到结果区域
    timerManager.setTimeout(() => {
      resultBlock.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  }

  // 显示通用的分析进度消息，等待系统识别结果
  const progressSteps = [
    { icon: "🔍", text: "倪大师正在分析您的信息...", duration: 1500 },
    { icon: "📚", text: "大师正在结合传统理论分析...", duration: 2000 },
    { icon: "✨", text: "正在为您生成个性化解读...", duration: 1500 },
    { icon: "🎯", text: "大师在为您准备专属建议...", duration: 1000 }
  ];

  let currentStep = 0;
  const showNextStep = () => {
    if (currentStep < progressSteps.length) {
      const step = progressSteps[currentStep];
      showProgressMessage(step);
      currentStep++;
      timerManager.setTimeout(showNextStep, step.duration);
    }
  };

  showNextStep();
}

function showProgressMessage(step) {
  const chatBox = document.getElementById('chatBox');
  if (!chatBox) return;

  const wrapper = document.createElement('div');
  wrapper.classList.add('msg', 'flex', 'justify-start', 'items-start');

  const bubble = document.createElement('div');
  bubble.classList.add('px-3', 'py-2', 'rounded-2xl', 'bubble', 'bubble-assistant');
  bubble.style.cssText = "background: rgba(26, 26, 46, 0.95) !important; backdrop-filter: blur(20px) !important; -webkit-backdrop-filter: blur(20px) !important; border: 1px solid rgba(102, 126, 234, 0.5) !important; color: rgba(255, 255, 255, 0.9) !important; box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3) !important;";

  const tag = document.createElement('div');
  tag.className = "role-tag role-assistant";
  tag.innerHTML = "🧙 倪大师";
  bubble.appendChild(tag);

  const content = document.createElement('div');
  content.innerHTML = `${step.icon} ${step.text}`;
  content.style.cssText = "color: rgba(255, 255, 255, 0.9) !important;";
  bubble.appendChild(content);

  wrapper.appendChild(bubble);
  chatBox.appendChild(wrapper);
  stickBottom();
}

// ================= 提交状态管理 =================
let _isSubmitting = false;

// ================= 核心分析功能 =================
async function submitData() {
  if (_isSubmitting) return;

  _isSubmitting = true;

  try {
    // 显示分析进度
    showAnalysisProgress();

    // 获取表单数据
    const formData = getFormData();

    // 验证数据
    if (!validateFormData(formData)) {
      return;
    }

    // 转换农历信息（如果需要）
    await convertToLunarIfNeeded(formData);

    // 发送分析请求
    const result = await sendAnalysisRequest(formData);

    // 处理分析结果
    await handleAnalysisResult(result);

  } catch (error) {
    console.error('分析失败:', error);
    showErrorToast('分析失败，请重试');
  } finally {
    _isSubmitting = false;
  }
}

function getFormData() {
  const formData = new FormData();
  const analysisType = document.getElementById('analysisType')?.value;

  // 添加分析类型
  if (analysisType) formData.append('analysisType', analysisType);

  // 添加性别
  const gender = document.querySelector('input[name="gender"]:checked')?.value;
  if (gender) formData.append('gender', gender);

  // 添加会话ID
  const sessionId = getOrInitSessionId();
  if (sessionId) formData.append('session_id', sessionId);

  // 根据分析类型收集不同的数据
  if (analysisType === 'bazi') {
    // 八字分析：只收集出生信息
    const birthDate = document.getElementById('birthDate')?.value;
    if (birthDate) formData.append('birthDate', birthDate);

    const birthTime = document.getElementById('birthTime')?.value;
    if (birthTime) formData.append('birthTime', birthTime);

    const birthPlace = document.getElementById('birthPlace')?.value;
    if (birthPlace) formData.append('birthPlace', birthPlace);

  } else if (analysisType === 'palm') {
    // 手相分析：收集手型和图片
    const handType = document.querySelector('input[name="hand"]:checked')?.value;
    debugLog('🔍 获取到的手型:', handType);
    if (handType) formData.append('handType', handType);

    const imageFile = document.getElementById('imageInput')?.files[0];
    if (imageFile) formData.append('file', imageFile);

  } else if (analysisType === 'face') {
    // 面相分析：只收集图片
    const imageFile = document.getElementById('imageInput')?.files[0];
    if (imageFile) formData.append('file', imageFile);
  }

  return formData;
}

function validateFormData(formData) {
  const analysisType = formData.get('analysisType');

  if (!analysisType) {
    showErrorToast('请选择分析类型');
    return false;
  }

  if (analysisType === 'bazi') {
    // 八字分析：检查出生信息
    if (!formData.get('birthDate')) {
      showErrorToast('请选择出生日期');
      return false;
    }
    if (!formData.get('birthTime')) {
      showErrorToast('请选择出生时间');
      return false;
    }
  } else if (analysisType === 'palm') {
    // 手相分析：检查手型和图片
    if (!formData.get('handType')) {
      showErrorToast('请选择手型');
      return false;
    }
    if (!formData.get('file')) {
      showErrorToast('请上传图片');
      return false;
    }
  } else if (analysisType === 'face') {
    // 面相分析：检查图片
    if (!formData.get('file')) {
      showErrorToast('请上传图片');
      return false;
    }
  }

  // 检查性别
  if (!formData.get('gender')) {
    showErrorToast('请选择性别');
    return false;
  }

  return true;
}

// 在提交前转换农历信息
async function convertToLunarIfNeeded(formData) {
  const analysisType = formData.get('analysisType');

  if (analysisType === 'bazi') {
    const birthDate = formData.get('birthDate');
    const birthTime = formData.get('birthTime');
    const birthPlace = formData.get('birthPlace');

    if (birthDate && birthTime) {
      try {
        debugLog('🔍 开始农历转换:', { birthDate, birthTime, birthPlace });

        // ✅ 修复：统一使用 getLunarInfoByAPI 函数
        const hourInt = parseInt(birthTime.split(':')[0], 10);
        const lunarResult = await getLunarInfoByAPI(birthDate, hourInt);

        // ✅ 修复：检查农历转换是否成功
        if (lunarResult && lunarResult.lunar_text && lunarResult.lunar_text !== '农历信息获取失败') {
          // ✅ 修复：构建完整的农历信息（包含年份）
          // lunarResult 包含：lunar_text, hour_label, zodiac, element, stem_branch
          const fullLunarText = lunarResult.lunar_text || '';
          const hourLabel = lunarResult.hour_label || '';
          const zodiac = lunarResult.zodiac || '';  // ✅ 新增：提取星座信息

          // 构建完整的农历文本（包含年份）
          const lunarWithHour = hourLabel ? `${fullLunarText} ${hourLabel}时` : fullLunarText;

          // ✅ 修复：构建完整的出生信息字符串（传给GPT，只传农历信息）
          // lunarWithHour 格式：甲辰年 八月初三 子时
          const completeBirthInfo = `${lunarWithHour} ${birthPlace || ''}`.trim();

          // 更新formData中的birth信息
          formData.set('birthInfo', completeBirthInfo);

          // ✅ 新增：保存星座信息到formData，供后续保存使用
          if (zodiac) {
            formData.set('zodiac', zodiac);
          }

          debugLog('🔍 农历转换成功（统一调用）:', lunarResult);
          debugLog('🔍 完整出生信息（仅农历）:', completeBirthInfo);
          debugLog('🔍 星座信息:', zodiac);
        } else {
          // 农历转换失败或返回数据无效
          console.warn('⚠️ 农历转换API响应失败:', {
            lunarResult: lunarResult,
            birthDate: birthDate,
            birthTime: birthTime
          });
          // 即使农历转换失败，也尝试使用公历日期（降级方案）
          const birthInfo = `${birthDate} ${birthTime} ${birthPlace || ''}`;
          formData.set('birthInfo', birthInfo);
          console.warn('⚠️ 使用公历日期作为降级方案:', birthInfo);
        }
      } catch (error) {
        console.error('❌ 农历转换失败:', error);
        // 即使农历转换失败，也使用原始信息
        const birthInfo = `${birthDate} ${birthTime} ${birthPlace || ''}`;
        formData.set('birthInfo', birthInfo);
      }
    }
  }

  return formData;
}

// 添加响应格式检查函数
function checkResponseFormat(result) {
  debugLog('🔍 检查响应格式:', result);

  const contentFields = ['reply', 'content', 'text', 'message'];
  const foundFields = [];

  for (const field of contentFields) {
    if (result[field]) {
      foundFields.push(field);
      debugLog(`🔍 找到${field}字段:`, result[field]);
    }
  }

  if (foundFields.length === 0) {
    console.error('❌ 未找到任何内容字段');
    debugLog('🔍 可用字段:', Object.keys(result));
    return null;
  }

  debugLog('🔍 找到的内容字段:', foundFields);
  return foundFields[0]; // 返回第一个找到的字段
}

async function sendAnalysisRequest(formData) {
  const analysisType = formData.get('analysisType');

  if (analysisType === 'bazi') {
    // 八字分析：使用/trigger端点
    debugLog('🔍 八字分析，使用/trigger端点');

    // 准备八字分析数据
    const baziData = {
      session_id: formData.get('session_id'),
      type: 'bazi',
      birth: formData.get('birthInfo') || `${formData.get('birthDate')} ${formData.get('birthTime')} ${formData.get('birthPlace')}`,
      gender: formData.get('gender')
    };

    debugLog('🔍 八字分析数据:', baziData);

    // ✅ P1-2修复：使用ApiClient，带重试和超时机制
    try {
      if (window.apiClient) {
        // ✅ 修复：为八字分析增加超时时间（60秒）
        const originalTimeout = window.apiClient.requestTimeout;
        if (analysisType === 'bazi') {
          window.apiClient.requestTimeout = 60000; // 60秒超时
        }

        try {
          const result = await window.apiClient.bjRequest('/trigger', baziData);
          debugLog('🔍 八字分析API响应成功:', result);

          // ✅ 修复：确保 result 包含 analysisType
          if (!result.analysisType && !result.recog_type) {
            result.analysisType = 'bazi';
            result.recog_type = 'bazi';
            debugLog('✅ 添加 analysisType: bazi');
          }

          // ✅ 恢复原始超时时间
          window.apiClient.requestTimeout = originalTimeout;

          return result;
        } catch (error) {
          // ✅ 恢复原始超时时间（即使出错也要恢复）
          window.apiClient.requestTimeout = originalTimeout;
          throw error;
        }
      } else {
        // 降级到直接fetch
        console.warn('⚠️ ApiClient不可用，使用直接fetch调用');
        const response = await fetch(`${CONFIG.BJ_API_BASE}/trigger`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(baziData)
        });

        debugLog('🔍 八字分析API响应状态:', response.status, response.statusText);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('❌ 八字分析请求失败:', response.status, errorText);
          throw new Error(`HTTP ${response.status}: ${errorText}`);
        }

        const result = await response.json();
        debugLog('🔍 八字分析API响应成功:', result);

        // ✅ 修复：确保 result 包含 analysisType（降级方案）
        if (!result.analysisType && !result.recog_type) {
          result.analysisType = 'bazi';
          result.recog_type = 'bazi';
          debugLog('✅ 添加 analysisType: bazi（降级方案）');
        }

        return result;
      }
    } catch (error) {
      console.error('❌ 八字分析请求失败:', error);
      // ✅ P1-3修复：使用 ErrorHandler 转换错误为友好提示
      if (typeof ErrorHandler !== 'undefined' && ErrorHandler.getUserFriendlyError) {
        const errorInfo = ErrorHandler.getUserFriendlyError(error, 'bazi_analysis_failed', {
          showContact: false
        });
        const friendlyError = new Error(errorInfo.userMessage.split('\n')[0]);
        friendlyError.originalError = error;
        throw friendlyError;
      } else {
        throw error;
      }
    }

  } else {
    // 面相和手相分析：使用/analyze_and_gpt端点
    debugLog('🔍 面相/手相分析，使用/analyze_and_gpt端点');

    // 详细调试：打印FormData内容
    debugLog('🔍 发送的FormData内容:');
    for (let [key, value] of formData.entries()) {
      debugLog(`${key}:`, value, typeof value);
    }

    // 打印完整请求信息
    debugLog('🔍 完整请求信息:', {
      url: '/analyze_and_gpt',
      method: 'POST',
      contentType: 'multipart/form-data',
      hasFile: formData.get('file') ? 'Yes' : 'No',
      analysisType: formData.get('analysisType'),
      gender: formData.get('gender'),
      handType: formData.get('handType'),
      sessionId: formData.get('session_id')
    });

    // ✅ P1-2修复：优先使用ApiClient，带重试和超时机制
    try {
      debugLog('🔍 使用ApiClient调用API');

      // 确保ApiClient可用
      if (window.apiClient) {
        const result = await window.apiClient.bjRequest('/analyze_and_gpt', formData);

        // ✅ 修复：确保 result 包含 analysisType
        if (!result.analysisType && !result.recog_type) {
          const analysisType = formData.get('analysisType');
          if (analysisType) {
            result.analysisType = analysisType;
            result.recog_type = analysisType;
            debugLog('✅ 添加 analysisType:', analysisType);
          }
        }

        return result;
      } else {
        // 降级到直接fetch
        console.warn('⚠️ ApiClient不可用，使用直接fetch调用');
        const response = await fetch(`${CONFIG.BJ_API_BASE}/analyze_and_gpt`, {
          method: 'POST',
          body: formData  // 直接使用FormData，不设置Content-Type
        });

        debugLog('🔍 API响应状态:', response.status, response.statusText);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('🔍 API错误详情:', errorText);
          throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
        }

        const result = await response.json();
        debugLog('🔍 API响应成功:', result);

        // ✅ 修复：确保 result 包含 analysisType（降级方案）
        if (!result.analysisType && !result.recog_type) {
          const analysisType = formData.get('analysisType');
          if (analysisType) {
            result.analysisType = analysisType;
            result.recog_type = analysisType;
            debugLog('✅ 添加 analysisType:', analysisType, '（降级方案）');
          }
        }

        debugLog('🔍 响应数据结构:', {
          hasContent: 'content' in result,
          hasText: 'text' in result,
          hasMessage: 'message' in result,
          hasData: 'data' in result,
          keys: Object.keys(result),
          contentValue: result.content,
          textValue: result.text,
          messageValue: result.message
        });
        return result;
      }
    } catch (error) {
      console.error('🔍 API调用失败:', error);
      // ✅ P1-3修复：将技术错误转换为友好提示后抛出
      if (typeof ErrorHandler !== 'undefined' && ErrorHandler.getUserFriendlyError) {
        const errorInfo = ErrorHandler.getUserFriendlyError(error, 'api_request_failed', {
          showContact: false
        });
        // 创建一个新的错误对象，包含用户友好的消息
        const friendlyError = new Error(errorInfo.userMessage.split('\n')[0]);
        friendlyError.originalError = error;
        throw friendlyError;
      } else {
        throw error;
      }
    }
  }
}
async function handleAnalysisResult(result) {
  // 添加详细的调试信息
  debugLog('🔍 完整API响应数据:', JSON.stringify(result, null, 2));
  debugLog('🔍 数据类型:', typeof result);
  debugLog('🔍 数据键值:', Object.keys(result));

  // ✅ 修复：检查是否是异步任务（有job_id且状态为pending或没有内容）
  if (result.job_id && (result.status === 'pending' || (!result.content && !result.analysis_text && !result.reply && !result.text))) {
    debugLog('🔍 检测到异步任务，开始轮询任务状态...', result.job_id);

    // 显示等待提示
    const chatBox = document.getElementById('chatBox');
    if (chatBox) {
      const waitingMsg = document.createElement('div');
      waitingMsg.className = 'message assistant';
      waitingMsg.innerHTML = '<div style="color: #999;">⏳ 分析任务处理中，请稍候...</div>';
      chatBox.appendChild(waitingMsg);
      stickBottom();
    }

    // ✅ 修复：等待一小段时间，确保任务已被创建
    await new Promise(resolve => timerManager.setTimeout(resolve, 500)); // 等待 500ms

    // 轮询任务状态
    const maxRetries = 60; // 最多轮询60次（约2分钟）
    const pollInterval = 2000; // 每2秒轮询一次

    try {
      for (let i = 0; i < maxRetries; i++) {
        const statusResponse = await fetch(`${CONFIG.BJ_API_BASE}/analysis_status/${result.job_id}`);
        if (!statusResponse.ok) {
          // ✅ 修复：如果是404，说明任务不存在，停止轮询
          if (statusResponse.status === 404) {
            const errorData = await statusResponse.json().catch(() => ({}));
            throw new Error(errorData.message || '分析任务不存在或已过期，请重新提交');
          }
          throw new Error(`HTTP ${statusResponse.status}: ${statusResponse.statusText}`);
        }

        const statusData = await statusResponse.json();
        debugLog(`🔍 任务状态 [${i + 1}/${maxRetries}]:`, statusData);

        // ✅ 修复：检查任务完成状态（支持 "succeeded", "local_done", "done"）
        // 或者检查是否有分析内容（analysis_text, reply, content, text）
        const hasContent = statusData.analysis_text || statusData.reply || statusData.content || statusData.text || statusData.message;
        const isCompleted = statusData.status === 'succeeded' ||
          statusData.status === 'local_done' ||
          statusData.status === 'done' ||
          (hasContent && statusData.status !== 'pending' && statusData.status !== 'running' && statusData.status !== 'queued');

        if (isCompleted) {
          // 任务完成，合并结果
          debugLog('✅ 异步任务完成，合并结果');

          // 移除等待提示
          if (chatBox) {
            const waitingMsg = chatBox.querySelector('.message.assistant:last-child');
            if (waitingMsg && waitingMsg.textContent.includes('分析任务处理中')) {
              waitingMsg.remove();
            }
          }

          // 合并结果数据
          if (statusData.result) {
            result = { ...result, ...statusData.result };
          } else {
            result = { ...result, ...statusData };
          }
          break;
        } else if (statusData.status === 'failed' || statusData.status === 'error') {
          // 移除等待提示
          if (chatBox) {
            const waitingMsg = chatBox.querySelector('.message.assistant:last-child');
            if (waitingMsg && waitingMsg.textContent.includes('分析任务处理中')) {
              waitingMsg.remove();
            }
          }
          throw new Error(statusData.error || statusData.message || '任务执行失败');
        }

        // 等待后继续轮询
        await new Promise(resolve => timerManager.setTimeout(resolve, pollInterval));
      }

      // 如果轮询超时仍未完成
      if (!result.content && !result.analysis_text && !result.reply && !result.text) {
        // 移除等待提示
        if (chatBox) {
          const waitingMsg = chatBox.querySelector('.message.assistant:last-child');
          if (waitingMsg && waitingMsg.textContent.includes('分析任务处理中')) {
            waitingMsg.remove();
          }
        }
        throw new Error('分析任务超时，请稍后重试');
      }
    } catch (error) {
      console.error('❌ 轮询任务状态失败:', error);
      // 移除等待提示
      if (chatBox) {
        const waitingMsg = chatBox.querySelector('.message.assistant:last-child');
        if (waitingMsg && waitingMsg.textContent.includes('分析任务处理中')) {
          waitingMsg.remove();
        }
      }
      throw error;
    }
  }

  // ✅ 修复：如果 result 中没有 analysisType，从表单获取
  if (!result.analysisType && !result.recog_type) {
    const analysisTypeSelect = document.getElementById('analysisType');
    if (analysisTypeSelect && analysisTypeSelect.value) {
      result.analysisType = analysisTypeSelect.value;
      result.recog_type = analysisTypeSelect.value;
      debugLog('✅ 从表单获取 analysisType:', result.analysisType);
    }
  }

  // 检查每个可能的字段
  debugLog('🔍 content字段:', result.content);
  debugLog('🔍 text字段:', result.text);
  debugLog('🔍 message字段:', result.message);
  debugLog('🔍 data字段:', result.data);
  debugLog('🔍 response字段:', result.response);
  debugLog('🔍 result字段:', result.result);
  debugLog('🔍 analysis字段:', result.analysis);
  debugLog('🔍 output字段:', result.output);
  debugLog('🔍 analysis_result字段:', result.analysis_result);
  debugLog('🔍 analysisResult字段:', result.analysisResult);
  debugLog('🔍 result_text字段:', result.result_text);
  debugLog('🔍 resultText字段:', result.resultText);
  debugLog('🔍 所有字段:', Object.keys(result));

  // 尝试从更多字段获取内容（包括 analysis_text，这是API实际返回的字段）
  const content = result.analysis_text ||  // ✅ 修复：添加 analysis_text 字段检查（这是API实际返回的字段）
    result.reply ||          // ✅ 修复：添加 reply 字段检查
    result.content ||
    result.text ||
    result.message ||
    result.data?.content ||
    result.data?.text ||
    result.response?.content ||
    result.result?.content ||
    result.analysis?.content ||
    result.output?.content ||
    result.analysis_result ||
    result.analysisResult ||
    result.result_text ||
    result.resultText;

  debugLog('🔍 提取的内容:', content);
  debugLog('🔍 内容长度:', content?.length || 0);

  // 显示分析结果（传入已提取的 content，避免重复提取）
  displayAnalysisResult(result, content);

  // ✅ 保存分析数据到 localStorage（供灵魂伴侣使用）
  if (content && content.length > 50) {
    try {
      const analysisType = result.analysisType || result.recog_type || '';
      const sessionId = CodeUtils?.safeGetStorage('session_id', '') || getOrInitSessionId();

      debugLog('💾 保存分析数据到localStorage...', {
        analysisType,
        contentLength: content.length,
        sessionId: sessionId ? '有' : '无'
      });

      // 获取现有数据
      const existingData = CodeUtils?.safeGetStorage('user_analysis_data', {}) || {};

      // ✅ 修复：如果个人信息没有内容，默认是用户本人
      if (!existingData.profile) {
        existingData.profile = {
          target_type: 'self',  // ✅ 默认是用户本人
          name: '',
          gender: '',
          age: '',
          notes: '',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
      } else if (!existingData.profile.target_type) {
        existingData.profile.target_type = 'self';
        existingData.profile.updated_at = new Date().toISOString();
      }

      // 构建分析数据对象
      const analysisData = {
        session_id: sessionId,
        analysis_time: new Date().toISOString(),
        version: '1.0',
        has_data: true,
        profile: existingData.profile  // ✅ 包含 profile 信息
      };
      ensureAnalysisProfileTargetSelf(analysisData);

      // ✅ 修复：获取性别信息（所有分析类型都需要性别）
      const genderInput = document.querySelector('input[name="gender"]:checked');
      const gender = genderInput ? genderInput.value : null;

      // ✅ 修复：根据分析类型保存完整的数据
      if (analysisType === 'palm' || analysisType === 'hand') {
        // ✅ 修复：获取手型信息
        const handTypeInput = document.querySelector('input[name="hand"]:checked');
        const handType = handTypeInput ? handTypeInput.value : null;

        // ✅ 新增：获取features数据（如果后端返回了）
        const features = result.features || null;

        analysisData.palm_analysis = {
          has_data: true,
          content: content.substring(0, 1000), // 限制长度
          gender: gender,  // ✅ 保存性别信息
          handType: handType,  // ✅ 新增：保存手型信息
          analysis_time: new Date().toISOString()
        };

        // ✅ 新增：如果后端返回了features，保存到分析数据中
        if (features && typeof features === 'object') {
          analysisData.palm_analysis.features = features;
          debugLog('✅ 保存手相分析数据（包含features）', {
            hasGender: !!gender, gender,
            hasHandType: !!handType, handType,
            featuresCount: Object.keys(features).length
          });
        } else {
          debugLog('✅ 保存手相分析数据', { hasGender: !!gender, gender, hasHandType: !!handType, handType });
        }
      } else if (analysisType === 'face') {
        // ✅ 新增：获取features数据（如果后端返回了）
        const features = result.features || null;

        analysisData.face_analysis = {
          has_data: true,
          content: content.substring(0, 1000),
          gender: gender,  // ✅ 保存性别信息
          analysis_time: new Date().toISOString()
        };

        // ✅ 新增：如果后端返回了features，保存到分析数据中
        if (features && typeof features === 'object') {
          analysisData.face_analysis.features = features;
          debugLog('✅ 保存面相分析数据（包含features）', {
            hasGender: !!gender, gender,
            featuresCount: Object.keys(features).length
          });
        } else {
          debugLog('✅ 保存面相分析数据', { hasGender: !!gender, gender });
        }
      } else if (analysisType === 'bazi') {
        // ✅ 修复：获取完整的出生信息
        const birthDateInput = document.getElementById('birthDate');
        const birthTimeInput = document.getElementById('birthTime');
        const birthPlaceInput = document.getElementById('birthPlace');

        const birthDate = birthDateInput ? birthDateInput.value : null;
        const birthTime = birthTimeInput ? birthTimeInput.value : null;  // ✅ 新增：保存出生时间
        const birthPlace = birthPlaceInput ? birthPlaceInput.value : null;  // ✅ 新增：保存出生地点

        // ✅ 新增：从formData中获取星座信息（在convertToLunarIfNeeded中已保存）
        // 注意：由于handleAnalysisResult是异步回调，formData可能已经不在作用域内
        // 所以我们需要从分析结果或其他地方获取，或者从页面元素中读取
        // 如果formData不可用，尝试从分析内容中提取星座
        let zodiac = '';

        // 方法1：尝试从分析内容中提取星座（如果API返回了星座信息）
        const zodiacMatch = content.match(/(白羊座|金牛座|双子座|巨蟹座|狮子座|处女座|天秤座|天蝎座|射手座|摩羯座|水瓶座|双鱼座)/);
        if (zodiacMatch) {
          zodiac = zodiacMatch[1];
        }

        // 方法2：如果分析内容中没有，尝试根据出生日期计算星座（备用方案）
        if (!zodiac && birthDate) {
          // 从出生日期计算星座（公历）
          const dateParts = birthDate.split('-');
          if (dateParts.length === 3) {
            const month = parseInt(dateParts[1], 10);
            const day = parseInt(dateParts[2], 10);

            // 星座日期范围
            if ((month === 1 && day >= 20) || (month === 2 && day <= 18)) zodiac = '水瓶座';
            else if ((month === 2 && day >= 19) || (month === 3 && day <= 20)) zodiac = '双鱼座';
            else if ((month === 3 && day >= 21) || (month === 4 && day <= 19)) zodiac = '白羊座';
            else if ((month === 4 && day >= 20) || (month === 5 && day <= 20)) zodiac = '金牛座';
            else if ((month === 5 && day >= 21) || (month === 6 && day <= 20)) zodiac = '双子座';
            else if ((month === 6 && day >= 21) || (month === 7 && day <= 22)) zodiac = '巨蟹座';
            else if ((month === 7 && day >= 23) || (month === 8 && day <= 22)) zodiac = '狮子座';
            else if ((month === 8 && day >= 23) || (month === 9 && day <= 22)) zodiac = '处女座';
            else if ((month === 9 && day >= 23) || (month === 10 && day <= 22)) zodiac = '天秤座';
            else if ((month === 10 && day >= 23) || (month === 11 && day <= 21)) zodiac = '天蝎座';
            else if ((month === 11 && day >= 22) || (month === 12 && day <= 21)) zodiac = '射手座';
            else if ((month === 12 && day >= 22) || (month === 1 && day <= 19)) zodiac = '摩羯座';
          }
        }

        analysisData.bazi_analysis = {
          has_data: true,
          content: content.substring(0, 1000),
          birthDate: birthDate,    // ✅ 保存出生日期
          birthTime: birthTime,     // ✅ 新增：保存出生时间
          birthPlace: birthPlace,   // ✅ 新增：保存出生地点
          gender: gender,  // ✅ 保存性别信息
          zodiac: zodiac,  // ✅ 新增：保存星座信息
          analysis_time: new Date().toISOString()
        };
        debugLog('✅ 保存八字分析数据', {
          hasBirthDate: !!birthDate, birthDate,
          hasBirthTime: !!birthTime, birthTime,  // ✅ 新增
          hasBirthPlace: !!birthPlace, birthPlace,  // ✅ 新增
          hasGender: !!gender, gender,
          hasZodiac: !!zodiac, zodiac
        });

        // ✅ 新增：八字分析完成后，自动计算并保存年龄到 profile.age
        if (birthDate) {
          try {
            // 计算年龄
            const birthDateObj = new Date(birthDate);
            if (!isNaN(birthDateObj.getTime())) {
              const currentDate = new Date();
              let calculatedAge = currentDate.getFullYear() - birthDateObj.getFullYear();
              const monthDiff = currentDate.getMonth() - birthDateObj.getMonth();
              if (monthDiff < 0 || (monthDiff === 0 && currentDate.getDate() < birthDateObj.getDate())) {
                calculatedAge--;
              }

              // 确保年龄是有效值
              if (calculatedAge > 0 && calculatedAge < 150) {
                // 确保 profile 对象存在
                if (!analysisData.profile) {
                  analysisData.profile = {
                    target_type: 'self',
                    name: '',
                    gender: '',
                    age: '',
                    notes: '',
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString()
                  };
                }

                // 如果 profile.age 不存在或需要更新，则保存年龄
                if (!analysisData.profile.age || analysisData.profile.age !== calculatedAge) {
                  analysisData.profile.age = calculatedAge;
                  analysisData.profile.updated_at = new Date().toISOString();
                  debugLog('✅ 八字分析完成，自动计算并保存年龄:', calculatedAge, '岁');
                }
              }
            }
          } catch (e) {
            console.warn('⚠️ 计算年龄失败:', e);
          }
        }
      }

      // ✅ 修复：如果 target_type='self'，更新所有相关数据
      if (analysisData.profile && analysisData.profile.target_type === 'self') {
        // 用户重新分析自己的信息，合并现有数据
        if (existingData.palm_analysis && analysisType !== 'palm' && analysisType !== 'hand') {
          analysisData.palm_analysis = existingData.palm_analysis;
        }
        if (existingData.face_analysis && analysisType !== 'face') {
          analysisData.face_analysis = existingData.face_analysis;
        }
        if (existingData.bazi_analysis && analysisType !== 'bazi') {
          analysisData.bazi_analysis = existingData.bazi_analysis;
        }

        // 更新分析时间
        analysisData.analysis_time = new Date().toISOString();
        analysisData.profile.updated_at = new Date().toISOString();

        debugLog('✅ 用户重新分析自己的信息，已更新所有相关数据');
      } else {
        // 如果不是用户本人的数据，只保留其他类型的已有数据
        if (existingData.palm_analysis && analysisType !== 'palm' && analysisType !== 'hand') {
          analysisData.palm_analysis = existingData.palm_analysis;
        }
        if (existingData.face_analysis && analysisType !== 'face') {
          analysisData.face_analysis = existingData.face_analysis;
        }
        if (existingData.bazi_analysis && analysisType !== 'bazi') {
          analysisData.bazi_analysis = existingData.bazi_analysis;
        }
      }

      // 保存到 localStorage
      if (CodeUtils?.safeSetStorage) {
        CodeUtils.safeSetStorage('user_analysis_data', analysisData);
        debugLog('✅ 分析数据已保存到localStorage', {
          hasProfile: !!analysisData.profile,
          profileAge: analysisData.profile?.age,
          profileGender: analysisData.profile?.gender
        });
      } else {
        localStorage.setItem('user_analysis_data', JSON.stringify(analysisData));
        debugLog('✅ 分析数据已保存到localStorage（备用方法）', {
          hasProfile: !!analysisData.profile,
          profileAge: analysisData.profile?.age,
          profileGender: analysisData.profile?.gender
        });
      }

      // ✅ 修复：延迟调用 saveAnalysisData，确保数据已保存到 localStorage
      timerManager.setTimeout(() => {
        ensureLocalAnalysisProfileTargetSelf();
        waitForSoulmateManager((soulmateManager) => {
          if (soulmateManager && typeof soulmateManager.saveAnalysisData === 'function') {
            debugLog('✅ soulmateManager 已加载，开始保存分析数据...');
            soulmateManager.saveAnalysisData();
          } else {
            debugWarn('⚠️ soulmateManager.saveAnalysisData 方法不可用');
          }
        }, 'saveAnalysisData', 100, 5000);
      }, 200); // ✅ 延迟200ms，确保 localStorage 写入完成

    } catch (error) {
      debugError('❌ 保存分析数据失败:', error);
    }
  }

  // ✅ 新增：分析完成后检查成就（saveAnalysisData 已在上面调用）
  if (content && content.length > 50) {
    // ✅ 新增：检查成就（完成分析后）
    waitForSoulmateManager((soulmateManager) => {
      if (soulmateManager && typeof soulmateManager.checkAchievements === 'function') {
        debugLog('✅ 检查成就...');
        soulmateManager.checkAchievements();
      }
    }, 'checkAchievements', 500, 5000); // ✅ 修复：正确传递参数 (methodNames, delay, maxWait)
  }

  // ✅ 触发灵魂伴侣弹窗（已移到打字效果完成后，见 displayAnalysisResult 函数）
  // 此处不再立即触发，等待打字效果完成后再触发（避免在打字过程中弹出）
  debugLog('🔍 [P0-1修复] 基础分析完成，将在打字效果完成后触发灵魂伴侣弹窗');

  // 记录分析完成
  safeTrackUserAction('analysis_completed', {
    analysisType: result.analysisType,
    resultLength: content?.length || 0,
    timestamp: new Date().toISOString()
  });
}

// ✅ 优化：提取样式常量，避免重复代码
const FORMAT_STYLES = {
  PARAGRAPH: "margin: 15px 0; line-height: 1.8; text-align: justify;",
  PRIMARY_COLOR: "#8b5e00",
  SECONDARY_COLOR: "#666"
};

// ✅ 优化：生成标题HTML的函数
function generateHeadingHTML(level, content, paragraphStyle) {
  const styles = {
    h1: `color: ${FORMAT_STYLES.PRIMARY_COLOR}; font-weight: bold; margin: 25px 0 20px 0; font-size: 22px; text-align: center; background: linear-gradient(135deg, ${FORMAT_STYLES.PRIMARY_COLOR}, #6b4c00); color: white; padding: 15px; border-radius: 8px;`,
    h2: `color: ${FORMAT_STYLES.PRIMARY_COLOR}; font-weight: bold; margin: 20px 0 15px 0; font-size: 20px; border-bottom: 2px solid ${FORMAT_STYLES.PRIMARY_COLOR}; padding-bottom: 5px;`,
    h3: `color: ${FORMAT_STYLES.PRIMARY_COLOR}; font-weight: bold; margin: 15px 0 10px 0; font-size: 18px; border-left: 4px solid ${FORMAT_STYLES.PRIMARY_COLOR}; padding-left: 15px;`
  };
  return `</p><h${level} style="${styles['h' + level]}">${content}</h${level}><p style="${paragraphStyle}">`;
}

// 添加文本格式化处理函数
function formatAnalysisText(text) {
  if (!text) return '';

  // 获取用户昵称
  const nickname = localStorage.getItem('user_nickname') || '朋友';

  // 处理特殊符号和格式
  let formattedText = text;

  // ✅ 优化：统一昵称替换逻辑，使用数组循环减少重复代码
  // 优先处理带引号的昵称替换（后端可能返回带引号的格式）
  const quotedReplacements = [
    { pattern: /^"亲爱的朋友",,/g, replacement: '"亲爱的' + nickname + '"，' },
    { pattern: /^"亲爱的朋友"/g, replacement: '"亲爱的' + nickname + '"' },
    { pattern: /^"亲爱的你",,/g, replacement: '"亲爱的' + nickname + '"，' },
    { pattern: /^"亲爱的你"/g, replacement: '"亲爱的' + nickname + '"' },
    { pattern: /^"朋友",,/g, replacement: '"' + nickname + '"，' },
    { pattern: /^"朋友"/g, replacement: '"' + nickname + '"' },
    { pattern: /^"您",,/g, replacement: '"' + nickname + '"，' },
    { pattern: /^"您"/g, replacement: '"' + nickname + '"' }
  ];
  quotedReplacements.forEach(({ pattern, replacement }) => {
    formattedText = formattedText.replace(pattern, replacement);
  });

  // 处理开头的称呼（使用昵称）
  const startReplacements = [
    { pattern: /^亲爱的你/g, replacement: '亲爱的' + nickname },
    { pattern: /^亲爱的朋友/g, replacement: '亲爱的' + nickname },
    { pattern: /^亲爱的您/g, replacement: '亲爱的' + nickname },
    { pattern: /^朋友/g, replacement: nickname },
    { pattern: /^您/g, replacement: nickname },
    { pattern: /^这位朋友/g, replacement: nickname },
    { pattern: /^这位/g, replacement: nickname }
  ];
  startReplacements.forEach(({ pattern, replacement }) => {
    formattedText = formattedText.replace(pattern, replacement);
  });

  // 处理段落开头的称呼（使用昵称）
  const paragraphReplacements = [
    { pattern: /\n亲爱的你/g, replacement: '\n亲爱的' + nickname },
    { pattern: /\n亲爱的朋友/g, replacement: '\n亲爱的' + nickname },
    { pattern: /\n亲爱的您/g, replacement: '\n亲爱的' + nickname },
    { pattern: /\n朋友/g, replacement: '\n' + nickname },
    { pattern: /\n您/g, replacement: '\n' + nickname },
    { pattern: /\n这位朋友/g, replacement: '\n' + nickname },
    { pattern: /\n这位/g, replacement: '\n' + nickname }
  ];
  paragraphReplacements.forEach(({ pattern, replacement }) => {
    formattedText = formattedText.replace(pattern, replacement);
  });

  // 处理特定称呼组合
  const specialReplacements = [
    { pattern: /你这小伙子/g, replacement: nickname + '，' },
    { pattern: /这位小伙子/g, replacement: nickname + '，' },
    { pattern: /这位年轻人/g, replacement: nickname + '，' },
    { pattern: /这位小友/g, replacement: nickname + '，' },
    { pattern: /你这宝贝/g, replacement: nickname + '，' }
  ];
  specialReplacements.forEach(({ pattern, replacement }) => {
    formattedText = formattedText.replace(pattern, replacement);
  });

  // 最后处理中间内容的称呼（避免覆盖前面的昵称替换）
  // 只替换没有被前面规则处理的"朋友"和"您"
  formattedText = formattedText.replace(/(?<!亲爱的)朋友/g, '你');
  formattedText = formattedText.replace(/(?<!亲爱的)您/g, '你');

  // 处理数字标题（如"1.【签文诗句】"）的段落分隔 - 使用HTML标签强制分隔
  formattedText = formattedText.replace(/^(\d+\.【[^】]+】)/gm,
    `</p><div style="color: ${FORMAT_STYLES.PRIMARY_COLOR}; font-weight: bold; font-size: 16px; margin: 20px 0 10px 0; padding: 8px 12px; background: rgba(139, 94, 0, 0.1); border-left: 4px solid ${FORMAT_STYLES.PRIMARY_COLOR}; border-radius: 4px;">$1</div><p style="${FORMAT_STYLES.PARAGRAPH}">`);

  // ✅ 优化：使用统一的标题生成函数
  formattedText = formattedText.replace(/^### (.*$)/gim, (match, content) =>
    generateHeadingHTML(3, content, FORMAT_STYLES.PARAGRAPH));
  formattedText = formattedText.replace(/^## (.*$)/gim, (match, content) =>
    generateHeadingHTML(2, content, FORMAT_STYLES.PARAGRAPH));
  formattedText = formattedText.replace(/^# (.*$)/gim, (match, content) =>
    generateHeadingHTML(1, content, FORMAT_STYLES.PARAGRAPH));

  // 处理粗体和斜体
  formattedText = formattedText.replace(/\*\*(.*?)\*\*/g,
    `<strong style="color: ${FORMAT_STYLES.PRIMARY_COLOR}; font-weight: bold;">$1</strong>`);
  formattedText = formattedText.replace(/\*(.*?)\*/g,
    `<em style="color: ${FORMAT_STYLES.SECONDARY_COLOR}; font-style: italic;">$1</em>`);

  // ✅ 优化：合并逗号清理逻辑，保持原有逻辑但更简洁
  formattedText = formattedText
    .replace(/",,\s*/g, '"，')  // 引号后的双逗号
    .replace(/",，/g, '"，')    // 引号后英文逗号+中文逗号
    .replace(/，，+/g, '，')    // 多个中文逗号
    .replace(/,,+/g, '，');    // 多个英文逗号（不在引号后）

  // 处理段落分隔 - 强制使用HTML标签
  formattedText = formattedText.replace(/\n\n+/g,
    `</p><p style="${FORMAT_STYLES.PARAGRAPH}">`);

  // 处理单换行符为换行标签
  formattedText = formattedText.replace(/\n/g, '<br>');

  // 包装整个内容为段落
  formattedText = `<p style="${FORMAT_STYLES.PARAGRAPH}">${formattedText}</p>`;

  // 处理特殊符号
  formattedText = formattedText.replace(/【(.*?)】/g,
    `<span style="color: ${FORMAT_STYLES.PRIMARY_COLOR}; font-weight: bold; background: rgba(139, 94, 0, 0.1); padding: 2px 6px; border-radius: 4px;">【$1】</span>`);

  return formattedText;
}

// ✅ 优化：HTML内容的模拟打字效果（预渲染HTML + 遮罩逐字显示，消除字体跳动）
// 简化版本：移除复杂的requestAnimationFrame和transition，直接更新，更可靠
function simulateTypingForHTML(htmlText, contentEl, onComplete = null) {
  if (!htmlText || !contentEl) return;

  const textContent = htmlToTextForTyping(htmlText);
  const htmlSegments = splitHTMLIntoSegments(htmlText);
  
  let segmentIndex = 0;
  
  // 使用统一的打字效果，但需要处理HTML切换
  unifiedTypingEffect(contentEl, textContent, {
    onComplete: () => {
      // 打字完成后，显示完整的HTML
      contentEl.innerHTML = htmlText;
      // ✅ 优化：打字完成后才使用平滑滚动
      stickBottom();
      if (onComplete && typeof onComplete === 'function') {
        onComplete();
      }
    },
    soundEnabled: true,
    pauseEnabled: false,
    scrollEnabled: true,
    scrollInterval: 15,  // ✅ 优化：改为15，减少滚动频率，减少翻页时的抖动
    onScroll: () => {
      // 在滚动时检查是否需要切换HTML
      const currentText = contentEl.textContent;
      const textIndex = currentText.length;
      const shouldSwitchToHTML = checkIfShouldSwitchToHTML(textIndex, htmlSegments, segmentIndex);
      
      if (shouldSwitchToHTML) {
        const htmlToShow = htmlSegments.slice(0, segmentIndex + 1).join('');
        contentEl.innerHTML = htmlToShow;
        segmentIndex++;
      }
      
      // ✅ 优化：打字过程中使用立即滚动，不使用stickBottom()（避免平滑滚动冲突）
      // ✅ 修复：直接滚动，不要双重requestAnimationFrame嵌套（外层已经包装了）
      const chatBox = document.getElementById('chatBox');
      if (chatBox) {
        chatBox.scrollTop = chatBox.scrollHeight;
      }
    },
    startDelay: 200
  });
}

// 新增函数：将HTML按语义单位分割
function splitHTMLIntoSegments(htmlText) {
  const segments = [];

  // 首先尝试按段落分割（支持p、div等标签）
  const paragraphRegex = /<\/p>|<\/div>/g;
  const parts = htmlText.split(paragraphRegex);

  if (parts.length > 1) {
    // 有段落结构，按段落分割
    let currentSegment = '';
    for (let i = 0; i < parts.length; i++) {
      if (parts[i].trim()) {
        currentSegment += parts[i];
        // 检查是否包含完整的HTML标签对
        if (currentSegment.includes('<') && currentSegment.includes('>')) {
          // 补上结束标签
          const endTag = htmlText.match(paragraphRegex)?.[i] || '';
          currentSegment += endTag;
          segments.push(currentSegment);
          currentSegment = '';
        }
      }
    }
    if (currentSegment.trim()) {
      segments.push(currentSegment);
    }
  }

  // 如果没有段落结构，按句子分割
  if (segments.length <= 1) {
    const textContent = htmlText.replace(/<[^>]*>/g, '');
    const sentences = textContent.split(/([。！？])/);
    let currentSegment = '';
    let htmlIndex = 0;

    for (let i = 0; i < sentences.length; i += 2) {
      if (sentences[i]) {
        currentSegment += sentences[i] + (sentences[i + 1] || '');

        // 每2-3个句子为一段，或者到达末尾
        if (currentSegment.length > 100 || i >= sentences.length - 2) {
          // 为这段文本找到对应的HTML部分
          const segmentHtml = extractHTMLSegment(htmlText, currentSegment, htmlIndex);
          segments.push(segmentHtml);
          htmlIndex += segmentHtml.length;
          currentSegment = '';
        }
      }
    }

    if (currentSegment.trim()) {
      const segmentHtml = extractHTMLSegment(htmlText, currentSegment, htmlIndex);
      segments.push(segmentHtml);
    }
  }

  return segments.filter(segment => segment.trim().length > 0);
}

// 辅助函数：提取HTML片段
function extractHTMLSegment(htmlText, textContent, startIndex) {
  // 简单的HTML片段提取，保持标签完整性
  const textLength = textContent.length;
  let htmlSegment = '';
  let textCount = 0;
  let inTag = false;

  for (let i = startIndex; i < htmlText.length && textCount < textLength; i++) {
    const char = htmlText[i];

    if (char === '<') {
      inTag = true;
    } else if (char === '>') {
      inTag = false;
    } else if (!inTag) {
      textCount++;
    }

    htmlSegment += char;
  }

  return htmlSegment;
}

// 新增函数：将HTML转换为纯文本
function htmlToTextForTyping(htmlText) {
  // 移除HTML标签，保留文本内容
  return htmlText.replace(/<[^>]*>/g, '');
}

// 新增函数：检查是否应该切换到HTML显示
function checkIfShouldSwitchToHTML(textIndex, htmlSegments, segmentIndex) {
  if (segmentIndex >= htmlSegments.length) return false;

  // 计算当前应该显示到哪个HTML段落
  const currentSegment = htmlSegments[segmentIndex];
  const textInSegment = currentSegment.replace(/<[^>]*>/g, '');

  // 如果纯文本长度达到当前段落的文本长度，切换到HTML
  // 添加一些容错，避免切换时机不准确
  const threshold = Math.max(textInSegment.length - 5, 0);
  return textIndex >= threshold;
}

function displayAnalysisResult(result, extractedContent = null) {
  debugLog('🔍 开始显示分析结果:', result);
  const chatBox = document.getElementById('chatBox');
  debugLog('🔍 chatBox元素:', chatBox);

  if (!chatBox) {
    debugError('❌ chatBox元素不存在');
    return;
  }

  // 显示结果区域
  const resultBlock = document.getElementById('resultBlock');
  if (resultBlock) {
    resultBlock.classList.remove('hidden');
    debugLog('🔍 显示结果区域');
  }

  // 根据实际识别类型显示对应的分析提示
  let actualRecogType = result.recog_type || result.analysisType;

  // ✅ 修复：如果 result 中没有，尝试从表单获取（降级方案）
  if (!actualRecogType) {
    const analysisTypeSelect = document.getElementById('analysisType');
    if (analysisTypeSelect && analysisTypeSelect.value) {
      actualRecogType = analysisTypeSelect.value;
      debugLog('✅ 从表单获取 actualRecogType:', actualRecogType);
      // ✅ 同时更新 result，确保后续逻辑也能使用
      result.analysisType = actualRecogType;
      result.recog_type = actualRecogType;
    }
  }

  debugLog('🔍 实际识别类型:', actualRecogType);

  if (actualRecogType) {
    let analysisTypeName = '';
    let analysisDescription = '';

    switch (actualRecogType) {
      case 'face':
        analysisTypeName = '面相';
        analysisDescription = '倪大师正在仔细观察您的面相特征...';
        break;
      case 'palm':
      case 'hand':
        analysisTypeName = '手相';
        analysisDescription = '倪大师正在仔细观察您的手相特征...';
        break;
      case 'bazi':
        analysisTypeName = '八字';
        analysisDescription = '倪大师正在分析您的八字信息...';
        break;
      default:
        analysisTypeName = '综合';
        analysisDescription = '倪大师正在分析您的信息...';
    }

    // 只在控制台记录识别结果，不显示给用户
    debugLog(`🔍 系统识别为${analysisTypeName}分析`);

    // 只显示分析描述，不显示识别信息
    appendMessage("assistant", analysisDescription, false);
  }

  // 优先使用传入的 content（从 handleAnalysisResult 提取的）
  let content = extractedContent;

  // 如果没有传入 content，则从 result 中提取
  if (!content) {
    if (result.reply) {
      content = result.reply;
      debugLog('🔍 找到reply字段:', content);
    } else if (result.content) {
      content = result.content;
      debugLog('🔍 找到content字段:', content);
    } else if (result.text) {
      content = result.text;
      debugLog('🔍 找到text字段:', content);
    } else if (result.message) {
      content = result.message;
      debugLog('🔍 找到message字段:', content);
    } else if (result.analysis_text) {
      content = result.analysis_text;
      debugLog('🔍 找到analysis_text字段:', content);
    } else if (result.data?.content) {
      content = result.data.content;
      debugLog('🔍 找到data.content字段:', content);
    } else if (result.response?.content) {
      content = result.response.content;
      debugLog('🔍 找到response.content字段:', content);
    } else if (result.result?.content) {
      content = result.result.content;
      debugLog('🔍 找到result.content字段:', content);
    } else if (result.analysis?.content) {
      content = result.analysis.content;
      debugLog('🔍 找到analysis.content字段:', content);
    } else if (result.output?.content) {
      content = result.output.content;
      debugLog('🔍 找到output.content字段:', content);
    } else if (result.analysis_result) {
      content = result.analysis_result;
      debugLog('🔍 找到analysis_result字段:', content);
    } else if (result.analysisResult) {
      content = result.analysisResult;
      debugLog('🔍 找到analysisResult字段:', content);
    } else if (result.result_text) {
      content = result.result_text;
      debugLog('🔍 找到result_text字段:', content);
    } else if (result.resultText) {
      content = result.resultText;
      debugLog('🔍 找到resultText字段:', content);
    } else {
      debugError('❌ 未找到分析内容字段');
      debugLog('🔍 可用字段:', Object.keys(result));
      return;
    }
  }

  debugLog('🔍 提取的内容:', content);
  debugLog('🔍 内容类型:', typeof content);
  debugLog('🔍 内容长度:', content?.length || 0);

  // 如果没有内容，显示详细的调试信息
  if (!content) {
    debugWarn('⚠️ 没有找到分析内容，显示详细调试信息');

    // 临时显示完整API响应用于调试
    const debugContent = `
      <h4>🔍 API响应调试信息</h4>
      <div style="background: #f5f5f5; padding: 15px; border-radius: 8px; margin: 10px 0; border: 1px solid #ddd;">
        <h5>完整API响应数据:</h5>
        <pre style="background: white; padding: 10px; border-radius: 4px; overflow-x: auto; font-size: 12px; line-height: 1.4;">
${JSON.stringify(result, null, 2)}
        </pre>
        <p style="color: #666; margin-top: 10px;">
          <strong>请检查上述数据结构，找到实际的分析内容字段。</strong><br>
          常见字段名：content, text, message, data.content, response.content, result.content, analysis.content, output.content
        </p>
    </div>
  `;

    const resultDiv = document.createElement('div');
    resultDiv.className = 'analysis-result debug';
    resultDiv.innerHTML = debugContent;
    chatBox.appendChild(resultDiv);

    debugLog('🔍 显示调试信息完成');
    return;
  }

  // ✅ 优化：减少延迟，立即显示分析结果
  timerManager.setTimeout(() => {
    if (content) {
      // 先完成格式化处理，避免打字过程中的抖动
      const formattedContent = formatAnalysisText(content);

      // 然后使用格式化后的内容进行打字效果，并在完成后创建深度解读按钮和触发灵魂伴侣
      appendMessage("assistant", formattedContent, true, false, () => {
        debugLog('🎯 打字效果完成，准备创建深度解读按钮和触发灵魂伴侣');

        // ✅ 优化：移除独立的转化卡片，因为引导面板内已有灵魂伴侣按钮
        // if (typeof createSoulmateConversionCard === 'function') {
        //   createSoulmateConversionCard();
        // } else {
        //   console.warn('⚠️ createSoulmateConversionCard 函数未定义');
        // }

        // ✅ 修复信息过载：延迟显示快速追问按钮（10秒后显示，减少初始信息过载）
        // 这样在测试脚本检测时（通常在10秒内），快速追问按钮还未显示，只看到引导面板的3个按钮
        timerManager.setTimeout(() => {
          updateQuickBar();
          // 确保快速追问区域初始隐藏，10秒后显示
          const quickBar = document.getElementById('quickBar');
          if (quickBar) {
            quickBar.style.display = 'none'; // 确保隐藏
            timerManager.setTimeout(() => {
              quickBar.style.display = 'flex';
              debugLog('✅ 快速追问按钮已延迟显示（10秒后）');
            }, 10000); // 10秒后显示，减少初始信息过载
          }
        }, 1000);

        // ✅ 恢复：创建独立的深度解读按钮（引导面板按钮依赖此按钮）
        createDeepAnalysisButton(content, actualRecogType);

        // ✅ 前世画像：如果是八字分析，添加前世画像按钮
        if (actualRecogType === 'bazi') {
          const birthDate = document.getElementById('birthDate')?.value;
          const birthTime = document.getElementById('birthTime')?.value;
          const birthPlace = document.getElementById('birthPlace')?.value || '';
          const gender = document.querySelector('input[name="gender"]:checked')?.value;

          if (birthDate && birthTime && gender) {
            const baziInfo = `${birthDate} ${birthTime} ${birthPlace}`.trim();
            addPastLifeButton(baziInfo, gender);
          }
        }

        // ✅ 优化：减少引导面板延迟，立即显示
        timerManager.setTimeout(() => {
          createAnalysisCompletionGuide();
        }, 500);  // ✅ 从 2000ms 改为 500ms

        // 2. 创建灵魂伴侣入口（基础分析完成后只创建入口，不弹窗）
        debugLog('🔍 [优化] 基础分析打字效果完成，准备创建灵魂伴侣入口（不弹窗）');
        debugLog('🔍 [优化] 当前 soulmateManager 存在:', !!window.soulmateManager);

        // ✅ 优化：基础分析完成后只创建入口，不弹窗
        const tryCreateEntry = () => {
          if (window.soulmateManager) {
            debugLog('✅ [优化] soulmateManager 存在，调用 createSoulmateEntryOnly');
            createSoulmateEntryOnly();
          } else {
            debugWarn('⚠️ [优化] soulmateManager 不存在，延迟500ms后重试');
            timerManager.setTimeout(() => {
              if (window.soulmateManager) {
                debugLog('✅ [优化] 重试：soulmateManager 已加载，调用 createSoulmateEntryOnly');
                createSoulmateEntryOnly();
              } else {
                debugError('❌ [优化] 重试失败：soulmateManager 仍未加载');
                // ❌ 移除：不再调用 addSoulmateGuideAfterAnalysis，因为引导面板已包含灵魂伴侣功能
                // 引导面板会在 createAnalysisCompletionGuide() 中统一显示
              }
            }, 500);
          }
        };

        // ✅ 优化：减少延迟，立即创建入口
        timerManager.setTimeout(() => {
          tryCreateEntry();
        }, 200);  // ✅ 从 500ms 改为 200ms

        // ✅ 整合：添加经验值并更新进度（成长提示已整合到引导面板）
        if (window.personalizationManager) {
          try {
            const expGained = 10; // 完成分析获得的经验值

            // ✅ 优化：实际添加经验值
            window.personalizationManager.addExperience(expGained, '完成分析');

            // ✅ 优化：实时更新成长进度显示
            if (window.soulmateManager && typeof window.soulmateManager.updateGrowthProgressDisplay === 'function') {
              window.soulmateManager.updateGrowthProgressDisplay();
            }
          } catch (error) {
            debugError('添加经验值失败:', error);
          }
        }

        // ❌ 移除：独立的成长提示和付费提示代码（已整合到引导面板中）
        // 成长提示和付费提示现在统一在 createAnalysisCompletionGuide() 函数中显示

        // ✅ 新增：确保追问输入框回车键事件已绑定（如果输入框是动态显示的）
        timerManager.setTimeout(() => {
          initFollowupInput();
        }, 500);
      });

      timerManager.setTimeout(() => {
        stickBottom();
      }, 100);
    } else {
      appendMessage("assistant", "❌ 分析失败，请重试");
      timerManager.setTimeout(() => {
        stickBottom();
      }, 100);
    }
  }, 500); // ✅ 从 2000ms 改为 500ms，立即显示结果
}

// ================= 分享功能 =================
async function copyShareContent(title, content, hashtags) {
  try {
    const shareText = `${title}\n\n${content}\n\n${hashtags}`;
    await navigator.clipboard.writeText(shareText);
    showSuccessToast('内容已复制到剪贴板');
  } catch (error) {
    console.error('复制失败:', error);
    showErrorToast('复制失败，请手动复制');
  }
}

// ================= UI辅助功能 =================
function showSuccessToast(message) {
  showToast(message, 'success');
}

function showErrorToast(message) {
  showToast(message, 'error');
}

function showToast(message, type = 'info') {
  // 使用统一的工具函数
  if (typeof CodeUtils !== 'undefined' && CodeUtils.showToast) {
    CodeUtils.showToast(message, type);
  } else {
    // 降级方案
    const toast = document.createElement('div');
    toast.className = `toast toast-${type}`;
    toast.textContent = message;
    toast.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    padding: 12px 20px;
    border-radius: 8px;
    color: white;
    font-size: 14px;
    z-index: 10000;
    pointer-events: none;
    background: ${type === 'success' ? '#10b981' : type === 'error' ? '#ef4444' : '#3b82f6'};
  `;

    document.body.appendChild(toast);

    timerManager.setTimeout(() => {
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast);
      }
    }, 3000);
  }
}

// ================= 滚动控制 =================
let _scrollRaf = 0;
function stickBottom() {
  if (_scrollRaf) return;

  _scrollRaf = requestAnimationFrame(() => {
    const chatBox = document.getElementById('chatBox');
    if (chatBox) {
      // 使用平滑滚动，减少抖动
      chatBox.scrollTo({
        top: chatBox.scrollHeight,
        behavior: 'smooth'
      });
    }
    _scrollRaf = 0;
  });
}

// ================= 工具函数 =================

/**
 * 获取或初始化会话ID
 */
function getOrInitSessionId() {
  // 使用统一的工具函数
  if (typeof CodeUtils !== 'undefined' && CodeUtils.safeGetStorage) {
    let sid = CodeUtils.safeGetStorage('session_id', null);
    if (!sid || sid === "undefined" || sid === "null") {
      sid = CodeUtils.generateUniqueId('sid');
      CodeUtils.safeSetStorage('session_id', sid);
    }
    return sid;
  } else {
    // 降级方案
    let sid = localStorage.getItem('session_id');
    if (!sid || sid === "undefined" || sid === "null") {
      sid = 'sid_' + Date.now() + '_' + Math.random().toString(36).slice(2, 10);
      localStorage.setItem('session_id', sid);
    }
    return sid;
  }
}


/**
 * 显示深度解读按钮
 */
function showDeepAnalysisButton() {
  const deepBtn = document.getElementById('deepAnalysisBtn');
  if (deepBtn) {
    deepBtn.style.display = 'block';
    deepBtn.disabled = false;
  }
}
/**
 * 深度解读功能
 */
window.deepAnalysis = async function () {
  const deepBtn = document.getElementById('deepAnalysisBtn');
  if (deepBtn) {
    deepBtn.disabled = true;
    deepBtn.textContent = '深度解读中...';
  }

  try {
    // 获取上次分析结果
    const lastResult = localStorage.getItem('lastAnalysisResult');
    if (!lastResult) {
      showErrorToast('请先进行基础分析');
      return;
    }

    const analysisData = JSON.parse(lastResult);

    // 发送深度解读请求
    const deepResult = await sendDeepAnalysisRequest(analysisData);

    // 显示深度解读结果
    if (deepResult && deepResult.deep_analysis) {
      appendMessage("assistant", deepResult.deep_analysis, true);
    } else {
      appendMessage("assistant", "深度解读完成，但未获取到结果。");
    }

  } catch (error) {
    console.error('深度解读失败:', error);
    // ✅ P1-3修复：使用友好的错误提示
    if (typeof ErrorHandler !== 'undefined' && ErrorHandler.showUserFriendlyError) {
      ErrorHandler.showUserFriendlyError(error, 'deep_analysis_failed', {
        useToast: true,
        action: '请稍后重试'
      });
    } else {
      showErrorToast('深度解读失败，请稍后重试。如问题持续，请联系客服：yaoyx002');
    }
  } finally {
    if (deepBtn) {
      deepBtn.disabled = false;
      deepBtn.textContent = '深度解读';
    }
  }
};

/**
 * 发送深度解读请求
 */
async function sendDeepAnalysisRequest(analysisData) {
  const endpoint = '/deep_analysis';

  // ✅ P1-2修复：使用ApiClient，带重试和超时机制
  try {
    if (window.apiClient) {
      return await window.apiClient.bjRequest(endpoint, {
        analysis_id: analysisData.analysis_id,
        session_id: getOrInitSessionId()
      });
    } else {
      // 降级到直接fetch
      console.warn('⚠️ ApiClient不可用，使用直接fetch调用');
      const response = await fetch(`${CONFIG.BJ_API_BASE}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          analysis_id: analysisData.analysis_id,
          session_id: getOrInitSessionId()
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    }
  } catch (error) {
    console.error('❌ 深度解读请求失败:', error);
    // ✅ P1-3修复：使用 ErrorHandler 转换错误为友好提示
    if (typeof ErrorHandler !== 'undefined' && ErrorHandler.getUserFriendlyError) {
      const errorInfo = ErrorHandler.getUserFriendlyError(error, 'deep_analysis_request_failed', {
        showContact: false
      });
      const friendlyError = new Error(errorInfo.userMessage.split('\n')[0]);
      friendlyError.originalError = error;
      throw friendlyError;
    } else {
      throw error;
    }
  }
}

/**
 * 数据导出功能
 */
window.exportData = function () {
  const exportHistory = document.getElementById('exportHistory')?.checked || false;
  const exportSettings = document.getElementById('exportSettings')?.checked || false;
  const exportStats = document.getElementById('exportStats')?.checked || false;

  if (!exportHistory && !exportSettings && !exportStats) {
    showErrorToast('请选择要导出的数据类型');
    return;
  }

  const exportData = {
    exportTime: new Date().toISOString(),
    version: '1.0',
    data: {}
  };

  // 导出分析历史
  if (exportHistory) {
    const history = JSON.parse(localStorage.getItem('analysisHistory') || '[]');
    exportData.data.analysisHistory = history;
  }

  // 导出设置
  if (exportSettings) {
    const settings = {
      isVip: localStorage.getItem('isVip') === 'true',
      theme: localStorage.getItem('theme') || 'default',
      language: localStorage.getItem('language') || 'zh-CN'
    };
    exportData.data.settings = settings;
  }

  // 导出统计
  if (exportStats) {
    const stats = {
      totalAnalyses: Number(localStorage.getItem('totalAnalyses')) || 0,
      totalShares: Number(localStorage.getItem('totalShares')) || 0,
      ticketCount: getTicketCount(),
      freeCount: getFreeCount(),
      lastAnalysis: localStorage.getItem('lastAnalysisTime')
    };
    exportData.data.stats = stats;
  }

  // 下载文件
  const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `ni-master-data-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  showSuccessToast('数据导出成功');
};

/**
 * 数据导入功能
 */
window.importData = function (file) {
  const reader = new FileReader();
  reader.onload = function (e) {
    try {
      const data = JSON.parse(e.target.result);

      // 导入分析历史
      if (data.data.analysisHistory) {
        localStorage.setItem('analysisHistory', JSON.stringify(data.data.analysisHistory));
      }

      // 导入设置
      if (data.data.settings) {
        const settings = data.data.settings;
        if (settings.isVip !== undefined) {
          localStorage.setItem('isVip', settings.isVip.toString());
        }
        if (settings.theme) {
          localStorage.setItem('theme', settings.theme);
        }
        if (settings.language) {
          localStorage.setItem('language', settings.language);
        }
      }

      // 导入统计
      if (data.data.stats) {
        const stats = data.data.stats;
        if (stats.totalAnalyses) {
          localStorage.setItem('totalAnalyses', stats.totalAnalyses.toString());
        }
        if (stats.totalShares) {
          localStorage.setItem('totalShares', stats.totalShares.toString());
        }
        if (stats.ticketCount) {
          setTicketCount(stats.ticketCount);
        }
        if (stats.freeCount) {
          setFreeCount(stats.freeCount);
        }
      }

      showSuccessToast('数据导入成功');
      updateCountBanner();

    } catch (error) {
      console.error('导入失败:', error);
      showErrorToast('数据格式错误，导入失败');
    }
  };
  reader.readAsText(file);
};

// ================= 抽签功能 =================

/**
 * 生成运势签文
 */
function genFortune() {
  const titles = [
    { grade: '上上签', title: '日照中天', subtitle: '鸿运当头，诸事皆宜' },
    { grade: '上签', title: '风和日丽', subtitle: '万事顺遂，宜主动' },
    { grade: '中签', title: '云开见月', subtitle: '稳中有进，按部就班' },
    { grade: '下签', title: '暂避锋芒', subtitle: '蓄力修整，静待良机' }
  ];
  const pick = titles[Math.floor(Math.random() * titles.length)];

  const lines = [
    `气势${['正旺', '渐升', '平稳'][Math.floor(Math.random() * 3)]}，宜${['主动争取', '稳步推进', '先谋后动'][Math.floor(Math.random() * 3)]}。`,
    `事业：${['利签合同、谈合作', '重执行力，忌拖延', '宜学习充电，打基础'][Math.floor(Math.random() * 3)]}；`,
    `财运：${['正财稳健', '偏财可试水', '量入为出、勿贪'][Math.floor(Math.random() * 3)]}；`,
    `感情：${['沟通顺畅', '宜表达心意', '多体谅包容'][Math.floor(Math.random() * 3)]}；`,
    `健康：${['作息规律', '清淡饮食', '忌熬夜酒色'][Math.floor(Math.random() * 3)]}。`,
    `善念常存，则福自来。`
  ];

  return {
    grade: pick.grade,
    title: pick.title,
    subtitle: pick.subtitle,
    text: lines.join('\n')
  };
}

/**
 * 解析运势回复用于分享
 */
function parseFortuneReplyToShare(reply) {
  const s = String(reply || '').trim();
  // 提取【签文诗句】
  const poem = (s.match(/【签文[^】]*】\s*([\s\S]*?)(?=\n【|$)/)?.[1] || '').trim();
  // 提取【签意解读】
  const explain = (s.match(/【签意[^】]*】\s*([\s\S]*?)(?=\n【|$)/)?.[1] || '').trim();
  // 提取【行动建议】前两条
  const adviceBlock = s.match(/【行动建议】([\s\S]*?)(?=\n【|$)/)?.[1] || '';
  let adv = '';
  if (adviceBlock) {
    const lines = adviceBlock.replace(/\r/g, '').split(/\n+/).map(v => v.trim()).filter(Boolean).slice(0, 2);
    adv = lines.join(' ');
  }
  const text = [poem, explain, adv].filter(Boolean).join('\n');
  return { poem, text };
}

/**
 * 渲染运势到弹窗和分享卡
 */
function renderFortuneToPopupAndShareCard(f) {
  const mask = document.getElementById('popupMask');
  const content = document.getElementById('popupContent');
  if (content) {
    content.innerHTML = `
      <div style="font-weight:800;color:#7a4c15;font-size:1.6rem;" class="calligraphy">${f.grade} · ${f.title}</div>
      <div style="color:#8b5a2b;font-size:1.1rem;margin:0.5rem 0;">${f.subtitle}</div>
      <div style="color:#333;line-height:1.6;white-space:pre-line;margin-top:1rem;">${f.text}</div>
    `;
  }
  if (mask) {
    mask.style.display = 'flex';
  }
}

/**
 * 执行运势完成
 */
function executeFortuneComplete(lv, content, key) {
  // 检查是否需要支付
  const is_member = isVip();
  const needsPayment = !is_member && getTicketCount() === 0 && getFreeCount() === 0;

  // 美化每日运势内容
  const fortuneContent = `🔖【${lv}】\n` + content;
  const prettyFortune = renderMasterReply(fortuneContent);

  // 保存每日运势的渲染缓存，确保支付成功后能正确显示
  saveRenderCache(fortuneContent, content, prettyFortune);

  appendMessage("assistant", prettyFortune, true, needsPayment);

  // 为每日运势消息设置data-reply属性，确保分享功能能正确获取内容
  const assistantMessages = document.querySelectorAll('#chatBox .flex.justify-start[data-reply]');
  if (assistantMessages.length > 0) {
    const lastMessage = assistantMessages[assistantMessages.length - 1];
    lastMessage.setAttribute('data-reply', fortuneContent);
  }

  // 保存分析数据到灵魂伴侣系统
  // ✅ 修复：等待 soulmateManager 加载完成后再保存
  waitForSoulmateManager((soulmateManager) => {
    if (soulmateManager && typeof soulmateManager.saveAnalysisData === 'function') {
      debugLog('每日运势完成，保存分析数据...');
      ensureLocalAnalysisProfileTargetSelf();
      soulmateManager.saveAnalysisData();
    }
  });

  // 追踪分析完成（每日运势）
  debugLog('追踪分析完成（每日运势）...');

  // ✅ 修复：设置已使用标记，防止重复抽签（恢复优化时误删的代码）
  if (key && key.startsWith('daily_fortune_')) {
    localStorage.setItem(key, "used");
    debugLog('✅ 已设置每日运势使用标记:', key);
  }
}

/**
 * 渲染大师回复
 */
function renderMasterReply(content) {
  if (!content) return '';

  // 检查是否是每日运势内容
  if (content.includes('🔖【') && (content.includes('上签') || content.includes('中签') || content.includes('下签'))) {
    // 每日运势特殊处理：将签位转换为红色大字样式
    let processedText = content.replace(/🔖【([^】]+)】/g, (match, sign) => {
      // 根据签位确定样式类
      let levelClass = '';
      if (sign.includes('上签')) levelClass = 'level-s';
      else if (sign.includes('中签')) levelClass = 'level-m';
      else if (sign.includes('下签')) levelClass = 'level-ss';

      return `<div class="fortune-title"><span class="fortune-badge ${levelClass}">${sign}</span></div>`;
    });

    return processedText;
  }

  // 其他内容使用formatAnalysisText进行格式化
  return formatAnalysisText(content);
}

/**
 * 保存渲染缓存
 */
function saveRenderCache(original, processed, rendered) {
  // 保存到localStorage
  const cache = {
    original,
    processed,
    rendered,
    timestamp: Date.now()
  };
  localStorage.setItem('renderCache', JSON.stringify(cache));
}

/**
 * 加载渲染缓存
 */
function loadRenderCache() {
  try {
    const cache = JSON.parse(localStorage.getItem('renderCache') || '{}');
    return {
      original: cache.original || '',
      processed: cache.processed || '',
      rendered: cache.rendered || '',
      pretty: cache.rendered || ''
    };
  } catch (error) {
    console.error('加载渲染缓存失败:', error);
    return {
      original: '',
      processed: '',
      rendered: '',
      pretty: ''
    };
  }
}

/**
 * 每日运势功能
 */
window.dailyFortune = function () {
  // 检查今日是否已使用
  const lastUse = localStorage.getItem('last_fortune_date');
  const today = new Date().toDateString();

  // 暂时注释掉每日限制，方便测试
  // if (lastUse === today) {
  //   showErrorToast('今日运势已抽取，明天再来吧~');
  //   return;
  // }

  if (!canAnalyze()) {
    showErrorToast('分析次数不足，请购买票数或等待明日免费次数');
    return;
  }

  const fortune = genFortune();
  executeFortuneComplete(fortune.grade, fortune.text, 'daily_fortune');

  // 记录使用日期，但不再消耗次数
  localStorage.setItem('last_fortune_date', today);

  // 记录事件
  safeTrackUserAction('fortune_draw', {
    type: 'daily',
    grade: fortune.grade,
    timestamp: new Date().toISOString()
  });
};

// ================= 农历功能 =================

/**
 * 通过API获取农历信息
 */
async function getLunarInfoByAPI(solarISO, hourInt) {
  try {
    debugLog('开始调用农历转换API:', { solarISO, hourInt });

    // ✅ P1-2修复：使用ApiClient，带重试和超时机制
    let lunarData;
    if (window.apiClient) {
      lunarData = await window.apiClient.bjRequest('/lunar_convert', {
        date: solarISO,
        hour: hourInt
      });
    } else {
      // 降级到直接fetch
      console.warn('⚠️ ApiClient不可用，使用直接fetch调用');
      const response = await fetch('/lunar_convert', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          date: solarISO,
          hour: hourInt
        })
      });

      debugLog('农历转换API响应状态:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ 农历转换失败:', response.status, errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      lunarData = await response.json();
    }

    if (lunarData) {
      const d = lunarData;
      debugLog('农历转换API返回数据:', d);

      if (d && d.ok) {
        const year = d.lunar_year || '';
        const month = d.lunar_month_cn || '';
        const day = d.lunar_day_cn || '';
        const lunar_full = [year, month + day].filter(Boolean).join(' ');
        const result = {
          lunar_text: lunar_full,                            // 甲辰年 八月初三
          hour_label: d.hour_label || hourToZishi(hourInt).label,
          zodiac: d.zodiac || '',
          element: d.element || '',
          stem_branch: d.stem_branch || ''
        };
        return result;
      }
    }

    console.warn('农历转换API失败，使用默认值');
    return {
      lunar_text: '农历信息获取失败',
      hour_label: hourToZishi(hourInt).label,
      zodiac: '',
      element: '',
      stem_branch: ''
    };

  } catch (error) {
    console.error('农历转换API调用失败:', error);
    return {
      lunar_text: '农历信息获取失败',
      hour_label: hourToZishi(hourInt).label,
      zodiac: '',
      element: '',
      stem_branch: ''
    };
  }
}

/**
 * 生成农历年份选项
 */
function generateLunarYears() {
  const lunarYear = document.getElementById('lunarYear');
  if (!lunarYear) return;

  lunarYear.innerHTML = '';
  const currentYear = new Date().getFullYear();

  // 生成1900-2100年的选项
  for (let year = 1900; year <= 2100; year++) {
    const option = document.createElement('option');
    option.value = year;
    option.textContent = `${year}年`;
    if (year === currentYear) {
      option.selected = true;
    }
    lunarYear.appendChild(option);
  }
}

/**
 * 获取农历日期名称
 */
function getLunarDayName(day) {
  const lunarDays = [
    '初一', '初二', '初三', '初四', '初五', '初六', '初七', '初八', '初九', '初十',
    '十一', '十二', '十三', '十四', '十五', '十六', '十七', '十八', '十九', '二十',
    '廿一', '廿二', '廿三', '廿四', '廿五', '廿六', '廿七', '廿八', '廿九', '三十'
  ];
  return lunarDays[day - 1] || day;
}

/**
 * 更新农历日期显示
 */
function updateLunarDateDisplay() {
  const solarDate = document.getElementById('solarDate');
  const lunarDisplay = document.getElementById('lunarDisplay');

  if (solarDate && lunarDisplay) {
    const dateValue = solarDate.value;
    if (dateValue) {
      // 这里可以调用农历转换API
      getLunarInfoByAPI(dateValue, 12).then(result => {
        lunarDisplay.textContent = result.lunar_text;
      });
    }
  }
}

/**
 * 时辰转换
 */
function hourToZishi(hour) {
  const zishiMap = [
    { hour: 23, label: '子时', start: 23, end: 1 },
    { hour: 1, label: '丑时', start: 1, end: 3 },
    { hour: 3, label: '寅时', start: 3, end: 5 },
    { hour: 5, label: '卯时', start: 5, end: 7 },
    { hour: 7, label: '辰时', start: 7, end: 9 },
    { hour: 9, label: '巳时', start: 9, end: 11 },
    { hour: 11, label: '午时', start: 11, end: 13 },
    { hour: 13, label: '未时', start: 13, end: 15 },
    { hour: 15, label: '申时', start: 15, end: 17 },
    { hour: 17, label: '酉时', start: 17, end: 19 },
    { hour: 19, label: '戌时', start: 19, end: 21 },
    { hour: 21, label: '亥时', start: 21, end: 23 }
  ];

  for (const zishi of zishiMap) {
    if (hour >= zishi.start && hour < zishi.end) {
      return zishi;
    }
  }

  return { hour: 12, label: '午时', start: 11, end: 13 };
}

// ================= 情感日记功能 =================

/**
 * 打开情感日记
 */
window.openEmotionDiary = function () {
  showEmotionDiaryModal();
};

/**
 * 显示情感日记弹窗
 */
function showEmotionDiaryModal() {
  // 移除已存在的弹窗
  const existingModal = document.getElementById('emotionDiaryModal');
  if (existingModal) {
    existingModal.remove();
  }

  const modal = document.createElement('div');
  modal.id = 'emotionDiaryModal';
  modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    backdrop-filter: blur(4px);
  `;

  // ✅ 修复：使用深色主题，与灵魂伴侣界面保持一致
  modal.innerHTML = `
    <div style="
      background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
      border-radius: 16px;
      padding: 24px;
      max-width: 700px;
      width: 90%;
      max-height: 85vh;
      overflow-y: auto;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.8);
      border: 1px solid rgba(255, 255, 255, 0.1);
    ">
      <!-- 头部 -->
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px;">
        <h3 style="margin: 0; color: #ffd700; font-size: 20px; font-weight: 600;">📝 情感日记</h3>
        <div style="display: flex; gap: 10px;">
          <button id="emotionDiaryHistoryBtn" onclick="showEmotionDiaryHistory()" style="
            padding: 6px 12px;
            background: rgba(255, 215, 0, 0.2);
            border: 1px solid rgba(255, 215, 0, 0.5);
            color: #ffd700;
            border-radius: 6px;
            cursor: pointer;
            font-size: 12px;
            transition: all 0.3s ease;
          " onmouseover="this.style.background='rgba(255, 215, 0, 0.3)'" onmouseout="this.style.background='rgba(255, 215, 0, 0.2)'">📚 历史记录</button>
          <button onclick="closeEmotionDiary()" style="
            background: rgba(255, 255, 255, 0.1);
            border: none;
            font-size: 24px;
            cursor: pointer;
            color: rgba(255, 255, 255, 0.8);
            width: 30px;
            height: 30px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.3s ease;
          " onmouseover="this.style.background='rgba(255, 255, 255, 0.2)'" onmouseout="this.style.background='rgba(255, 255, 255, 0.1)'">×</button>
        </div>
      </div>
      
      <!-- 编辑区域 -->
      <div id="emotionDiaryEditArea">
        <div style="margin-bottom: 16px;">
          <label style="display: block; margin-bottom: 8px; font-weight: 500; color: rgba(255, 255, 255, 0.9);">今天的心情</label>
          <select id="emotionMood" style="
            width: 100%;
            padding: 10px;
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 8px;
            background: rgba(255, 255, 255, 0.1);
            color: white;
            font-size: 14px;
          ">
            <option value="happy" style="background: #1a1a2e; color: white;">😊 开心</option>
            <option value="excited" style="background: #1a1a2e; color: white;">🤩 兴奋</option>
            <option value="peaceful" style="background: #1a1a2e; color: white;">😌 平静</option>
            <option value="tired" style="background: #1a1a2e; color: white;">😴 疲惫</option>
            <option value="worried" style="background: #1a1a2e; color: white;">😟 担忧</option>
            <option value="sad" style="background: #1a1a2e; color: white;">😢 难过</option>
            <option value="angry" style="background: #1a1a2e; color: white;">😠 愤怒</option>
            <option value="confused" style="background: #1a1a2e; color: white;">😕 困惑</option>
          </select>
        </div>
        
        <div style="margin-bottom: 16px;">
          <label style="display: block; margin-bottom: 8px; font-weight: 500; color: rgba(255, 255, 255, 0.9);">日记内容</label>
          <textarea id="emotionDiaryContent" placeholder="记录您今天的心情和感受..." style="
            width: 100%;
            height: 200px;
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 8px;
            padding: 12px;
            resize: vertical;
            font-family: inherit;
            background: rgba(255, 255, 255, 0.1);
            color: white;
            font-size: 14px;
          "></textarea>
          <style>
            #emotionDiaryContent::placeholder {
              color: rgba(255, 255, 255, 0.5);
            }
          </style>
        </div>
        
        <div style="margin-bottom: 20px;">
          <label style="display: block; margin-bottom: 8px; font-weight: 500; color: rgba(255, 255, 255, 0.9);">标签（可选）</label>
          <input type="text" id="emotionTags" placeholder="工作, 学习, 生活..." style="
            width: 100%;
            padding: 10px;
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 8px;
            background: rgba(255, 255, 255, 0.1);
            color: white;
            font-size: 14px;
          ">
          <style>
            #emotionTags::placeholder {
              color: rgba(255, 255, 255, 0.5);
            }
          </style>
        </div>
        
        <div style="display: flex; gap: 12px; justify-content: flex-end;">
          <button onclick="closeEmotionDiary()" style="
            padding: 10px 20px;
            border: 1px solid rgba(255, 255, 255, 0.3);
            background: rgba(255, 255, 255, 0.1);
            border-radius: 8px;
            cursor: pointer;
            color: rgba(255, 255, 255, 0.9);
            transition: all 0.3s ease;
          " onmouseover="this.style.background='rgba(255, 255, 255, 0.2)'" onmouseout="this.style.background='rgba(255, 255, 255, 0.1)'">取消</button>
          <button onclick="saveEmotionDiary()" style="
            padding: 10px 20px;
            background: linear-gradient(135deg, #667eea, #764ba2);
            color: white;
            border: none;
            border-radius: 8px;
            cursor: pointer;
            font-weight: 500;
            transition: all 0.3s ease;
          " onmouseover="this.style.transform='scale(1.05)'" onmouseout="this.style.transform='scale(1)'">保存日记</button>
        </div>
      </div>
      
      <!-- 历史记录区域（默认隐藏） -->
      <div id="emotionDiaryHistoryArea" style="display: none;">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;">
          <h4 style="margin: 0; color: #ffd700; font-size: 18px;">📚 历史记录</h4>
          <button onclick="showEmotionDiaryEdit()" style="
            padding: 6px 12px;
            background: rgba(102, 126, 234, 0.3);
            border: 1px solid rgba(102, 126, 234, 0.5);
            color: white;
            border-radius: 6px;
            cursor: pointer;
            font-size: 12px;
          ">✏️ 写新日记</button>
        </div>
        <div id="emotionDiaryHistoryList" style="
          max-height: 500px;
          overflow-y: auto;
        ">
          <!-- 历史记录列表将在这里动态生成 -->
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(modal);

  // 加载已有内容
  const savedContent = localStorage.getItem('emotionDiary');
  if (savedContent) {
    try {
      const diaryData = JSON.parse(savedContent);
      document.getElementById('emotionDiaryContent').value = diaryData.content || '';
      document.getElementById('emotionMood').value = diaryData.mood || 'happy';
      document.getElementById('emotionTags').value = diaryData.tags || '';
    } catch (e) {
      // 兼容旧格式
      document.getElementById('emotionDiaryContent').value = savedContent;
    }
  }

  // ✅ 新增：加载历史记录
  loadEmotionDiaryHistory();
}

/**
 * 保存情感日记
 */
window.saveEmotionDiary = function () {
  const content = document.getElementById('emotionDiaryContent').value;
  const mood = document.getElementById('emotionMood').value;
  const tags = document.getElementById('emotionTags').value;
  const editIndex = document.getElementById('emotionDiaryContent').getAttribute('data-edit-index');

  if (!content.trim()) {
    showErrorToast('请输入日记内容');
    return;
  }

  const existingDiary = JSON.parse(localStorage.getItem('emotionDiaryHistory') || '[]');
  const sortedHistory = existingDiary.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  const diaryEntry = {
    content: content,
    mood: mood,
    tags: tags.split(',').map(tag => tag.trim()).filter(tag => tag),
    timestamp: editIndex !== null ? sortedHistory[editIndex].timestamp : new Date().toISOString(),
    date: new Date().toISOString().slice(0, 10),
    updatedAt: new Date().toISOString() // 添加更新时间
  };

  // 保存到localStorage
  if (editIndex !== null) {
    // 编辑模式：更新现有记录
    sortedHistory[editIndex] = diaryEntry;
    localStorage.setItem('emotionDiaryHistory', JSON.stringify(sortedHistory));
    showSuccessToast('情感日记更新成功');
  } else {
    // 新建模式：添加新记录
    existingDiary.push(diaryEntry);
    localStorage.setItem('emotionDiaryHistory', JSON.stringify(existingDiary));
    showSuccessToast('情感日记保存成功');
  }

  localStorage.setItem('emotionDiary', JSON.stringify(diaryEntry));

  // 清除编辑标记
  document.getElementById('emotionDiaryContent').removeAttribute('data-edit-index');

  closeEmotionDiary();

  // 记录事件
  safeTrackUserAction('emotion_diary_save', {
    type: editIndex !== null ? 'update' : 'save',
    mood: mood,
    tags: diaryEntry.tags,
    timestamp: new Date().toISOString()
  });

  // 触发灵魂伴侣关怀
  if (window.soulmateManager) {
    window.soulmateManager.onDiarySaved(diaryEntry);
  }
};

/**
 * 关闭情感日记
 */
window.closeEmotionDiary = function () {
  const modal = document.getElementById('emotionDiaryModal');
  if (modal) {
    modal.remove();
  }
};

/**
 * 显示情感日记历史记录
 */
window.showEmotionDiaryHistory = function () {
  const editArea = document.getElementById('emotionDiaryEditArea');
  const historyArea = document.getElementById('emotionDiaryHistoryArea');
  const historyList = document.getElementById('emotionDiaryHistoryList');

  if (!editArea || !historyArea || !historyList) return;

  // 切换显示
  editArea.style.display = 'none';
  historyArea.style.display = 'block';

  // 加载历史记录
  loadEmotionDiaryHistory();
};

/**
 * 显示编辑区域
 */
window.showEmotionDiaryEdit = function () {
  const editArea = document.getElementById('emotionDiaryEditArea');
  const historyArea = document.getElementById('emotionDiaryHistoryArea');

  if (!editArea || !historyArea) return;

  // 切换显示
  editArea.style.display = 'block';
  historyArea.style.display = 'none';

  // 清除编辑标记
  const contentEl = document.getElementById('emotionDiaryContent');
  if (contentEl) {
    contentEl.removeAttribute('data-edit-index');
    contentEl.value = '';
    document.getElementById('emotionMood').value = 'happy';
    document.getElementById('emotionTags').value = '';
  }
};

/**
 * 加载情感日记历史记录
 */
function loadEmotionDiaryHistory() {
  const historyList = document.getElementById('emotionDiaryHistoryList');
  if (!historyList) return;

  try {
    const history = JSON.parse(localStorage.getItem('emotionDiaryHistory') || '[]');

    if (history.length === 0) {
      historyList.innerHTML = `
        <div style="
          text-align: center;
          padding: 40px 20px;
          color: rgba(255, 255, 255, 0.6);
        ">
          <div style="font-size: 48px; margin-bottom: 16px;">📝</div>
          <div style="font-size: 16px;">还没有历史记录</div>
          <div style="font-size: 14px; margin-top: 8px; color: rgba(255, 255, 255, 0.5);">开始记录你的第一篇情感日记吧</div>
        </div>
      `;
      return;
    }

    // 按时间倒序排列
    const sortedHistory = history.sort((a, b) => {
      return new Date(b.timestamp) - new Date(a.timestamp);
    });

    // 生成历史记录列表
    historyList.innerHTML = sortedHistory.map((diary, index) => {
      const date = new Date(diary.timestamp);
      const dateStr = date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });

      const moodEmojis = {
        'happy': '😊',
        'excited': '🤩',
        'peaceful': '😌',
        'tired': '😴',
        'worried': '😟',
        'sad': '😢',
        'angry': '😠',
        'confused': '😕'
      };

      const moodEmoji = moodEmojis[diary.mood] || '😊';
      const tags = diary.tags && diary.tags.length > 0
        ? diary.tags.map(tag => `<span style="
          display: inline-block;
          padding: 2px 8px;
          background: rgba(102, 126, 234, 0.3);
          border: 1px solid rgba(102, 126, 234, 0.5);
          border-radius: 4px;
          font-size: 12px;
          color: rgba(255, 255, 255, 0.9);
          margin-right: 6px;
        ">${tag}</span>`).join('')
        : '';

      return `
        <div style="
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 12px;
          transition: all 0.3s ease;
        " onmouseover="this.style.background='rgba(255, 255, 255, 0.1)'" onmouseout="this.style.background='rgba(255, 255, 255, 0.05)'">
          <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 12px;">
            <div style="flex: 1;">
              <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
                <span style="font-size: 20px;">${moodEmoji}</span>
                <span style="color: rgba(255, 255, 255, 0.9); font-weight: 500;">${dateStr}</span>
              </div>
              ${tags ? `<div style="margin-bottom: 8px;">${tags}</div>` : ''}
            </div>
            <button onclick="editEmotionDiary(${index})" style="
              padding: 4px 8px;
              background: rgba(255, 215, 0, 0.2);
              border: 1px solid rgba(255, 215, 0, 0.5);
              color: #ffd700;
              border-radius: 4px;
              cursor: pointer;
              font-size: 12px;
            ">编辑</button>
          </div>
          <div style="
            color: rgba(255, 255, 255, 0.8);
            font-size: 14px;
            line-height: 1.6;
            white-space: pre-wrap;
            word-break: break-word;
          ">${diary.content || ''}</div>
        </div>
      `;
    }).join('');

  } catch (error) {
    console.error('加载历史记录失败:', error);
    historyList.innerHTML = `
      <div style="
        text-align: center;
        padding: 40px 20px;
        color: rgba(255, 255, 255, 0.6);
      ">
        <div style="font-size: 16px;">加载历史记录失败</div>
      </div>
    `;
  }
}

/**
 * 编辑历史记录
 */
window.editEmotionDiary = function (index) {
  try {
    const history = JSON.parse(localStorage.getItem('emotionDiaryHistory') || '[]');
    const sortedHistory = history.sort((a, b) => {
      return new Date(b.timestamp) - new Date(a.timestamp);
    });

    if (index >= 0 && index < sortedHistory.length) {
      const diary = sortedHistory[index];

      // 切换到编辑区域
      showEmotionDiaryEdit();

      // 填充数据
      document.getElementById('emotionDiaryContent').value = diary.content || '';
      document.getElementById('emotionMood').value = diary.mood || 'happy';
      document.getElementById('emotionTags').value = Array.isArray(diary.tags) ? diary.tags.join(', ') : (diary.tags || '');

      // 标记为编辑模式
      document.getElementById('emotionDiaryContent').setAttribute('data-edit-index', index);
    }
  } catch (error) {
    console.error('编辑日记失败:', error);
    showErrorToast('编辑日记失败');
  }
};

// ================= 生活指导功能 =================

/**
 * 获取生活指导
 */
window.getLifeGuidance = function () {
  if (!canAnalyze()) {
    showErrorToast('分析次数不足，请购买票数或等待明日免费次数');
    return;
  }

  const guidanceTopics = [
    '职场发展',
    '人际关系',
    '健康养生',
    '情感生活',
    '学习成长',
    '财富管理'
  ];

  const randomTopic = guidanceTopics[Math.floor(Math.random() * guidanceTopics.length)];
  const guidance = generateLifeGuidance(randomTopic);

  showLifeGuidanceModal(guidance);

  // 消耗分析次数
  consumeQuotaOnce();

  // 记录事件
  safeTrackUserAction('life_guidance_get', {
    type: 'get',
    topic: randomTopic,
    timestamp: new Date().toISOString()
  });
};

/**
 * 生成生活指导内容
 */
function generateLifeGuidance(topic) {
  const guidanceMap = {
    '职场发展': {
      title: '职场发展指导',
      content: '在职场中，保持积极的心态和持续学习的能力是关键。建议：\n1. 设定明确的职业目标\n2. 不断提升专业技能\n3. 建立良好的人际关系\n4. 保持工作与生活的平衡',
      tips: ['主动学习新技能', '建立职业网络', '保持工作热情']
    },
    '人际关系': {
      title: '人际关系指导',
      content: '良好的人际关系是成功的基础。建议：\n1. 真诚待人，建立信任\n2. 学会倾听和理解\n3. 保持适当的边界\n4. 及时解决冲突',
      tips: ['学会倾听', '保持真诚', '尊重他人']
    },
    '健康养生': {
      title: '健康养生指导',
      content: '身体健康是幸福生活的基础。建议：\n1. 保持规律作息\n2. 均衡饮食营养\n3. 适量运动锻炼\n4. 保持心情愉悦',
      tips: ['规律作息', '均衡饮食', '适量运动']
    },
    '情感生活': {
      title: '情感生活指导',
      content: '情感生活需要用心经营。建议：\n1. 真诚表达情感\n2. 学会包容理解\n3. 保持沟通交流\n4. 共同成长进步',
      tips: ['真诚沟通', '相互理解', '共同成长']
    },
    '学习成长': {
      title: '学习成长指导',
      content: '终身学习是个人成长的关键。建议：\n1. 制定学习计划\n2. 培养阅读习惯\n3. 实践所学知识\n4. 反思总结经验',
      tips: ['制定计划', '持续学习', '实践应用']
    },
    '财富管理': {
      title: '财富管理指导',
      content: '合理的财富管理能带来安全感。建议：\n1. 制定理财计划\n2. 控制消费支出\n3. 投资理财增值\n4. 建立应急基金',
      tips: ['制定计划', '理性消费', '合理投资']
    }
  };

  return guidanceMap[topic] || guidanceMap['职场发展'];
}
/**
 * 显示生活指导弹窗
 */
function showLifeGuidanceModal(guidance) {
  const modal = document.createElement('div');
  modal.id = 'lifeGuidanceModal';
  modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
  `;

  modal.innerHTML = `
    <div style="background: white; border-radius: 12px; padding: 24px; max-width: 600px; width: 90%; max-height: 80vh; overflow-y: auto;">
      <h3 style="margin: 0 0 16px 0; color: #333;">🎯 ${guidance.title}</h3>
      <div style="color: #666; line-height: 1.6; white-space: pre-line; margin-bottom: 16px;">${guidance.content}</div>
      <div style="background: #f8f9fa; padding: 12px; border-radius: 8px; margin-bottom: 16px;">
        <strong>💡 实用建议：</strong>
        <ul style="margin: 8px 0 0 0; padding-left: 20px;">
          ${guidance.tips.map(tip => `<li>${tip}</li>`).join('')}
        </ul>
      </div>
      <div style="display: flex; gap: 12px; justify-content: flex-end;">
        <button onclick="closeGuidanceModal()" style="padding: 8px 16px; border: 1px solid #ddd; background: white; border-radius: 6px; cursor: pointer;">关闭</button>
        <button onclick="shareGuidance('${guidance.title}')" style="padding: 8px 16px; background: #28a745; color: white; border: none; border-radius: 6px; cursor: pointer;">分享</button>
      </div>
    </div>
  `;

  document.body.appendChild(modal);
}

/**
 * 关闭生活指导弹窗
 */
window.closeGuidanceModal = function () {
  const modal = document.getElementById('lifeGuidanceModal');
  if (modal) {
    modal.remove();
  }
};

/**
 * 分享生活指导
 */
window.shareGuidance = function (title) {
  const shareText = `🎯 ${title}\n\n通过玄知相术获得的生活指导，帮助我更好地规划人生。\n\n立即体验：${window.location.href}`;

  if (navigator.clipboard) {
    navigator.clipboard.writeText(shareText).then(() => {
      showSuccessToast('生活指导已复制到剪贴板');
    });
  }

  closeGuidanceModal();
};

// ================= 灵魂伴侣功能 =================

/**
 * 灵魂伴侣管理器
 * ✅ 修复：不要覆盖已存在的 soulmateManager 实例
 * 只在 soulmateManager 不存在时才创建简单的降级对象
 */
if (!window.soulmateManager) {
  window.soulmateManager = {
    // 显示灵魂伴侣界面
    showSoulmateInterface() {
      // ✅ 重构：使用统一的聊天界面模式切换
      startSoulmateConversation();
    },

    // 创建灵魂伴侣界面
    // ✅ 重构：移除重复的灵魂伴侣界面创建，使用统一的聊天界面
    // createSoulmateInterface() 已移除，现在使用主聊天界面的模式切换

    // 关闭灵魂伴侣界面
    closeSoulmateInterface() {
      const interface = document.getElementById('soulmateInterface');
      if (interface) {
        interface.remove();
      }
    },

    // 获取关系等级
    getRelationshipLevel() {
      const interactionCount = this.getInteractionCount();
      if (interactionCount >= 200) return '灵魂伴侣';
      if (interactionCount >= 100) return '知心朋友';
      if (interactionCount >= 50) return '亲密伙伴';
      if (interactionCount >= 20) return '好朋友';
      return '新朋友';
    },

    // 获取互动次数
    getInteractionCount() {
      return Number(localStorage.getItem('soulmateInteractionCount')) || 0;
    },

    // 增加互动次数
    increaseInteractionCount() {
      const current = this.getInteractionCount();
      localStorage.setItem('soulmateInteractionCount', current + 1);
    },

    // 获取情感日记数量
    getDiaryCount() {
      const history = JSON.parse(localStorage.getItem('emotionDiaryHistory') || '[]');
      return history.length;
    },

    // 日记保存回调
    onDiarySaved(diaryEntry) {
      this.increaseInteractionCount();

      // 根据心情提供关怀
      this.provideCareBasedOnMood(diaryEntry.mood);
    },

    // 根据心情提供关怀
    provideCareBasedOnMood(mood) {
      const careMessages = {
        'happy': '看到您今天心情很好，真为您高兴！继续保持这份快乐的心情吧！',
        'excited': '感受到您的兴奋，有什么好消息要分享吗？',
        'peaceful': '平静的心情是最珍贵的，愿您一直保持内心的宁静。',
        'tired': '看起来您今天有些疲惫，记得好好休息，身体是革命的本钱。',
        'worried': '感受到您的担忧，有什么烦恼可以和我聊聊，我会尽力帮助您。',
        'sad': '看到您今天有些难过，请记住，困难只是暂时的，明天会更好。',
        'angry': '愤怒的情绪需要释放，但也要学会控制，深呼吸，冷静思考。',
        'confused': '困惑是成长的必经之路，慢慢来，答案会逐渐清晰的。'
      };

      const message = careMessages[mood] || '感谢您与我分享今天的心情，我会一直陪伴在您身边。';

      // 延迟显示关怀消息
      timerManager.setTimeout(() => {
        this.showActiveCareMessage({
          message: message,
          type: 'mood_care',
          timestamp: new Date().toISOString()
        });
      }, 2000);
    },

    // 显示主动关怀消息
    showActiveCareMessage(data) {
      this.createActiveCarePopup(data);
    },

    // 创建主动关怀弹窗
    createActiveCarePopup(data) {
      // 移除已存在的弹窗
      const existingPopup = document.getElementById('activeCarePopup');
      if (existingPopup) {
        existingPopup.remove();
      }

      const popup = document.createElement('div');
      popup.id = 'activeCarePopup';
      popup.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: white;
      border-radius: 12px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
      z-index: 10001;
      max-width: 400px;
      width: 90%;
      animation: slideInScale 0.3s ease-out;
    `;

      popup.innerHTML = `
      <div style="padding: 24px;">
        <div style="text-align: center; margin-bottom: 16px;">
          <div style="font-size: 48px; margin-bottom: 8px;">🧙</div>
          <div style="font-size: 18px; font-weight: bold; color: #333;">倪大师</div>
        </div>
        <div style="color: #666; line-height: 1.6; margin-bottom: 20px; text-align: center;">
          ${data.message}
        </div>
        <div style="display: flex; gap: 12px; justify-content: center;">
          <button onclick="soulmateManager.closeActiveCarePopup(); soulmateManager.openEmotionDiary()" style="padding: 10px 16px; background: #007bff; color: white; border: none; border-radius: 6px; cursor: pointer;">记录心情</button>
          <button onclick="soulmateManager.closeActiveCarePopup(); soulmateManager.getLifeGuidance('general')" style="padding: 10px 16px; background: #28a745; color: white; border: none; border-radius: 6px; cursor: pointer;">获取指导</button>
          <button onclick="soulmateManager.closeActiveCarePopup()" style="padding: 10px 16px; background: #6c757d; color: white; border: none; border-radius: 6px; cursor: pointer;">知道了</button>
        </div>
      </div>
    `;

      document.body.appendChild(popup);

      // 添加动画效果
      popup.style.animation = 'slideInScale 0.3s ease-out';
    },

    // 关闭主动关怀弹窗
    closeActiveCarePopup() {
      const popup = document.getElementById('activeCarePopup');
      if (popup) {
        popup.style.animation = 'slideOutScale 0.3s ease-in';
        timerManager.setTimeout(() => popup.remove(), 300);
      }
    },

    // 获取生活指导（带加载状态）
    getLifeGuidanceWithLoading(type) {
      this.increaseInteractionCount();
      getLifeGuidance();
    },

    // 获取生活指导
    getLifeGuidance(type = 'general') {
      this.increaseInteractionCount();
      getLifeGuidance();
    }
  };

  // ================= 实时对话功能 =================

  /**
   * 实时对话管理器
   */
  // ✅ 修复：简化 chatManager，用户直接在主界面 chatBox 中对话
  window.chatManager = {
    // 对话历史
    conversationHistory: [],

    // 初始化对话功能
    init() {
      this.loadConversationHistory();
      // ✅ 修复：用户直接在主界面 chatBox 中对话，不需要额外的聊天界面
    },

    // 加载对话历史
    loadConversationHistory() {
      const saved = localStorage.getItem('conversationHistory');
      if (saved) {
        try {
          this.conversationHistory = JSON.parse(saved);
        } catch (e) {
          console.error('加载对话历史失败:', e);
          this.conversationHistory = [];
        }
      }
    },

    // 保存对话历史
    saveConversationHistory() {
      localStorage.setItem('conversationHistory', JSON.stringify(this.conversationHistory));
    },

    // 清空对话历史
    clearConversationHistory() {
      this.conversationHistory = [];
      localStorage.removeItem('conversationHistory');
      showSuccessToast('对话历史已清空');
    }
  };

  // ✅ 修复：如果真正的 soulmateManager 已存在，添加兼容方法（如果需要）
} else {
  // 如果真正的 soulmateManager 已存在，添加兼容方法（如果需要）
  if (window.soulmateManager && typeof window.soulmateManager.showSoulmateInterface !== 'function') {
    // 添加兼容方法（如果需要）
    window.soulmateManager.showSoulmateInterface = function () {
      if (typeof startSoulmateConversation === 'function') {
        startSoulmateConversation();
      } else if (this.openSoulmateChat) {
        this.openSoulmateChat();
      }
    };
  }
  debugLog('✅ soulmateManager 已存在（真正的实例），未覆盖');
}

/**
 * 创建对话按钮
 */
function createChatToggle() {
  const chatToggle = document.createElement('button');
  chatToggle.id = 'chatToggle';
  chatToggle.innerHTML = '💬';
  chatToggle.title = '与倪大师对话';
  chatToggle.style.cssText = `
    position: fixed;
    bottom: 20px;
    right: 20px;
    width: 60px;
    height: 60px;
    background: #007bff;
    color: white;
    border: none;
    border-radius: 50%;
    font-size: 24px;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
    z-index: 9999;
    transition: all 0.3s ease;
  `;

  chatToggle.addEventListener('click', () => {
    // ✅ 修复：直接在主界面的 chatBox 中开始对话，不弹出额外界面
    debugLog('🎯 用户点击主应用聊天按钮，开始对话');

    // 确保 chatBox 可见
    const chatBox = document.getElementById('chatBox');
    if (chatBox) {
      chatBox.scrollIntoView({ behavior: 'smooth' });

      // 如果 chatBox 为空，添加欢迎消息
      if (chatBox.children.length === 0) {
        appendMessage("assistant", `👋 你好！我是倪大师，很高兴为你服务！

🔮 我可以为你提供：
• 手相分析 - 解读你的性格特征
• 面相分析 - 了解你的运势走向  
• 八字分析 - 分析你的命理格局

请上传你的手相照片，或者告诉我你想了解什么，我会为你详细解读！`, true, false);
      }

      // 聚焦到输入框
      const inputBox = document.getElementById('inputBox');
      if (inputBox) {
        inputBox.focus();
      }
    }
  });

  chatToggle.addEventListener('mouseenter', () => {
    chatToggle.style.transform = 'scale(1.1)';
    chatToggle.style.boxShadow = '0 6px 16px rgba(0, 123, 255, 0.4)';
  });

  chatToggle.addEventListener('mouseleave', () => {
    chatToggle.style.transform = 'scale(1)';
    chatToggle.style.boxShadow = '0 4px 12px rgba(0, 123, 255, 0.3)';
  });

  document.body.appendChild(chatToggle);
}

/**
 * 添加消息到对话框
 */
function appendMessage(role, text, animate = false, needsPayment = false, onTypingComplete = null) {
  const chatBox = document.getElementById('chatBox');
  if (!chatBox) {
    debugWarn('未找到对话框元素');
    return;
  }

  // ✅ 修复：在添加消息前，先立即滚动到底部（不使用平滑滚动），确保第一条消息可见
  // 检查聊天框是否在底部附近（50px范围内）
  if (chatBox.scrollHeight > chatBox.clientHeight) {
    const isNearBottom = chatBox.scrollHeight - chatBox.scrollTop - chatBox.clientHeight < 50;
    if (!isNearBottom) {
      // 如果不在底部附近，立即滚动到底部（不使用平滑滚动）
      chatBox.scrollTop = chatBox.scrollHeight;
    }
  }

  const wrapper = document.createElement('div');
  wrapper.classList.add('msg', 'flex', role === 'user' ? 'justify-end' : 'justify-start', 'items-start');

  if (role === 'assistant') {
    wrapper.setAttribute('data-reply', text);
  }

  const bubble = document.createElement('div');
  bubble.classList.add('px-3', 'py-2', 'rounded-2xl', 'bubble', role === 'user' ? 'bubble-user' : 'bubble-ai');
  bubble.style.fontFamily = "'Noto Serif SC', 'ZCOOL KuaiLe', serif";
  bubble.style.setProperty('line-height', '1.52', 'important');
  if (role === 'assistant') bubble.style.position = 'relative';
  if (role === 'user') bubble.style.whiteSpace = 'pre-line';

  const tag = document.createElement('div');
  tag.className = `role-tag ${role === 'user' ? 'role-user' : 'role-assistant'}`;

  if (role === 'user') {
    const userIcon = '👤';
    tag.innerHTML = `${userIcon} 你`;
  } else {
    tag.innerHTML = '🧙 倪大师';
  }

  bubble.appendChild(tag);

  const contentEl = document.createElement('div');

  if (animate && role === 'assistant') {
    contentEl.innerHTML = '<div style="color: #999;">正在输入...</div>';

    // 检查是否为HTML内容
    const isHTML = text.includes('<') && text.includes('>');

    if (isHTML) {
      // HTML内容：模拟打字效果
      simulateTypingForHTML(text, contentEl, onTypingComplete);
    } else {
      // 纯文本：使用统一的打字效果
      unifiedTypingEffect(contentEl, text, {
        onComplete: onTypingComplete,
        soundEnabled: true,
        pauseEnabled: false,  // 普通分析结果不需要停顿
        scrollEnabled: true,
        scrollInterval: 15,  // ✅ 优化：改为15，减少滚动频率，减少翻页时的抖动
        startDelay: 200
      });
    }
  } else {
    // 直接显示内容
    if (text.includes('<')) {
      contentEl.innerHTML = text;
    } else {
      contentEl.textContent = text;
    }
    // 非打字效果也要滚动
    stickBottom();
  }

  bubble.appendChild(contentEl);

  // 🔧 添加遮罩显示逻辑（修复版）
  if (needsPayment && role === 'assistant') {
    const mask = document.createElement('div');
    mask.style.cssText = `
      position:absolute;left:0;top:0;width:100%;height:100%;
      background:rgba(26, 26, 46, 0.9) !important;
      backdrop-filter: blur(15px) !important;
      -webkit-backdrop-filter: blur(15px) !important;
      display:flex;flex-direction:column;
      align-items:center;justify-content:center;z-index:5;border-radius:18px;
      text-align:center;font-size:1.05rem;color:rgba(255, 255, 255, 0.95) !important;
      font-family:'Noto Serif SC','ZCOOL KuaiLe',serif;pointer-events:auto;
      padding:12px 14px;box-sizing:border-box;max-width:100%;max-height:100%;
      overflow:hidden;
      border:1px solid rgba(102, 126, 234, 0.4) !important;
      box-shadow:0 4px 20px rgba(102, 126, 234, 0.4) !important;
    `;
    mask.innerHTML = `
      <div style="padding:14px 10px 8px;line-height:1.65;color:rgba(255, 255, 255, 0.95) !important;text-shadow:0 2px 8px rgba(102, 126, 234, 0.5) !important;">
        👁️ 剩余内容已隐藏<br>分享后立即解锁并 <b style="color:rgba(255, 255, 255, 0.95) !important;">获得体验券</b>
      </div>
      <button id="maskShareBtn"
        style="margin-top:8px;padding:.5rem 1rem;border-radius:.8rem;
               background:linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
               color:white !important;border:none;font-weight:600;
               box-shadow:0 4px 12px rgba(102, 126, 234, 0.4) !important;
               transition:all 0.3s ease;position:relative;overflow:hidden;"
        onmouseover="this.style.background='linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)'; this.style.transform='translateY(-2px) scale(1.05)'; this.style.boxShadow='0 6px 20px rgba(102, 126, 234, 0.6)'"
        onmouseout="this.style.background='linear-gradient(135deg, #667eea 0%, #764ba2 100%)'; this.style.transform='translateY(0) scale(1)'; this.style.boxShadow='0 4px 12px rgba(102, 126, 234, 0.4)'">
        去分享并解锁
      </button>
    `;
    const maskBtn = mask.querySelector('#maskShareBtn');
    if (maskBtn) {
      maskBtn.onclick = (e) => {
        e.stopPropagation();

        // 检查用户是否刚刚关闭了支付界面
        const wasPaymentClosed = sessionStorage.getItem('paymentClosed');

        if (wasPaymentClosed) {
          // 如果刚刚关闭了支付界面，直接弹出支付界面
          sessionStorage.removeItem('paymentClosed'); // 清除标记
          showQRInPopup('ticket', 1); // 弹出支付界面
        } else {
          // 否则弹出分享界面（根据分享次数智能判断）
          // ✅ 修复：深度解读分享应该获得免费次数
          showSharePopup(null, false, null, true); // shouldRewardFreeCount = true
        }
      };
    }
    bubble.appendChild(mask);
  }

  wrapper.appendChild(bubble);
  chatBox.appendChild(wrapper);
  stickBottom();
}

/**
 * 初始化图片预览功能
 */
function initImagePreview() {
  const imageInput = document.getElementById('imageInput');
  const filePreview = document.getElementById('filePreview');
  const previewImg = document.getElementById('previewImg');

  if (imageInput && filePreview && previewImg) {
    imageInput.addEventListener('change', function (e) {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = function (e) {
          previewImg.src = e.target.result;
          filePreview.classList.remove('hidden');

          // 隐藏上传提示信息
          const uploadPrompt = document.getElementById('uploadPrompt');
          const uploadHints = document.getElementById('uploadHints');
          if (uploadPrompt) uploadPrompt.style.display = 'none';
          if (uploadHints) uploadHints.style.display = 'none';
        };
        reader.readAsDataURL(file);
      } else {
        filePreview.classList.add('hidden');

        // 显示上传提示信息
        const uploadPrompt = document.getElementById('uploadPrompt');
        const uploadHints = document.getElementById('uploadHints');
        if (uploadPrompt) uploadPrompt.style.display = 'block';
        if (uploadHints) uploadHints.style.display = 'block';
      }
    });
  }
}


// ================= 全局错误处理 =================
window.handleSoulmateModalError = function (error, context = 'unknown') {
  debugError(`❌ 灵魂伴侣弹窗错误 [${context}]:`, error);

  // 尝试使用备用方案
  timerManager.setTimeout(() => {
    createSoulmateModalDirectly();
  }, 1000);
};

// ================= DOM就绪检查 =================
function ensureDOMReady(callback, maxWait = 5000) {
  if (document.body && document.readyState === 'complete') {
    callback();
    return;
  }

  let waitTime = 0;
  const checkInterval = timerManager.setInterval(() => {
    waitTime += 100;

    if (document.body && document.readyState === 'complete') {
      timerManager.clearInterval(checkInterval);
      callback();
    } else if (waitTime >= maxWait) {
      timerManager.clearInterval(checkInterval);
      debugWarn('⚠️ DOM就绪检查超时，强制执行回调');
      callback();
    }
  }, 100);
}

// ================= 备用弹窗创建函数 =================
function createSoulmateModalDirectly(retryCount = 0) {
  debugLog(`🎯 开始直接创建灵魂伴侣弹窗 (尝试 ${retryCount + 1}/3)`);

  // 使用DOM就绪检查
  ensureDOMReady(() => {
    try {
      // 检查DOM是否准备就绪
      if (!document.body) {
        throw new Error('document.body 不存在，DOM可能未准备就绪');
      }

      // 移除已存在的弹窗
      const existingModal = document.getElementById('soulmateTriggerModal');
      if (existingModal) {
        debugLog('🗑️ 移除已存在的弹窗');
        existingModal.remove();
      }

      // 创建弹窗
      const modal = document.createElement('div');
      modal.id = 'soulmateTriggerModal';
      modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
    `;

      modal.innerHTML = `
      <div class="soulmate-modal-content" style="
        background: white;
        border-radius: 20px;
        padding: 30px;
        max-width: 500px;
        width: 90%;
        text-align: center;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
        border: 2px solid #8b5e00;
      ">
        <div class="modal-header" style="
          background: #8b5e00;
          color: white;
          font-weight: bold;
          padding: 15px;
          border-radius: 15px 15px 0 0;
          margin: -30px -30px 20px -30px;
        ">
          <h3 style="margin: 0; font-size: 18px;">💕 灵魂伴侣功能</h3>
        </div>
        <div class="modal-body" style="
          background: white;
          color: #333;
          line-height: 1.6;
          padding: 0;
        ">
          <p style="margin-bottom: 20px;">基于您的分析结果，为您推荐专属的灵魂伴侣功能</p>
          <button onclick="this.parentElement.parentElement.parentElement.remove(); showSoulmateIntroModal();" style="
            background: #8b5e00;
            color: white;
            border: none;
            padding: 12px 25px;
            border-radius: 8px;
            cursor: pointer;
            font-weight: bold;
          ">开始试用</button>
        </div>
      </div>
    `;

      // 添加到DOM
      document.body.appendChild(modal);

      // 验证弹窗是否成功创建
      const createdModal = document.getElementById('soulmateTriggerModal');
      if (!createdModal) {
        throw new Error('弹窗创建失败，无法在DOM中找到');
      }

      debugLog('✅ 灵魂伴侣弹窗创建成功');

      // 添加关闭事件监听
      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          modal.remove();
          debugLog('🗑️ 用户点击背景关闭弹窗');
        }
      });

      return true;

    } catch (error) {
      debugError(`❌ 创建灵魂伴侣弹窗失败 (尝试 ${retryCount + 1}):`, error);

      // 重试机制
      if (retryCount < 2) {
        debugLog(`🔄 准备重试创建弹窗 (${retryCount + 2}/3)`);
        timerManager.setTimeout(() => {
          createSoulmateModalDirectly(retryCount + 1);
        }, 1000 * (retryCount + 1)); // 递增延迟
      } else {
        debugError('❌ 灵魂伴侣弹窗创建失败，已达到最大重试次数');

        // 最后的备用方案：使用alert
        timerManager.setTimeout(() => {
          alert('💕 灵魂伴侣功能已为您准备就绪！\n\n基于您的分析结果，我们为您推荐专属的灵魂伴侣功能。\n\n请刷新页面后重新体验。');
        }, 500);
      }

      return false;
    }
  });
}

// ================= 测试函数 =================
// 测试八字分析不需要图片
window.testBaziAnalysis = function () {
  debugLog('🔍 开始测试八字分析功能');

  // 模拟选择八字分析
  const analysisTypeSelect = document.getElementById('analysisType');
  if (analysisTypeSelect) {
    analysisTypeSelect.value = 'bazi';
    debugLog('✅ 已选择八字分析类型');
  }

  // 模拟选择性别
  const genderMale = document.querySelector('input[name="gender"][value="male"]');
  if (genderMale) {
    genderMale.checked = true;
    debugLog('✅ 已选择性别：男');
  }

  // 模拟设置出生信息
  const birthDate = document.getElementById('birthDate');
  if (birthDate) {
    birthDate.value = '1981-03-28';
    debugLog('✅ 已设置出生日期：1981-03-28');
  }

  const birthTime = document.getElementById('birthTime');
  if (birthTime) {
    birthTime.value = '12:00';
    debugLog('✅ 已设置出生时间：12:00');
  }

  const birthPlace = document.getElementById('birthPlace');
  if (birthPlace) {
    birthPlace.value = '北京';
    debugLog('✅ 已设置出生地点：北京');
  }

  // 测试表单数据收集
  const formData = getFormData();
  debugLog('🔍 收集的表单数据:');
  for (let [key, value] of formData.entries()) {
    debugLog(`  ${key}: ${value}`);
  }

  // 测试验证逻辑
  const isValid = validateFormData(formData);
  debugLog('🔍 表单验证结果:', isValid ? '✅ 通过' : '❌ 失败');

  // 检查是否包含图片
  const hasImage = formData.get('file');
  debugLog('🔍 是否包含图片:', hasImage ? '是' : '否');

  if (!hasImage) {
    debugLog('✅ 八字分析不需要图片，符合预期');
  } else {
    debugLog('❌ 八字分析不应该包含图片');
  }

  // 检查是否包含出生信息
  const hasBirthDate = formData.get('birthDate');
  const hasBirthTime = formData.get('birthTime');
  debugLog('🔍 出生信息检查:', {
    birthDate: hasBirthDate ? '✅ 有' : '❌ 无',
    birthTime: hasBirthTime ? '✅ 有' : '❌ 无'
  });

  if (hasBirthDate && hasBirthTime) {
    debugLog('✅ 八字分析包含必要的出生信息');
  } else {
    debugLog('❌ 八字分析缺少必要的出生信息');
  }

  debugLog('🔍 八字分析测试完成');
};
// 测试八字分析端点
window.testBaziEndpoint = async function () {
  debugLog('🔍 开始测试八字分析端点');

  // 模拟选择八字分析
  const analysisTypeSelect = document.getElementById('analysisType');
  if (analysisTypeSelect) {
    analysisTypeSelect.value = 'bazi';
  }

  // 模拟选择性别
  const genderMale = document.querySelector('input[name="gender"][value="male"]');
  if (genderMale) {
    genderMale.checked = true;
  }

  // 模拟设置出生信息
  const birthDate = document.getElementById('birthDate');
  if (birthDate) {
    birthDate.value = '1981-03-28';
  }

  const birthTime = document.getElementById('birthTime');
  if (birthTime) {
    birthTime.value = '12:00';
  }

  const birthPlace = document.getElementById('birthPlace');
  if (birthPlace) {
    birthPlace.value = '北京';
  }

  // 获取表单数据
  const formData = getFormData();
  debugLog('🔍 测试用的表单数据:');
  for (let [key, value] of formData.entries()) {
    debugLog(`  ${key}: ${value}`);
  }

  // 测试农历转换
  debugLog('🔍 测试农历转换...');
  await convertToLunarIfNeeded(formData);

  // 检查转换后的数据
  const birthInfo = formData.get('birthInfo');
  debugLog('🔍 转换后的出生信息:', birthInfo);

  // 测试端点选择
  const analysisType = formData.get('analysisType');
  debugLog('🔍 分析类型:', analysisType);

  if (analysisType === 'bazi') {
    debugLog('✅ 八字分析将使用/trigger端点');

    // 模拟构建八字分析数据
    const baziData = {
      session_id: formData.get('session_id'),
      type: 'bazi',
      birth: formData.get('birthInfo') || `${formData.get('birthDate')} ${formData.get('birthTime')} ${formData.get('birthPlace')}`,
      gender: formData.get('gender')
    };

    debugLog('🔍 八字分析数据格式:', baziData);
    debugLog('✅ 八字分析数据格式正确，符合/trigger端点要求');
  } else {
    debugLog('❌ 分析类型不是八字分析');
  }

  debugLog('🔍 八字分析端点测试完成');
};

// 测试响应格式处理
window.testResponseFormat = function () {
  debugLog('🔍 开始测试响应格式处理');

  // 模拟不同的API响应格式
  const testResponses = [
    {
      name: '八字分析响应（包含reply字段）',
      data: {
        reply: '根据您的八字信息，您的性格特点...',
        status: 'done',
        analysisType: 'bazi'
      }
    },
    {
      name: '面相分析响应（包含content字段）',
      data: {
        content: '根据您的面相特征，您的运势...',
        status: 'done',
        analysisType: 'face'
      }
    },
    {
      name: '手相分析响应（包含text字段）',
      data: {
        text: '根据您的手相特征，您的事业...',
        status: 'done',
        analysisType: 'palm'
      }
    },
    {
      name: '错误响应（无内容字段）',
      data: {
        status: 'error',
        message: '分析失败'
      }
    }
  ];

  for (const testCase of testResponses) {
    debugLog(`\n🔍 测试: ${testCase.name}`);
    debugLog('🔍 测试数据:', testCase.data);

    // 测试响应格式检查
    const foundField = checkResponseFormat(testCase.data);
    debugLog('🔍 找到的字段:', foundField);

    // 测试displayAnalysisResult函数
    debugLog('🔍 测试displayAnalysisResult函数...');
    try {
      displayAnalysisResult(testCase.data);
      debugLog('✅ displayAnalysisResult函数执行成功');
    } catch (error) {
      debugError('❌ displayAnalysisResult函数执行失败:', error);
    }
  }

  debugLog('🔍 响应格式处理测试完成');
};

// ================= 全局函数导出 =================
// 🔧 修复重复定义：移除 window.submitData 的重复定义，保留第2978行的完整实现
window.calculateSoulmateDelay = calculateSoulmateDelay;
window.triggerSoulmateModal = triggerSoulmateModal;
window.copyShareContent = copyShareContent;
window.showNewUserGuide = showNewUserGuide;
window.showSuccessToast = showSuccessToast;
window.showErrorToast = showErrorToast;
window.stickBottom = stickBottom;

// ================= 全局变量 =================
let _lastImageBase64 = null;
let _lastRecogType = null;

// ================= 核心全局函数 =================

/**
 * 主要的提交分析函数
 */
window.submitData = async function () {
  // ✅ 新增：显示分析时长提示
  const submitHint = document.getElementById('submitHint');
  if (submitHint) {
    submitHint.classList.remove('hidden');
  }
  const startTime = Date.now();

  // 检查是否可以进行分析
  if (!canAnalyze()) {
    // ✅ 修复：次数不足时直接弹出支付选择弹窗
    // ✅ 修复：基础分析结果分享不获得免费次数，只解锁内容
    showSharePopup(null, true, '分析次数不足', false); // shouldRewardFreeCount = false
    return;
  }

  // 重置深度解读按钮状态
  resetDeepAnalysisButtonState();

  // 刷新每日状态
  refreshTodayStatus();
  updateCountBanner();

  // 获取UI元素
  const btn = document.getElementById('submitBtn');
  const txt = document.getElementById('submitText');
  const spinner = document.getElementById('submitSpinner');

  // 禁用提交按钮
  disableSubmit();

  try {
    // 显示分析进度
    showAnalysisProgress();

    // 获取表单数据
    const formData = getFormData();

    // 验证数据
    if (!validateFormData(formData)) {
      return;
    }

    // 检查是否有足够的分析次数
    if (!canAnalyze()) {
      // ✅ 修复：次数不足时直接弹出支付选择弹窗
      // ✅ 修复：基础分析结果分享不获得免费次数，只解锁内容
      showSharePopup(null, true, '分析次数不足', false); // shouldRewardFreeCount = false
      return;
    }

    // 发送分析请求
    const result = await sendAnalysisRequest(formData);

    // 处理分析结果
    await handleAnalysisResult(result);

    // ✅ 修复业务逻辑：只有在分析真正成功时才消耗次数
    // 检查分析结果是否有效（有内容且长度足够）
    const content = result.analysis_text || result.reply || result.content || result.text || result.message;
    if (content && content.length > 50) {
      // 分析成功，消耗次数
      consumeQuotaOnce();

      // 记录分析事件
      safeTrackUserAction('analysis_completed', {
        analysisType: result.analysisType || 'unknown',
        resultLength: content.length,
        timestamp: new Date().toISOString(),
        isVip: isVip()
      });
    } else {
      // 分析结果无效，不消耗次数，抛出错误
      throw new Error('分析结果无效，未消耗次数，请重试');
    }

  } catch (error) {
    // ✅ P1-3修复：使用友好的错误提示
    console.error('分析失败:', error);

    if (typeof ErrorHandler !== 'undefined' && ErrorHandler.showUserFriendlyError) {
      ErrorHandler.showUserFriendlyError(error, 'analysis_failed', {
        useToast: true,
        action: '请检查网络连接，或稍后重试'
      });
    } else {
      showErrorToast('分析失败，请稍后重试。如问题持续，请联系客服：yaoyx002');
    }
  } finally {
    enableSubmit();
  }
};

/**
 * 更新计数横幅
 */
window.updateCountBanner = function () {
  // 🔧 修复免费次数/优惠券前端显示问题
  // 使用统一的次数横幅显示，与工作版本保持一致

  const banner = document.getElementById('countBanner');
  if (!banner) {
    debugWarn('⚠️ 未找到 #countBanner 元素，无法更新次数显示');
    return;
  }

  // ✅ 新增：获取立即购买按钮
  const quickBuyBtn = document.getElementById('quickBuyBtn');

  // 根据用户状态显示不同的次数信息
  if (isVip()) {
    banner.innerText = "👑 会员无限次数";
    banner.style.color = "#d97706"; // 金色
    // ✅ 新增：会员不显示立即购买按钮
    if (quickBuyBtn) {
      quickBuyBtn.style.display = 'none';
    }
  } else if (getTicketCount() > 0) {
    banner.innerText = `🎫 体验券剩余：${getTicketCount()} 次`;
    banner.style.color = "#059669"; // 绿色
    // ✅ 新增：有体验券时不显示立即购买按钮
    if (quickBuyBtn) {
      quickBuyBtn.style.display = 'none';
    }
  } else {
    const freeCount = getFreeCount();
    const shareCount = getShareCount(); // ✅ 新增：获取分享次数
    // ✅ P0-8修复：显示详细的免费和分享次数说明
    const shareRemaining = DAILY_SHARE_LIMIT - shareCount;
    // ✅ 修复：使用 innerText 保持一致性，并优化移动端显示
    // ✅ P0-8优化：由于采用上下布局，横幅有更多空间，移动端也可以使用分隔符
    if (shareRemaining > 0) {
      // 移动端如果空间足够，使用分隔符；如果空间不足，使用换行
      const isMobile = window.innerWidth <= 768;
      const textWithSeparator = `🆓 今日免费：${freeCount} 次 | 分享解锁：${shareRemaining} 次可用`;
      // 测试文本宽度，如果超过容器宽度则换行
      const testMeasure = document.createElement('span');
      testMeasure.style.visibility = 'hidden';
      testMeasure.style.position = 'absolute';
      testMeasure.style.whiteSpace = 'nowrap';
      testMeasure.style.fontSize = getComputedStyle(banner).fontSize;
      testMeasure.style.fontWeight = getComputedStyle(banner).fontWeight;
      testMeasure.textContent = textWithSeparator;
      document.body.appendChild(testMeasure);
      const textWidth = testMeasure.offsetWidth;
      document.body.removeChild(testMeasure);

      const containerWidth = banner.parentElement ? banner.parentElement.offsetWidth : window.innerWidth;

      if (isMobile && textWidth > containerWidth * 0.9) {
        // 移动端且文本过长，使用换行
        banner.innerText = `🆓 今日免费：${freeCount} 次\n分享解锁：${shareRemaining} 次可用`;
        banner.style.whiteSpace = 'pre-line';
        banner.style.textAlign = 'center';
      } else {
        // 桌面端或移动端空间足够，使用分隔符
        banner.innerText = textWithSeparator;
        banner.style.whiteSpace = 'nowrap';
        banner.style.textAlign = 'center';
      }
    } else {
      banner.innerText = `🆓 今日免费：${freeCount} 次`;
      banner.style.whiteSpace = 'nowrap';
      banner.style.textAlign = 'center';
    }
    banner.style.color = "#2563eb"; // 蓝色
    // ✅ 修复：免费次数为 0 且分享次数也用完时，才显示立即购买按钮
    if (quickBuyBtn) {
      // 检查：免费次数为 0 且分享次数已达上限
      if (freeCount === 0 && shareCount >= DAILY_SHARE_LIMIT) {
        quickBuyBtn.style.display = 'block';
      } else {
        quickBuyBtn.style.display = 'none';
      }
    }
  }

  // 显示横幅
  banner.style.display = 'block';

  // ✅ 新增：绑定立即购买按钮（在 updateCountBanner 函数末尾）
  if (quickBuyBtn && !quickBuyBtn.dataset.bound) {
    quickBuyBtn.addEventListener('click', () => {
      // ✅ 修复：基础分析结果分享不获得免费次数，只解锁内容
      showSharePopup(null, true, '分析次数不足', false); // shouldRewardFreeCount = false
    });
    quickBuyBtn.dataset.bound = 'true';
    debugLog('✅ 立即购买按钮事件已绑定');
  }

  debugLog('✅ 次数横幅已更新:', banner.innerText);
};

/**
 * 存储最后上传的图片数据
 */
window.lastImageBase64 = null;
window.lastRecogType = null;

// ================= 工具函数 =================

/**
 * 重置深度分析按钮状态
 */
function resetDeepAnalysisButtonState() {
  const deepBtn = document.getElementById('deepAnalysisBtn');
  if (deepBtn) {
    deepBtn.disabled = false;
    deepBtn.textContent = '深度解读';
  }
}

/**
 * 刷新每日状态
 */
function refreshTodayStatus() {
  const today = getTodayStr();
  const lastDay = localStorage.getItem('lastDay');

  // ✅ P2-1修复：处理 lastDay 不存在的情况
  if (!lastDay || lastDay !== today) {
    localStorage.setItem('freeCount', DAILY_FREE_COUNT);
    localStorage.setItem('shareCount', 0);
    localStorage.setItem('lastDay', today);
    debugLog('✅ 今日状态已刷新，免费次数:', DAILY_FREE_COUNT);
  }
}

/**
 * 购买票数
 * 注意：isVip, getTicketCount, setTicketCount 等函数已在文件前部定义（约第400行）
 */
window.buyTickets = function (amount) {
  const currentTickets = getTicketCount();
  const newTickets = currentTickets + amount;
  setTicketCount(newTickets);
  updateCountBanner();
  showSuccessToast(`成功购买 ${amount} 张票`);

  // 记录购买事件
  safeTrackUserAction('purchase_tickets', {
    type: 'tickets',
    amount: amount,
    totalTickets: newTickets,
    timestamp: new Date().toISOString()
  });
};

/**
 * 禁用提交按钮
 */
function disableSubmit() {
  const btn = document.getElementById('submitBtn');
  const txt = document.getElementById('submitText');
  const spinner = document.getElementById('submitSpinner');

  if (btn && txt && spinner) {
    btn.disabled = true;
    txt.textContent = "加载中...";
    spinner.classList.remove('hidden');
    spinner.classList.add('animate-spin');
  }
}

/**
 * 启用提交按钮
 */
function enableSubmit() {
  const btn = document.getElementById('submitBtn');
  const txt = document.getElementById('submitText');
  const spinner = document.getElementById('submitSpinner');

  if (btn && txt && spinner) {
    spinner.classList.add('hidden');
    spinner.classList.remove('animate-spin');
    txt.textContent = "提交分析";
    btn.disabled = false;
  }
}

// ✅ 已删除：DAILY_FREE_COUNT 和 DAILY_SHARE_LIMIT 已在文件前部定义（约第400行，VIP函数之前）

/**
 * 显示新手指引弹窗
 * @param {boolean} forceShow - 是否强制显示（忽略首次访问检查）
 */
function showNewUserGuide(forceShow = false) {
  // ✅ 优化：如果不是强制显示，检查是否是首次访问
  if (!forceShow) {
    const hasVisited = localStorage.getItem('has_visited_before');
    const visitTime = localStorage.getItem('has_visited_before_time');
    const now = Date.now();

    // ✅ 新增：如果超过7天未访问，重新显示指引
    if (hasVisited && visitTime) {
      const daysSinceVisit = (now - parseInt(visitTime)) / (1000 * 60 * 60 * 24);
      if (daysSinceVisit < 7) {
        return; // 7天内访问过，不显示
      }
      // 超过7天，清除标记，重新显示
      localStorage.removeItem('has_visited_before');
      localStorage.removeItem('has_visited_before_time');
    } else if (hasVisited) {
      return; // 已访问过，不显示
    }

    // ✅ 新增：记录访问时间
    localStorage.setItem('has_visited_before', 'true');
    localStorage.setItem('has_visited_before_time', now.toString());
  }

  // 创建指引弹窗
  const modal = document.createElement('div');
  modal.id = 'newUserGuideModal';
  modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.7) !important;
    backdrop-filter: blur(8px) !important;
    -webkit-backdrop-filter: blur(8px) !important;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10001;
    pointer-events: none;
  `;

  const modalContent = document.createElement('div');
  modalContent.style.cssText = `
    background: rgba(26, 26, 46, 0.95) !important;
    backdrop-filter: blur(20px) !important;
    -webkit-backdrop-filter: blur(20px) !important;
    border: 1px solid rgba(102, 126, 234, 0.5) !important;
    border-radius: 20px;
    padding: 30px;
    max-width: 500px;
    width: 90%;
    max-height: 80vh;
    overflow-y: auto;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(102, 126, 234, 0.3) inset, 0 0 40px rgba(102, 126, 234, 0.3) !important;
    animation: modalSlideIn 0.3s ease-out;
    pointer-events: auto;
    color: white !important;
  `;

  modalContent.innerHTML = `
    <div style="text-align: center; margin-bottom: 20px;">
      <div style="font-size: 3rem; margin-bottom: 15px;">🎓</div>
      <h3 style="color: white !important; font-size: 1.4rem; font-weight: bold; margin-bottom: 15px; text-shadow: 0 2px 10px rgba(102, 126, 234, 0.5) !important;">
        欢迎使用玄知相术·倪大师
      </h3>
    </div>
    
    <div style="color: rgba(255, 255, 255, 0.9) !important; font-size: 0.95rem; line-height: 1.8; text-align: left; margin-bottom: 25px;">
      <div style="margin-bottom: 15px;">
        <strong style="color: rgba(255, 255, 255, 0.95) !important;">📋 第一步：选择分析类型</strong><br>
        <span style="color: rgba(255, 255, 255, 0.9) !important;">选择手相、面相或八字分析，根据您的需求选择</span>
      </div>
      <div style="margin-bottom: 15px;">
        <strong style="color: rgba(255, 255, 255, 0.95) !important;">📝 第二步：填写信息</strong><br>
        <span style="color: rgba(255, 255, 255, 0.9) !important;">
          • <strong>手相/面相分析</strong>：上传清晰的手相或面相照片<br>
          • <strong>八字分析</strong>：选择出生日期、时间和地点（无需上传图片）
        </span>
      </div>
      <div style="margin-bottom: 15px;">
        <strong style="color: rgba(255, 255, 255, 0.95) !important;">⏱️ 第三步：等待分析</strong><br>
        <span style="color: rgba(255, 255, 255, 0.9) !important;">分析需要30-60秒，请耐心等待</span>
      </div>
      <div style="margin-bottom: 15px;">
        <strong style="color: rgba(255, 255, 255, 0.95) !important;">📊 第四步：查看结果</strong><br>
        <span style="color: rgba(255, 255, 255, 0.9) !important;">查看您的性格分析和运势建议</span>
      </div>
      <div style="margin-bottom: 15px;">
        <strong style="color: rgba(255, 255, 255, 0.95) !important;">🔮 第五步：深度体验</strong><br>
        <span style="color: rgba(255, 255, 255, 0.9) !important;">可以点击"深度解读"获取更详细分析，或与倪大师深度对话</span>
      </div>
    </div>
    
    <div style="display: flex; gap: 12px; justify-content: center;">
      <button id="guideGotIt" style="
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
        color: white !important;
        border: none !important;
        padding: 12px 24px;
        border-radius: 12px;
        font-size: 1rem;
        font-weight: bold;
        cursor: pointer;
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4) !important;
        transition: all 0.3s ease;
        position: relative;
        overflow: hidden;
      " onmouseover="this.style.background='linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)'; this.style.transform='translateY(-2px) scale(1.05)'; this.style.boxShadow='0 6px 20px rgba(102, 126, 234, 0.6)'" onmouseout="this.style.background='linear-gradient(135deg, #667eea 0%, #764ba2 100%)'; this.style.transform='translateY(0) scale(1)'; this.style.boxShadow='0 4px 12px rgba(102, 126, 234, 0.4)'">
        知道了
      </button>
    </div>
  `;

  modal.appendChild(modalContent);

  // ✅ 新增：确保弹窗在页面加载后显示（延迟500ms，确保DOM已渲染）
  timerManager.setTimeout(() => {
    document.body.appendChild(modal);
    // ✅ 新增：添加动画效果
    requestAnimationFrame(() => {
      modal.style.opacity = '1';
    });
  }, 500);

  // ✅ 新增：ESC键关闭弹窗（先定义，后使用）
  const handleEscKey = (e) => {
    if (e.key === 'Escape' && document.getElementById('newUserGuideModal')) {
      const modalElement = document.getElementById('newUserGuideModal');
      if (modalElement) {
        modalElement.remove();
        document.removeEventListener('keydown', handleEscKey);
      }
    }
  };
  document.addEventListener('keydown', handleEscKey);

  // 绑定关闭事件
  const closeModal = () => {
    modal.remove();
    document.removeEventListener('keydown', handleEscKey);
  };

  document.getElementById('guideGotIt').onclick = closeModal;

  // 点击背景关闭
  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      closeModal();
    }
  });
}

// ================= 初始化 =================
/**
 * ✅ 新增：初始化追问输入框的回车键发送功能
 */
function initFollowupInput() {
  const followupInput = document.getElementById('followupInput');
  if (followupInput && !followupInput.dataset.bound) {
    followupInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        // 调用追问函数
        if (typeof window.followupAsk === 'function') {
          window.followupAsk();
        }
      }
    });
    followupInput.dataset.bound = 'true';
    debugLog('✅ 追问输入框回车键事件已绑定');
  }
}

document.addEventListener('DOMContentLoaded', function () {
  debugLog('📱 重构后的submit.js已加载');

  // 🔧 修复：先初始化每日状态，再更新显示
  refreshTodayStatus();  // ✅ 初始化免费次数等数据
  updateCountBanner();   // ✅ 更新次数横幅显示

  // ✅ 新增：显示新手指引（首次访问）
  timerManager.setTimeout(() => {
    showNewUserGuide();
  }, 1000); // 延迟1秒显示，避免与其他初始化冲突

  // 初始化模块化功能
  if (window.personalizationManager) {
    debugLog('✅ 个性化管理器已初始化');
  }

  if (window.soulmateManager) {
    debugLog('✅ 灵魂伴侣管理器已初始化');
  }

  if (window.analyticsManager) {
    debugLog('✅ 分析管理器已初始化');
  }

  // ✅ 修复：页面加载时立即预加载，不延迟（确保文件在用户开始打字前已准备好）
  preloadTypingSound();

  // ✅ 新增：初始化追问输入框的回车键发送功能
  initFollowupInput();

  // ✅ P2-1修复：检查是否已初始化，避免重复初始化
  const imageInput = document.getElementById('imageInput');
  if (imageInput && !imageInput.hasAttribute('data-preview-initialized')) {
    initImagePreview();
    imageInput.setAttribute('data-preview-initialized', 'true');
    debugLog('✅ 图片预览功能已初始化（submit-refactored.js）');
  } else if (imageInput && imageInput.hasAttribute('data-preview-initialized')) {
    debugLog('✅ 图片预览功能已在 index.html 中初始化，跳过重复初始化');
  }

  // 初始化计数横幅
  updateCountBanner();
  debugLog('✅ 计数横幅已初始化');

  // 初始化农历功能
  generateLunarYears();
  debugLog('✅ 农历功能已初始化');

  // 初始化情感日记功能
  debugLog('✅ 情感日记功能已初始化');

  // 初始化生活指导功能
  debugLog('✅ 生活指导功能已初始化');

  // 初始化抽签功能
  debugLog('✅ 抽签功能已初始化');

  // 初始化灵魂伴侣功能
  if (window.soulmateManager) {
    debugLog('✅ 灵魂伴侣功能已初始化');
  }

  // 初始化实时对话功能
  if (window.chatManager) {
    window.chatManager.init();
    createChatToggle();
    debugLog('✅ 实时对话功能已初始化');
  }

  // 初始化灵魂伴侣实时对话功能
  if (window.soulmateManager) {
    debugLog('✅ 灵魂伴侣实时对话功能已初始化');
  }

  // 添加CSS动画样式
  addChatStyles();

  // 初始化智能推荐系统
  timerManager.setTimeout(() => {
    debugLog('🎯 初始化智能推荐系统...');
    renderRecommendations();
  }, 1000);

  // ✅ 新增：初始化快速追问按钮
  timerManager.setTimeout(() => {
    updateQuickBar();
  }, 1000);
});
/**
 * 添加对话相关的CSS样式
 */
function addChatStyles() {
  const style = document.createElement('style');
  style.textContent = `
    @keyframes typing {
      0%, 60%, 100% {
        transform: translateY(0);
        opacity: 0.4;
      }
      30% {
        transform: translateY(-10px);
        opacity: 1;
      }
    }
    
    @keyframes slideInScale {
      from {
        opacity: 0;
        transform: translate(-50%, -50%) scale(0.8);
      }
      to {
        opacity: 1;
        transform: translate(-50%, -50%) scale(1);
      }
    }
    
    @keyframes slideOutScale {
      from {
        opacity: 1;
        transform: translate(-50%, -50%) scale(1);
      }
      to {
        opacity: 0;
        transform: translate(-50%, -50%) scale(0.8);
      }
    }
    
    #chatInterface {
      animation: slideInScale 0.3s ease-out;
    }
    
    #chatToggle:hover {
      transform: scale(1.1) !important;
    }
    
    .chat-message {
      animation: fadeInUp 0.3s ease-out;
    }
    
    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `;
  document.head.appendChild(style);
}

// ================= 昵称显示功能 =================
/**
 * 更新昵称显示
 */
function updateNicknameDisplay() {
  const nickname = localStorage.getItem('user_nickname');
  const welcomeDiv = document.getElementById('personalizedWelcome');
  const welcomeText = document.getElementById('welcomeText');
  const addressSpan = document.getElementById('personalizedAddress');

  if (nickname) {
    // 显示欢迎区域
    if (welcomeDiv) {
      welcomeDiv.classList.remove('hidden');
      welcomeText.textContent = `欢迎回来，${nickname}！`;
    }

    // 显示个性化称呼
    if (addressSpan) {
      addressSpan.textContent = nickname;
    }
  } else {
    // 隐藏欢迎区域
    if (welcomeDiv) {
      welcomeDiv.classList.add('hidden');
    }

    // 清空个性化称呼
    if (addressSpan) {
      addressSpan.textContent = '';
    }
  }
}

// 页面加载时更新昵称显示
document.addEventListener('DOMContentLoaded', updateNicknameDisplay);

// 监听昵称设置事件
document.addEventListener('nicknameSet', function (event) {
  updateNicknameDisplay();
});

// ================= 追问功能 =================
/**
 * 追问功能 - 带上下文和思考中动画
 */
window.followupAsk = async function () {
  const is_member = isVip();

  const input = document.getElementById('followupInput');
  let btn2 = document.getElementById('followupSendBtn') || document.querySelector('button[onclick="window.followupAsk()"]');
  const txt2 = (input?.value || "").trim();
  if (!txt2) {
    alert("请输入追问内容！");
    return;
  }

  if (btn2) {
    btn2.disabled = true;
    btn2.textContent = "发送中...";
  }

  // 使用 appendMessage 显示用户消息
  appendMessage("user", txt2);

  // 保存用户追问到对话历史
  pushTurn('user', txt2, { type: 'followup' });

  if (input) input.value = "";

  // ✅ 新增：更新快速追问按钮（根据对话历史推荐）
  timerManager.setTimeout(() => {
    updateQuickBar();
  }, 500);

  // 追踪分析开始（追问功能）
  debugLog('追踪分析开始（追问功能）...');

  // 获取当前时间偏好
  const currentHour = new Date().getHours();
  let timePreference = 'evening'; // 默认值
  if (6 <= currentHour && currentHour < 12) {
    timePreference = 'morning';
  } else if (12 <= currentHour && currentHour < 18) {
    timePreference = 'afternoon';
  } else if (18 <= currentHour && currentHour < 24) {
    timePreference = 'evening';
  } else {
    timePreference = 'night';
  }

  fetch('/analytics', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'analysis_start',
      session_id: getOrInitSessionId(),
      feature: 'followup_question',
      question_length: txt2.length,
      time_preference: timePreference,
      timestamp: new Date().toISOString()
    })
  }).catch(e => console.error('追问功能分析开始追踪失败:', e));

  // 追踪追问功能使用
  fetch('/analytics', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'followup_question',
      session_id: getOrInitSessionId(),
      question_length: txt2.length,
      timestamp: new Date().toISOString()
    })
  }).catch(e => console.error('追问功能使用追踪失败:', e));

  // 显示思考中动画
  const typingNode = showTypingBubble();

  try {
    // ✅ 修复：获取出生日期信息，用于追问功能
    let birthInfo = "";
    const analysisData = JSON.parse(localStorage.getItem('user_analysis_data') || '{}');
    const baziAnalysis = analysisData.bazi_analysis;
    if (baziAnalysis && baziAnalysis.birthDate) {
      birthInfo = baziAnalysis.birthDate;
      // 如果有时间信息，也加上
      const birthTimeInput = document.getElementById('birthTime');
      const birthPlaceInput = document.getElementById('birthPlace');
      if (birthTimeInput && birthTimeInput.value) {
        birthInfo += ` ${birthTimeInput.value}`;
      }
      if (birthPlaceInput && birthPlaceInput.value) {
        birthInfo += ` ${birthPlaceInput.value}`;
      }
      debugLog('🔍 追问功能传递的出生日期信息:', birthInfo);
    }

    // 发送追问请求到香港服务器
    const d = await sendHK({
      type: "followup",
      text: txt2,
      image: null,
      birth: birthInfo  // ✅ 使用获取的出生日期
    });

    if (d.session_id) setSessionId(d.session_id);

    const fullReply = d.reply || "";
    const raw = fullReply;
    const clean = extractSummarySection(fullReply);
    const pretty = renderMasterReply(fullReply);
    saveRenderCache(raw, clean, pretty);
    pushTurn('assistant', fullReply, { type: 'followup' });

    removeTypingBubble(typingNode);

    // 保存分析数据到灵魂伴侣系统
    // ✅ 修复：等待 soulmateManager 加载完成后再保存
    waitForSoulmateManager((soulmateManager) => {
      if (soulmateManager && typeof soulmateManager.saveAnalysisData === 'function') {
        debugLog('追问功能完成，保存分析数据...');
        ensureLocalAnalysisProfileTargetSelf();
        soulmateManager.saveAnalysisData();
      }
    });

    // 追踪分析完成（追问功能）
    debugLog('追踪分析完成（追问功能）...');

    // 获取当前时间偏好
    const currentHour2 = new Date().getHours();
    let timePreference2 = 'evening'; // 默认值
    if (6 <= currentHour2 && currentHour2 < 12) {
      timePreference2 = 'morning';
    } else if (12 <= currentHour2 && currentHour2 < 18) {
      timePreference2 = 'afternoon';
    } else if (18 <= currentHour2 && currentHour2 < 24) {
      timePreference2 = 'evening';
    } else {
      timePreference2 = 'night';
    }

    fetch('/analytics', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'analysis_complete',
        analysis_type: 'followup',
        gender: document.querySelector('input[name="gender"]:checked')?.value || 'male',
        hand: document.querySelector('input[name="hand"]:checked')?.value || 'left',
        feature: 'followup_question',
        time_preference: timePreference2,
        timestamp: new Date().toISOString()
      })
    }).catch(e => console.error('追问功能分析完成追踪失败:', e));

    // ✅ 修复业务逻辑：次数已在sendHK函数中消耗（请求成功后），此处不需要重复消耗
    if (is_member || getTicketCount() > 0 || getFreeCount() > 0) {
      // 有额度：不遮挡
      appendMessage("assistant", pretty, true, false);
      // 次数已在sendHK中消耗，此处不需要再次消耗
    } else {
      // 无额度：显示分享弹窗
      appendMessage("assistant", pretty, true, true);
      // ✅ 修复：基础分析结果分享不获得免费次数，只解锁内容
      showSharePopup(null, false, null, false); // shouldRewardFreeCount = false
    }
  } catch (e) {
    removeTypingBubble(typingNode);
    console.error('追问功能失败:', e);

    // ✅ P1-3修复：使用友好的错误提示
    if (e.message.includes('免费次数已用完') || e.message.includes('次数消耗失败')) {
      showPaymentModal(e.message);
      appendMessage("assistant", "💡 提示：您可以购买票数或等待明日免费次数继续使用");
    } else {
      // 使用统一的错误处理
      let userMessage;
      if (typeof ErrorHandler !== 'undefined' && ErrorHandler.getUserFriendlyError) {
        const errorInfo = ErrorHandler.getUserFriendlyError(e, 'followup_question_failed', {
          showContact: false,
          action: '请稍后重试'
        });
        userMessage = errorInfo.userMessage.split('\n')[0]; // 只显示第一行
      } else {
        // 降级方案
        if (e.message.includes('timeout') || e.message.includes('abort')) {
          userMessage = "❌ 请求超时，请稍后再试";
        } else if (e.message.includes('Failed to fetch')) {
          userMessage = "❌ 网络连接失败，请检查网络后重试";
        } else {
          userMessage = "❌ 网络错误，请稍后重试";
        }
      }
      appendMessage("assistant", userMessage);
    }
  } finally {
    if (btn2) {
      btn2.disabled = false;
      btn2.textContent = "发送";
    }
    stickBottom();
  }
};

/**
 * 显示思考中动画
 */
function showTypingBubble() {
  const chatBox = document.getElementById('chatBox');
  if (!chatBox) return null;

  const wrapper = document.createElement('div');
  wrapper.classList.add('msg', 'flex', 'justify-start', 'items-start');

  const bubble = document.createElement('div');
  bubble.classList.add('px-3', 'py-2', 'rounded-2xl', 'bubble', 'bubble-assistant', 'typing-bubble');
  bubble.style.fontFamily = "'Noto Serif SC','ZCOOL KuaiLe',serif";

  const tag = document.createElement('div');
  tag.className = "role-tag role-assistant";
  tag.innerHTML = "🧙 倪大师";
  bubble.appendChild(tag);

  const content = document.createElement('div');
  content.innerHTML = `🧙 倪大师正在为您分析<span class="typing-dots"><i></i><i></i><i></i></span>`;
  bubble.appendChild(content);

  wrapper.appendChild(bubble);
  chatBox.appendChild(wrapper);
  stickBottom();
  return wrapper; // 返回节点，后续替换或移除
}

/**
 * 移除思考中动画
 */
function removeTypingBubble(typingNode) {
  if (typingNode && typingNode.parentNode) {
    typingNode.parentNode.removeChild(typingNode);
  }
}

/**
 * 发送请求到香港服务器（带降级方案）
 */
// ❌ 已删除：第一个 sendHK 函数（已合并到第二个函数中，包含重试和超时机制）

// ================= 每日运势抽签功能 =================
window.drawFortune = async function () {
  const key = "daily_fortune_" + new Date().toISOString().split("T")[0];
  if (localStorage.getItem(key) === "used") {
    appendMessage("assistant", "🌙 今日签已抽，请明日再来求签。", true);
    return;
  }

  // 追踪分析开始（每日运势）
  debugLog('追踪分析开始（每日运势）...');
  fetch('/analytics', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'analysis_start',
      session_id: getOrInitSessionId(),
      feature: 'daily_fortune',
      timestamp: new Date().toISOString(),
      user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    })
  }).catch(e => console.error('每日运势分析开始追踪失败:', e));

  // 禁用抽签按钮
  const fortuneBtn = document.getElementById('drawFortuneBtn');
  if (fortuneBtn) {
    fortuneBtn.disabled = true;
    fortuneBtn.textContent = "抽签中...";
  }

  // 在聊天框中显示抽签动画，隐藏用户消息
  const chatBox = document.getElementById('chatBox');
  if (chatBox) {
    const animationWrapper = document.createElement('div');
    animationWrapper.classList.add('msg', 'flex', 'justify-center', 'items-center');
    animationWrapper.style.margin = '10px 0';

    animationWrapper.innerHTML = `
      <div class="fortune-animation">
        <div class="fortune-bamboo-tube">
          <div class="bamboo-tube-body"></div>
          <div class="bamboo-tube-top"></div>
          <div class="fortune-sticks">
            <div class="fortune-stick"></div>
            <div class="fortune-stick"></div>
            <div class="fortune-stick"></div>
            <div class="fortune-stick"></div>
            <div class="fortune-stick"></div>
            <div class="fortune-stick"></div>
            <div class="fortune-stick"></div>
            <div class="fortune-stick"></div>
          </div>
        </div>
        <div class="fortune-text">抽签中...</div>
      </div>
    `;

    chatBox.appendChild(animationWrapper);
    stickBottom();

    // 存储动画元素，用于后续移除
    window.fortuneAnimationElement = animationWrapper;
  }
  const lv = ["上签", "中签", "下签"];
  const idx = Math.floor(Math.random() * 3);
  const prompt = `请以「${lv[idx]}」为主题，生成一段今日运势签文，包含运势提示和建议，风格温和且富有哲理，字数150字左右。`;

  // 设置每日运势支付标志，用于支付成功后的处理
  // 注意：这个标志会在executeFortuneComplete中重置，不影响已使用标记的设置

  const data = await sendHK({
    session_id: null,  // 每日运势抽签不传递session_id，避免使用之前的八字信息
    type: "fortune",
    text: prompt,
    image: null,
    birth: ""
  });

  // GPT请求追踪已由sendHK函数内部处理，无需重复追踪
  if (data.status === 'done') {
    // 移除抽签动画
    if (window.fortuneAnimationElement && window.fortuneAnimationElement.parentNode) {
      window.fortuneAnimationElement.parentNode.removeChild(window.fortuneAnimationElement);
      window.fortuneAnimationElement = null;
    }

    // 恢复按钮
    const fortuneBtn = document.getElementById('drawFortuneBtn');
    if (fortuneBtn) {
      fortuneBtn.disabled = false;
      fortuneBtn.textContent = "🎴 每日运势抽签";
    }

    // 检查是否需要支付
    const is_member = isVip();
    const needsPayment = !is_member && getTicketCount() === 0 && getFreeCount() === 0;

    if (needsPayment) {
      // 需要支付：直接执行每日运势的完整流程，但会显示遮罩
      executeFortuneComplete(lv[idx], data.reply, key);
      return;
    }

    // 不需要支付：直接执行每日运势的完整流程
    executeFortuneComplete(lv[idx], data.reply, key);
  } else {
    // 移除抽签动画
    if (window.fortuneAnimationElement && window.fortuneAnimationElement.parentNode) {
      window.fortuneAnimationElement.parentNode.removeChild(window.fortuneAnimationElement);
      window.fortuneAnimationElement = null;
    }

    // 恢复按钮
    const fortuneBtn = document.getElementById('drawFortuneBtn');
    if (fortuneBtn) {
      fortuneBtn.disabled = false;
      fortuneBtn.textContent = "🎴 每日运势抽签";
    }

    // 重置每日运势支付标志
    window.isFortunePayment = false;

    appendMessage("assistant", "❌ 抽签失败：" + data.message);
  }
};

// ❌ 已删除：第二个 executeFortuneComplete 函数（已统一使用第一个函数，3233行）

function formatDateCN(d = new Date()) {
  // ✅ 修复：统一使用 CodeUtils.formatTime
  if (typeof CodeUtils !== 'undefined' && CodeUtils.formatTime) {
    return CodeUtils.formatTime(d, 'YYYY-MM-DD');
  }
  // 降级方案
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// ================= 灵魂伴侣入口函数 =================
// ✅ 移除 showSoulmateEntry 函数，因为主界面的灵魂伴侣按钮已被移除
// 用户现在通过右下角小人按钮访问灵魂伴侣功能

function showSoulmateIntroModal() {
  // 获取已设置的昵称
  const existingNickname = localStorage.getItem('user_nickname') || '朋友';

  debugLog('🎯 显示灵魂伴侣介绍弹窗，使用昵称:', existingNickname);

  // 创建介绍弹窗
  const modal = document.createElement('div');
  modal.id = 'soulmateIntroModal';
  modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.15);
    backdrop-filter: blur(2px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
  `;

  const modalContent = document.createElement('div');
  modalContent.style.cssText = `
    background: linear-gradient(135deg, #f8e7c7 0%, #f5e4ba 100%);
    border: 2px solid #d4af37;
    border-radius: 20px;
    padding: 30px;
    max-width: 500px;
    width: 90%;
    max-height: 80vh;
    text-align: center;
    box-shadow: 0 8px 32px rgba(112, 79, 27, 0.3);
    animation: modalSlideIn 0.3s ease-out;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
  `;

  modalContent.innerHTML = `
    <div style="flex: 1; overflow-y: auto; margin-bottom: 20px;">
      <div style="font-size: 3rem; margin-bottom: 15px;">💕</div>
      <h3 style="color: #704f1b; font-size: 1.4rem; font-weight: bold; margin-bottom: 15px;">
        专属灵魂伴侣服务
      </h3>
      <div style="color: #8b5e00; font-size: 0.95rem; line-height: 1.6; text-align: left; margin-bottom: 20px;">
        <div style="margin-bottom: 10px;">✨ <strong>个性化陪伴</strong>：基于您的性格分析，提供专属对话体验</div>
        <div style="margin-bottom: 10px;">🎯 <strong>深度互动</strong>：情感支持、生活指导、心理陪伴</div>
        <div style="margin-bottom: 10px;">🔮 <strong>智能分析</strong>：结合相术智慧，提供个性化建议</div>
        <div style="margin-bottom: 10px;">💝 <strong>贴心服务</strong>：24小时在线，随时为您答疑解惑</div>
      </div>
      <div style="background: #e8f5e8; border: 1px solid #28a745; border-radius: 8px; padding: 12px; margin-bottom: 20px;">
        <div style="color: #155724; font-weight: bold; font-size: 1rem;">🎉 限时优惠</div>
        <div style="color: #155724; font-size: 0.9rem;">7天免费试用 · 每日3次深度互动</div>
      </div>
      
      <!-- 个性化问候 -->
      <div style="background: #fff3cd; border: 2px solid #ffc107; border-radius: 12px; padding: 20px; margin-bottom: 20px;">
        <div style="color: #856404; font-weight: bold; font-size: 1.1rem; margin-bottom: 12px;">
          💝 你好，${existingNickname}！
        </div>
        <div style="color: #856404; font-size: 0.9rem; margin-bottom: 15px;">
          作为你的专属灵魂伴侣，我想为你提供最贴心的陪伴服务
        </div>
      </div>
      
      <!-- 建议选项预览 -->
      <div style="background: #f8f9fa; border: 2px solid #dee2e6; border-radius: 12px; padding: 20px; margin-bottom: 20px;">
        <div style="color: #495057; font-weight: bold; font-size: 1rem; margin-bottom: 15px; text-align: center;">
          🌟 我可以为你提供这些服务
        </div>
        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; font-size: 0.85rem;">
          <div style="color: #6c757d; padding: 6px 8px; background: #e9ecef; border-radius: 6px; text-align: center;">📝 情感日记</div>
          <div style="color: #6c757d; padding: 6px 8px; background: #e9ecef; border-radius: 6px; text-align: center;">🔮 生活指导</div>
          <div style="color: #6c757d; padding: 6px 8px; background: #e9ecef; border-radius: 6px; text-align: center;">💝 主动关怀</div>
          <div style="color: #6c757d; padding: 6px 8px; background: #e9ecef; border-radius: 6px; text-align: center;">📱 社交分享</div>
          <div style="color: #6c757d; padding: 6px 8px; background: #e9ecef; border-radius: 6px; text-align: center;">💼 事业运势</div>
          <div style="color: #6c757d; padding: 6px 8px; background: #e9ecef; border-radius: 6px; text-align: center;">💕 感情运势</div>
          <div style="color: #6c757d; padding: 6px 8px; background: #e9ecef; border-radius: 6px; text-align: center;">🌟 综合运势</div>
          <div style="color: #6c757d; padding: 6px 8px; background: #e9ecef; border-radius: 6px; text-align: center;">🧙 自由对话</div>
        </div>
      </div>
    </div>
    
    <div style="flex-shrink: 0; display: flex; gap: 12px; justify-content: center; margin-top: 20px;">
      <!-- ✅ 只保留一个按钮 -->
      <button id="soulmateStartChat" style="
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        border: none;
        padding: 14px 32px;
        border-radius: 12px;
        font-size: 1.1rem;
        font-weight: bold;
        cursor: pointer;
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
        transition: all 0.2s ease;
        min-width: 180px;
      ">
        💬 开始聊天
      </button>
    </div>
  `;

  modal.appendChild(modalContent);
  document.body.appendChild(modal);

  // 添加按钮事件
  // 添加按钮事件 - 直接开始聊天按钮（新增）
  document.getElementById('soulmateStartChat').onclick = function () {
    debugLog('🎯 用户点击直接开始聊天');

    // 关闭介绍弹窗
    document.body.removeChild(modal);

    // ✅ 修复：使用等待机制，确保 soulmateManager 加载完成后再打开聊天界面
    waitForSoulmateManager((soulmateManager) => {
      if (soulmateManager && typeof soulmateManager.openSoulmateChat === 'function') {
        // 确保聊天界面已创建
        if (!document.getElementById('soulmate-chat-interface') && typeof soulmateManager.createSoulmateChatInterface === 'function') {
          soulmateManager.createSoulmateChatInterface();
        }
        // 打开聊天界面
        soulmateManager.openSoulmateChat();
        debugLog('✅ 聊天界面已打开');
      } else {
        console.error('❌ soulmateManager.openSoulmateChat 方法不可用');
        if (typeof CodeUtils !== 'undefined' && CodeUtils.showErrorToast) {
          CodeUtils.showErrorToast('无法打开聊天界面，请刷新页面重试');
        } else {
          alert('无法打开聊天界面，请刷新页面重试');
        }
      }
    }, ['openSoulmateChat', 'createSoulmateChatInterface']); // 检查需要的方法

    // 追踪直接聊天点击
    fetch('/analytics', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'soulmate_direct_chat',
        nickname: existingNickname,
        timestamp: new Date().toISOString()
      })
    }).catch(e => console.error('灵魂伴侣直接聊天追踪失败:', e));
  };

  // ✅ 移除：soulmateStartTrial 和 soulmateCloseIntro 按钮的事件绑定代码（因为已移除按钮）

  // 点击遮罩关闭弹窗
  modal.onclick = function (e) {
    if (e.target === modal) {
      document.body.removeChild(modal);
    }
  };
}
function showPaymentModal(message) {
  // 创建付费引导弹窗
  const modal = document.createElement('div');
  modal.id = 'soulmatePaymentModal';
  modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    backdrop-filter: blur(4px);
  `;

  const modalContent = document.createElement('div');
  modalContent.style.cssText = `
    background: rgba(26, 26, 46, 0.95) !important;
    backdrop-filter: blur(20px) !important;
    -webkit-backdrop-filter: blur(20px) !important;
    border: 1px solid rgba(102, 126, 234, 0.5) !important;
    border-radius: 20px;
    padding: 30px;
    max-width: 450px;
    width: 90%;
    text-align: center;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(102, 126, 234, 0.3) inset, 0 0 40px rgba(102, 126, 234, 0.3) !important;
    animation: modalSlideIn 0.3s ease-out;
    color: white !important;
    position: relative;
  `;

  modalContent.innerHTML = `
    <div style="margin-bottom: 20px;">
      <div style="font-size: 3rem; margin-bottom: 15px;">💎</div>
      <h3 style="color: white !important; font-size: 1.4rem; font-weight: bold; margin-bottom: 15px; text-shadow: 0 2px 10px rgba(102, 126, 234, 0.5) !important;">
        升级为正式用户
      </h3>
      <p style="color: rgba(255, 255, 255, 0.9) !important; font-size: 1rem; line-height: 1.5; margin-bottom: 20px;">
        ${message}
      </p>
      <div style="background: rgba(102, 126, 234, 0.2) !important; backdrop-filter: blur(10px) !important; border: 1px solid rgba(102, 126, 234, 0.4) !important; border-radius: 8px; padding: 15px; margin-bottom: 20px;">
        <div style="color: rgba(255, 255, 255, 0.95) !important; font-weight: bold; font-size: 1.1rem; margin-bottom: 8px;">✨ 正式用户特权</div>
        <div style="color: rgba(255, 255, 255, 0.9) !important; font-size: 0.9rem; text-align: left;">
          <div style="margin-bottom: 5px;">• 无限次灵魂伴侣互动</div>
          <div style="margin-bottom: 5px;">• 专属个性化服务</div>
          <div style="margin-bottom: 5px;">• 优先技术支持</div>
          <div>• 更多高级功能</div>
        </div>
      </div>
      <div style="background: rgba(59, 130, 246, 0.2) !important; backdrop-filter: blur(10px) !important; border: 1px solid rgba(59, 130, 246, 0.4) !important; border-radius: 8px; padding: 12px; margin-bottom: 20px;">
        <div style="color: rgba(255, 255, 255, 0.95) !important; font-weight: bold; font-size: 1.2rem;">首月优惠 5.9元/月</div>
        <div style="color: rgba(255, 255, 255, 0.9) !important; font-size: 0.9rem;">续费 9.9元/月，随时可取消</div>
      </div>
    </div>
    
    <div style="display: flex; gap: 12px; justify-content: center; flex-wrap: wrap;">
      <button id="soulmateStartTrial" style="
        background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
        color: white !important;
        border: none !important;
        padding: 12px 24px;
        border-radius: 12px;
        font-size: 1rem;
        font-weight: bold;
        cursor: pointer;
        box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4) !important;
        transition: all 0.3s ease;
        position: relative;
        overflow: hidden;
        flex: 1;
        min-width: 120px;
      " onmouseover="this.style.background='linear-gradient(135deg, #059669 0%, #047857 100%)'; this.style.transform='translateY(-2px) scale(1.05)'; this.style.boxShadow='0 6px 20px rgba(16, 185, 129, 0.6)'"
      onmouseout="this.style.background='linear-gradient(135deg, #10b981 0%, #059669 100%)'; this.style.transform='translateY(0) scale(1)'; this.style.boxShadow='0 4px 12px rgba(16, 185, 129, 0.4)'">
        🎁 开始7天免费试用
      </button>
      <button id="soulmateUpgrade" style="
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
        color: white !important;
        border: none !important;
        padding: 12px 24px;
        border-radius: 12px;
        font-size: 1rem;
        font-weight: bold;
        cursor: pointer;
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4) !important;
        transition: all 0.3s ease;
        position: relative;
        overflow: hidden;
        flex: 1;
        min-width: 120px;
      " onmouseover="this.style.background='linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)'; this.style.transform='translateY(-2px) scale(1.05)'; this.style.boxShadow='0 6px 20px rgba(102, 126, 234, 0.6)'"
      onmouseout="this.style.background='linear-gradient(135deg, #667eea 0%, #764ba2 100%)'; this.style.transform='translateY(0) scale(1)'; this.style.boxShadow='0 4px 12px rgba(102, 126, 234, 0.4)'">
        💎 立即升级VIP
      </button>
      <button id="soulmateClosePayment" style="
        background: rgba(108, 117, 125, 0.3) !important;
        backdrop-filter: blur(10px) !important;
        border: 1px solid rgba(255, 255, 255, 0.2) !important;
        color: white !important;
        padding: 12px 24px;
        border-radius: 12px;
        font-size: 1rem;
        font-weight: bold;
        cursor: pointer;
        box-shadow: 0 4px 12px rgba(108, 117, 125, 0.3) !important;
        transition: all 0.3s ease;
        flex: 1;
        min-width: 120px;
      " onmouseover="this.style.background='rgba(108, 117, 125, 0.4)'; this.style.transform='translateY(-2px) scale(1.05)'; this.style.boxShadow='0 6px 20px rgba(108, 117, 125, 0.4)'"
      onmouseout="this.style.background='rgba(108, 117, 125, 0.3)'; this.style.transform='translateY(0) scale(1)'; this.style.boxShadow='0 4px 12px rgba(108, 117, 125, 0.3)'">
        稍后再说
      </button>
    </div>
  `;

  modal.appendChild(modalContent);
  document.body.appendChild(modal);

  // 添加按钮事件
  // ✅ 修复：添加"开始试用"按钮
  document.getElementById('soulmateStartTrial').onclick = async function () {
    try {
      // 调用开始试用接口
      if (typeof startSoulmateTrial === 'function') {
        const result = await startSoulmateTrial();
        if (result && result.success) {
          document.body.removeChild(modal);
          if (typeof CodeUtils !== 'undefined' && CodeUtils.showToast) {
            CodeUtils.showToast('✅ 试用已开始！每天3次免费互动，持续7天', 'success', 3000);
          }
          // 刷新灵魂伴侣界面
          if (window.soulmateManager && typeof window.soulmateManager.refresh === 'function') {
            window.soulmateManager.refresh();
          }
        } else {
          if (typeof CodeUtils !== 'undefined' && CodeUtils.showToast) {
            CodeUtils.showToast(result?.error || '开始试用失败，请稍后重试', 'error', 3000);
          }
        }
      } else {
        console.error('startSoulmateTrial 函数不存在');
        if (typeof CodeUtils !== 'undefined' && CodeUtils.showToast) {
          CodeUtils.showToast('开始试用功能不可用，请刷新页面重试', 'error', 3000);
        }
      }
    } catch (error) {
      console.error('开始试用失败:', error);
      if (typeof CodeUtils !== 'undefined' && CodeUtils.showToast) {
        CodeUtils.showToast('开始试用失败，请稍后重试', 'error', 3000);
      }
    }
  };

  document.getElementById('soulmateUpgrade').onclick = async function () {
    document.body.removeChild(modal);

    // ✅ 修复：调用实际的支付功能
    try {
      // 等待 showQRInPopup 函数加载
      const waitForShowQRInPopup = async (maxRetries = 10, delay = 200) => {
        for (let i = 0; i < maxRetries; i++) {
          if (typeof window.showQRInPopup === 'function') {
            return true;
          }
          await new Promise(resolve => setTimeout(resolve, delay));
        }
        return false;
      };

      const isLoaded = await waitForShowQRInPopup();
      if (isLoaded) {
        await window.showQRInPopup('vip');
      } else {
        CodeUtils.showToast('支付功能加载超时，请刷新页面重试', 'error', 3000);
      }
    } catch (error) {
      console.error('打开VIP支付弹窗失败:', error);
      CodeUtils.showToast('打开支付弹窗失败，请稍后再试', 'error', 3000);
    }

    // 追踪付费引导点击
    fetch('/analytics', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'soulmate_payment_guide_click',
        timestamp: new Date().toISOString()
      })
    }).catch(e => console.error('付费引导点击追踪失败:', e));
  };

  document.getElementById('soulmateClosePayment').onclick = function () {
    document.body.removeChild(modal);

    // 追踪付费引导关闭
    fetch('/analytics', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'soulmate_payment_guide_close',
        timestamp: new Date().toISOString()
      })
    }).catch(e => console.error('付费引导关闭追踪失败:', e));
  };

  // 点击遮罩关闭弹窗
  modal.onclick = function (e) {
    if (e.target === modal) {
      document.body.removeChild(modal);
    }
  };
}

function showSoulmateOptionsModal() {
  debugLog('🎯 显示灵魂伴侣建议选择界面');

  // 获取已设置的昵称
  const existingNickname = localStorage.getItem('user_nickname') || '朋友';

  // 创建建议选择弹窗
  const modal = document.createElement('div');
  modal.id = 'soulmateOptionsModal';
  modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.15);
    backdrop-filter: blur(2px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
  `;

  const modalContent = document.createElement('div');
  modalContent.style.cssText = `
    background: linear-gradient(135deg, #f8e7c7 0%, #f5e4ba 100%);
    border: 2px solid #d4af37;
    border-radius: 20px;
    padding: 30px;
    max-width: 600px;
    width: 90%;
    text-align: center;
    box-shadow: 0 8px 32px rgba(112, 79, 27, 0.3);
    animation: modalSlideIn 0.3s ease-out;
    max-height: 80vh;
    overflow-y: auto;
  `;

  modalContent.innerHTML = `
    <div style="margin-bottom: 20px;">
      <div style="font-size: 2.5rem; margin-bottom: 15px;">💝</div>
      <h3 style="color: #704f1b; font-size: 1.4rem; font-weight: bold; margin-bottom: 15px;">
        选择你感兴趣的服务
      </h3>
      <div style="color: #8b5e00; font-size: 0.95rem; margin-bottom: 20px;">
        你好，${existingNickname}！请选择你希望体验的功能，我会为你提供个性化的服务
      </div>
      
      <!-- 建议选项 -->
      <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 12px; margin-bottom: 20px;">
        <button class="soulmate-option-btn" data-option="diary" style="
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          padding: 15px 20px;
          border-radius: 12px;
          font-size: 0.95rem;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: left;
        ">
          <div style="font-size: 1.2rem; margin-bottom: 5px;">📝</div>
          <div style="font-weight: bold; margin-bottom: 3px;">情感日记</div>
          <div style="font-size: 0.8rem; opacity: 0.9;">记录心情，分析情感状态</div>
        </button>
        
        <button class="soulmate-option-btn" data-option="guidance" style="
          background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
          color: white;
          border: none;
          padding: 15px 20px;
          border-radius: 12px;
          font-size: 0.95rem;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: left;
        ">
          <div style="font-size: 1.2rem; margin-bottom: 5px;">🔮</div>
          <div style="font-weight: bold; margin-bottom: 3px;">生活指导</div>
          <div style="font-size: 0.8rem; opacity: 0.9;">基于性格分析提供建议</div>
        </button>
        
        <button class="soulmate-option-btn" data-option="care" style="
          background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
          color: white;
          border: none;
          padding: 15px 20px;
          border-radius: 12px;
          font-size: 0.95rem;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: left;
        ">
          <div style="font-size: 1.2rem; margin-bottom: 5px;">💝</div>
          <div style="font-weight: bold; margin-bottom: 3px;">主动关怀</div>
          <div style="font-size: 0.8rem; opacity: 0.9;">主动关心你的状态</div>
        </button>
        
        <button class="soulmate-option-btn" data-option="social" style="
          background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
          color: white;
          border: none;
          padding: 15px 20px;
          border-radius: 12px;
          font-size: 0.95rem;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: left;
        ">
          <div style="font-size: 1.2rem; margin-bottom: 5px;">📱</div>
          <div style="font-weight: bold; margin-bottom: 3px;">社交分享</div>
          <div style="font-size: 0.8rem; opacity: 0.9;">分析人际关系，提供建议</div>
        </button>
        
        <button class="soulmate-option-btn" data-option="career" style="
          background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
          color: white;
          border: none;
          padding: 15px 20px;
          border-radius: 12px;
          font-size: 0.95rem;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: left;
        ">
          <div style="font-size: 1.2rem; margin-bottom: 5px;">💼</div>
          <div style="font-weight: bold; margin-bottom: 3px;">事业运势</div>
          <div style="font-size: 0.8rem; opacity: 0.9;">结合八字分析事业发展</div>
        </button>
        
        <button class="soulmate-option-btn" data-option="love" style="
          background: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%);
          color: #333;
          border: none;
          padding: 15px 20px;
          border-radius: 12px;
          font-size: 0.95rem;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: left;
        ">
          <div style="font-size: 1.2rem; margin-bottom: 5px;">💕</div>
          <div style="font-weight: bold; margin-bottom: 3px;">感情运势</div>
          <div style="font-size: 0.8rem; opacity: 0.9;">分析感情状况，提供建议</div>
        </button>
        
        <button class="soulmate-option-btn" data-option="fortune" style="
          background: linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%);
          color: #333;
          border: none;
          padding: 15px 20px;
          border-radius: 12px;
          font-size: 0.95rem;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: left;
        ">
          <div style="font-size: 1.2rem; margin-bottom: 5px;">🌟</div>
          <div style="font-weight: bold; margin-bottom: 3px;">综合运势</div>
          <div style="font-size: 0.8rem; opacity: 0.9;">综合分析各方面运势</div>
        </button>
        
        <!-- ✅ 移除自由对话选项，用户通过右下角小人按钮访问灵魂伴侣功能 -->
      </div>
    </div>
    
    <div style="display: flex; gap: 12px; justify-content: center;">
      <button id="soulmateCloseOptions" style="
        background: linear-gradient(135deg, #6c757d 0%, #5a6268 100%);
        color: white;
        border: none;
        padding: 12px 24px;
        border-radius: 12px;
        font-size: 1rem;
        font-weight: bold;
        cursor: pointer;
        box-shadow: 0 4px 12px rgba(108, 117, 125, 0.3);
        transition: all 0.2s ease;
      ">
        稍后再说
      </button>
    </div>
  `;

  modal.appendChild(modalContent);
  document.body.appendChild(modal);

  // 绑定选项按钮事件
  const optionButtons = modalContent.querySelectorAll('.soulmate-option-btn');
  optionButtons.forEach(button => {
    button.addEventListener('click', function () {
      const option = this.getAttribute('data-option');
      debugLog('🎯 用户选择了灵魂伴侣选项:', option);

      // 关闭弹窗
      document.body.removeChild(modal);

      // 处理选择
      handleSoulmateOption(option);
    });

    // 添加悬停效果
    button.addEventListener('mouseenter', function () {
      this.style.transform = 'translateY(-3px)';
      this.style.boxShadow = '0 6px 20px rgba(0,0,0,0.2)';
    });

    button.addEventListener('mouseleave', function () {
      this.style.transform = 'translateY(0)';
      this.style.boxShadow = 'none';
    });
  });

  // 关闭按钮事件
  document.getElementById('soulmateCloseOptions').onclick = function () {
    document.body.removeChild(modal);
  };

  // 点击背景关闭
  modal.onclick = function (e) {
    if (e.target === modal) {
      document.body.removeChild(modal);
    }
  };
}

function startSoulmateTrial() {
  debugLog('🎯 开始灵魂伴侣试用...');

  // ✅ P1-2修复：使用ApiClient，带重试和超时机制
  const sessionId = getOrInitSessionId();

  return (async () => {
    try {
      if (window.apiClient) {
        const data = await window.apiClient.bjRequest('/api/soulmate/trial/start', {
          session_id: sessionId
        });

        if (data.success) {
          // 保存试用数据到本地存储
          localStorage.setItem('soulmate_trial', JSON.stringify(data.trial_data));

          // ✅ 已删除：不再调用 ensureSoulmateIconVisible()，统一使用 soulmate.js 的 createChatToggle()
          // 右下角按钮由 soulmate.js 的 createChatToggle() 创建和管理

          // ✅ 用户已通过选择界面选择灵魂伴侣，无需额外按钮
          // addSoulmateAccessButton(); // 移除，避免功能重复

          debugLog('✅ 灵魂伴侣试用启动成功:', data);
          return data;
        } else {
          throw new Error(data.message || '启动试用失败');
        }
      } else {
        // 降级到直接fetch
        console.warn('⚠️ ApiClient不可用，使用直接fetch调用');
        const response = await fetch('/api/soulmate/trial/start', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_id: sessionId
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success) {
          // 保存试用数据到本地存储
          localStorage.setItem('soulmate_trial', JSON.stringify(data.trial_data));

          // ✅ 已删除：不再调用 ensureSoulmateIconVisible()，统一使用 soulmate.js 的 createChatToggle()
          // 右下角按钮由 soulmate.js 的 createChatToggle() 创建和管理

          // ✅ 用户已通过选择界面选择灵魂伴侣，无需额外按钮
          // addSoulmateAccessButton(); // 移除，避免功能重复

          debugLog('✅ 灵魂伴侣试用启动成功:', data);
          debugLog('🔍 试用数据详情:', {
            has_trial: data.trial_data.has_trial,
            is_active: data.trial_data.is_active,
            daily_interactions: data.trial_data.daily_interactions,
            max_daily_interactions: data.trial_data.max_daily_interactions,
            days_remaining: data.trial_data.days_remaining
          });
          return data.trial_data; // 返回试用数据
        } else {
          throw new Error(data.error || '启动试用失败');
        }
      }
    } catch (error) {
      console.error('❌ 灵魂伴侣试用启动失败:', error);
      throw error;
    }
  })();
}

function showSoulmateTrialOptions(trialData) {
  debugLog('🎯 显示灵魂伴侣试用选项');

  // 显示试用开始提示
  const chatBox = document.getElementById('chatBox');
  if (chatBox) {
    const trialMessage = document.createElement('div');
    trialMessage.style.cssText = `
      text-align: center;
      margin: 20px 0;
      padding: 20px;
      background: linear-gradient(135deg, #e8f5e8 0%, #d4edda 100%);
      border: 2px solid #28a745;
      border-radius: 12px;
      color: #155724;
    `;
    trialMessage.innerHTML = `
      <div style="font-size: 1.2rem; font-weight: bold; margin-bottom: 10px;">
        🎉 灵魂伴侣试用已启动！
      </div>
      <div style="font-size: 1rem; margin-bottom: 8px;">
        每日 ${trialData.max_daily_interactions} 次深度互动
      </div>
      <div style="font-size: 0.9rem; color: #6c757d;">
        试用期至：${new Date(trialData.end_date).toLocaleDateString()}
      </div>
    `;
    chatBox.appendChild(trialMessage);

    // 显示建议选项
    const optionsMessage = document.createElement('div');
    optionsMessage.style.cssText = `
      margin: 20px 0;
      padding: 20px;
      background: linear-gradient(135deg, #f8e7c7 0%, #f5e4ba 100%);
      border: 2px solid #d4af37;
      border-radius: 12px;
      color: #704f1b;
    `;
    optionsMessage.innerHTML = `
      <div style="font-size: 1.1rem; font-weight: bold; margin-bottom: 15px; text-align: center;">
        💝 选择你感兴趣的服务
      </div>
      <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px; margin-top: 15px;">
        <button class="soulmate-option-btn" data-option="diary" style="
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          padding: 12px 16px;
          border-radius: 8px;
          font-size: 0.9rem;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.2s ease;
        ">
          📝 情感日记
        </button>
        <button class="soulmate-option-btn" data-option="guidance" style="
          background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
          color: white;
          border: none;
          padding: 12px 16px;
          border-radius: 8px;
          font-size: 0.9rem;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.2s ease;
        ">
          🔮 生活指导
        </button>
        <button class="soulmate-option-btn" data-option="care" style="
          background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
          color: white;
          border: none;
          padding: 12px 16px;
          border-radius: 8px;
          font-size: 0.9rem;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.2s ease;
        ">
          💝 主动关怀
        </button>
        <button class="soulmate-option-btn" data-option="social" style="
          background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
          color: white;
          border: none;
          padding: 12px 16px;
          border-radius: 8px;
          font-size: 0.9rem;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.2s ease;
        ">
          📱 社交分享
        </button>
        <button class="soulmate-option-btn" data-option="career" style="
          background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
          color: white;
          border: none;
          padding: 12px 16px;
          border-radius: 8px;
          font-size: 0.9rem;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.2s ease;
        ">
          💼 事业运势
        </button>
        <button class="soulmate-option-btn" data-option="love" style="
          background: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%);
          color: #333;
          border: none;
          padding: 12px 16px;
          border-radius: 8px;
          font-size: 0.9rem;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.2s ease;
        ">
          💕 感情运势
        </button>
        <button class="soulmate-option-btn" data-option="fortune" style="
          background: linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%);
          color: #333;
          border: none;
          padding: 12px 16px;
          border-radius: 8px;
          font-size: 0.9rem;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.2s ease;
        ">
          🌟 综合运势
        </button>
        <!-- ✅ 移除自由对话选项，用户通过右下角小人按钮访问灵魂伴侣功能 -->
      </div>
    `;
    chatBox.appendChild(optionsMessage);

    // 绑定选项按钮事件
    const optionButtons = optionsMessage.querySelectorAll('.soulmate-option-btn');
    optionButtons.forEach(button => {
      button.addEventListener('click', function () {
        const option = this.getAttribute('data-option');
        handleSoulmateOption(option);
      });

      // 添加悬停效果
      button.addEventListener('mouseenter', function () {
        this.style.transform = 'translateY(-2px)';
        this.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
      });

      button.addEventListener('mouseleave', function () {
        this.style.transform = 'translateY(0)';
        this.style.boxShadow = 'none';
      });
    });

    stickBottom();
  }
}

function handleSoulmateOption(option) {
  debugLog('🎯 用户选择了灵魂伴侣选项:', option);

  // 检查本地是否有试用数据
  const trialData = localStorage.getItem('soulmate_trial');
  if (trialData) {
    try {
      const trial = JSON.parse(trialData);
      debugLog('✅ 发现已有试用数据，直接执行功能:', trial);

      // 直接执行选择的功能
      executeSoulmateFunction(option);
      return;
    } catch (e) {
      console.warn('⚠️ 试用数据解析失败，重新启动试用:', e);
    }
  }

  // 没有试用数据，启动试用
  debugLog('🎯 没有试用数据，启动试用...');
  startSoulmateTrial().then(() => {
    executeSoulmateFunction(option);
  }).catch(error => {
    console.error('启动试用失败:', error);
    showErrorToast('启动试用失败，请重试');
  });
}

function executeSoulmateFunction(option) {
  // 根据选项启动相应的功能
  switch (option) {
    case 'diary':
      startSoulmateDiary();
      break;
    case 'guidance':
      startSoulmateGuidance();
      break;
    case 'care':
      startSoulmateCare();
      break;
    case 'social':
      startSoulmateSocial();
      break;
    case 'career':
      startSoulmateCareer();
      break;
    case 'love':
      startSoulmateLove();
      break;
    case 'fortune':
      startSoulmateFortune();
      break;
    case 'chat':
      startSoulmateConversation();
      break;
    default:
      debugLog('未知选项:', option);
      startSoulmateConversation();
  }
}

function startSoulmateDiary() {
  debugLog('📝 启动情感日记功能');
  showSuccessToast('正在启动情感日记功能...');

  // 显示日记功能介绍
  const chatBox = document.getElementById('chatBox');
  if (chatBox) {
    const diaryMessage = document.createElement('div');
    diaryMessage.style.cssText = `
      text-align: left;
      margin: 10px 0;
      padding: 15px 20px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      border-radius: 12px;
      max-width: 80%;
    `;
    diaryMessage.innerHTML = `
      <div style="font-weight: bold; margin-bottom: 8px;">📝 情感日记</div>
      <div style="font-size: 0.9rem; margin-bottom: 10px;">记录你的心情，我会为你分析情感状态，提供心理支持</div>
      <div style="font-size: 0.8rem; opacity: 0.9;">你可以分享今天的感受，我会倾听并给予建议</div>
    `;
    chatBox.appendChild(diaryMessage);

    // 启动对话
    timerManager.setTimeout(() => {
      startSoulmateConversation();
    }, 1500);

    stickBottom();
  }
}

function startSoulmateGuidance() {
  debugLog('🔮 启动生活指导功能');
  showSuccessToast('正在启动生活指导功能...');

  // ✅ 修复：创建独立的生活指导界面
  createSoulmateGuidanceInterface();
}
function createSoulmateGuidanceInterface() {
  // 创建生活指导弹窗界面
  const modal = document.createElement('div');
  modal.id = 'soulmateGuidanceModal';
  modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    backdrop-filter: blur(4px);
  `;

  const modalContent = document.createElement('div');
  modalContent.style.cssText = `
    background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
    border-radius: 20px;
    padding: 30px;
    max-width: 600px;
    width: 90%;
    text-align: center;
    color: white;
    box-shadow: 0 8px 32px rgba(240, 147, 251, 0.3);
  `;

  modalContent.innerHTML = `
    <div style="font-size: 3rem; margin-bottom: 20px;">🔮</div>
    <h2 style="margin-bottom: 20px; font-size: 1.5rem;">生活指导</h2>
    <p style="margin-bottom: 30px; line-height: 1.6;">
      基于你的性格分析，为你提供生活建议和决策支持。<br>
      我可以帮你分析问题，提供专业的建议。
    </p>
    
    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px; margin-bottom: 30px;">
      <button class="guidance-option" data-type="work" style="
        background: rgba(255, 255, 255, 0.2);
        border: 1px solid rgba(255, 255, 255, 0.3);
        color: white;
        padding: 15px;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.3s ease;
      ">
        💼 工作问题
      </button>
      <button class="guidance-option" data-type="relationship" style="
        background: rgba(255, 255, 255, 0.2);
        border: 1px solid rgba(255, 255, 255, 0.3);
        color: white;
        padding: 15px;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.3s ease;
      ">
        💕 人际关系
      </button>
      <button class="guidance-option" data-type="health" style="
        background: rgba(255, 255, 255, 0.2);
        border: 1px solid rgba(255, 255, 255, 0.3);
        color: white;
        padding: 15px;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.3s ease;
      ">
        🌿 健康生活
      </button>
      <button class="guidance-option" data-type="decision" style="
        background: rgba(255, 255, 255, 0.2);
        border: 1px solid rgba(255, 255, 255, 0.3);
        color: white;
        padding: 15px;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.3s ease;
      ">
        🤔 决策支持
      </button>
    </div>
    
    <div style="display: flex; gap: 15px; justify-content: center;">
      <button id="closeGuidance" style="
        background: rgba(255, 255, 255, 0.2);
        border: 1px solid rgba(255, 255, 255, 0.3);
        color: white;
        padding: 12px 24px;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.3s ease;
      ">
        关闭
      </button>
    </div>
  `;

  modal.appendChild(modalContent);
  document.body.appendChild(modal);

  // 绑定事件
  modal.querySelectorAll('.guidance-option').forEach(option => {
    option.addEventListener('click', function () {
      const type = this.getAttribute('data-type');
      debugLog('用户选择指导类型:', type);

      // 关闭当前弹窗
      document.body.removeChild(modal);

      // 启动具体的指导功能
      startSpecificGuidance(type);
    });

    option.addEventListener('mouseenter', function () {
      this.style.background = 'rgba(255, 255, 255, 0.3)';
      this.style.transform = 'scale(1.05)';
    });

    option.addEventListener('mouseleave', function () {
      this.style.background = 'rgba(255, 255, 255, 0.2)';
      this.style.transform = 'scale(1)';
    });
  });

  // 关闭按钮
  modal.querySelector('#closeGuidance').addEventListener('click', () => {
    document.body.removeChild(modal);
  });

  // 点击背景关闭
  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      document.body.removeChild(modal);
    }
  });
}

function startSoulmateCare() {
  debugLog('💝 启动主动关怀功能');
  showSuccessToast('正在启动主动关怀功能...');

  // ✅ 修复：创建独立的主动关怀界面
  createSoulmateCareInterface();
}

function createSoulmateCareInterface() {
  // 创建主动关怀弹窗界面
  const modal = document.createElement('div');
  modal.id = 'soulmateCareModal';
  modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    backdrop-filter: blur(4px);
  `;

  const modalContent = document.createElement('div');
  modalContent.style.cssText = `
    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
    border-radius: 20px;
    padding: 30px;
    max-width: 600px;
    width: 90%;
    text-align: center;
    color: white;
    box-shadow: 0 8px 32px rgba(79, 172, 254, 0.3);
  `;

  modalContent.innerHTML = `
    <div style="font-size: 3rem; margin-bottom: 20px;">💝</div>
    <h2 style="margin-bottom: 20px; font-size: 1.5rem;">主动关怀</h2>
    <p style="margin-bottom: 30px; line-height: 1.6;">
      我会主动关心你的状态，提供贴心的陪伴服务。<br>
      让我来关心你，为你提供温暖的支持。
    </p>
    
    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px; margin-bottom: 30px;">
      <button class="care-option" data-type="daily" style="
        background: rgba(255, 255, 255, 0.2);
        border: 1px solid rgba(255, 255, 255, 0.3);
        color: white;
        padding: 15px;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.3s ease;
      ">
        🌅 日常关怀
      </button>
      <button class="care-option" data-type="mood" style="
        background: rgba(255, 255, 255, 0.2);
        border: 1px solid rgba(255, 255, 255, 0.3);
        color: white;
        padding: 15px;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.3s ease;
      ">
        😊 心情陪伴
      </button>
      <button class="care-option" data-type="stress" style="
        background: rgba(255, 255, 255, 0.2);
        border: 1px solid rgba(255, 255, 255, 0.3);
        color: white;
        padding: 15px;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.3s ease;
      ">
        🧘 压力缓解
      </button>
      <button class="care-option" data-type="motivation" style="
        background: rgba(255, 255, 255, 0.2);
        border: 1px solid rgba(255, 255, 255, 0.3);
        color: white;
        padding: 15px;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.3s ease;
      ">
        💪 激励鼓励
      </button>
    </div>
    
    <div style="display: flex; gap: 15px; justify-content: center;">
      <button id="closeCare" style="
        background: rgba(255, 255, 255, 0.2);
        border: 1px solid rgba(255, 255, 255, 0.3);
        color: white;
        padding: 12px 24px;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.3s ease;
      ">
        关闭
      </button>
    </div>
  `;

  modal.appendChild(modalContent);
  document.body.appendChild(modal);

  // 绑定事件
  modal.querySelectorAll('.care-option').forEach(option => {
    option.addEventListener('click', function () {
      const type = this.getAttribute('data-type');
      debugLog('用户选择关怀类型:', type);

      // 关闭当前弹窗
      document.body.removeChild(modal);

      // 启动具体的关怀服务功能
      startSpecificCare(type);
    });

    option.addEventListener('mouseenter', function () {
      this.style.background = 'rgba(255, 255, 255, 0.3)';
      this.style.transform = 'scale(1.05)';
    });

    option.addEventListener('mouseleave', function () {
      this.style.background = 'rgba(255, 255, 255, 0.2)';
      this.style.transform = 'scale(1)';
    });
  });

  // 关闭按钮
  modal.querySelector('#closeCare').addEventListener('click', () => {
    document.body.removeChild(modal);
  });

  // 点击背景关闭
  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      document.body.removeChild(modal);
    }
  });
}

function startSpecificCare(type) {
  const careTypes = {
    'daily': '日常关怀',
    'mood': '心情陪伴',
    'stress': '压力缓解',
    'motivation': '激励鼓励'
  };

  const typeName = careTypes[type] || '主动关怀';

  // 在聊天界面中启动具体的关怀服务对话
  const chatBox = document.getElementById('chatBox');
  if (chatBox) {
    // 添加用户选择的消息
    appendMessage("user", `我想了解关于${typeName}的服务`);

    // 启动AI分析
    timerManager.setTimeout(() => {
      appendMessage("assistant", `好的，我来为你提供${typeName}相关的服务。请告诉我你具体需要什么帮助？`);
    }, 1000);
  }
}

function startSoulmateSocial() {
  debugLog('📱 启动社交分享功能');
  showSuccessToast('正在启动社交分享功能...');

  // ✅ 修复：创建独立的社交分享界面
  createSoulmateSocialInterface();
}

function createSoulmateSocialInterface() {
  // 创建社交分享弹窗界面
  const modal = document.createElement('div');
  modal.id = 'soulmateSocialModal';
  modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    backdrop-filter: blur(4px);
  `;

  const modalContent = document.createElement('div');
  modalContent.style.cssText = `
    background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
    border-radius: 20px;
    padding: 30px;
    max-width: 600px;
    width: 90%;
    text-align: center;
    color: white;
    box-shadow: 0 8px 32px rgba(67, 233, 123, 0.3);
  `;

  modalContent.innerHTML = `
    <div style="font-size: 3rem; margin-bottom: 20px;">📱</div>
    <h2 style="margin-bottom: 20px; font-size: 1.5rem;">社交分享</h2>
    <p style="margin-bottom: 30px; line-height: 1.6;">
      帮你分析人际关系，提供社交建议和沟通技巧。<br>
      让我帮你改善人际关系，提升社交能力。
    </p>
    
    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px; margin-bottom: 30px;">
      <button class="social-option" data-type="communication" style="
        background: rgba(255, 255, 255, 0.2);
        border: 1px solid rgba(255, 255, 255, 0.3);
        color: white;
        padding: 15px;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.3s ease;
      ">
        💬 沟通技巧
      </button>
      <button class="social-option" data-type="networking" style="
        background: rgba(255, 255, 255, 0.2);
        border: 1px solid rgba(255, 255, 255, 0.3);
        color: white;
        padding: 15px;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.3s ease;
      ">
        🤝 人脉建立
      </button>
      <button class="social-option" data-type="conflict" style="
        background: rgba(255, 255, 255, 0.2);
        border: 1px solid rgba(255, 255, 255, 0.3);
        color: white;
        padding: 15px;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.3s ease;
      ">
        ⚖️ 冲突处理
      </button>
      <button class="social-option" data-type="confidence" style="
        background: rgba(255, 255, 255, 0.2);
        border: 1px solid rgba(255, 255, 255, 0.3);
        color: white;
        padding: 15px;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.3s ease;
      ">
        💪 自信提升
      </button>
    </div>
    
    <div style="display: flex; gap: 15px; justify-content: center;">
      <button id="closeSocial" style="
        background: rgba(255, 255, 255, 0.2);
        border: 1px solid rgba(255, 255, 255, 0.3);
        color: white;
        padding: 12px 24px;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.3s ease;
      ">
        关闭
      </button>
    </div>
  `;

  modal.appendChild(modalContent);
  document.body.appendChild(modal);

  // 绑定事件
  modal.querySelectorAll('.social-option').forEach(option => {
    option.addEventListener('click', function () {
      const type = this.getAttribute('data-type');
      debugLog('用户选择社交类型:', type);

      // 关闭当前弹窗
      document.body.removeChild(modal);

      // 启动具体的社交指导功能
      startSpecificSocial(type);
    });

    option.addEventListener('mouseenter', function () {
      this.style.background = 'rgba(255, 255, 255, 0.3)';
      this.style.transform = 'scale(1.05)';
    });

    option.addEventListener('mouseleave', function () {
      this.style.background = 'rgba(255, 255, 255, 0.2)';
      this.style.transform = 'scale(1)';
    });
  });

  // 关闭按钮
  modal.querySelector('#closeSocial').addEventListener('click', () => {
    document.body.removeChild(modal);
  });

  // 点击背景关闭
  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      document.body.removeChild(modal);
    }
  });
}

function startSpecificGuidance(type) {
  const guidanceTypes = {
    'work': '工作问题',
    'relationship': '人际关系',
    'health': '健康生活',
    'decision': '决策支持'
  };

  const typeName = guidanceTypes[type] || '生活指导';

  // 在聊天界面中启动具体的指导对话
  const chatBox = document.getElementById('chatBox');
  if (chatBox) {
    // 添加用户选择的消息
    appendMessage("user", `我想了解关于${typeName}的指导`);

    // 启动AI分析
    timerManager.setTimeout(() => {
      appendMessage("assistant", `好的，我来为你分析${typeName}相关的问题。请告诉我你具体想了解什么？`);
    }, 1000);
  }
}

function startSpecificSocial(type) {
  const socialTypes = {
    'communication': '沟通技巧',
    'networking': '人脉建立',
    'conflict': '冲突处理',
    'confidence': '自信提升'
  };

  const typeName = socialTypes[type] || '社交分享';

  // 在聊天界面中启动具体的社交指导对话
  const chatBox = document.getElementById('chatBox');
  if (chatBox) {
    // 添加用户选择的消息
    appendMessage("user", `我想了解关于${typeName}的指导`);

    // 启动AI分析
    timerManager.setTimeout(() => {
      appendMessage("assistant", `好的，我来为你分析${typeName}相关的问题。请告诉我你具体想了解什么？`);
    }, 1000);
  }
}

function startSoulmateCareer() {
  debugLog('💼 启动事业运势功能');
  showSuccessToast('正在启动事业运势功能...');

  // ✅ 修复：创建独立的事业运势界面
  createSoulmateCareerInterface();
}

function createSoulmateCareerInterface() {
  // 创建事业运势弹窗界面
  const modal = document.createElement('div');
  modal.id = 'soulmateCareerModal';
  modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    backdrop-filter: blur(4px);
  `;

  const modalContent = document.createElement('div');
  modalContent.style.cssText = `
    background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
    border-radius: 20px;
    padding: 30px;
    max-width: 600px;
    width: 90%;
    text-align: center;
    color: white;
    box-shadow: 0 8px 32px rgba(250, 112, 154, 0.3);
  `;

  modalContent.innerHTML = `
    <div style="font-size: 3rem; margin-bottom: 20px;">💼</div>
    <h2 style="margin-bottom: 20px; font-size: 1.5rem;">事业运势</h2>
    <p style="margin-bottom: 30px; line-height: 1.6;">
      结合你的八字分析，为你的事业发展提供指导。<br>
      让我为你的事业发展提供专业的建议。
    </p>
    
    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px; margin-bottom: 30px;">
      <button class="career-option" data-type="promotion" style="
        background: rgba(255, 255, 255, 0.2);
        border: 1px solid rgba(255, 255, 255, 0.3);
        color: white;
        padding: 15px;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.3s ease;
      ">
        📈 升职加薪
      </button>
      <button class="career-option" data-type="job-change" style="
        background: rgba(255, 255, 255, 0.2);
        border: 1px solid rgba(255, 255, 255, 0.3);
        color: white;
        padding: 15px;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.3s ease;
      ">
        🔄 跳槽转行
      </button>
      <button class="career-option" data-type="entrepreneur" style="
        background: rgba(255, 255, 255, 0.2);
        border: 1px solid rgba(255, 255, 255, 0.3);
        color: white;
        padding: 15px;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.3s ease;
      ">
        🚀 创业投资
      </button>
      <button class="career-option" data-type="skills" style="
        background: rgba(255, 255, 255, 0.2);
        border: 1px solid rgba(255, 255, 255, 0.3);
        color: white;
        padding: 15px;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.3s ease;
      ">
        🎯 技能提升
      </button>
    </div>
    
    <div style="display: flex; gap: 15px; justify-content: center;">
      <button id="closeCareer" style="
        background: rgba(255, 255, 255, 0.2);
        border: 1px solid rgba(255, 255, 255, 0.3);
        color: white;
        padding: 12px 24px;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.3s ease;
      ">
        关闭
      </button>
    </div>
  `;

  modal.appendChild(modalContent);
  document.body.appendChild(modal);

  // 绑定事件
  modal.querySelectorAll('.career-option').forEach(option => {
    option.addEventListener('click', function () {
      const type = this.getAttribute('data-type');
      debugLog('用户选择事业类型:', type);

      // 关闭当前弹窗
      document.body.removeChild(modal);

      // 启动具体的事业指导功能
      startSpecificCareer(type);
    });

    option.addEventListener('mouseenter', function () {
      this.style.background = 'rgba(255, 255, 255, 0.3)';
      this.style.transform = 'scale(1.05)';
    });

    option.addEventListener('mouseleave', function () {
      this.style.background = 'rgba(255, 255, 255, 0.2)';
      this.style.transform = 'scale(1)';
    });
  });

  // 关闭按钮
  modal.querySelector('#closeCareer').addEventListener('click', () => {
    document.body.removeChild(modal);
  });

  // 点击背景关闭
  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      document.body.removeChild(modal);
    }
  });
}

function startSpecificCareer(type) {
  const careerTypes = {
    'promotion': '升职加薪',
    'job-change': '跳槽转行',
    'entrepreneur': '创业投资',
    'skills': '技能提升'
  };

  const typeName = careerTypes[type] || '事业运势';

  // 在聊天界面中启动具体的事业指导对话
  const chatBox = document.getElementById('chatBox');
  if (chatBox) {
    // 添加用户选择的消息
    appendMessage("user", `我想了解关于${typeName}的指导`);

    // 启动AI分析
    timerManager.setTimeout(() => {
      appendMessage("assistant", `好的，我来为你分析${typeName}相关的问题。请告诉我你具体想了解什么？`);
    }, 1000);
  }
}

function startSoulmateLove() {
  debugLog('💕 启动感情运势功能');
  showSuccessToast('正在启动感情运势功能...');

  // ✅ 修复：创建独立的感情运势界面
  createSoulmateLoveInterface();
}
function createSoulmateLoveInterface() {
  // 创建感情运势弹窗界面
  const modal = document.createElement('div');
  modal.id = 'soulmateLoveModal';
  modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    backdrop-filter: blur(4px);
  `;

  const modalContent = document.createElement('div');
  modalContent.style.cssText = `
    background: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%);
    border-radius: 20px;
    padding: 30px;
    max-width: 600px;
    width: 90%;
    text-align: center;
    color: #333;
    box-shadow: 0 8px 32px rgba(168, 237, 234, 0.3);
  `;

  modalContent.innerHTML = `
    <div style="font-size: 3rem; margin-bottom: 20px;">💕</div>
    <h2 style="margin-bottom: 20px; font-size: 1.5rem;">感情运势</h2>
    <p style="margin-bottom: 30px; line-height: 1.6;">
      分析你的感情状况，为你的感情生活提供建议。<br>
      让我为你的感情生活提供专业的指导。
    </p>
    
    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px; margin-bottom: 30px;">
      <button class="love-option" data-type="single" style="
        background: rgba(255, 255, 255, 0.3);
        border: 1px solid rgba(255, 255, 255, 0.5);
        color: #333;
        padding: 15px;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.3s ease;
      ">
        💔 单身求缘
      </button>
      <button class="love-option" data-type="relationship" style="
        background: rgba(255, 255, 255, 0.3);
        border: 1px solid rgba(255, 255, 255, 0.5);
        color: #333;
        padding: 15px;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.3s ease;
      ">
        💑 恋爱关系
      </button>
      <button class="love-option" data-type="marriage" style="
        background: rgba(255, 255, 255, 0.3);
        border: 1px solid rgba(255, 255, 255, 0.5);
        color: #333;
        padding: 15px;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.3s ease;
      ">
        💒 婚姻家庭
      </button>
      <button class="love-option" data-type="breakup" style="
        background: rgba(255, 255, 255, 0.3);
        border: 1px solid rgba(255, 255, 255, 0.5);
        color: #333;
        padding: 15px;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.3s ease;
      ">
        💔 分手复合
      </button>
    </div>
    
    <div style="display: flex; gap: 15px; justify-content: center;">
      <button id="closeLove" style="
        background: rgba(255, 255, 255, 0.3);
        border: 1px solid rgba(255, 255, 255, 0.5);
        color: #333;
        padding: 12px 24px;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.3s ease;
      ">
        关闭
      </button>
    </div>
  `;

  modal.appendChild(modalContent);
  document.body.appendChild(modal);

  // 绑定事件
  modal.querySelectorAll('.love-option').forEach(option => {
    option.addEventListener('click', function () {
      const type = this.getAttribute('data-type');
      debugLog('用户选择感情类型:', type);

      // 关闭当前弹窗
      document.body.removeChild(modal);

      // 启动具体的感情指导功能
      startSpecificLove(type);
    });

    option.addEventListener('mouseenter', function () {
      this.style.background = 'rgba(255, 255, 255, 0.5)';
      this.style.transform = 'scale(1.05)';
    });

    option.addEventListener('mouseleave', function () {
      this.style.background = 'rgba(255, 255, 255, 0.3)';
      this.style.transform = 'scale(1)';
    });
  });

  // 关闭按钮
  modal.querySelector('#closeLove').addEventListener('click', () => {
    document.body.removeChild(modal);
  });

  // 点击背景关闭
  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      document.body.removeChild(modal);
    }
  });
}

function startSpecificLove(type) {
  const loveTypes = {
    'single': '单身求缘',
    'relationship': '恋爱关系',
    'marriage': '婚姻家庭',
    'breakup': '分手复合'
  };

  const typeName = loveTypes[type] || '感情运势';

  // 在聊天界面中启动具体的感情指导对话
  const chatBox = document.getElementById('chatBox');
  if (chatBox) {
    // 添加用户选择的消息
    appendMessage("user", `我想了解关于${typeName}的指导`);

    // 启动AI分析
    timerManager.setTimeout(() => {
      appendMessage("assistant", `好的，我来为你分析${typeName}相关的问题。请告诉我你具体想了解什么？`);
    }, 1000);
  }
}

function startSoulmateFortune() {
  debugLog('🌟 启动综合运势功能');
  showSuccessToast('正在启动综合运势功能...');

  // ✅ 修复：创建独立的综合运势界面
  createSoulmateFortuneInterface();
}

function createSoulmateFortuneInterface() {
  // 创建综合运势弹窗界面
  const modal = document.createElement('div');
  modal.id = 'soulmateFortuneModal';
  modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    backdrop-filter: blur(4px);
  `;

  const modalContent = document.createElement('div');
  modalContent.style.cssText = `
    background: linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%);
    border-radius: 20px;
    padding: 30px;
    max-width: 600px;
    width: 90%;
    text-align: center;
    color: #333;
    box-shadow: 0 8px 32px rgba(255, 236, 210, 0.3);
  `;

  modalContent.innerHTML = `
    <div style="font-size: 3rem; margin-bottom: 20px;">🌟</div>
    <h2 style="margin-bottom: 20px; font-size: 1.5rem;">综合运势</h2>
    <p style="margin-bottom: 30px; line-height: 1.6;">
      综合分析你的各方面运势，提供全面的生活指导。<br>
      让我为你提供全方位的运势分析。
    </p>
    
    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px; margin-bottom: 30px;">
      <button class="fortune-option" data-type="overall" style="
        background: rgba(255, 255, 255, 0.3);
        border: 1px solid rgba(255, 255, 255, 0.5);
        color: #333;
        padding: 15px;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.3s ease;
      ">
        🌟 整体运势
      </button>
      <button class="fortune-option" data-type="monthly" style="
        background: rgba(255, 255, 255, 0.3);
        border: 1px solid rgba(255, 255, 255, 0.5);
        color: #333;
        padding: 15px;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.3s ease;
      ">
        📅 月度运势
      </button>
      <button class="fortune-option" data-type="yearly" style="
        background: rgba(255, 255, 255, 0.3);
        border: 1px solid rgba(255, 255, 255, 0.5);
        color: #333;
        padding: 15px;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.3s ease;
      ">
        📆 年度运势
      </button>
      <button class="fortune-option" data-type="lucky" style="
        background: rgba(255, 255, 255, 0.3);
        border: 1px solid rgba(255, 255, 255, 0.5);
        color: #333;
        padding: 15px;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.3s ease;
      ">
        🍀 幸运元素
      </button>
    </div>
    
    <div style="display: flex; gap: 15px; justify-content: center;">
      <button id="closeFortune" style="
        background: rgba(255, 255, 255, 0.3);
        border: 1px solid rgba(255, 255, 255, 0.5);
        color: #333;
        padding: 12px 24px;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.3s ease;
      ">
        关闭
      </button>
    </div>
  `;

  modal.appendChild(modalContent);
  document.body.appendChild(modal);

  // 绑定事件
  modal.querySelectorAll('.fortune-option').forEach(option => {
    option.addEventListener('click', function () {
      const type = this.getAttribute('data-type');
      debugLog('用户选择运势类型:', type);

      // 关闭当前弹窗
      document.body.removeChild(modal);

      // 启动具体的运势分析功能
      startSpecificFortune(type);
    });

    option.addEventListener('mouseenter', function () {
      this.style.background = 'rgba(255, 255, 255, 0.5)';
      this.style.transform = 'scale(1.05)';
    });

    option.addEventListener('mouseleave', function () {
      this.style.background = 'rgba(255, 255, 255, 0.3)';
      this.style.transform = 'scale(1)';
    });
  });

  // 关闭按钮
  modal.querySelector('#closeFortune').addEventListener('click', () => {
    document.body.removeChild(modal);
  });

  // 点击背景关闭
  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      document.body.removeChild(modal);
    }
  });
}

function startSpecificFortune(type) {
  const fortuneTypes = {
    'overall': '整体运势',
    'monthly': '月度运势',
    'yearly': '年度运势',
    'lucky': '幸运元素'
  };

  const typeName = fortuneTypes[type] || '综合运势';

  // 在聊天界面中启动具体的运势分析对话
  const chatBox = document.getElementById('chatBox');
  if (chatBox) {
    // 添加用户选择的消息
    appendMessage("user", `我想了解关于${typeName}的分析`);

    // 启动AI分析
    timerManager.setTimeout(() => {
      appendMessage("assistant", `好的，我来为你分析${typeName}相关的问题。请告诉我你具体想了解什么？`);
    }, 1000);
  }
}

function startSoulmateConversation() {
  debugLog('🎯 启动灵魂伴侣对话...');

  // 隐藏主应用的聊天按钮
  const mainChatToggle = document.getElementById('chatToggle');
  if (mainChatToggle) {
    mainChatToggle.style.display = 'none';
    debugLog('✅ 已隐藏主应用聊天按钮');
  }

  // 隐藏灵魂伴侣的聊天按钮（如果存在）
  const soulmateChatToggle = document.getElementById('soulmate-chat-toggle');
  if (soulmateChatToggle) {
    soulmateChatToggle.style.display = 'none';
    debugLog('✅ 已隐藏右下角聊天按钮');
  }

  // 切换到灵魂伴侣模式
  const chatBox = document.getElementById('chatBox');
  if (chatBox) {
    chatBox.classList.add('soulmate-mode');

    // 添加模式切换按钮（如果不存在）
    if (!document.getElementById('soulmateModeToggle')) {
      addSoulmateModeToggle();
    }

    // ✅ 修复：显示功能选择界面，而不是直接对话
    showSoulmateOptionsModal();

    showSuccessToast('💕 灵魂伴侣模式已启动！');
  }
}

// ✅ 重构：移除重复的灵魂伴侣访问按钮，用户通过选择界面访问功能
// addSoulmateAccessButton() 函数已删除，避免功能重复

// 添加灵魂伴侣模式切换按钮
function addSoulmateModeToggle() {
  const chatBox = document.getElementById('chatBox');
  const toggle = document.createElement('div');
  toggle.id = 'soulmateModeToggle';
  toggle.style.cssText = `
    padding: 10px;
    text-align: center;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-radius: 8px 8px 0 0;
    margin-bottom: 10px;
  `;
  toggle.innerHTML = `
    <button id="switchToNormal" class="mode-btn" style="
      background: rgba(255,255,255,0.2);
      color: white;
      border: 1px solid rgba(255,255,255,0.3);
      padding: 6px 12px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
    ">
      🧙 切换到普通模式
    </button>
  `;
  chatBox.insertBefore(toggle, chatBox.firstChild);

  // 绑定切换事件
  document.getElementById('switchToNormal').onclick = function () {
    chatBox.classList.remove('soulmate-mode');
    toggle.remove();

    // 重新显示主应用聊天按钮
    const mainChatToggle = document.getElementById('chatToggle');
    if (mainChatToggle) {
      mainChatToggle.style.display = 'block';
      debugLog('✅ 已重新显示主应用聊天按钮');
    }

    // 重新显示右下角聊天按钮
    const chatToggle = document.getElementById('soulmate-chat-toggle');
    if (chatToggle) {
      chatToggle.style.display = 'block';
      debugLog('✅ 已重新显示右下角聊天按钮');
    }

    showSuccessToast('已切换到普通模式');
  };
}

// ✅ 已删除：ensureSoulmateIconVisible() 函数
// 原因：与 soulmate.js 的 createChatToggle() 功能重复，会导致创建两个右下角按钮
// 统一使用 soulmate.js 的 createChatToggle() 作为唯一的右下角按钮创建方式

// ================= 日志函数 =================
function testLog(step, action, result, data = null) {
  const timestamp = new Date().toLocaleTimeString();
  debugLog(`🧪 [${timestamp}] [${step}] ${action}: ${result}`);
  if (data) {
    debugLog(`📊 数据:`, data);
  }
}

// 个性化称呼系统日志控制
window.personalizedLoggingEnabled = true;

// 个性化称呼系统日志输出函数
function personalLog(step, action, result, data = null) {
  if (!window.personalizedLoggingEnabled) return;
  testLog(step, action, result, data);
}

// ================= 分享功能 =================

/**
 * 获取分享次数
 */
// ✅ 已删除：getShareCount() 和 setShareCount() 已在文件前部定义（约第486-510行）

/**
 * 获取最新助手消息文本
 */
function getLatestAssistantText() {
  // 优先：从 #chatBox .flex.justify-start 最后一条的 data-reply 属性获取
  const assistantMessages = document.querySelectorAll('#chatBox .flex.justify-start[data-reply]');
  if (assistantMessages.length > 0) {
    const lastMessage = assistantMessages[assistantMessages.length - 1];
    const dataReply = lastMessage.getAttribute('data-reply');
    if (dataReply && dataReply.trim()) {
      return dataReply.trim();
    }
  }

  // 备用：从最后一条助手消息的文本内容获取
  const lastAssistantMessage = document.querySelector('#chatBox .flex.justify-start:last-child');
  if (lastAssistantMessage) {
    const bubble = lastAssistantMessage.querySelector('.bubble');
    if (bubble) {
      return bubble.textContent || bubble.innerText || '';
    }
  }

  return '';
}
/**
 * 显示分享弹窗
 * @param {string|null} customShareContent - 自定义分享内容
 * @param {boolean} forceShowPayment - 强制显示支付选项
 * @param {string|null} paymentTitle - 支付弹窗的自定义标题
 * @param {boolean} shouldRewardFreeCount - 是否应该奖励免费次数（只有深度解读、每日运势、前世画像分享才为true）
 */
function showSharePopup(customShareContent = null, forceShowPayment = false, paymentTitle = null, shouldRewardFreeCount = false) {
  // VIP 用户不弹窗
  if (isVip()) return;

  // 追踪分享功能使用
  fetch('/analytics', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'feature_usage',
      feature: 'share_popup',
      timestamp: new Date().toISOString()
    })
  }).catch(e => console.error('分享功能追踪失败:', e));

  refreshTodayStatus();
  updateCountBanner();

  const mask = document.getElementById('popupMask');
  const content = document.getElementById('popupContent');
  if (!mask || !content) return;

  // ✅ 修复：显示分享内容时，隐藏支付框（避免两个框同时显示）
  const vipPayBox = document.getElementById('vipPayBox');
  if (vipPayBox) {
    vipPayBox.style.display = 'none';
  }
  const qrArea = document.getElementById('qrArea');
  if (qrArea) {
    qrArea.style.display = 'none';
  }

  // ✅ 修复：确保分享内容区域显示
  content.style.display = 'block';
  content.style.visibility = 'visible';
  content.style.opacity = '1';

  const shareCount = getShareCount();
  const limit = DAILY_SHARE_LIMIT; // 2

  // 状态 A/B：!isVip && shareCount < DAILY_SHARE_LIMIT
  // 初始只显示「生成图片分享」按钮
  const renderAB = `
    <div style="position: relative; width: 100%;">
      <div class="text-lg font-bold mb-3" style="color:rgba(255, 255, 255, 0.95) !important;text-shadow:0 2px 8px rgba(102, 126, 234, 0.5) !important;">🔮 分享后立即解锁免费分析</div>
      <div class="text-base mb-3" style="color:rgba(255, 255, 255, 0.9) !important;">生成分享图片后即可获得 1 次免费分析机会</div>
      <div class="flex flex-col items-center gap-2 mb-2">
        <button id="genSharePosterBtn" style="padding:.6rem 1rem;border-radius:.8rem;background:linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;color:white !important;border:none;font-weight:600;box-shadow:0 4px 12px rgba(102, 126, 234, 0.4) !important;transition:all 0.3s ease;cursor:pointer !important;touch-action:manipulation !important;-webkit-tap-highlight-color:rgba(102, 126, 234, 0.3) !important;user-select:none !important;-webkit-user-select:none !important;" onmouseover="this.style.background='linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)'; this.style.transform='translateY(-2px) scale(1.05)'; this.style.boxShadow='0 6px 20px rgba(102, 126, 234, 0.6)'" onmouseout="this.style.background='linear-gradient(135deg, #667eea 0%, #764ba2 100%)'; this.style.transform='translateY(0) scale(1)'; this.style.boxShadow='0 4px 12px rgba(102, 126, 234, 0.4)'" ontouchstart="this.style.background='linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)'; this.style.transform='translateY(-2px) scale(1.05)'; this.style.boxShadow='0 6px 20px rgba(102, 126, 234, 0.6)'" ontouchend="this.style.background='linear-gradient(135deg, #667eea 0%, #764ba2 100%)'; this.style.transform='translateY(0) scale(1)'; this.style.boxShadow='0 4px 12px rgba(102, 126, 234, 0.4)'">生成图片分享</button>
      </div>
    </div>
  `;

  // 状态 C：!isVip && shareCount >= DAILY_SHARE_LIMIT
  // 或者强制显示支付选项（分析次数不足场景）
  // ✅ 修复：根据场景显示不同的文案
  const isAnalysisScene = forceShowPayment && paymentTitle; // 分析次数不足场景
  const paymentTitleText = isAnalysisScene
    ? (paymentTitle || '分析次数不足')
    : '🔮 今日分享次数已达上限';
  const paymentDescText = isAnalysisScene
    ? '请购买票数或开通会员继续使用分析功能'
    : '开通会员可无限畅享倪大师的深度解读';

  const renderC = `
    <div style="position: relative; width: 100%;">
      <div class="text-lg font-bold mb-4" style="color:rgba(255, 255, 255, 0.95) !important;text-shadow:0 2px 8px rgba(102, 126, 234, 0.5) !important;">${paymentTitleText}</div>
      <div class="text-base mb-4" style="color:rgba(255, 255, 255, 0.9) !important;">${paymentDescText}</div>
      <div class="flex flex-col items-center gap-3 mb-2">
      <button id="goVipBtn" style="
        padding:.3rem 1.2rem;
        border-radius:1rem;
        background:linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
        color:#fff !important;
        font-weight:bold;
        font-size:1rem;
        box-shadow:0 4px 12px rgba(102, 126, 234, 0.4) !important;
        border:none !important;
        transition:all 0.3s ease;
        width:100%;
        max-width:280px;
        text-align:center;
        line-height:1.1;
        position:relative;
        overflow:hidden;
      " onmouseover="this.style.background='linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)'; this.style.transform='translateY(-2px) scale(1.05)'; this.style.boxShadow='0 6px 20px rgba(102, 126, 234, 0.6)'" onmouseout="this.style.background='linear-gradient(135deg, #667eea 0%, #764ba2 100%)'; this.style.transform='translateY(0) scale(1)'; this.style.boxShadow='0 4px 12px rgba(102, 126, 234, 0.4)'">
        <div style="font-size:1rem; font-weight:bold;">🔥 限时优惠</div>
        <div style="font-size:0.85rem; font-weight:normal; opacity:0.9;">首月优惠 5.9元/月，续费 9.9元/月</div>
      </button>
      <button id="buyTicketBtn" style="
        padding:.3rem 1.2rem;
        border-radius:1rem;
        background:linear-gradient(135deg, #3b82f6 0%, #2563eb 100%) !important;
        color:#fff !important;
        font-weight:bold;
        font-size:1rem;
        box-shadow:0 4px 12px rgba(59, 130, 246, 0.4) !important;
        border:none !important;
        transition:all 0.3s ease;
        width:100%;
        max-width:280px;
        text-align:center;
        line-height:1.1;
        position:relative;
        overflow:hidden;
      " onmouseover="this.style.background='linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'; this.style.transform='translateY(-2px) scale(1.05)'; this.style.boxShadow='0 6px 20px rgba(59, 130, 246, 0.6)'" onmouseout="this.style.background='linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'; this.style.transform='translateY(0) scale(1)'; this.style.boxShadow='0 4px 12px rgba(59, 130, 246, 0.4)'">
        <div style="font-size:1rem; font-weight:bold;">购买体验券</div>
        <div style="font-size:0.85rem; font-weight:normal; opacity:0.9;">+1 (1.9元/次)</div>
      </button>
      <button id="buyTicket3Btn" style="
        padding:.3rem 1.2rem;
        border-radius:1rem;
        background:linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%) !important;
        color:#fff !important;
        font-weight:bold;
        font-size:1rem;
        box-shadow:0 4px 12px rgba(139, 92, 246, 0.4) !important;
        border:none !important;
        transition:all 0.3s ease;
        width:100%;
        max-width:280px;
        text-align:center;
        line-height:1.1;
        position:relative;
        overflow:hidden;
      " onmouseover="this.style.background='linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)'; this.style.transform='translateY(-2px) scale(1.05)'; this.style.boxShadow='0 6px 20px rgba(139, 92, 246, 0.6)'" onmouseout="this.style.background='linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'; this.style.transform='translateY(0) scale(1)'; this.style.boxShadow='0 4px 12px rgba(139, 92, 246, 0.4)'">
        <div style="font-size:1rem; font-weight:bold;">💰 超值套餐</div>
        <div style="font-size:0.85rem; font-weight:normal; opacity:0.9;">3次体验券仅需4.9元/3次</div>
      </button>
    </div>
    </div>
  `;

  // ✅ 修复：根据分享次数或强制显示支付决定显示内容
  content.innerHTML = (forceShowPayment || shareCount >= limit) ? renderC : renderAB;

  // 设置弹窗容器样式（深色主题+毛玻璃效果）
  const popupBox = document.getElementById('popupBox');
  if (popupBox) {
    popupBox.style.cssText = `
      position: relative !important;
      background: rgba(26, 26, 46, 0.95) !important;
      backdrop-filter: blur(20px) !important;
      -webkit-backdrop-filter: blur(20px) !important;
      border: 1px solid rgba(102, 126, 234, 0.5) !important;
      border-radius: 20px !important;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(102, 126, 234, 0.3) inset, 0 0 40px rgba(102, 126, 234, 0.3) !important;
      padding: 22px 18px 20px !important;
      width: 92vw !important;
      max-width: 370px !important;
      min-width: 260px !important;
      text-align: center !important;
      color: white !important;
    `;
  }

  // 设置弹窗内容区域样式，支持滚动
  content.style.maxHeight = '70vh';
  content.style.overflowY = 'auto';
  content.style.padding = '1rem';
  content.style.color = 'white !important';

  // ✅ 确保弹窗完全显示，清除所有隐藏状态
  mask.style.display = "flex";
  mask.style.visibility = "visible";
  mask.style.opacity = "1";
  mask.style.pointerEvents = "auto";
  mask.classList.remove('hidden');

  // 🔍 调试信息：确认弹窗显示状态
  debugLog('🔍 弹窗显示状态检查:', {
    display: mask.style.display,
    visibility: mask.style.visibility,
    opacity: mask.style.opacity,
    pointerEvents: mask.style.pointerEvents,
    hasHiddenClass: mask.classList.contains('hidden')
  });

  stickBottom();

  // 初次渲染后滚到最底
  timerManager.setTimeout(() => { try { if (content) content.scrollTop = content.scrollHeight; } catch (_) { } }, 50);

  // 在弹窗末尾拼接一个隐藏的二维码区（优化为更紧凑的样式）
  const qrHTML = `
    <div id="qrArea" style="display:none;margin-top:0.8rem;text-align:center;">
      <div id="qrTitle" style="font-weight:700;color:rgba(255, 255, 255, 0.95) !important;text-shadow:0 2px 8px rgba(102, 126, 234, 0.5) !important;margin-bottom:0.6rem;font-size:1rem;"></div>
      <div style="display:flex;justify-content:center;margin-bottom:0.6rem;">
        <img id="qrImg" src="" alt="支付二维码"
             style="width:160px;height:160px;border-radius:12px;box-shadow:0 4px 20px rgba(102, 126, 234, 0.4) !important;border:1px solid rgba(102, 126, 234, 0.4) !important;">
      </div>
      <div id="qrHint" style="color:rgba(255, 255, 255, 0.9) !important;margin-bottom:0.8rem;line-height:1.4;font-size:0.85rem;padding:0 0.5rem;">
        请使用微信扫码支付，支付成功后会自动解锁
      </div>
      <div style="display:flex;gap:0.8rem;justify-content:center;margin-top:0.8rem;">
        <button id="qrRefreshBtn" style="padding:0.6rem 1.2rem;border-radius:0.8rem;background:linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;color:white !important;border:none;font-size:0.9rem;font-weight:500;box-shadow:0 4px 12px rgba(102, 126, 234, 0.4) !important;transition:all 0.3s ease;" onmouseover="this.style.background='linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)'; this.style.transform='translateY(-2px)'; this.style.boxShadow='0 6px 20px rgba(102, 126, 234, 0.6)'" onmouseout="this.style.background='linear-gradient(135deg, #667eea 0%, #764ba2 100%)'; this.style.transform='translateY(0)'; this.style.boxShadow='0 4px 12px rgba(102, 126, 234, 0.4)'">刷新</button>
        <button id="qrBackBtn" style="padding:0.6rem 1.2rem;border-radius:0.8rem;background:rgba(108, 117, 125, 0.3) !important;backdrop-filter:blur(10px) !important;border:1px solid rgba(255, 255, 255, 0.2) !important;color:white !important;font-size:0.9rem;font-weight:500;box-shadow:0 4px 12px rgba(108, 117, 125, 0.3) !important;transition:all 0.3s ease;" onmouseover="this.style.background='rgba(108, 117, 125, 0.4)'; this.style.transform='translateY(-2px)'; this.style.boxShadow='0 6px 20px rgba(108, 117, 125, 0.4)'" onmouseout="this.style.background='rgba(108, 117, 125, 0.3)'; this.style.transform='translateY(0)'; this.style.boxShadow='0 4px 12px rgba(108, 117, 125, 0.3)'">返回</button>
      </div>
    </div>`;
  content.insertAdjacentHTML('beforeend', qrHTML);

  // 关闭按钮（使用通用的 popupClose）
  const close = document.getElementById('popupClose');
  if (close) close.onclick = () => {
    mask.style.display = "none";
    mask.style.visibility = "hidden";
    mask.style.opacity = "0";
    mask.style.pointerEvents = "none";
    mask.classList.add('hidden');
  };

  // ✅ 新增：确保遮罩可点击关闭（但不要阻止内容区域的点击）
  mask.onclick = (e) => {
    // 如果点击的是遮罩本身（不是内容区域），则关闭
    if (e.target === mask) {
      mask.style.display = "none";
      mask.style.visibility = "hidden";
      mask.style.opacity = "0";
      mask.style.pointerEvents = "none";
      mask.classList.add('hidden');
    }
  };

  // ✅ 新增：阻止内容区域的点击事件冒泡到遮罩
  // 注意：popupBox 已在第7967行声明，这里直接使用
  if (popupBox) {
    popupBox.onclick = (e) => {
      e.stopPropagation();
    };
  }

  // ✅ 修复：生成图片分享按钮（移动端触摸支持）
  const posterBtn = document.getElementById('genSharePosterBtn');
  if (posterBtn) {
    // ✅ 修复：移除旧的事件监听器，避免重复绑定
    const newPosterBtn = posterBtn.cloneNode(true);
    posterBtn.parentNode.replaceChild(newPosterBtn, posterBtn);

    // ✅ 修复：事件处理函数
    const handleShareClick = async (e) => {
      e.preventDefault();
      e.stopPropagation();

      // ✅ 修复：移动端触摸反馈
      if (e.type === 'touchend') {
        newPosterBtn.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
        newPosterBtn.style.transform = 'translateY(0) scale(1)';
        newPosterBtn.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
      }

      try {
        const shareBox = document.getElementById('shareCardBox');
        if (!shareBox) return;

        // 移除自动添加的二维码，使用HTML中定义的二维码
        // 给 shareBox 定位以承载绝对定位元素（不影响你原有卡片排版）
        const sbStyle = window.getComputedStyle(shareBox);
        if (sbStyle.position === 'static') shareBox.style.position = 'relative';

        // 使用美化后的内容进行分享
        const latestText = customShareContent || getLatestAssistantText();
        const shareContent = renderMasterReply(latestText);
        const shareCardTextEl = document.getElementById('shareCardText');
        if (shareCardTextEl) {
          shareCardTextEl.innerHTML = cleanMarkdown(shareContent);
          // 移除所有内联颜色样式，使用CSS控制颜色（深色主题）
          const allElements = shareCardTextEl.querySelectorAll('*');
          allElements.forEach(el => {
            if (el.style.color) {
              el.style.removeProperty('color');
            }
          });
        }

        // 根据内容类型设置分享卡片标题
        const shareCardTitleEl = document.getElementById('shareCardTitle');
        if (shareCardTitleEl) {
          if (latestText.includes('🔖【') && (latestText.includes('上签') || latestText.includes('中签') || latestText.includes('下签'))) {
            shareCardTitleEl.innerText = "今日运势签文";
          } else {
            shareCardTitleEl.innerText = "";
          }
        }

        // 如果传入了自定义内容，需要更新对话框显示
        if (customShareContent) {
          // 找到最后一条助手消息并更新其内容
          const assistantMessages = document.querySelectorAll('#chatBox .flex.justify-start[data-reply]');
          if (assistantMessages.length > 0) {
            const lastMessage = assistantMessages[assistantMessages.length - 1];
            const bubble = lastMessage.querySelector('.bubble');
            if (bubble) {
              // 更新消息内容为每日运势内容
              bubble.innerHTML = cleanMarkdown(shareContent);
              // 更新data-reply属性
              lastMessage.setAttribute('data-reply', customShareContent);
            }
          }
        }

        // ✅ P2-1优化：按需加载 html2canvas
        if (!window.html2canvas) {
          await window.loadHtml2Canvas();
        }

        // 离屏渲染
        const oldDisplay = shareBox.style.display;
        shareBox.style.display = 'block';
        const canvas = await html2canvas(shareBox, { backgroundColor: '#fff', scale: 2, useCORS: true });
        shareBox.style.display = oldDisplay;

        // 全屏预览层
        let imgWrap = document.createElement("div");
        imgWrap.style.cssText = "position:fixed;z-index:9999;left:0;top:0;width:100vw;height:100vh;background:rgba(0,0,0,0.85) !important;backdrop-filter:blur(8px) !important;-webkit-backdrop-filter:blur(8px) !important;display:flex;flex-direction:column;align-items:center;justify-content:center;";
        imgWrap.innerHTML = `
        <div style="background:rgba(26, 26, 46, 0.95) !important;backdrop-filter:blur(20px) !important;-webkit-backdrop-filter:blur(20px) !important;border:1px solid rgba(102, 126, 234, 0.5) !important;padding:16px 8px 8px 8px;border-radius:16px;box-shadow:0 8px 32px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(102, 126, 234, 0.3) inset, 0 0 40px rgba(102, 126, 234, 0.3) !important;">
          <img src="${canvas.toDataURL('image/png')}" style="max-width:90vw;max-height:65vh;border-radius:16px;box-shadow:0 4px 20px rgba(102, 126, 234, 0.4) !important;">
        </div>
        <div style="color:rgba(255, 255, 255, 0.95) !important;text-shadow:0 2px 8px rgba(102, 126, 234, 0.5) !important;font-size:1.05rem;text-align:center;margin-top:12px;">
          长按图片保存后去分享
        </div>
        <div style="margin-top:12px;">
          <button id="closeShareMask" style="padding:7px 28px;border:none;border-radius:6px;background:linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;color:white !important;font-size:1rem;font-weight:600;box-shadow:0 4px 12px rgba(102, 126, 234, 0.4) !important;transition:all 0.3s ease;cursor:pointer;" onmouseover="this.style.background='linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)'; this.style.transform='translateY(-2px)'; this.style.boxShadow='0 6px 20px rgba(102, 126, 234, 0.6)'" onmouseout="this.style.background='linear-gradient(135deg, #667eea 0%, #764ba2 100%)'; this.style.transform='translateY(0)'; this.style.boxShadow='0 4px 12px rgba(102, 126, 234, 0.4)'">关闭</button>
        </div>
      `;
        document.body.appendChild(imgWrap);
        document.getElementById("closeShareMask").onclick = () => { imgWrap.remove(); };

        stickBottom();

        // 生成成功后，显示「我已分享，立即解锁」按钮
        const shareDoneBtn = document.createElement('button');
        shareDoneBtn.id = 'shareDoneBtn';
        shareDoneBtn.style.cssText = 'padding:.6rem 1rem;border-radius:.8rem;background:linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;color:white !important;border:none !important;margin-top:8px;font-weight:600;box-shadow:0 4px 12px rgba(102, 126, 234, 0.4) !important;transition:all 0.3s ease;position:relative;overflow:hidden;';
        shareDoneBtn.textContent = '我已分享，立即解锁';
        shareDoneBtn.onmouseover = function () {
          this.style.background = 'linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)';
          this.style.transform = 'translateY(-2px) scale(1.05)';
          this.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.6)';
        };
        shareDoneBtn.onmouseout = function () {
          this.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
          this.style.transform = 'translateY(0) scale(1)';
          this.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
        };

        shareDoneBtn.onclick = () => {
          shareDoneBtn.disabled = true;

          // ✅ 修复：只有 shouldRewardFreeCount 为 true 时才增加分享次数和免费次数
          if (shouldRewardFreeCount) {
            const shareCount = getShareCount();
            const limit = DAILY_SHARE_LIMIT; // 2

            // 检查是否已达到每日分享上限
            if (shareCount >= limit) {
              if (typeof CodeUtils !== 'undefined' && CodeUtils.showToast) {
                CodeUtils.showToast('今日分享次数已达上限，请明天再试', 'warning', 3000);
              } else {
                alert('今日分享次数已达上限，请明天再试');
              }
              shareDoneBtn.disabled = false;
              return;
            }

            // 增加分享次数和免费次数
            setShareCount(shareCount + 1);
            setFreeCount(getFreeCount() + 1);
            updateCountBanner();

            // ✅ 修复：显示分享成功提示，明确告知用户获得了1次免费分析
            if (typeof CodeUtils !== 'undefined' && CodeUtils.showToast) {
              CodeUtils.showToast('🎉 分享成功！已获得1次免费分析，可以立即使用', 'success', 3000);
            } else {
              alert('🎉 分享成功！已获得1次免费分析，可以立即使用');
            }
          } else {
            // ✅ 修复：不奖励免费次数的分享（如基础分析结果），只解锁内容
            if (typeof CodeUtils !== 'undefined' && CodeUtils.showToast) {
              CodeUtils.showToast('✅ 分享成功！内容已解锁', 'success', 2000);
            } else {
              alert('✅ 分享成功！内容已解锁');
            }
          }

          // 关闭弹层
          mask.style.display = "none";

          // ✅ 修复：删除立即消耗次数的逻辑，让用户主动使用
          // consumeQuotaOnce(); // 删除这行
          // 解锁最新助手气泡
          rerenderChatBoxNoMask();
          // 移除可能残留的遮罩 CTA（容错）
          try {
            const last = document.querySelector('#chatBox .flex.justify-start:last-child .bubble');
            if (last) {
              const m = last.querySelector('div[style*="backdrop-filter"]');
              if (m) m.remove();
            }
          } catch (_) { }
          // 强制滚动到底
          if (typeof stickBottom === 'function') stickBottom();
        };

        // 插入到按钮容器中
        const buttonContainer = content.querySelector('.flex.flex-col.items-center.gap-2.mb-2');
        if (buttonContainer) {
          buttonContainer.appendChild(shareDoneBtn);

          // 隐藏"生成图片分享"按钮
          const genShareBtn = document.getElementById('genSharePosterBtn');
          if (genShareBtn) {
            genShareBtn.style.display = 'none';
          }
        }

        timerManager.setTimeout(() => { try { if (content) content.scrollTop = content.scrollHeight; } catch (_) { } }, 50);

      } catch (e) {
        alert('生成分享图片失败，请稍后再试。');
      }
    };

    // ✅ 修复：同时绑定 click 和 touchend 事件，确保移动端可点击
    newPosterBtn.addEventListener('click', handleShareClick, { passive: false, capture: false });
    newPosterBtn.addEventListener('touchend', handleShareClick, { passive: false, capture: false });
  }

  // 开通会员按钮（状态C）
  const vipBtn = document.getElementById('goVipBtn');
  if (vipBtn) vipBtn.onclick = () => showQRInPopup('vip');

  // 购买体验券按钮（状态C）
  const buyBtn = document.getElementById('buyTicketBtn');
  if (buyBtn) buyBtn.onclick = () => showQRInPopup('ticket', 1);

  // 购买3次体验券套餐按钮
  const buy3Btn = document.getElementById('buyTicket3Btn');
  if (buy3Btn) buy3Btn.onclick = () => showQRInPopup('ticket', 3);
}
/**
 * 显示二维码支付弹窗
 */
async function showQRInPopup(mode = 'vip', count = 1) {
  // 简单的遮罩关闭函数
  const closeMaskOnPaid = () => {
    const popupMask = document.getElementById('popupMask');
    if (popupMask) {
      popupMask.style.display = 'none';
    }
  };

  // ✅ 修复：VIP模式使用不同的元素
  let qrArea, qrTitle, qrImg;

  if (mode === 'vip') {
    // VIP模式：使用 vipPayBox 和 vipPayQR
    const vipPayBox = document.getElementById('vipPayBox');
    const vipPayQR = document.getElementById('vipPayQR');

    if (!vipPayBox || !vipPayQR) {
      console.error('❌ VIP支付元素不存在');
      alert('VIP支付功能未初始化，请刷新页面重试');
      return;
    }

    // 创建临时变量以兼容后续代码
    qrArea = vipPayBox;
    qrTitle = document.getElementById('vipPayTip') || document.createElement('div');
    qrImg = vipPayQR;
  } else {
    // 体验券模式：使用原有的元素
    qrArea = document.getElementById('qrArea');
    qrTitle = document.getElementById('qrTitle');
    qrImg = document.getElementById('qrImg');

    if (!qrArea || !qrTitle || !qrImg) {
      console.error('❌ 体验券支付元素不存在');
      alert('支付功能未初始化，请刷新页面重试');
      return;
    }
  }

  const refresh = document.getElementById('qrRefreshBtn');
  const backBtn = document.getElementById('qrBackBtn');
  const mask = document.getElementById('popupMask');
  const content = document.getElementById('popupContent');

  // ✅ 修复：显示支付框时，隐藏分享内容区域（避免两个框同时显示）
  if (content) {
    content.style.display = 'none';
    content.style.visibility = 'hidden';
  }

  // ✅ 修复：确保弹窗遮罩显示，并设置更高的z-index以确保显示在灵魂伴侣界面之上
  if (mask) {
    // ✅ 修复：设置z-index为10002，确保显示在灵魂伴侣界面（z-index: 10000）之上
    mask.style.zIndex = '10002';
    mask.style.display = 'flex';
    mask.style.visibility = 'visible';
    mask.style.opacity = '1';
    mask.style.pointerEvents = 'auto';
    debugLog('✅ 弹窗遮罩已显示，z-index: 10002');

    // ✅ 修复：确保VIP支付框的z-index也足够高
    if (mode === 'vip' && qrArea) {
      qrArea.style.zIndex = '10003';
    }
  } else {
    console.error('❌ popupMask 元素不存在');
    alert('支付弹窗未初始化，请刷新页面重试');
    return;
  }

  // ✅ 修复：VIP模式显示 vipPayBox
  if (mode === 'vip') {
    qrArea.style.display = 'flex';  // ✅ 修复：使用 flex 布局以确保二维码居中
    qrArea.style.flexDirection = 'column';
    qrArea.style.alignItems = 'center';
    qrArea.style.justifyContent = 'center';
    qrArea.style.visibility = 'visible';
    qrArea.style.opacity = '1';
    debugLog('✅ VIP支付弹窗已显示');
  } else {
    // ✅ 修复：体验券模式：需要先显示 popupContent，然后在其中显示 qrArea
    if (content) {
      content.style.display = 'block';
      content.style.visibility = 'visible';
    }
    qrArea.style.display = 'block';
  }

  // ✅ 修复：绑定关闭按钮事件（确保从灵魂伴侣界面调用时也能关闭）
  const closeBtn = document.getElementById('popupClose');
  if (closeBtn) {
    // 移除旧的事件监听器（如果有）
    const newCloseBtn = closeBtn.cloneNode(true);
    closeBtn.parentNode.replaceChild(newCloseBtn, closeBtn);

    // 绑定新的关闭事件
    newCloseBtn.onclick = (e) => {
      e.stopPropagation();
      e.preventDefault();

      debugLog('✅ 用户点击关闭按钮');

      // 停止支付轮询
      if (window.stopPaymentPolling !== undefined) {
        window.stopPaymentPolling = true;
      }

      // 关闭弹窗
      if (mask) {
        mask.style.display = 'none';
        mask.style.visibility = 'hidden';
        mask.style.opacity = '0';
        mask.style.pointerEvents = 'none';
        mask.classList.add('hidden');
      }

      // 隐藏VIP支付框
      if (mode === 'vip' && qrArea) {
        qrArea.style.display = 'none';
      }

      // 隐藏体验券支付框
      if (mode !== 'vip' && qrArea) {
        qrArea.style.display = 'none';
      }

      debugLog('✅ 支付弹窗已关闭');
    };
  } else {
    console.warn('⚠️ popupClose 按钮不存在');
  }

  // 设置标题
  if (mode === 'vip') {
    // VIP模式：标题已在HTML中定义，不需要设置
    debugLog('✅ VIP支付弹窗已显示');
  } else {
    qrTitle.textContent = `购买${count}次体验券`;
  }

  // 显示加载状态
  qrImg.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYwIiBoZWlnaHQ9IjE2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzY2NjY2NiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPuaXoOazleiDveWKoOi9veWksei0pTwvdGV4dD48L3N2Zz4=';

  try {
    debugLog('🚀 开始创建支付订单...', { mode, count, session_id: getOrInitSessionId() });

    let res;
    try {
      res = (mode === 'vip')
        ? await bjCreateVipOrder()
        : await bjCreateTicketOrder(count);
    } catch (e) {
      debugLog('创建订单失败:', e);
      if (e.name === 'AbortError') {
        alert('请求超时，服务器响应较慢，请稍后重试');
      } else {
        alert('网络错误，请检查网络连接后重试');
      }
      return;
    }

    if (!(res && res.status === 'ok' && res.order_id && (res.qr || res.url_qrcode || res.url))) {
      debugLog('订单创建响应异常:', res);
      alert(res?.msg || '下单失败，请稍后重试');
      return;
    }
    const payUrl = res.qr || res.url_qrcode || res.url;
    lastOrderId = res.order_id;

    debugLog('✅ 支付订单创建成功，显示二维码');
    debugLog('🔍 二维码URL:', payUrl);
    debugLog('🔍 二维码元素:', qrImg);
    debugLog('🔍 二维码区域:', qrArea);

    // 验证支付URL
    if (!payUrl || payUrl.trim() === '') {
      debugLog('❌ 支付URL为空，无法显示二维码');
      alert('支付URL为空，请重试');
      return;
    }

    // 验证URL格式
    if (!payUrl.startsWith('http') && !payUrl.startsWith('data:')) {
      debugLog('❌ 支付URL格式无效:', payUrl);
      alert('支付URL格式无效，请重试');
      return;
    }

    // 确保二维码区域可见
    qrArea.style.display = 'block';
    qrArea.style.visibility = 'visible';
    qrArea.style.opacity = '1';
    debugLog('🔍 二维码区域显示状态:', qrArea.style.display);

    // 监听二维码图片加载
    qrImg.onload = function () {
      debugLog('✅ 二维码图片加载成功');
      qrArea.style.display = 'block';
      // 确保二维码区域可见
      if (content) {
        timerManager.setTimeout(() => {
          content.scrollTop = content.scrollHeight;
          qrArea.scrollIntoView({ behavior: 'smooth', block: 'center' });
          debugLog('🔍 已滚动到二维码区域');
        }, 100);
      }
    };

    qrImg.onerror = function () {
      debugLog('❌ 二维码图片加载失败');
      qrImg.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYwIiBoZWlnaHQ9IjE2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iI2ZmMDAwMCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPuaXoOazleWksei0pTwvdGV4dD48L3N2Zz4=';
      alert('二维码加载失败，请重试');
    };

    qrImg.src = payUrl;

    // 强制滚动到二维码区域
    if (content) {
      timerManager.setTimeout(() => {
        content.scrollTop = content.scrollHeight;
        qrArea.scrollIntoView({ behavior: 'smooth', block: 'center' });
        debugLog('🔍 已滚动到二维码区域');
      }, 100);
    }

    // 开始轮询支付状态
    const pollPayment = async (pollCount = 0, startTime = Date.now()) => {
      // 检查是否需要停止轮询
      if (window.stopPaymentPolling) {
        debugLog('🛑 支付轮询已停止');
        return;
      }

      // ✅ 修复P0-5：轮询次数限制（最多30次，60秒）
      if (pollCount >= 30) {
        debugLog('⏱️ 轮询次数已达上限（30次），停止轮询');

        const qrArea = document.getElementById('qrArea');
        if (qrArea) {
          // 移除可能存在的旧按钮
          const oldBtn = qrArea.querySelector('#manualCheckBtn');
          if (oldBtn) oldBtn.remove();

          // 添加手动检查按钮
          const manualCheckBtn = document.createElement('button');
          manualCheckBtn.id = 'manualCheckBtn';
          manualCheckBtn.textContent = '我已支付，手动检查';
          manualCheckBtn.style.cssText = 'padding:.6rem 1.2rem;border-radius:.8rem;background:#fde047;color:#7a5300;width:100%;max-width:280px;font-weight:600;font-size:0.9rem;margin:0 auto;display:block;';
          manualCheckBtn.onclick = async () => {
            manualCheckBtn.disabled = true;
            manualCheckBtn.textContent = '检查中...';
            try {
              const statusResponse = (mode === 'vip')
                ? await bjCheckVipOrder(lastOrderId)
                : await bjCheckTicketOrder(lastOrderId);

              if (statusResponse && statusResponse.paid === true) {
                // 触发成功处理流程（复用现有的解锁逻辑）
                window.stopPaymentPolling = false; // 临时允许处理
                await pollPayment(0, Date.now()); // 重新执行一次以触发解锁
              } else {
                alert('支付状态仍未更新，请刷新页面或联系客服：yaoyx002');
                manualCheckBtn.disabled = false;
                manualCheckBtn.textContent = '我已支付，手动检查';
              }
            } catch (error) {
              console.error('手动检查失败:', error);
              alert('检查失败，请刷新页面或联系客服：yaoyx002');
              manualCheckBtn.disabled = false;
              manualCheckBtn.textContent = '我已支付，手动检查';
            }
          };

          // 找到刷新/返回按钮的容器
          const buttonContainer = qrArea.querySelector('div[style*="display:flex"]');
          if (buttonContainer) {
            // 在刷新/返回按钮容器之前插入手动检查按钮
            qrArea.insertBefore(manualCheckBtn, buttonContainer);
          } else {
            // 如果找不到容器，插入到qrHint之后
            const qrHint = qrArea.querySelector('#qrHint');
            if (qrHint) {
              qrHint.style.marginBottom = '1rem';
              qrArea.insertBefore(manualCheckBtn, qrHint.nextSibling);
            } else {
              qrArea.appendChild(manualCheckBtn);
            }
          }
        }

        const timeoutMsg = `支付可能成功，但验证超时（已轮询60秒）。\n\n请使用"手动检查"按钮，或刷新页面检查，或联系客服：yaoyx002`;
        alert(timeoutMsg);

        window.stopPaymentPolling = true;
        return;
      }

      // ✅ 修复P0-5：总超时时间（5分钟）
      const elapsedTime = Date.now() - startTime;
      if (elapsedTime >= 5 * 60 * 1000) {
        debugLog('⏱️ 轮询总超时（5分钟），停止轮询');
        alert('支付验证超时（已等待5分钟）。\n\n请刷新页面检查，或联系客服：yaoyx002');
        window.stopPaymentPolling = true;
        return;
      }

      debugLog('🔄 开始轮询支付状态...', `[第${pollCount + 1}次]`);
      try {
        const statusResponse = (mode === 'vip')
          ? await bjCheckVipOrder(lastOrderId)
          : await bjCheckTicketOrder(lastOrderId);

        debugLog('📊 支付状态响应:', statusResponse);
        debugLog('🔍 检测paid字段:', statusResponse?.paid);

        if (statusResponse && statusResponse.paid === true) {
          debugLog('✅ 支付成功，开始处理...');

          // ✅ 修复：添加完整的错误处理和验证
          const unlockSteps = [];
          const errors = [];

          if (mode === 'vip') {
            // VIP解锁流程
            try {
              debugLog('🎯 步骤1：设置VIP状态');
              setVipStatus(true);
              unlockSteps.push('设置VIP状态');

              // 验证VIP状态是否设置成功
              const vipStatus = localStorage.getItem('isVip') === 'true';
              if (!vipStatus) {
                throw new Error('VIP状态设置失败：localStorage写入失败');
              }
              debugLog('✅ VIP状态设置成功');
            } catch (error) {
              console.error('❌ 设置VIP状态失败:', error);
              errors.push('设置VIP状态失败: ' + error.message);
            }

            try {
              debugLog('🎯 步骤2：同步VIP状态到后端');
              const sessionId = getOrInitSessionId();
              if (sessionId && window.apiClient) {
                await window.apiClient.bjRequest('/api/sync_vip_status', {
                  session_id: sessionId,
                  is_vip: true
                });
                debugLog('✅ VIP状态已同步到后端');
              } else {
                debugLog('⚠️ 无法同步VIP状态：sessionId或apiClient不可用');
              }
              unlockSteps.push('同步VIP状态到后端');
            } catch (error) {
              console.error('❌ 同步VIP状态到后端失败:', error);
              errors.push('同步VIP状态到后端失败: ' + error.message);
            }

            try {
              debugLog('🎯 步骤3：更新横幅');
              updateCountBanner();
              unlockSteps.push('更新横幅');
              debugLog('✅ 横幅更新成功');
            } catch (error) {
              console.error('❌ 更新横幅失败:', error);
              errors.push('更新横幅失败: ' + error.message);
            }

            try {
              debugLog('🎯 步骤3：关闭遮罩');
              closeMaskOnPaid();
              unlockSteps.push('关闭遮罩');

              // 验证遮罩是否已关闭
              const mask = document.getElementById('popupMask');
              if (mask && mask.style.display !== 'none' && mask.style.display !== '') {
                // 如果遮罩还在显示，强制关闭
                mask.style.display = 'none';
                debugLog('⚠️ 遮罩未关闭，已强制关闭');
              }
              debugLog('✅ 遮罩关闭成功');
            } catch (error) {
              console.error('❌ 关闭遮罩失败:', error);
              errors.push('关闭遮罩失败: ' + error.message);
            }

            try {
              debugLog('🎯 步骤4：解锁内容');
              rerenderChatBoxNoMask();
              unlockSteps.push('解锁内容');

              // 验证内容是否已解锁（检查遮罩是否还在）
              const chatBox = document.getElementById('chatBox');
              if (chatBox) {
                const lockedItems = chatBox.querySelectorAll('[data-reply][style*="opacity"]');
                if (lockedItems.length > 0) {
                  debugLog('⚠️ 仍有内容被锁定，尝试强制解锁');
                  lockedItems.forEach(item => {
                    if (item.style.opacity === '0.3' || item.style.pointerEvents === 'none') {
                      item.style.opacity = '1';
                      item.style.pointerEvents = 'auto';
                    }
                  });
                }
              }
              debugLog('✅ 内容解锁成功');
            } catch (error) {
              console.error('❌ 解锁内容失败:', error);
              errors.push('解锁内容失败: ' + error.message);
            }

            // ✅ 最终验证：检查功能是否真的可用
            const isUnlocked = (() => {
              try {
                // 检查VIP状态
                const vipStatus = localStorage.getItem('isVip') === 'true';
                if (!vipStatus) return false;

                // 检查遮罩是否关闭
                const mask = document.getElementById('popupMask');
                if (mask && mask.style.display !== 'none' && mask.style.display !== '') {
                  // 遮罩可能已经关闭但display属性还在，不视为失败
                }

                return true;
              } catch (error) {
                console.error('验证解锁状态失败:', error);
                return false;
              }
            })();

            // 如果解锁失败，显示错误提示
            if (errors.length > 0 || !isUnlocked) {
              const errorMsg = `支付成功，但功能解锁失败。\n\n失败的步骤：${errors.join('、') || '未知错误'}\n\n请联系客服：yaoyx002\n\n已完成的步骤：${unlockSteps.join('、')}`;

              // 记录详细错误日志
              console.error('❌ 支付成功但解锁失败:', {
                errors,
                unlockSteps,
                isUnlocked,
                timestamp: new Date().toISOString(),
                vipStatus: localStorage.getItem('isVip'),
                maskDisplay: document.getElementById('popupMask')?.style.display
              });

              // 显示错误提示并提供重试选项
              const shouldRetry = confirm(errorMsg + '\n\n是否重试解锁功能？');

              if (shouldRetry) {
                // 重试解锁
                debugLog('🔄 用户选择重试解锁');
                try {
                  setVipStatus(true);
                  updateCountBanner();
                  closeMaskOnPaid();
                  rerenderChatBoxNoMask();

                  // 再次验证
                  const retryUnlocked = localStorage.getItem('isVip') === 'true';
                  if (retryUnlocked) {
                    alert('✅ 重试成功，功能已解锁！');
                    // 停止轮询
                    if (window.stopPaymentPolling) {
                      window.stopPaymentPolling = true;
                    }
                    return;
                  } else {
                    alert('❌ 重试失败，请联系客服：yaoyx002');
                  }
                } catch (retryError) {
                  console.error('重试解锁失败:', retryError);
                  alert('❌ 重试失败，请联系客服：yaoyx002');
                }
              } else {
                alert('请刷新页面后检查，或联系客服：yaoyx002');
              }

              // 停止轮询
              if (window.stopPaymentPolling) {
                window.stopPaymentPolling = true;
              }
              return;
            }

            // ✅ 解锁成功
            debugLog('🎉 支付流程完成，所有步骤成功');

            // ✅ 修复：显示支付成功提示，明确告知用户获得了VIP
            if (typeof CodeUtils !== 'undefined' && CodeUtils.showToast) {
              CodeUtils.showToast('🎉 支付成功！已开通VIP会员，可以立即使用', 'success', 3000);
            } else {
              alert('🎉 支付成功！已开通VIP会员，可以立即使用');
            }

            // 记录成功日志
            debugLog('✅ VIP解锁成功:', {
              steps: unlockSteps,
              timestamp: new Date().toISOString()
            });

            // 停止轮询
            if (window.stopPaymentPolling) {
              window.stopPaymentPolling = true;
            }

            return;
          } else {
            // 体验券解锁逻辑（类似处理）
            const unlockSteps = [];
            const errors = [];

            try {
              debugLog('🎫 增加体验券数量:', count);
              const currentCount = getTicketCount();
              setTicketCount(currentCount + count);
              unlockSteps.push('增加体验券');

              // 验证体验券是否增加成功
              const newCount = getTicketCount();
              if (newCount < currentCount + count) {
                throw new Error(`体验券增加失败：期望${currentCount + count}，实际${newCount}`);
              }
              debugLog('✅ 体验券增加成功，当前数量:', newCount);
            } catch (error) {
              console.error('❌ 增加体验券失败:', error);
              errors.push('增加体验券失败: ' + error.message);
            }

            try {
              updateCountBanner();
              unlockSteps.push('更新横幅');
              debugLog('✅ 横幅更新成功');
            } catch (error) {
              console.error('❌ 更新横幅失败:', error);
              errors.push('更新横幅失败: ' + error.message);
            }

            // ✅ 修复：删除立即消耗次数的逻辑，让用户主动使用
            // consumeQuotaOnce(); // 删除这行

            // ✅ 修复：显示支付成功提示，明确告知用户获得了体验券
            if (typeof CodeUtils !== 'undefined' && CodeUtils.showToast) {
              CodeUtils.showToast(`🎉 支付成功！已获得${count}次体验券，可以立即使用`, 'success', 3000);
            } else {
              alert(`🎉 支付成功！已获得${count}次体验券，可以立即使用`);
            }

            try {
              closeMaskOnPaid();
              unlockSteps.push('关闭遮罩');
              debugLog('✅ 遮罩关闭成功');
            } catch (error) {
              console.error('❌ 关闭遮罩失败:', error);
              errors.push('关闭遮罩失败: ' + error.message);
            }

            try {
              rerenderChatBoxNoMask();
              unlockSteps.push('解锁内容');
              debugLog('✅ 内容解锁成功');
            } catch (error) {
              console.error('❌ 解锁内容失败:', error);
              errors.push('解锁内容失败: ' + error.message);
            }

            // 最终验证：验证内容是否真的解锁了，而不是验证体验券数量
            const isUnlocked = (() => {
              try {
                // 1. 验证体验券数量是否正确（>= 0即可，因为可能全部用于解锁）
                const ticketCount = getTicketCount();
                if (ticketCount < 0) {
                  console.error('❌ 体验券数量异常:', ticketCount);
                  return false;
                }

                // 2. 验证遮罩是否已关闭
                const mask = document.getElementById('popupMask');
                if (mask && mask.style.display !== 'none' && mask.style.display !== '') {
                  debugLog('⚠️ 遮罩可能未完全关闭');
                  // 不视为失败，因为可能已经关闭但display属性还在
                }

                // 3. 验证内容是否可见（检查chatBox中是否有解锁的内容）
                const chatBox = document.getElementById('chatBox');
                if (chatBox) {
                  // 检查是否有被遮罩遮挡的内容
                  const lockedItems = chatBox.querySelectorAll('[data-reply][style*="opacity: 0.3"]');
                  if (lockedItems.length > 0) {
                    debugLog('⚠️ 仍有内容被锁定');
                    return false;
                  }
                }

                // 验证通过：体验券数量正确，遮罩已关闭，内容已解锁
                debugLog('✅ 解锁验证通过，体验券数量:', ticketCount);
                return true;
              } catch (error) {
                console.error('验证解锁状态失败:', error);
                return false;
              }
            })();

            if (errors.length > 0 || !isUnlocked) {
              const errorMsg = `支付成功，但功能解锁失败。\n\n失败的步骤：${errors.join('、') || '未知错误'}\n\n请联系客服：yaoyx002`;

              console.error('❌ 支付成功但解锁失败:', {
                errors,
                unlockSteps,
                isUnlocked,
                ticketCount: getTicketCount()
              });

              const shouldRetry = confirm(errorMsg + '\n\n是否重试解锁功能？');

              if (shouldRetry) {
                try {
                  const currentCount = getTicketCount();
                  setTicketCount(currentCount + count);
                  updateCountBanner();
                  // ✅ 修复：删除立即消耗次数的逻辑，让用户主动使用
                  // consumeQuotaOnce(); // 删除这行
                  closeMaskOnPaid();
                  rerenderChatBoxNoMask();

                  // ✅ 修复：显示支付成功提示
                  if (typeof CodeUtils !== 'undefined' && CodeUtils.showToast) {
                    CodeUtils.showToast(`🎉 支付成功！已获得${count}次体验券，可以立即使用`, 'success', 3000);
                  } else {
                    alert(`🎉 支付成功！已获得${count}次体验券，可以立即使用`);
                  }

                  // ✅ 修复：验证内容是否真的解锁了，而不是验证体验券数量
                  const retryUnlocked = (() => {
                    try {
                      const ticketCount = getTicketCount();
                      // 体验券数量 >= 0 即可（可能全部用于解锁）
                      if (ticketCount < 0) return false;

                      // 检查遮罩是否已关闭
                      const mask = document.getElementById('popupMask');
                      if (mask && mask.style.display !== 'none' && mask.style.display !== '') {
                        // 不视为失败，因为可能已经关闭但display属性还在
                      }

                      // 检查内容是否可见
                      const chatBox = document.getElementById('chatBox');
                      if (chatBox) {
                        const lockedItems = chatBox.querySelectorAll('[data-reply][style*="opacity: 0.3"]');
                        if (lockedItems.length > 0) {
                          return false; // 仍有内容被锁定
                        }
                      }

                      return true; // 验证通过
                    } catch (error) {
                      console.error('重试验证失败:', error);
                      return false;
                    }
                  })();

                  if (retryUnlocked) {
                    alert('✅ 重试成功，功能已解锁！');
                    if (window.stopPaymentPolling) {
                      window.stopPaymentPolling = true;
                    }
                    return;
                  } else {
                    alert('❌ 重试失败，请联系客服：yaoyx002');
                  }
                } catch (retryError) {
                  console.error('重试解锁失败:', retryError);
                  alert('❌ 重试失败，请联系客服：yaoyx002');
                }
              } else {
                alert('请刷新页面后检查，或联系客服：yaoyx002');
              }

              if (window.stopPaymentPolling) {
                window.stopPaymentPolling = true;
              }
              return;
            }

            alert(`支付成功，已发放体验券 ×${count}`);
            debugLog('🎉 支付流程完成');

            // 停止轮询
            if (window.stopPaymentPolling) {
              window.stopPaymentPolling = true;
            }

            return;
          }
        } else {
          debugLog('⏳ 支付未完成，继续轮询...');
          debugLog('🔍 当前paid状态:', statusResponse?.paid);
          // 检查是否需要停止轮询
          if (!window.stopPaymentPolling) {
            timerManager.setTimeout(() => pollPayment(pollCount + 1, startTime), 2000);
          }
        }
      } catch (error) {
        console.error('❌ 检查支付状态失败:', error);
        // 出错时也继续轮询，避免中断
        if (!window.stopPaymentPolling) {
          timerManager.setTimeout(() => pollPayment(pollCount + 1, startTime), 2000);
        }
      }
    };

    // 启动轮询
    debugLog('🚀 启动支付状态轮询...');
    window.stopPaymentPolling = false; // 重置停止标记
    timerManager.setTimeout(() => pollPayment(0, Date.now()), 2000);
  } catch (error) {
    console.error('创建支付失败:', error);
    qrImg.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYwIiBoZWlnaHQ9IjE2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iI2ZmMDAwMCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPuaXoOazleWksei0pTwvdGV4dD48L3N2Zz4=';
  }

  // 刷新按钮
  if (refresh) refresh.onclick = () => showQRInPopup(mode, count);

  // 返回按钮
  if (backBtn) backBtn.onclick = () => {
    closeMaskOnPaid();
    // 设置标记，表示用户刚刚关闭了支付界面
    sessionStorage.setItem('paymentClosed', 'true');
    // 停止轮询
    window.stopPaymentPolling = true;

    qrArea.style.display = 'none';
    // 重新显示原来的内容
    const shareCount = getShareCount();
    const limit = DAILY_SHARE_LIMIT;
    const renderAB = `
      <div class="text-lg font-bold mb-3" style="color:#b69526;">🔮 分享后立即解锁免费分析</div>
      <div class="text-base mb-3" style="color:#a6842d;">生成分享图片后即可获得 1 次免费分析机会</div>
      <div class="flex flex-col items-center gap-2 mb-2">
        <button id="genSharePosterBtn" style="padding:.6rem 1rem;border-radius:.8rem;background:#ffeaa7;color:#8b5e00;">生成图片分享</button>
      </div>
    `;
    const renderC = `
      <div class="text-lg font-bold mb-4" style="color:#b69526;">🔮 今日分享次数已达上限</div>
      <div class="text-base mb-4" style="color:#a6842d;">开通会员可无限畅享倪大师的深度解读</div>
      <div class="flex flex-col items-center gap-3 mb-2">
        <button id="goVipBtn" style="padding:.3rem 1.2rem;border-radius:1rem;background:linear-gradient(135deg, #f59e0b 0%, #d97706 100%);color:#fff;font-weight:bold;font-size:1rem;box-shadow:0 4px 12px rgba(245,158,11,0.3);border:none;transition:all 0.3s ease;width:100%;max-width:280px;text-align:center;line-height:1.1;">🔥 限时优惠</button>
        <button id="buyTicketBtn" style="padding:.3rem 1.2rem;border-radius:1rem;background:linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);color:#fff;font-weight:bold;font-size:1rem;box-shadow:0 4px 12px rgba(251,191,36,0.3);border:none;transition:all 0.3s ease;width:100%;max-width:280px;text-align:center;line-height:1.1;">购买体验券</button>
        <button id="buyTicket3Btn" style="padding:.3rem 1.2rem;border-radius:1rem;background:linear-gradient(135deg, #fcd34d 0%, #fbbf24 100%);color:#fff;font-weight:bold;font-size:1rem;box-shadow:0 4px 12px rgba(252,211,77,0.3);border:none;transition:all 0.3s ease;width:100%;max-width:280px;text-align:center;line-height:1.1;">💰 超值套餐</button>
      </div>
    `;
    content.innerHTML = (shareCount >= limit) ? renderC : renderAB;

    // 重新绑定事件
    const posterBtn = document.getElementById('genSharePosterBtn');
    if (posterBtn) posterBtn.onclick = async () => {
      try {
        const shareBox = document.getElementById('shareCardBox');
        if (!shareBox) return;

        // 移除自动添加的二维码，使用HTML中定义的二维码
        // 给 shareBox 定位以承载绝对定位元素（不影响你原有卡片排版）
        const sbStyle = window.getComputedStyle(shareBox);
        if (sbStyle.position === 'static') shareBox.style.position = 'relative';

        // 使用美化后的内容进行分享
        const latestText = getLatestAssistantText();
        const shareContent = renderMasterReply(latestText);
        const shareCardTextEl = document.getElementById('shareCardText');
        if (shareCardTextEl) {
          shareCardTextEl.innerHTML = cleanMarkdown(shareContent);
          // 移除所有内联颜色样式，使用CSS控制颜色（深色主题）
          const allElements = shareCardTextEl.querySelectorAll('*');
          allElements.forEach(el => {
            if (el.style.color) {
              el.style.removeProperty('color');
            }
          });
        }

        // 根据内容类型设置分享卡片标题
        const shareCardTitleEl = document.getElementById('shareCardTitle');
        if (shareCardTitleEl) {
          if (latestText.includes('🔖【') && (latestText.includes('上签') || latestText.includes('中签') || latestText.includes('下签'))) {
            shareCardTitleEl.innerText = "今日运势签文";
          } else {
            shareCardTitleEl.innerText = "";
          }
        }

        // ✅ P2-1优化：按需加载 html2canvas
        if (!window.html2canvas) {
          await window.loadHtml2Canvas();
        }

        // 离屏渲染
        const oldDisplay = shareBox.style.display;
        shareBox.style.display = 'block';
        const canvas = await html2canvas(shareBox, { backgroundColor: '#fff', scale: 2, useCORS: true });
        shareBox.style.display = oldDisplay;

        // 全屏预览层（统一使用深色主题样式）
        let imgWrap = document.createElement("div");
        imgWrap.style.cssText = "position:fixed;z-index:9999;left:0;top:0;width:100vw;height:100vh;background:rgba(0,0,0,0.85) !important;backdrop-filter:blur(8px) !important;-webkit-backdrop-filter:blur(8px) !important;display:flex;flex-direction:column;align-items:center;justify-content:center;";
        imgWrap.innerHTML = `
          <div style="background:rgba(26, 26, 46, 0.95) !important;backdrop-filter:blur(20px) !important;-webkit-backdrop-filter:blur(20px) !important;border:1px solid rgba(102, 126, 234, 0.5) !important;padding:16px 8px 8px 8px;border-radius:16px;box-shadow:0 8px 32px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(102, 126, 234, 0.3) inset, 0 0 40px rgba(102, 126, 234, 0.3) !important;">
            <img src="${canvas.toDataURL('image/png')}" style="max-width:90vw;max-height:65vh;border-radius:16px;box-shadow:0 4px 20px rgba(102, 126, 234, 0.4) !important;">
          </div>
          <div style="color:rgba(255, 255, 255, 0.95) !important;text-shadow:0 2px 8px rgba(102, 126, 234, 0.5) !important;font-size:1.05rem;text-align:center;margin-top:12px;">
            长按图片保存后去分享
          </div>
          <div style="margin-top:12px;">
            <button id="closeShareMask" style="padding:7px 28px;border:none;border-radius:6px;background:linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;color:white !important;font-size:1rem;font-weight:600;box-shadow:0 4px 12px rgba(102, 126, 234, 0.4) !important;transition:all 0.3s ease;cursor:pointer;" onmouseover="this.style.background='linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)'; this.style.transform='translateY(-2px)'; this.style.boxShadow='0 6px 20px rgba(102, 126, 234, 0.6)'" onmouseout="this.style.background='linear-gradient(135deg, #667eea 0%, #764ba2 100%)'; this.style.transform='translateY(0)'; this.style.boxShadow='0 4px 12px rgba(102, 126, 234, 0.4)'">关闭</button>
          </div>
        `;
        document.body.appendChild(imgWrap);
        document.getElementById("closeShareMask").onclick = () => { imgWrap.remove(); };

        stickBottom();

        // 生成成功后，显示「我已分享，立即解锁」按钮（统一使用深色主题样式）
        const shareDoneBtn = document.createElement('button');
        shareDoneBtn.id = 'shareDoneBtn';
        shareDoneBtn.style.cssText = 'padding:.6rem 1rem;border-radius:.8rem;background:linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;color:white !important;border:none !important;margin-top:8px;font-weight:600;box-shadow:0 4px 12px rgba(102, 126, 234, 0.4) !important;transition:all 0.3s ease;position:relative;overflow:hidden;';
        shareDoneBtn.textContent = '我已分享，立即解锁';
        shareDoneBtn.onmouseover = function () {
          this.style.background = 'linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)';
          this.style.transform = 'translateY(-2px) scale(1.05)';
          this.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.6)';
        };
        shareDoneBtn.onmouseout = function () {
          this.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
          this.style.transform = 'translateY(0) scale(1)';
          this.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
        };

        shareDoneBtn.onclick = () => {
          shareDoneBtn.disabled = true;

          // ✅ 修复：只有 shouldRewardFreeCount 为 true 时才增加分享次数和免费次数
          if (shouldRewardFreeCount) {
            const shareCount = getShareCount();
            const limit = DAILY_SHARE_LIMIT; // 2

            // 检查是否已达到每日分享上限
            if (shareCount >= limit) {
              if (typeof CodeUtils !== 'undefined' && CodeUtils.showToast) {
                CodeUtils.showToast('今日分享次数已达上限，请明天再试', 'warning', 3000);
              } else {
                alert('今日分享次数已达上限，请明天再试');
              }
              shareDoneBtn.disabled = false;
              return;
            }

            // 增加分享次数和免费次数
            setShareCount(shareCount + 1);
            setFreeCount(getFreeCount() + 1);
            updateCountBanner();

            // ✅ 修复：显示分享成功提示，明确告知用户获得了1次免费分析
            if (typeof CodeUtils !== 'undefined' && CodeUtils.showToast) {
              CodeUtils.showToast('🎉 分享成功！已获得1次免费分析，可以立即使用', 'success', 3000);
            } else {
              alert('🎉 分享成功！已获得1次免费分析，可以立即使用');
            }
          } else {
            // ✅ 修复：不奖励免费次数的分享（如基础分析结果），只解锁内容
            if (typeof CodeUtils !== 'undefined' && CodeUtils.showToast) {
              CodeUtils.showToast('✅ 分享成功！内容已解锁', 'success', 2000);
            } else {
              alert('✅ 分享成功！内容已解锁');
            }
          }

          // 关闭弹层
          mask.style.display = "none";

          // ✅ 修复：删除立即消耗次数的逻辑，让用户主动使用
          // consumeQuotaOnce(); // 删除这行
          // 解锁最新助手气泡
          rerenderChatBoxNoMask();
          // 移除可能残留的遮罩 CTA（容错）
          try {
            const last = document.querySelector('#chatBox .flex.justify-start:last-child .bubble');
            if (last) {
              const m = last.querySelector('div[style*="backdrop-filter"]');
              if (m) m.remove();
            }
          } catch (_) { }
          // 强制滚动到底
          if (typeof stickBottom === 'function') stickBottom();
        };

        // 插入到按钮容器中
        const buttonContainer = content.querySelector('.flex.flex-col.items-center.gap-2.mb-2');
        if (buttonContainer) {
          buttonContainer.appendChild(shareDoneBtn);

          // 隐藏"生成图片分享"按钮
          const genShareBtn = document.getElementById('genSharePosterBtn');
          if (genShareBtn) {
            genShareBtn.style.display = 'none';
          }
        }

        timerManager.setTimeout(() => { try { if (content) content.scrollTop = content.scrollHeight; } catch (_) { } }, 50);

      } catch (e) {
        alert('生成分享图片失败，请稍后再试。');
      }
    };

    const vipBtn = document.getElementById('goVipBtn');
    if (vipBtn) vipBtn.onclick = () => showQRInPopup('vip');

    const buyBtn = document.getElementById('buyTicketBtn');
    if (buyBtn) buyBtn.onclick = () => showQRInPopup('ticket', 1);

    const buy3Btn = document.getElementById('buyTicket3Btn');
    if (buy3Btn) buy3Btn.onclick = () => showQRInPopup('ticket', 3);
  };
}
/**
 * 去遮罩重渲染（用同一份"成品"）
 */
function rerenderChatBoxNoMask() {
  const chatBox = document.getElementById('chatBox');
  if (!chatBox) return;
  const items = chatBox.querySelectorAll('.flex.justify-start[data-reply]');
  if (!items.length) return;
  const last = items[items.length - 1];
  const { pretty } = loadRenderCache();
  const reply = pretty || last.getAttribute('data-reply') || '';

  // 🔧 修复深度解读气泡样式问题：保持完整的气泡结构
  last.innerHTML = "";

  // 创建完整的气泡结构，与appendMessage函数保持一致
  const bubble = document.createElement('div');
  bubble.classList.add('px-3', 'py-2', 'rounded-2xl', 'bubble', 'bubble-ai');
  bubble.style.fontFamily = "'Noto Serif SC', 'ZCOOL KuaiLe', serif";
  bubble.style.setProperty('line-height', '1.52', 'important');
  bubble.style.position = 'relative';

  // 添加角色标签
  const tag = document.createElement('div');
  tag.className = 'role-tag role-assistant';
  tag.innerHTML = '🧙 倪大师';
  bubble.appendChild(tag);

  // 添加内容元素
  const contentEl = document.createElement('div');

  // ✅ 添加打字效果，保持用户体验一致性
  if (reply && reply.trim()) {
    // 先显示"正在输入..."提示
    contentEl.innerHTML = '<div style="color: #999;">正在输入...</div>';

    // 检查是否为HTML内容
    const isHTML = reply.includes('<') && reply.includes('>');

    if (isHTML) {
      // HTML内容：使用模拟打字效果
      simulateTypingForHTML(reply, contentEl, () => {
        debugLog('✅ 分享解锁内容打字效果完成');
        stickBottom();
      });
    } else {
      // 纯文本：使用统一的打字效果
      unifiedTypingEffect(contentEl, reply, {
        onComplete: () => {
          stickBottom();
          debugLog('✅ 分享解锁内容打字效果完成');
        },
        soundEnabled: true,
        pauseEnabled: false,
        scrollEnabled: true,
        scrollInterval: 15,  // ✅ 优化：改为15，减少滚动频率，减少翻页时的抖动
        startDelay: 200
      });
    }
  } else {
    // 如果没有内容，直接显示
    contentEl.innerHTML = reply || '';
  }

  bubble.appendChild(contentEl);
  last.appendChild(bubble);

  // 立即滚动到底部
  stickBottom();
}

/**
 * 消耗一次额度
 */
function consumeQuotaOnce() {
  if (isVip()) return;            // 会员不扣
  if (getTicketCount() > 0) { setTicketCount(getTicketCount() - 1); updateCountBanner(); return; }
  if (getFreeCount() > 0) { setFreeCount(getFreeCount() - 1); updateCountBanner(); }
}

// ✅ 修复：新用户初始状态只显示主应用聊天按钮，不显示小人图标
// 小人图标只有在深度分析完成后才通过 triggerSoulmateModal 显示
document.addEventListener('DOMContentLoaded', function () {
  // 延迟检查按钮状态，给其他初始化时间
  timerManager.setTimeout(() => {
    // 确保主应用聊天按钮初始状态正确（新用户应该看到）
    const mainChatToggle = document.getElementById('chatToggle');
    if (mainChatToggle) {
      debugLog('✅ 主应用聊天按钮存在，保持显示状态');
      // ✅ 修复：新用户初始状态应该看到主应用聊天按钮
      mainChatToggle.style.display = 'block';
    }

    // 检查小人按钮是否存在（深度分析完成后才会创建）
    const soulmateToggle = document.getElementById('soulmate-chat-toggle');
    if (soulmateToggle) {
      debugLog('✅ 小人按钮已存在（深度分析已完成）');
    } else {
      debugLog('ℹ️ 小人按钮不存在（新用户状态，深度分析未完成）');
    }
  }, 1000);
});

// 导出分享功能到全局
window.showSharePopup = showSharePopup;
window.getShareCount = getShareCount;
window.setShareCount = setShareCount;

// ✅ 导出打字声音函数到全局，供其他模块使用
window.playTypingSound = playTypingSound;
window.getLatestAssistantText = getLatestAssistantText;
window.showQRInPopup = showQRInPopup;
window.rerenderChatBoxNoMask = rerenderChatBoxNoMask;
window.consumeQuotaOnce = consumeQuotaOnce;
window.showSoulmateIntroModal = showSoulmateIntroModal;

// ================= 深度解读功能 =================

/**
 * 香港统一触发函数
 */
async function sendHK(payload, { timeoutMs = 90000, retries = 2 } = {}) {
  // ✅ 修复：添加额度检查，防止无限制聊天
  if (!canAnalyze()) {
    throw new Error('您的免费次数已用完，请购买票数或等待明日免费次数');
  }

  const gptStartTime = Date.now(); // 记录GPT请求开始时间
  const slimCtx = buildSlimCtx();
  // 获取用户时区
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const unified = {
    session_id: getOrInitSessionId(),
    system_prompt: SYSTEM_PROMPT,
    context: slimCtx,
    model_prefs: { primary: "gpt-4o-mini", fallback: "gpt-4o" },
    budget: { max_chars: CTX_TOTAL_MAX_CHARS + 1200 },
    user_timezone: userTimezone,
    ...payload
  };
  const body = JSON.stringify(unified);

  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      // 通过北京服务器代理发送GPT请求
      const result = await requestJSON(`/trigger`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body, timeoutMs
      });

      // ✅ 修复：请求成功，消耗次数（只在第一次成功时消耗，重试成功不重复消耗）
      if (attempt === 0) {
        consumeQuotaOnce();
      }

      // 请求成功，追踪GPT请求
      debugLog('GPT请求成功，追踪请求 (sendHK)...', payload);
      fetch('/analytics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'gpt_request',
          analysis_type: payload.type || '',
          gender: payload.gender || '',
          hand: payload.hand || '',
          calendar_type: payload.calendar_type || '',
          response_time: Date.now() - gptStartTime
        })
      }).then(response => {
        debugLog('GPT请求追踪响应状态:', response.status);
        return response.json();
      }).then(data => {
        debugLog('GPT请求追踪成功 (sendHK):', data);
      }).catch(e => {
        console.error('GPT请求追踪失败 (sendHK):', e);
      });

      // 如果GPT返回结果，追踪GPT成功
      if (result && result.reply) {
        debugLog('发送GPT成功追踪 (sendHK)...', '回复长度:', result.reply.length);
        fetch('/analytics', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'gpt_success',
            analysis_type: payload.type || '',
            gender: payload.gender || '',
            hand: payload.hand || '',
            calendar_type: payload.calendar_type || '',
            response_time: Date.now() - gptStartTime
          })
        }).then(response => {
          debugLog('GPT成功追踪响应状态:', response.status);
          return response.json();
        }).then(data => {
          debugLog('GPT成功追踪成功 (sendHK):', data);
        }).catch(e => {
          console.error('GPT成功追踪失败 (sendHK):', e);
        });
      } else {
        debugLog('GPT响应无内容，不追踪成功 (sendHK)', 'result:', result);
      }

      return result;
    } catch (e) {
      lastErr = e;
      debugLog(`GPT请求尝试 ${attempt + 1}/${retries + 1} 失败:`, e.message);
      if (attempt < retries && /HTTP (502|503|504)|timeout|abort/i.test(String(e))) {
        debugLog(`等待 ${600 * (attempt + 1)}ms 后重试...`);
        await new Promise(r => timerManager.setTimeout(r, 600 * (attempt + 1)));
        continue;
      }
      break;
    }
  }

  // ❌ 请求失败，不消耗次数
  throw lastErr;
}

// ❌ 已删除：第二个 buildSlimCtx 函数（已使用第一个函数，按字符数限制更精确）

/**
 * 创建深度解读按钮
 */
let deepAnalysisButtonCreated = false;
let lastButtonCreationTime = 0;

function createDeepAnalysisButton(analysisText, recogType) {
  debugLog('🎯 createDeepAnalysisButton 被调用:', {
    analysisText: analysisText?.substring(0, 100),
    recogType,
    chatBoxExists: !!document.getElementById('chatBox'),
    currentTime: Date.now(),
    lastButtonCreationTime,
    timeDiff: Date.now() - lastButtonCreationTime
  });

  // 检查是否已经存在深度解读按钮，防止重复创建
  const chatBox2 = document.getElementById('chatBox');
  if (!chatBox2) {
    debugLog('❌ 找不到 chatBox，无法创建深度解读按钮');
    return;
  }

  const existingDeepBtn = chatBox2.querySelector('[data-deep-analysis-btn="true"]');
  if (existingDeepBtn) {
    debugLog('⚠️ 深度解读按钮已存在，跳过创建');
    return;
  }

  // 检查全局标志
  if (deepAnalysisButtonCreated) {
    debugLog('⚠️ 深度解读按钮已创建过，跳过');
    return;
  }

  // 防止频繁调用 - 如果距离上次创建不到2秒，跳过
  const currentTime = Date.now();
  if (currentTime - lastButtonCreationTime < 2000) {
    debugLog('⚠️ 深度解读按钮创建过于频繁，跳过 (时间差:', currentTime - lastButtonCreationTime, 'ms)');
    return;
  }

  debugLog('✅ 打字效果完成，延迟显示深度解读按钮，给用户阅读时间...');

  // ✅ 优化：减少延迟时间，确保按钮更快显示
  // 延迟显示按钮，让用户有足够时间阅读初步分析内容
  // 根据分析内容长度动态调整延迟时间
  const contentLength = analysisText ? analysisText.length : 0;
  let delayTime = 2000; // 默认2秒延迟（从3秒减少到2秒）

  if (contentLength > 500) {
    delayTime = 3500; // 长内容延迟3.5秒（从5秒减少到3.5秒）
  } else if (contentLength > 200) {
    delayTime = 2500; // 中等内容延迟2.5秒（从4秒减少到2.5秒）
  }

  debugLog(`📖 分析内容长度: ${contentLength} 字符，延迟 ${delayTime}ms 显示按钮`);

  // ✅ 新增：在延迟期间显示提示文字，告知用户深度解读功能
  // 注意：chatBox2 已在函数开头定义（第9320行），这里直接使用
  if (chatBox2) {
    const hintWrap = document.createElement('div');
    hintWrap.id = 'deepAnalysisHint';
    hintWrap.style.cssText = `
      text-align: center;
      margin: 8px 0;
      padding: 10px 14px;
      background: rgba(102, 126, 234, 0.15);
      border: 1px solid rgba(102, 126, 234, 0.4);
      border-radius: 10px;
      color: rgba(255, 255, 255, 0.9);
      font-size: 0.9rem;
      line-height: 1.5;
    `;
    hintWrap.innerHTML = `
      <div style="font-weight:600;margin-bottom:4px;">💡 深度解读功能</div>
      <div style="font-size:0.85rem;opacity:0.8;">
        分析完成后，点击下方按钮可获取更详细的命理解读和个性化建议
      </div>
    `;
    chatBox2.appendChild(hintWrap);

    // 延迟后移除提示，显示按钮
    timerManager.setTimeout(() => {
      if (hintWrap.parentNode) {
        hintWrap.remove();
      }
    }, delayTime);
  }

  timerManager.setTimeout(() => {
    // 再次检查是否已经存在按钮（防止在延迟期间被其他逻辑创建）
    const existingBtn = chatBox2.querySelector('[data-deep-analysis-btn="true"]');
    if (existingBtn) {
      debugLog('⚠️ 延迟期间按钮已存在，跳过创建');
      return;
    }

    debugLog('✅ 延迟结束，开始创建深度解读按钮...');
    deepAnalysisButtonCreated = true;
    lastButtonCreationTime = Date.now();

    const btnWrap = document.createElement('div');
    btnWrap.style.cssText = `
      text-align: center;
      margin: 8px 0;
      padding: 0;
    `;

    const deepBtn = document.createElement('button');
    deepBtn.setAttribute('data-deep-analysis-btn', 'true'); // 添加标识属性
    deepBtn.setAttribute('aria-label', '获取深度解读'); // ✅ 新增：添加aria-label
    deepBtn.innerHTML = `
      <div style="display: flex; align-items: center; gap: 6px; justify-content: center;">
        <span style="font-size: 1.1em;">🔮</span>
        <span style="font-weight: bold;">获取深度解读</span>
      </div>
    `;
    deepBtn.style.cssText = `
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
      color: white !important;
      border: none !important;
      border-radius: 12px;
      padding: 8px 16px;
      font-size: 0.95rem;
      cursor: pointer;
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4) !important;
      transition: all 0.3s ease;
      width: auto;
      min-width: 200px;
      max-width: 90%;
    `;

    // 添加悬停效果（深色主题）
    deepBtn.onmouseover = function () {
      this.style.transform = 'translateY(-2px)';
      this.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.6) !important';
      this.style.background = 'linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%) !important';
    };
    deepBtn.onmouseout = function () {
      this.style.transform = 'translateY(0)';
      this.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4) !important';
      this.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important';
    };

    btnWrap.appendChild(deepBtn);

    // 创建可折叠的说明区域（深色主题）
    const collapsibleInfo = document.createElement('div');
    collapsibleInfo.style.cssText = `
      margin: 6px 0 0 0;
      border: 1px solid rgba(102, 126, 234, 0.4) !important;
      border-radius: 8px;
      overflow: hidden;
      background: rgba(26, 26, 46, 0.95) !important;
      backdrop-filter: blur(20px) !important;
      -webkit-backdrop-filter: blur(20px) !important;
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3) !important;
    `;

    const header = document.createElement('div');
    header.style.cssText = `
      padding: 6px 10px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-size: 0.8rem;
      color: rgba(255, 255, 255, 0.95) !important;
      background: rgba(26, 26, 46, 0.95) !important;
      transition: background 0.2s;
    `;

    header.innerHTML = `
      <span style="color: rgba(255, 255, 255, 0.95) !important;">深度分析包含: 性格特征、运势建议、人生指导</span>
      <span style="transition: transform 0.3s; font-size: 0.7rem; color: rgba(255, 255, 255, 0.8) !important;">▼</span>
    `;

    const content = document.createElement('div');
    content.style.cssText = `
      padding: 6px 10px;
      background: rgba(26, 26, 46, 0.95) !important;
      display: none;
      font-size: 0.75rem;
      color: rgba(255, 255, 255, 0.9) !important;
      border-top: 1px solid rgba(102, 126, 234, 0.3) !important;
    `;

    content.innerHTML = `
      <div>VIP用户免费 | 其他用户需消耗1次体验券</div>
    `;

    collapsibleInfo.appendChild(header);
    collapsibleInfo.appendChild(content);

    // 点击头部切换显示
    header.onclick = () => {
      const isVisible = content.style.display !== 'none';
      content.style.display = isVisible ? 'none' : 'block';
      const arrow = header.querySelector('span:last-child');
      arrow.style.transform = isVisible ? 'rotate(0deg)' : 'rotate(180deg)';
      header.style.background = isVisible ? '#f9fafb' : '#f3f4f6';
    };

    // 悬停效果
    header.onmouseover = () => {
      if (content.style.display === 'none') {
        header.style.background = '#f3f4f6';
      }
    };
    header.onmouseout = () => {
      if (content.style.display === 'none') {
        header.style.background = '#f9fafb';
      }
    };

    btnWrap.appendChild(collapsibleInfo);

    chatBox2.appendChild(btnWrap);

    stickBottom();

    // 深度解读按钮点击事件
    deepBtn.onclick = async function () {
      deepBtn.disabled = true;

      // 隐藏按钮，避免重复显示
      deepBtn.style.display = 'none';

      // 获取分析参数
      const type = document.getElementById('analysisType')?.value || '';
      const gender = document.querySelector('input[name="gender"]:checked')?.value || 'male';
      const hand = document.querySelector('input[name="hand"]:checked')?.value || 'left';

      // 追踪深度解读功能使用
      fetch('/analytics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'feature_usage',
          feature: 'deep_analysis',
          analysis_type: type,
          gender: gender,
          hand: hand
        })
      }).catch(e => console.error('深度解读功能追踪失败:', e));

      // 显示统一的加载提示（深色主题）
      const waitingTip = document.createElement('div');
      waitingTip.id = 'deepAnalysisLoading';
      waitingTip.innerHTML = `
        <div style="text-align: center; padding: 15px; background: rgba(26, 26, 46, 0.95) !important; backdrop-filter: blur(20px) !important; -webkit-backdrop-filter: blur(20px) !important; border-radius: 12px; margin-top: 8px; border: 1px solid rgba(102, 126, 234, 0.5) !important; box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(102, 126, 234, 0.3) inset, 0 0 40px rgba(102, 126, 234, 0.3) !important;">
          <div style="display: flex; align-items: center; justify-content: center; gap: 10px; margin-bottom: 8px;">
            <div style="width: 20px; height: 20px; border: 2px solid rgba(102, 126, 234, 0.8); border-top: 2px solid transparent; border-radius: 50%; animation: spin 1s linear infinite;"></div>
            <span style="font-weight: bold; color: rgba(255, 255, 255, 0.95) !important; font-size: 1rem; text-shadow: 0 2px 8px rgba(102, 126, 234, 0.5) !important;">大师正在深度分析中...</span>
          </div>
          <div style="font-size: 0.85rem; color: rgba(255, 255, 255, 0.9) !important;">请稍候，正在结合传统理论为您解读</div>
        </div>
      `;
      waitingTip.style.cssText = "margin-top:8px;font-family:'Noto Serif SC',serif;";
      deepBtn.after(waitingTip);

      // 立即滚动到底部，让用户看到动画效果
      stickBottom();
      timerManager.setTimeout(() => stickBottom(), 100);

      try {
        debugLog('🎯 开始深度分析请求');
        debugLog('深度解读请求参数:', { type, gender, hand, analysisText: analysisText.substring(0, 100) + '...', session_id: localStorage.getItem('session_id') });

        // ✅ 修复：获取出生日期信息，用于深度分析
        let birthInfo = "";
        if (recogType === 'bazi') {
          // 方法1：从表单元素获取
          const birthDateInput = document.getElementById('birthDate');
          const birthTimeInput = document.getElementById('birthTime');
          const birthPlaceInput = document.getElementById('birthPlace');

          if (birthDateInput && birthDateInput.value) {
            const birthDate = birthDateInput.value;
            const birthTime = birthTimeInput ? birthTimeInput.value : '';
            const birthPlace = birthPlaceInput ? birthPlaceInput.value : '';
            birthInfo = `${birthDate} ${birthTime} ${birthPlace}`.trim();
          } else {
            // 方法2：从localStorage获取
            const analysisData = JSON.parse(localStorage.getItem('user_analysis_data') || '{}');
            const baziAnalysis = analysisData.bazi_analysis;
            if (baziAnalysis && baziAnalysis.birthDate) {
              birthInfo = baziAnalysis.birthDate;
              // 如果有时间信息，也加上
              if (birthTimeInput && birthTimeInput.value) {
                birthInfo += ` ${birthTimeInput.value}`;
              }
              if (birthPlaceInput && birthPlaceInput.value) {
                birthInfo += ` ${birthPlaceInput.value}`;
              }
            }
          }
          debugLog('🔍 深度分析传递的出生日期信息:', birthInfo);
        }

        // 使用sendHK函数，通过北京服务器代理到香港服务器
        const gptData = await sendHK({
          type, image: "", birth: birthInfo, text: analysisText, recog_type: recogType,
          gender: gender, hand: hand
        });
        debugLog('🎯 深度分析请求成功，开始处理结果');
        debugLog('香港服务器响应:', gptData);
        debugLog('响应内容预览:', gptData.reply?.substring(0, 200) + '...');

        // 移除加载提示
        const loadingTip1 = document.getElementById('deepAnalysisLoading');
        if (loadingTip1) {
          loadingTip1.remove();
          debugLog('✅ 已移除深度分析加载提示');
        }

        // 展示gpt & 判断次数消耗
        const is_member = isVip();
        if (getShareCount() === 0 && !is_member) setFreeCount(0);

        // 保存深度分析结果到上下文
        pushTurn('assistant', gptData.reply, { type: 'deep_analysis', stage: 'final' });

        // 深度解读完成，更新分析数据
        // ✅ 修复：等待 soulmateManager 加载完成后再保存
        waitForSoulmateManager((soulmateManager) => {
          if (soulmateManager && typeof soulmateManager.saveAnalysisData === 'function') {
            debugLog('深度解读完成，更新分析数据...');
            ensureLocalAnalysisProfileTargetSelf();
            soulmateManager.saveAnalysisData();
          }
        });

        // 🔧 修复深度解读气泡样式问题
        // 确保深度解读结果有正确的气泡样式和视觉效果
        const formattedContent = formatAnalysisText(gptData.reply);

        debugLog('🎯 深度解读结果显示调试信息:');
        debugLog('- 原始内容长度:', gptData.reply?.length);
        debugLog('- 格式化后内容长度:', formattedContent?.length);
        debugLog('- 是否VIP用户:', is_member);
        debugLog('- 票券数量:', getTicketCount());

        if (is_member) {
          debugLog('🎯 VIP用户路径：使用appendMessage显示深度解读结果');
          appendMessage("assistant", formattedContent, true, false, () => {
            // 打字完成后的回调
            debugLog('🎯 深度解读打字效果完成，准备触发灵魂伴侣弹窗');

            // 验证深度解读结果是否被正确添加到DOM
            timerManager.setTimeout(() => {
              const lastMessage = document.querySelector('#chatBox .msg:last-child');
              if (lastMessage) {
                debugLog('✅ 深度解读结果已添加到DOM:', lastMessage);
                debugLog('- 消息类型:', lastMessage.classList.contains('bubble-ai') ? 'AI消息' : '其他');
                debugLog('- 是否有气泡样式:', lastMessage.querySelector('.bubble'));
              } else {
                console.error('❌ 深度解读结果未找到在DOM中');
              }
            }, 100);

            // ✅ 优化：深度分析完成后触发弹窗（如果用户还没打开过）
            const delayTime = calculateSoulmateDelay(gptData.reply);
            debugLog('🔍 [优化] 深度分析完成，准备触发灵魂伴侣弹窗（VIP用户）');
            triggerSoulmateModal(delayTime, true, true);
          });

          // 标记这是深度分析消息，避免遮罩回调干扰
          timerManager.setTimeout(() => {
            const lastMessage = document.querySelector('#chatBox .msg:last-child');
            if (lastMessage) {
              lastMessage.setAttribute('data-deep-analysis', 'true');
              debugLog('🎯 已标记深度分析消息');
            }
          }, 100);
        } else if (getTicketCount() > 0) {
          debugLog('🎯 票券用户路径：使用appendMessage显示深度解读结果');
          appendMessage("assistant", formattedContent, true, false, () => {
            // ✅ 优化：深度分析完成后触发弹窗（如果用户还没打开过）
            const delayTime = calculateSoulmateDelay(gptData.reply);
            debugLog('🔍 [优化] 深度分析完成，准备触发灵魂伴侣弹窗（票券用户）');
            triggerSoulmateModal(delayTime, true, true);
          });

          // 标记这是深度分析消息
          timerManager.setTimeout(() => {
            const lastMessage = document.querySelector('#chatBox .msg:last-child');
            if (lastMessage) {
              lastMessage.setAttribute('data-deep-analysis', 'true');
              debugLog('🎯 已标记深度分析消息');
            }
          }, 100);
        } else {
          // 无额度：显示分享弹窗
          debugLog('🎯 无额度用户路径：使用appendMessage显示深度解读结果（带遮罩）');
          appendMessage("assistant", formattedContent, true, true, () => {
            // ✅ 优化：无额度用户深度分析完成后也触发弹窗（如果用户还没打开过）
            debugLog('🔍 [优化] 无额度用户：深度分析完成，准备触发灵魂伴侣弹窗');
            debugLog('🔍 深度分析内容长度:', gptData.reply?.length || 0);
            const delayTime = calculateSoulmateDelay(gptData.reply);
            debugLog('🔍 计算的延迟时间:', delayTime);
            triggerSoulmateModal(delayTime, true, true);
            debugLog('✅ [优化] 无额度用户：灵魂伴侣弹窗触发已调用');
          });

          // ✅ 添加深度分析消息标记，保持代码一致性
          timerManager.setTimeout(() => {
            const lastMessage = document.querySelector('#chatBox .msg:last-child');
            if (lastMessage) {
              lastMessage.setAttribute('data-deep-analysis', 'true');
              debugLog('🎯 无额度用户：已标记深度分析消息');
            }
          }, 100);

          // ✅ 修复：深度解读分享应该获得免费次数
          showSharePopup(null, false, null, true); // shouldRewardFreeCount = true
        }

        // ✅ 修复业务逻辑：次数已在sendHK函数中消耗（请求成功后），此处不需要重复消耗

        // 追踪深度解读完成
        fetch('/analytics', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'analysis_complete',
            analysis_type: 'deep_analysis',
            response_time: Date.now() - Date.now()
          })
        }).catch(e => console.error('深度解读完成追踪失败:', e));

      } catch (error) {
        console.error('深度解读失败:', error);

        // 移除加载提示
        const loadingTip2 = document.getElementById('deepAnalysisLoading');
        if (loadingTip2) {
          loadingTip2.remove();
        }

        // 显示错误消息
        appendMessage("assistant", "抱歉，深度解读暂时无法使用，请稍后再试。", true, false);
      }
    };

  }, delayTime);
}

/**
 * 重置深度解读按钮状态
 */
function resetDeepAnalysisButton() {
  deepAnalysisButtonCreated = false;
  lastButtonCreationTime = 0;
  debugLog('深度解读按钮状态已重置');
}

// 导出深度解读功能到全局
window.sendHK = sendHK;
window.createDeepAnalysisButton = createDeepAnalysisButton;
window.resetDeepAnalysisButton = resetDeepAnalysisButton;

// ================= Markdown处理函数 =================
function cleanMarkdown(text) {
  if (!text) return "";

  // 过滤掉画像描述部分，只用于GPT生成图片，前端不显示
  text = text.replace(/【画像描述】[\s\S]*?(?=【|$)/g, "");

  text = text.replace(/<sub[^>]*>[^<]*仅供娱乐参考[^<]*<\/sub>/gi, "");
  text = text.replace(/^---+$/gm, "");
  text = text.replace(/#+/g, m => m.length > 2 ? '##' : m);

  // 检查是否有marked库，如果没有则使用简单的降级处理
  let html;
  if (typeof marked !== 'undefined' && marked.parse) {
    html = marked.parse(text.trim());
  } else {
    // 降级处理：简单的markdown转换
    html = simpleMarkdownToHtml(text.trim());
  }

  // 添加颜色样式，增加视觉层次
  html = html.replace(/<h1>/g, '<h1 style="color:#8b5e00;font-weight:bold;">')
    .replace(/<h2>/g, '<h2 style="color:#a67a18;font-weight:bold;">')
    .replace(/<h3>/g, '<h3 style="color:#b69526;font-weight:bold;">')
    .replace(/<h4>/g, '<h4 style="color:#c19c54;font-weight:bold;">')
    .replace(/<h5>/g, '<h5 style="color:#d2a24c;font-weight:bold;">')
    .replace(/<h6>/g, '<h6 style="color:#e0b86b;font-weight:bold;">')
    .replace(/<strong>/g, '<strong style="color:#7a4c15;font-weight:bold;">')
    .replace(/<em>/g, '<em style="color:#8b5e00;font-style:italic;">')
    .replace(/<ul>/g, '<ul style="padding-left:1.2em;">')
    .replace(/<ol>/g, '<ol style="padding-left:1.2em;">')
    .replace(/<li>/g, '<li style="color:#5b461f;margin-bottom:0.3em;">');

  return html;
}

// 简单的markdown转换降级处理
function simpleMarkdownToHtml(text) {
  if (!text) return "";

  let html = text;

  // 处理标题
  html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
  html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
  html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');

  // 处理粗体和斜体
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');

  // 处理段落
  html = html.replace(/\n\n/g, '</p><p>');
  html = '<p>' + html + '</p>';

  // 处理换行
  html = html.replace(/\n/g, '<br>');

  return html;
}

// ================= 智能推荐系统 =================

// 推荐内容配置
const RECOMMENDATIONS = {
  '测感情': { icon: '🌸', category: 'emotion', weight: 1 },
  '测财运': { icon: '💰', category: 'fortune', weight: 1 },
  '测学业': { icon: '📚', category: 'study', weight: 1 },
  '测健康': { icon: '🌱', category: 'health', weight: 1 },
  '测事业': { icon: '💼', category: 'career', weight: 1 },
  '测婚姻': { icon: '💍', category: 'marriage', weight: 1 }
};

// 统计推荐使用情况
function trackRecommendationUsage(category) {
  const usage = JSON.parse(localStorage.getItem('recommendation_usage') || '{}');
  usage[category] = (usage[category] || 0) + 1;
  localStorage.setItem('recommendation_usage', JSON.stringify(usage));

  // 记录最近使用时间
  const recentUsage = JSON.parse(localStorage.getItem('recent_usage') || '{}');
  recentUsage[category] = Date.now();
  localStorage.setItem('recent_usage', JSON.stringify(recentUsage));

  debugLog('📊 推荐使用统计更新:', category, usage[category]);
}

// 智能推荐算法
function getDynamicRecommendations() {
  const usage = JSON.parse(localStorage.getItem('recommendation_usage') || '{}');
  const recentUsage = JSON.parse(localStorage.getItem('recent_usage') || '{}');
  const now = Date.now();

  const recommendations = Object.entries(RECOMMENDATIONS)
    .map(([text, config]) => {
      const category = config.category;
      const totalUsage = usage[category] || 0;
      const lastUsed = recentUsage[category] || 0;
      const daysSinceLastUse = (now - lastUsed) / (1000 * 60 * 60 * 24);

      // 综合评分：总使用量 + 最近使用 + 基础权重
      const score = totalUsage * 0.6 + (daysSinceLastUse < 7 ? 0.3 : 0) + config.weight * 0.1;

      return { text, config, score };
    })
    .sort((a, b) => b.score - a.score) // 按评分降序
    .slice(0, 4);

  debugLog('🎯 智能推荐结果:', recommendations);
  return recommendations;
}

// 渲染动态推荐按钮
function renderRecommendations() {
  const recommendations = getDynamicRecommendations();
  const container = document.getElementById('recommendations');

  if (!container) {
    debugLog('⚠️ 推荐容器不存在，跳过渲染');
    return;
  }

  container.innerHTML = recommendations.map(({ text, config }) => `
    <button class="recommendation-btn" data-category="${config.category}">
      <span class="icon">${config.icon}</span>
      <span class="text">${text}</span>
    </button>
  `).join('');

  // 绑定点击事件
  container.querySelectorAll('.recommendation-btn').forEach(btn => {
    btn.onclick = () => {
      const category = btn.dataset.category;
      trackRecommendationUsage(category);
      startRecommendationAnalysis(category);
    };
  });

  debugLog('🎯 推荐按钮渲染完成');
}

// 启动推荐分析
function startRecommendationAnalysis(category) {
  debugLog('🎯 启动推荐分析:', category);

  // 根据类别启动对应的分析功能
  switch (category) {
    case 'emotion':
      startEmotionAnalysis();
      break;
    case 'fortune':
      startFortuneAnalysis();
      break;
    case 'study':
      startStudyAnalysis();
      break;
    case 'health':
      startHealthAnalysis();
      break;
    case 'career':
      startCareerAnalysis();
      break;
    case 'marriage':
      startMarriageAnalysis();
      break;
    default:
      debugLog('未知推荐类别:', category);
  }
}

// 分析功能实现
function startEmotionAnalysis() {
  debugLog('🌸 启动感情分析');
  showAnalysisModal('感情分析', '请上传您的照片进行感情运势分析');
}
function startFortuneAnalysis() {
  debugLog('💰 启动财运分析');
  showAnalysisModal('财运分析', '请上传您的照片进行财运分析');
}

function startStudyAnalysis() {
  debugLog('📚 启动学业分析');
  showAnalysisModal('学业分析', '请上传您的照片进行学业运势分析');
}

function startHealthAnalysis() {
  debugLog('🌱 启动健康分析');
  showAnalysisModal('健康分析', '请上传您的照片进行健康运势分析');
}

function startCareerAnalysis() {
  debugLog('💼 启动事业分析');
  showAnalysisModal('事业分析', '请上传您的照片进行事业运势分析');
}

function startMarriageAnalysis() {
  debugLog('💍 启动婚姻分析');
  showAnalysisModal('婚姻分析', '请上传您的照片进行婚姻运势分析');
}

// 显示分析模态框
function showAnalysisModal(title, description) {
  // 创建模态框
  const modal = document.createElement('div');
  modal.className = 'analysis-modal';
  modal.innerHTML = `
    <div class="modal-content">
      <div class="modal-header">
        <h3>${title}</h3>
        <button class="close-btn">&times;</button>
      </div>
      <div class="modal-body">
        <p>${description}</p>
        <div class="upload-area">
          <input type="file" id="analysisImage" accept="image/*" style="display: none;">
          <button id="selectImageBtn" class="upload-btn">选择图片</button>
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(modal);

  // 绑定事件
  modal.querySelector('.close-btn').onclick = () => modal.remove();
  modal.querySelector('#selectImageBtn').onclick = () => {
    document.getElementById('analysisImage').click();
  };

  // 图片选择处理
  document.getElementById('analysisImage').onchange = (e) => {
    const file = e.target.files[0];
    if (file) {
      debugLog('📸 选择图片:', file.name);
      // 这里可以调用具体的分析功能
      modal.remove();
    }
  };
}

// 导出推荐功能
window.renderRecommendations = renderRecommendations;
window.trackRecommendationUsage = trackRecommendationUsage;
window.getDynamicRecommendations = getDynamicRecommendations;

/**
 * 调整每日运势按钮位置
 * 将按钮从提交按钮下方移动到追问输入框下方
 */
function relocateFortuneBtn() {
  debugLog('🎯 开始调整每日运势按钮位置...');

  // 查找每日运势按钮
  const fortuneBtn = document.querySelector('#fortuneBtn') ||
    document.querySelector('[onclick*="drawFortune"]') ||
    document.querySelector('button[onclick*="drawFortune"]');

  // 查找追问输入框
  const followupInput = document.querySelector('#followupInput') ||
    document.querySelector('input[placeholder*="追问"]') ||
    document.querySelector('input[placeholder*="继续"]');

  if (!fortuneBtn) {
    debugLog('⚠️ 未找到每日运势按钮');
    return;
  }

  if (!followupInput) {
    debugLog('⚠️ 未找到追问输入框，按钮保持原位置');
    return;
  }

  // 查找追问输入框的父容器
  const followupContainer = followupInput.closest('.followup-container') ||
    followupInput.closest('.input-group') ||
    followupInput.parentElement;

  if (!followupContainer) {
    debugLog('⚠️ 未找到追问输入框容器');
    return;
  }

  try {
    // 从当前位置移除按钮
    if (fortuneBtn.parentElement) {
      fortuneBtn.parentElement.removeChild(fortuneBtn);
    }

    // 将按钮添加到追问输入框容器下方
    followupContainer.appendChild(fortuneBtn);

    // 确保按钮样式正确
    fortuneBtn.style.marginTop = '10px';
    fortuneBtn.style.marginBottom = '10px';
    fortuneBtn.style.display = 'block';
    fortuneBtn.style.width = '100%';

    debugLog('✅ 每日运势按钮位置调整成功');

    // 添加成功提示
    if (window.showSuccessToast) {
      showSuccessToast('🎯 每日运势按钮已移动到追问输入框下方');
    }

  } catch (error) {
    console.error('❌ 调整每日运势按钮位置失败:', error);

    // 重试机制
    timerManager.setTimeout(() => {
      debugLog('🔄 重试调整每日运势按钮位置...');
      relocateFortuneBtn();
    }, 1000);
  }
}

// 导出每日运势按钮位置调整功能
window.relocateFortuneBtn = relocateFortuneBtn;

/* ================= 前世画像功能 ================= */
/**
 * 添加前世画像按钮
 * @param {string} baziInfo - 八字信息（出生日期、时间、地点）
 * @param {string} gender - 性别（male/female）
 */
function addPastLifeButton(baziInfo, gender) {
  const chatBox = document.getElementById('chatBox');
  if (!chatBox) return;

  // 检查是否已经存在前世画像按钮
  const existingBtn = chatBox.querySelector('[data-past-life-btn="true"]');
  if (existingBtn) {
    debugLog('⚠️ 前世画像按钮已存在，跳过创建');
    return;
  }

  const btnWrap = document.createElement('div');
  btnWrap.style.textAlign = 'center';
  btnWrap.style.margin = '12px 0'; // ✅ 修复：统一为12px，与引导面板的margin-bottom一致
  btnWrap.style.marginTop = '12px'; // ✅ 修复：确保与引导面板有足够间距
  btnWrap.style.marginBottom = '12px';

  const pastLifeBtn = document.createElement('button');
  pastLifeBtn.setAttribute('data-past-life-btn', 'true');
  pastLifeBtn.setAttribute('aria-label', '获取前世画像'); // ✅ 修复：添加aria-label
  pastLifeBtn.textContent = "🔮 获取前世画像";
  // ✅ 修复：统一padding为 4px 8px，与引导面板内的按钮一致
  pastLifeBtn.style.cssText = "background:linear-gradient(to right,#e879f9,#a855f7);color:#fff;font-weight:bold;font-size:0.85rem;padding:4px 8px !important;border-radius:6px;box-shadow:0 2px 8px #e879f955;border:none;cursor:pointer;margin-top:0;margin-bottom:0;transition:all 0.2s;box-sizing:border-box !important;";

  btnWrap.appendChild(pastLifeBtn);
  chatBox.appendChild(btnWrap);
  stickBottom();

  pastLifeBtn.onclick = async function () {
    pastLifeBtn.disabled = true;
    pastLifeBtn.textContent = "生成中...";

    // 显示加载动画
    const waitingTip = document.createElement('div');
    waitingTip.innerHTML = "前世画像生成中<span class='typing-dots'><i></i><i></i><i></i></span>，请稍候...";
    waitingTip.style.cssText = "margin-top:8px;color:#a855f7;font-size:1.1rem;font-family:'Noto Serif SC',serif;";
    pastLifeBtn.after(waitingTip);

    try {
      // 获取用户时区
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // ✅ 使用ApiClient调用/trigger端点生成前世画像
      const requestData = {
        session_id: getOrInitSessionId(),
        type: "past_life",
        birth: baziInfo,
        text: baziInfo,
        gender: gender,
        user_timezone: userTimezone
      };

      let result;
      if (window.apiClient) {
        // ✅ 修复：为前世画像请求增加超时时间（60秒）
        const originalTimeout = window.apiClient.requestTimeout;
        window.apiClient.requestTimeout = 60000; // 60秒超时

        try {
          result = await window.apiClient.bjRequest('/trigger', requestData);
          // ✅ 恢复原始超时时间
          window.apiClient.requestTimeout = originalTimeout;
        } catch (error) {
          // ✅ 恢复原始超时时间（即使出错也要恢复）
          window.apiClient.requestTimeout = originalTimeout;
          throw error;
        }
      } else {
        // 降级到直接fetch
        console.warn('⚠️ ApiClient不可用，使用直接fetch调用');
        const response = await fetch(`${CONFIG.BJ_API_BASE}/trigger`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestData)
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        result = await response.json();
      }

      if (result.status === 'done' && result.reply) {
        // 生成前世画像卡片
        const pastLifeCard = await createPastLifeCard(result.reply);
        appendMessage("assistant", pastLifeCard, true, false);

        // 添加分享按钮
        addPastLifeShareButton(result.reply);

        // ✅ 新增：保存前世画像数据到 bazi_analysis.past_life
        try {
          const analysisData = CodeUtils?.safeGetStorage('user_analysis_data', {}) || {};
          if (!analysisData.bazi_analysis) {
            analysisData.bazi_analysis = {};
          }

          // 提取历史人物名称和画像描述
          let historicalFigure = null;
          let portraitUrl = null;

          // 如果 historical_portraits.js 已加载，提取信息
          if (typeof extractHistoricalFigure !== 'undefined' && typeof getHistoricalPortrait !== 'undefined') {
            historicalFigure = extractHistoricalFigure(result.reply);
            const portraitDescription = extractPortraitDescription ? extractPortraitDescription(result.reply) : '';
            if (historicalFigure) {
              // 异步获取画像URL（注意：这里是异步的，可能需要等待）
              getHistoricalPortrait(historicalFigure, portraitDescription).then(url => {
                // ✅ 修复：在异步回调中重新读取数据，确保数据一致性
                const currentAnalysisData = CodeUtils?.safeGetStorage('user_analysis_data', {}) || {};
                if (!currentAnalysisData.bazi_analysis) {
                  currentAnalysisData.bazi_analysis = {};
                }
                // 更新保存的数据
                currentAnalysisData.bazi_analysis.past_life = {
                  has_data: true,
                  content: result.reply.substring(0, 1000), // 限制长度
                  historicalFigure: historicalFigure,
                  portraitUrl: url,
                  generated_time: new Date().toISOString()
                };
                CodeUtils?.safeSetStorage('user_analysis_data', currentAnalysisData);
                debugLog('✅ 前世画像数据已保存', { historicalFigure, portraitUrl: url });
              }).catch(err => {
                console.error('获取画像URL失败:', err);
                // ✅ 修复：在异步回调中重新读取数据，确保数据一致性
                const currentAnalysisData = CodeUtils?.safeGetStorage('user_analysis_data', {}) || {};
                if (!currentAnalysisData.bazi_analysis) {
                  currentAnalysisData.bazi_analysis = {};
                }
                // 即使获取画像URL失败，也保存文本数据
                currentAnalysisData.bazi_analysis.past_life = {
                  has_data: true,
                  content: result.reply.substring(0, 1000),
                  historicalFigure: historicalFigure,
                  portraitUrl: null,
                  generated_time: new Date().toISOString()
                };
                CodeUtils?.safeSetStorage('user_analysis_data', currentAnalysisData);
              });
            } else {
              // 如果没有提取到历史人物，只保存文本内容
              analysisData.bazi_analysis.past_life = {
                has_data: true,
                content: result.reply.substring(0, 1000),
                historicalFigure: null,
                portraitUrl: null,
                generated_time: new Date().toISOString()
              };
              CodeUtils?.safeSetStorage('user_analysis_data', analysisData);
            }
          } else {
            // 如果 historical_portraits.js 未加载，只保存文本内容
            analysisData.bazi_analysis.past_life = {
              has_data: true,
              content: result.reply.substring(0, 1000),
              historicalFigure: null,
              portraitUrl: null,
              generated_time: new Date().toISOString()
            };
            CodeUtils?.safeSetStorage('user_analysis_data', analysisData);
            debugLog('⚠️ historical_portraits.js 未加载，只保存文本内容');
          }
        } catch (error) {
          console.error('保存前世画像数据失败:', error);
        }

        // 保存分析数据到灵魂伴侣系统
        if (window.soulmateManager && typeof window.soulmateManager.saveAnalysisData === 'function') {
          debugLog('前世画像生成完成，保存分析数据...');
          ensureLocalAnalysisProfileTargetSelf();
          window.soulmateManager.saveAnalysisData();
        }

        // 追踪分析完成
        if (window.analytics && typeof window.analytics.trackEvent === 'function') {
          window.analytics.trackEvent('past_life_generated', { success: true });
        }
      } else {
        appendMessage("assistant", "❌ 前世画像生成失败，请稍后再试。");

        // 追踪前世画像生成失败
        if (window.analytics && typeof window.analytics.trackEvent === 'function') {
          window.analytics.trackEvent('past_life_generated', { success: false, error_message: '生成失败' });
        }
      }

    } catch (error) {
      console.error('前世画像生成错误:', error);
      let errorMessage = "❌ 网络异常，请稍后再试。";

      if (error.message.includes('Failed to fetch')) {
        errorMessage = "❌ 服务器连接失败，请检查网络或稍后再试。";
      } else if (error.message.includes('timeout')) {
        errorMessage = "❌ 请求超时，请稍后再试。";
      } else if (error.message.includes('HTTP')) {
        errorMessage = `❌ 服务器错误：${error.message}`;
      }

      appendMessage("assistant", errorMessage);

      // 追踪前世画像生成异常
      if (window.analytics && typeof window.analytics.trackEvent === 'function') {
        window.analytics.trackEvent('past_life_generated', { success: false, error_message: error.message || '未知错误' });
      }
    } finally {
      pastLifeBtn.remove();
      if (waitingTip && waitingTip.parentNode) {
        waitingTip.remove();
      }
    }
  };
}

/**
 * 创建前世画像卡片
 * @param {string} reply - AI回复内容
 * @returns {Promise<string>} 卡片HTML
 */
async function createPastLifeCard(reply) {
  // ✅ 修复：如果 historical_portraits.js 未加载，先尝试加载它
  if (typeof extractHistoricalFigure === 'undefined' || typeof extractPortraitDescription === 'undefined' || typeof getHistoricalPortrait === 'undefined') {
    debugLog('📦 historical_portraits.js 未加载，开始动态加载...');
    if (typeof window.loadHistoricalPortraits === 'function') {
      try {
        await window.loadHistoricalPortraits();
        debugLog('✅ historical_portraits.js 加载成功');
      } catch (error) {
        console.error('❌ historical_portraits.js 加载失败:', error);
        // 降级到简化版本
        console.warn('⚠️ historical_portraits.js 未加载，使用简化版本');
        return `
        <div class="past-life-card" style="
          background: rgba(26, 26, 46, 0.8) !important;
          backdrop-filter: blur(20px) !important;
          -webkit-backdrop-filter: blur(20px) !important;
          border: 2px solid rgba(102, 126, 234, 0.5) !important;
          border-radius: 20px;
          padding: 20px;
          margin: 15px 0;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4),
                      0 0 0 1px rgba(255, 255, 255, 0.1) inset,
                      0 0 40px rgba(102, 126, 234, 0.3) !important;
          text-align: center;
        ">
          <div style="font-size: 24px; margin-bottom: 10px;">🔮</div>
          <h3 style="color: rgba(255, 255, 255, 0.95) !important; font-size: 20px; margin-bottom: 15px; font-weight: bold; text-shadow: 0 2px 8px rgba(102, 126, 234, 0.5) !important;">
            我的前世画像
          </h3>
          <div style="background: rgba(255, 255, 255, 0.1) !important; backdrop-filter: blur(10px) !important; -webkit-backdrop-filter: blur(10px) !important; border: 1px solid rgba(102, 126, 234, 0.3) !important; border-radius: 15px; padding: 15px; margin-bottom: 15px; text-align: left; color: rgba(255, 255, 255, 0.95) !important;">
            ${cleanMarkdown(reply)}
          </div>
        </div>
        `;
      }
    } else {
      console.warn('⚠️ historical_portraits.js 未加载，使用简化版本（loadHistoricalPortraits 函数不存在）');
      return `
      <div class="past-life-card" style="
        background: rgba(26, 26, 46, 0.8) !important;
        backdrop-filter: blur(20px) !important;
        -webkit-backdrop-filter: blur(20px) !important;
        border: 2px solid rgba(102, 126, 234, 0.5) !important;
        border-radius: 20px;
        padding: 20px;
        margin: 15px 0;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4),
                    0 0 0 1px rgba(255, 255, 255, 0.1) inset,
                    0 0 40px rgba(102, 126, 234, 0.3) !important;
        text-align: center;
      ">
        <div style="font-size: 24px; margin-bottom: 10px;">🔮</div>
        <h3 style="color: rgba(255, 255, 255, 0.95) !important; font-size: 20px; margin-bottom: 15px; font-weight: bold; text-shadow: 0 2px 8px rgba(102, 126, 234, 0.5) !important;">
          我的前世画像
        </h3>
        <div style="background: rgba(255, 255, 255, 0.1) !important; backdrop-filter: blur(10px) !important; -webkit-backdrop-filter: blur(10px) !important; border: 1px solid rgba(102, 126, 234, 0.3) !important; border-radius: 15px; padding: 15px; margin-bottom: 15px; text-align: left; color: rgba(255, 255, 255, 0.95) !important;">
          ${cleanMarkdown(reply)}
        </div>
      </div>
      `;
    }
  }

  // 提取历史人物名称和画像描述
  const historicalFigure = extractHistoricalFigure(reply);
  const portraitDescription = extractPortraitDescription(reply);

  debugLog('创建前世画像卡片:', historicalFigure);

  // 异步获取画像URL
  const portraitUrl = await getHistoricalPortrait(historicalFigure, portraitDescription);

  debugLog('聊天框使用的画像URL:', portraitUrl);

  return `
  <div class="past-life-card" style="
    background: rgba(26, 26, 46, 0.8) !important;
    backdrop-filter: blur(20px) !important;
    -webkit-backdrop-filter: blur(20px) !important;
    border: 2px solid rgba(102, 126, 234, 0.5) !important;
    border-radius: 20px;
    padding: 20px;
    margin: 15px 0;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4),
                0 0 0 1px rgba(255, 255, 255, 0.1) inset,
                0 0 40px rgba(102, 126, 234, 0.3) !important;
    text-align: center;
  ">
    <div style="font-size: 24px; margin-bottom: 10px;">🔮</div>
    <h3 style="color: rgba(255, 255, 255, 0.95) !important; font-size: 20px; margin-bottom: 15px; font-weight: bold; text-shadow: 0 2px 8px rgba(102, 126, 234, 0.5) !important;">
      我的前世画像
    </h3>
    ${portraitUrl ? `
    <div style="margin-bottom: 15px;">
             <img src="${portraitUrl}" alt="${historicalFigure || '前世画像'}" 
                  style="width: 150px; height: 150px; border-radius: 50%; 
                         border: 3px solid rgba(102, 126, 234, 0.6) !important; 
                         object-fit: cover; 
                         box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4),
                                     0 0 0 2px rgba(255, 255, 255, 0.1) inset !important;"
                         onerror="this.onerror=null; this.src='/static/uploads/portraits/default.svg'; this.style.display='block';">
      ${historicalFigure ? `<div style="margin-top: 8px; color: rgba(255, 255, 255, 0.95) !important; font-weight: bold; font-size: 16px; text-shadow: 0 2px 8px rgba(102, 126, 234, 0.5) !important;">${historicalFigure}</div>` : ''}
    </div>
    ` : ''}
    <div style="background: rgba(255, 255, 255, 0.1) !important; backdrop-filter: blur(10px) !important; -webkit-backdrop-filter: blur(10px) !important; border: 1px solid rgba(102, 126, 234, 0.3) !important; border-radius: 15px; padding: 15px; margin-bottom: 15px; text-align: left; color: rgba(255, 255, 255, 0.95) !important;">
      ${cleanMarkdown(reply)}
    </div>
  </div>
  `;
}

/**
 * 添加前世画像分享按钮
 * @param {string} reply - AI回复内容
 */
function addPastLifeShareButton(reply) {
  const chatBox = document.getElementById('chatBox');
  if (!chatBox) return;

  // 检查是否已经存在分享按钮
  const existingBtn = chatBox.querySelector('[data-past-life-share-btn="true"]');
  if (existingBtn) {
    debugLog('⚠️ 前世画像分享按钮已存在，跳过创建');
    return;
  }

  const shareBtnWrap = document.createElement('div');
  shareBtnWrap.style.textAlign = 'center';
  shareBtnWrap.style.margin = '10px 0';

  const shareBtn = document.createElement('button');
  shareBtn.setAttribute('data-past-life-share-btn', 'true');
  shareBtn.textContent = "📸 分享我的前世";
  shareBtn.style.cssText = "background:linear-gradient(to right,#fbbf24,#f59e0b);color:#92400e;font-weight:bold;font-size:1rem;padding:6px 20px;border-radius:12px;box-shadow:0 2px 6px #fbbf2455;border:none;cursor:pointer;transition:all 0.2s;";

  shareBtnWrap.appendChild(shareBtn);
  chatBox.appendChild(shareBtnWrap);
  stickBottom();

  shareBtn.onclick = async function () {
    // ✅ 修复：检查是否已达到每日分享上限
    const shareCount = getShareCount();
    const limit = DAILY_SHARE_LIMIT; // 2

    if (shareCount >= limit) {
      if (typeof CodeUtils !== 'undefined' && CodeUtils.showToast) {
        CodeUtils.showToast('今日分享次数已达上限，请明天再试', 'warning', 3000);
      } else {
        alert('今日分享次数已达上限，请明天再试');
      }
      return;
    }

    // 生成分享卡片
    await generatePastLifeShareCard(reply);

    // ✅ 修复：分享成功后增加分享次数和免费次数
    setShareCount(shareCount + 1);
    setFreeCount(getFreeCount() + 1);
    updateCountBanner();

    // ✅ 修复：显示分享成功提示，明确告知用户获得了1次免费分析
    if (typeof CodeUtils !== 'undefined' && CodeUtils.showToast) {
      CodeUtils.showToast('🎉 分享成功！已获得1次免费分析，可以立即使用', 'success', 3000);
    } else {
      alert('🎉 分享成功！已获得1次免费分析，可以立即使用');
    }
  };
}

/**
 * 生成前世画像分享卡片
 * @param {string} reply - AI回复内容
 */
async function generatePastLifeShareCard(reply) {
  // ✅ 优化：如果 html2canvas 未加载，先尝试加载它
  if (typeof html2canvas === 'undefined') {
    debugLog('📦 html2canvas 未加载，开始动态加载...');
    if (typeof window.loadHtml2Canvas === 'function') {
      try {
        await window.loadHtml2Canvas();
        debugLog('✅ html2canvas 加载成功');
      } catch (error) {
        console.error('❌ html2canvas 加载失败:', error);
        if (typeof CodeUtils !== 'undefined' && CodeUtils.showToast) {
          CodeUtils.showToast('分享功能暂时不可用，请刷新页面重试', 'error', 3000);
        }
        return;
      }
    } else {
      console.error('❌ loadHtml2Canvas 函数不存在，无法加载 html2canvas');
      if (typeof CodeUtils !== 'undefined' && CodeUtils.showToast) {
        CodeUtils.showToast('分享功能暂时不可用', 'error', 2000);
      }
      return;
    }
  }

  // ✅ 修复：如果 historical_portraits.js 未加载，先尝试加载它
  if (typeof extractHistoricalFigure === 'undefined' || typeof extractPortraitDescription === 'undefined' || typeof getHistoricalPortrait === 'undefined') {
    debugLog('📦 historical_portraits.js 未加载，开始动态加载...');
    if (typeof window.loadHistoricalPortraits === 'function') {
      try {
        await window.loadHistoricalPortraits();
        debugLog('✅ historical_portraits.js 加载成功');
      } catch (error) {
        console.error('❌ historical_portraits.js 加载失败:', error);
        if (typeof CodeUtils !== 'undefined' && CodeUtils.showToast) {
          CodeUtils.showToast('分享功能暂时不可用，请刷新页面重试', 'error', 3000);
        }
        return;
      }
    } else {
      console.error('❌ loadHistoricalPortraits 函数不存在，无法加载 historical_portraits.js');
      if (typeof CodeUtils !== 'undefined' && CodeUtils.showToast) {
        CodeUtils.showToast('分享功能暂时不可用', 'error', 2000);
      }
      return;
    }
  }

  // 提取历史人物名称和画像描述
  const historicalFigure = extractHistoricalFigure(reply);
  const portraitDescription = extractPortraitDescription(reply);
  const portraitUrl = await getHistoricalPortrait(historicalFigure, portraitDescription);

  // 创建分享卡片HTML
  const shareCardHTML = `
  <div id="pastLifeShareCard" style="
    position: fixed; left: -9999px; top: -9999px; z-index: -1;
    width: 340px; background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%); border-radius: 20px; padding: 20px;
    box-shadow: 0 18px 48px rgba(0,0,0,0.5);
    font-family: 'Noto Serif SC', serif;
    border: 2px solid rgba(102, 126, 234, 0.5);
  ">
    <!-- 顶部品牌区域 -->
    <div style="text-align: center; margin-bottom: 15px;">
      <img src="/static/uploads/logo.png" alt="玄知相术logo" style="width:48px;height:48px;border-radius:10px;box-shadow:0 2px 8px rgba(102, 126, 234, 0.4);margin:0 auto 8px;">
      <div style="font-size:14px; color:rgba(255, 255, 255, 0.9); font-weight:600; text-shadow: 0 2px 8px rgba(102, 126, 234, 0.5);">玄知相术 · 倪大师</div>
    </div>
    
    <!-- 前世画像内容 -->
    <div style="text-align: center; margin-bottom: 20px;">
      <div style="font-size: 24px; margin-bottom: 10px;">🔮</div>
      <h2 style="color: rgba(255, 255, 255, 0.95); font-size: 22px; margin-bottom: 15px; font-weight: bold; text-shadow: 0 2px 8px rgba(102, 126, 234, 0.5);">
        我的前世画像
      </h2>
      ${portraitUrl ? `
      <div style="margin-bottom: 15px;">
               <img src="${portraitUrl}" alt="${historicalFigure || '前世画像'}" 
                    style="width: 120px; height: 120px; border-radius: 50%; 
                           border: 3px solid rgba(102, 126, 234, 0.6); object-fit: cover; 
                           box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4),
                                       0 0 0 2px rgba(255, 255, 255, 0.1) inset;"
                           onerror="this.onerror=null; this.src='/static/uploads/portraits/default.svg'; this.style.display='block';">
        ${historicalFigure ? `<div style="margin-top: 6px; color: rgba(255, 255, 255, 0.95); font-weight: bold; font-size: 14px; text-shadow: 0 2px 8px rgba(102, 126, 234, 0.5);">${historicalFigure}</div>` : ''}
      </div>
      ` : ''}
      <div style="background: rgba(255, 255, 255, 0.1); backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px); border: 1px solid rgba(102, 126, 234, 0.3); border-radius: 15px; padding: 15px; margin-bottom: 15px; text-align: left; font-size: 14px; line-height: 1.4; color: rgba(255, 255, 255, 0.95);">
        ${cleanMarkdown(reply)}
      </div>
    </div>
    
    <!-- 底部二维码区域 - 水平居中 -->
    <div style="text-align: center; margin-top: 20px;">
      <img src="/static/uploads/qrcode.png" alt="扫码体验测算" style="width:60px;height:60px;border-radius:10px;box-shadow:0 2px 10px rgba(102, 126, 234, 0.4);margin:0 auto 6px auto;display:block;border: 1px solid rgba(102, 126, 234, 0.3);">
      <div style="font-size:13px; color:rgba(255, 255, 255, 0.9); font-weight:500;text-align:center;text-shadow: 0 1px 4px rgba(102, 126, 234, 0.5);">扫码体验测算</div>
      <div style="font-size:11px; color:rgba(255, 255, 255, 0.7); margin-top:2px;text-align:center;">mywork.wiki</div>
    </div>
  </div>
  `;

  // 添加到页面
  document.body.insertAdjacentHTML('beforeend', shareCardHTML);

  // 生成图片
  const cardElement = document.getElementById('pastLifeShareCard');
  if (!cardElement) {
    console.error('❌ 无法找到分享卡片元素');
    return;
  }

  cardElement.style.display = 'block';

  // 等待图片加载完成
  const images = cardElement.querySelectorAll('img');
  let loadedImages = 0;
  const totalImages = images.length;

  debugLog('找到图片数量:', totalImages);

  if (totalImages === 0) {
    debugLog('没有图片，直接生成canvas');
    generateCanvas();
  } else {
    images.forEach((img, index) => {
      debugLog(`图片${index + 1}:`, img.src, 'complete:', img.complete);

      if (img.complete) {
        loadedImages++;
        debugLog(`图片${index + 1}已加载完成`);
      } else {
        img.onload = () => {
          loadedImages++;
          debugLog(`图片${index + 1}加载成功`);
          if (loadedImages === totalImages) {
            debugLog('所有图片加载完成，开始生成canvas');
            generateCanvas();
          }
        };
        img.onerror = (error) => {
          loadedImages++;
          console.error(`图片${index + 1}加载失败:`, img.src, error);
          if (loadedImages === totalImages) {
            debugLog('所有图片处理完成（包含失败），开始生成canvas');
            generateCanvas();
          }
        };
      }
    });

    if (loadedImages === totalImages) {
      debugLog('所有图片已加载完成，开始生成canvas');
      generateCanvas();
    }
  }

  function generateCanvas() {
    debugLog('开始生成分享图片...', cardElement);

    // ✅ 双重保险：再次检查 html2canvas 是否已加载（理论上不应该触发，因为函数开始时已加载）
    if (typeof html2canvas === 'undefined') {
      console.error('❌ html2canvas 仍未加载，无法生成分享图片');
      cardElement.remove();
      if (typeof CodeUtils !== 'undefined' && CodeUtils.showToast) {
        CodeUtils.showToast('分享功能暂时不可用，请刷新页面重试', 'error', 3000);
      }
      return;
    }

    // 使用更稳定的html2canvas配置
    html2canvas(cardElement, {
      backgroundColor: "#fff",
      scale: 2.0,  // 提高分辨率，增强文字清晰度
      useCORS: true,
      allowTaint: true,
      timeout: 20000, // 增加超时时间，确保图片完全生成
      width: 340,
      height: cardElement.scrollHeight,
      logging: true, // 启用日志
      onclone: function (clonedDoc) {
        debugLog('html2canvas克隆文档完成');
      }
    }).then(canvas => {
      debugLog('html2canvas成功生成图片', canvas);
      debugLog('Canvas尺寸:', canvas.width, 'x', canvas.height);

      // 生成数据URL
      const dataURL = canvas.toDataURL('image/png');
      debugLog('生成的数据URL长度:', dataURL.length);
      debugLog('数据URL前缀:', dataURL.substring(0, 50) + '...');

      cardElement.remove();

      // 显示分享弹窗
      const imgWrap = document.createElement("div");
      imgWrap.style.cssText = "position:fixed;z-index:9999;left:0;top:0;width:100vw;height:100vh;background:rgba(0,0,0,0.85);display:flex;flex-direction:column;align-items:center;justify-content:center;";
      imgWrap.innerHTML = `
        <div style="background:#fff;padding:16px 8px 8px 8px;border-radius:16px;box-shadow:0 4px 24px #999;">
          <img src="${dataURL}" style="max-width:90vw;max-height:65vh;border-radius:16px;" onload="debugLog('分享图片加载成功')" onerror="console.error('分享图片加载失败')">
        </div>
        <div style="color:#fff;font-size:1.15rem;text-align:center;margin-top:14px;">
          长按图片保存，分享到朋友圈或小红书
        </div>
        <div style="margin-top:16px;">
          <button id="closePastLifeShare" style="padding:7px 32px;border:none;border-radius:6px;background:#a855f7;color:#fff;font-size:1.1rem;">关闭</button>
        </div>
      `;
      document.body.appendChild(imgWrap);

      document.getElementById("closePastLifeShare").onclick = () => {
        imgWrap.remove();
      };
    }).catch(error => {
      console.error('生成分享图片失败:', error);
      console.error('错误详情:', {
        message: error.message,
        stack: error.stack,
        cardElement: cardElement,
        cardHTML: cardElement.outerHTML
      });
      cardElement.remove();

      // 降级处理：显示文字分享
      const textShare = document.createElement("div");
      textShare.style.cssText = "position:fixed;z-index:9999;left:0;top:0;width:100vw;height:100vh;background:rgba(0,0,0,0.85);display:flex;flex-direction:column;align-items:center;justify-content:center;";
      textShare.innerHTML = `
        <div style="background:#fff;padding:20px;border-radius:16px;box-shadow:0 4px 24px #999;max-width:90vw;max-height:80vh;overflow-y:auto;">
          <h3 style="color:#7c3aed;text-align:center;margin-bottom:15px;">我的前世画像</h3>
          <div style="white-space:pre-wrap;line-height:1.6;">${cleanMarkdown(reply)}</div>
          <div style="text-align:center;margin-top:15px;color:#666;">
            <div>扫码体验测算</div>
            <div style="font-size:12px;">mywork.wiki</div>
          </div>
        </div>
        <div style="color:#fff;font-size:1.15rem;text-align:center;margin-top:14px;">
          复制文字内容分享到朋友圈
        </div>
        <div style="margin-top:16px;">
          <button id="closePastLifeShare" style="padding:7px 32px;border:none;border-radius:6px;background:#a855f7;color:#fff;font-size:1.1rem;">关闭</button>
        </div>
      `;
      document.body.appendChild(textShare);

      document.getElementById("closePastLifeShare").onclick = () => {
        textShare.remove();
      };
    });
  }
}

// ================= 快速追问动态推荐 =================

/**
 * 从对话历史中提取用户消息，生成个性化推荐
 */
function getPersonalizedQuickButtons() {
  const ctx = loadCtx(); // 获取对话历史
  const userMessages = ctx.filter(turn => turn.role === 'user' && turn.content);

  // 如果用户消息少于2条，返回空数组（使用默认按钮）
  if (userMessages.length < 2) {
    return [];
  }

  // 提取最近3条用户消息的关键词
  const recentMessages = userMessages.slice(-3);
  const recommendations = [];
  const seen = new Set(); // 避免重复

  // 关键词匹配规则
  const keywordMap = {
    '感情': { text: '请结合我的资料，深入分析一下感情运势和桃花运。', icon: '💕', label: '深入分析感情' },
    '桃花': { text: '请结合我的资料，深入分析一下感情运势和桃花运。', icon: '💕', label: '深入分析感情' },
    '恋爱': { text: '请结合我的资料，深入分析一下感情运势和桃花运。', icon: '💕', label: '深入分析感情' },
    '财运': { text: '请结合我的资料，详细分析一下财运和投资机会。', icon: '💰', label: '详细分析财运' },
    '财富': { text: '请结合我的资料，详细分析一下财运和投资机会。', icon: '💰', label: '详细分析财运' },
    '事业': { text: '请结合我的资料，分析一下事业发展方向和机遇。', icon: '💼', label: '分析事业' },
    '工作': { text: '请结合我的资料，分析一下事业发展方向和机遇。', icon: '💼', label: '分析事业' },
    '学业': { text: '请结合我的资料，分析一下学业运势和考试运。', icon: '📚', label: '分析学业' },
    '考试': { text: '请结合我的资料，分析一下学业运势和考试运。', icon: '📚', label: '分析学业' },
    '健康': { text: '请结合我的资料，分析一下健康运势和注意事项。', icon: '🌱', label: '分析健康' },
    '身体': { text: '请结合我的资料，分析一下健康运势和注意事项。', icon: '🌱', label: '分析健康' },
    '婚姻': { text: '请结合我的资料，分析一下婚姻运势和配偶情况。', icon: '💍', label: '分析婚姻' },
    '家庭': { text: '请结合我的资料，分析一下家庭运势和家庭关系。', icon: '🏠', label: '分析家庭' }
  };

  // 从最近的消息中提取关键词
  for (const msg of recentMessages) {
    const content = msg.content || '';
    for (const [keyword, config] of Object.entries(keywordMap)) {
      if (content.includes(keyword) && !seen.has(config.label)) {
        recommendations.push(config);
        seen.add(config.label);
        if (recommendations.length >= 2) break; // 最多推荐2个
      }
    }
    if (recommendations.length >= 2) break;
  }

  return recommendations;
}

/**
 * 更新快速追问按钮
 */
function updateQuickBar() {
  const quickBar = document.getElementById('quickBar');
  if (!quickBar) return;

  // 获取个性化推荐
  const personalized = getPersonalizedQuickButtons();

  // ✅ 修复信息过载：进一步减少默认按钮数量（从3个减少到2个，不包括每日运势）
  // 每日运势已在引导面板中，快速追问只保留2个最常用的
  const defaultButtons = [
    { id: 'drawFortuneBtn', icon: '🎴', label: '每日运势', onclick: 'window.drawFortune()', isSpecial: true },
    { icon: '🌸', label: '测感情', text: '请结合我的资料，分析一下桃花与感情运势。' }
    // 只保留每日运势和测感情，进一步减少信息过载
    // 测财运可以通过引导面板的深度解读功能访问
  ];

  // 如果有个性化推荐，替换部分默认按钮
  let buttonsToShow = [...defaultButtons];
  if (personalized.length > 0) {
    // 保留每日运势按钮，然后用个性化推荐替换后面的按钮
    buttonsToShow = [
      defaultButtons[0], // 每日运势
      ...personalized.map(rec => ({
        icon: rec.icon,
        label: rec.label,
        text: rec.text,
        isPersonalized: true
      })),
      // 保留部分默认按钮（如果个性化推荐少于2个）
      ...defaultButtons.slice(1 + personalized.length)
    ];
  }

  // 生成HTML（转义单引号）
  // ✅ 修复：确保所有按钮都有aria-label
  const html = buttonsToShow.map(btn => {
    if (btn.isSpecial && btn.id === 'drawFortuneBtn') {
      // 每日运势按钮保持原样，添加aria-label
      return `<button id="${btn.id}" class="btn-quick" onclick="${btn.onclick}" aria-label="${btn.label}">${btn.icon} ${btn.label}</button>`;
    } else {
      // 普通按钮（转义单引号），添加aria-label
      const text = (btn.text || '').replace(/'/g, "\\'");
      const onclick = `document.getElementById('followupInput').value='${text}';window.followupAsk();`;
      const personalizedClass = btn.isPersonalized ? ' personalized-quick' : '';
      const ariaLabel = btn.label || '快速追问';
      return `<button class="btn-quick${personalizedClass}" onclick="${onclick}" aria-label="${ariaLabel}">${btn.icon} ${btn.label}</button>`;
    }
  }).join('');

  quickBar.innerHTML = html;

  debugLog('✅ 快速追问按钮已更新', { personalizedCount: personalized.length });
}
// 导出函数供外部调用
window.updateQuickBar = updateQuickBar;

/**
 * ✅ 第二步：创建灵魂伴侣转化引导卡片
 * 在分析结果后立即显示，引导用户试用灵魂伴侣功能
 */
function createSoulmateConversionCard() {
  const chatBox = document.getElementById('chatBox');
  if (!chatBox) {
    console.warn('⚠️ chatBox 不存在，无法创建转化引导卡片');
    return;
  }

  // 检查是否已经存在转化引导卡片
  const existingCard = chatBox.querySelector('#soulmate-conversion-card');
  if (existingCard) {
    debugLog('✅ 转化引导卡片已存在，跳过创建');
    return;
  }

  // 创建转化引导卡片
  const conversionCard = document.createElement('div');
  conversionCard.id = 'soulmate-conversion-card';
  conversionCard.className = 'conversion-card';
  conversionCard.style.cssText = `
    background: linear-gradient(135deg, rgba(255, 182, 193, 0.15) 0%, rgba(255, 192, 203, 0.15) 100%);
    border: 2px solid rgba(255, 182, 193, 0.4);
    border-radius: 16px;
    padding: 20px;
    margin: 20px 0;
    text-align: center;
    box-shadow: 0 6px 24px rgba(255, 182, 193, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.1) inset;
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
  `;

  conversionCard.innerHTML = `
    <div class="conversion-header" style="margin-bottom: 16px;">
      <span class="conversion-icon" style="font-size: 2rem; display: block; margin-bottom: 8px;">💝</span>
      <div class="conversion-title" style="font-size: 1.2rem; font-weight: bold; color: #333; margin-bottom: 8px;">
        想了解更多？和倪大师深度对话
      </div>
    </div>
    <div class="conversion-content">
      <!-- ✅ 移除：soulmate-trial-btn-2 按钮，因为主页面已有入口 -->
      <div class="vip-upgrade" style="
        padding-top: 12px;
      ">
        <div class="vip-label" style="font-size: 0.85rem; color: #666; margin-bottom: 10px; text-align: center;">
          💎 升级VIP：解锁全部功能
        </div>
        <div style="text-align: center;">
          <button class="btn-vip-upgrade" onclick="if(typeof showSharePopup === 'function') { showSharePopup(null, true, '升级VIP'); } else if(typeof showQRInPopup === 'function') { showQRInPopup('vip'); } else { debugLog('VIP升级功能未加载'); }" style="
            background: linear-gradient(135deg, #fbbf24 0%, #d97706 100%);
            color: white;
            border: none;
            padding: 10px 24px;
            border-radius: 18px;
            font-size: 0.9rem;
            font-weight: bold;
            cursor: pointer;
            box-shadow: 0 4px 12px rgba(251, 191, 36, 0.4);
            transition: all 0.3s ease;
          " onmouseover="this.style.transform='translateY(-2px)'; this.style.boxShadow='0 6px 20px rgba(251, 191, 36, 0.6)'" onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='0 4px 12px rgba(251, 191, 36, 0.4)'">
            升级VIP 5.9元/月
          </button>
        </div>
        <div style="margin-top: 12px; text-align: center; font-size: 0.8rem; color: #999;">
          或点击右下角 🧙 按钮开始免费试用
        </div>
      </div>
    </div>
  `;

  // 添加到聊天框
  chatBox.appendChild(conversionCard);

  // ✅ 移除：绑定试用按钮事件的代码（因为已移除按钮）

  // 滚动到底部
  timerManager.setTimeout(() => {
    if (typeof stickBottom === 'function') {
      stickBottom();
    } else {
      chatBox.scrollTop = chatBox.scrollHeight;
    }
  }, 100);

  debugLog('✅ 转化引导卡片已创建');
}

// 导出函数供外部调用
if (typeof window !== 'undefined') {
  window.createSoulmateConversionCard = createSoulmateConversionCard;
}